import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { SUPPORTED_LANGUAGE_CODES, DEFAULT_LANGUAGE, getInitialLanguage } from './constants/Languages';
import { legalTranslationsEn, legalTranslationsPt, legalTranslationsEs } from './i18n/legalTranslations';
import { prepaymentTranslationsEn, prepaymentTranslationsPt, prepaymentTranslationsEs } from './i18n/prepaymentTranslations';
import { servicePackageTranslationsEn, servicePackageTranslationsPt, servicePackageTranslationsEs } from './i18n/servicePackageTranslations';
import { consentFormTranslationsEn, consentFormTranslationsPt, consentFormTranslationsEs } from './i18n/consentFormTranslations';
import { stockTranslationsEn, stockTranslationsPt, stockTranslationsEs } from './i18n/stockTranslations';
import { expenseTranslationsEn, expenseTranslationsPt, expenseTranslationsEs } from './i18n/expenseTranslations';
import { dashboardTranslationsEn, dashboardTranslationsPt, dashboardTranslationsEs } from './i18n/dashboardTranslations';
import { vatTranslationsEn, vatTranslationsPt, vatTranslationsEs } from './i18n/vatTranslations';
import { revenueMoloniTranslationsEn, revenueMoloniTranslationsPt, revenueMoloniTranslationsEs } from './i18n/revenueMoloniTranslations';
import { freeServiceTranslationsEn, freeServiceTranslationsPt, freeServiceTranslationsEs } from './i18n/freeServiceTranslations';
import { servicePricingTranslationsEn, servicePricingTranslationsPt, servicePricingTranslationsEs } from './i18n/servicePricingTranslations';
import {
  servicePackageManagementTranslationsEn,
  servicePackageManagementTranslationsPt,
  servicePackageManagementTranslationsEs,
} from './i18n/servicePackageManagementTranslations';
import {
  serviceComboTranslationsEn,
  serviceComboTranslationsPt,
  serviceComboTranslationsEs,
} from './i18n/serviceComboTranslations';

const resources = {
  en: {
    translation: {
      brand: 'Salovare',
      language: 'Language',
      languages: {
        en: 'English',
        pt: 'Português',
        es: 'Español'
      },
      login: {
        email: 'Email Address',
        password: 'Password',
        signIn: 'Sign In',
        signingIn: 'Signing in...',
        required: 'Email and password are required',
        invalid: 'Invalid credentials',
        serverError: 'Unable to connect to the server. Please check your connection and try again.',
        adminQuestion: 'Admin?',
        teamMemberQuestion: 'Team Member?',
        or: 'OR',
        noAccount: "Don't have an account? Sign up",
        googleFailed: 'Google sign-in failed. Please try again.',
        registerStorePrompt: "Don't have an account?",
        registerStoreCta: 'Get started',
        storeRegistrationSuccess: 'Store created. Check your email to set your password and finish your account setup.',
        backToLogin: 'Back to Login',
        continue: 'Continue',
        continuing: 'Continuing...',
        emailRequired: 'Email is required',
        emailInvalid: 'Enter a valid email address',
        emailCheckFailed: 'Unable to verify this email right now. Please try again.',
        passwordRequired: 'Password is required',
        changeEmail: 'Use a different email',
        setPasswordPrompt: 'Your account exists, but you have not set a password yet. We can email you a link to create one.',
        sendSetPasswordLink: 'Send password setup link',
        phoneOnlyCta: 'Registered with phone only?',
        claim: {
          phone: 'Phone number',
          phoneRequired: 'Phone number is required',
          sendCode: 'Send verification code',
          sendingCode: 'Sending code...',
          codeSent: 'If this phone is registered without an email, a verification code has been sent.',
          code: 'Verification code',
          codeRequired: 'Enter the verification code',
          verify: 'Verify code',
          verifying: 'Verifying...',
          complete: 'Send password setup link',
          completing: 'Sending...',
          success: 'Password setup email sent. Check your inbox.',
          back: 'Back to email login',
          error: 'Could not continue. Please try again.',
        },
      },
      googleAuth: {
        continueWithGoogle: 'Continue with Google',
        continueWithGoogleSignUp: 'Sign up with Google',
      },
      forgotPassword: {
        forgotPassword: 'Forgot your password? Let\'s fix that!',
        emailRequired: 'Enter your email first to reset your password',
        sending: 'Sending...',
        success: 'Password reset link sent! Check your email.',
        error: 'An error occurred. Please try again.',
      },
      resetPassword: {
        title: 'Reset Your Password',
        email: 'Email Address',
        newPassword: 'New Password',
        confirmPassword: 'Confirm Password',
        resetPassword: 'Reset Password',
        resetting: 'Resetting...',
        backToLogin: 'Back to Login',
        missingToken: 'Missing reset token',
        missingEmail: 'Missing email address',
        required: 'Password and confirmation are required',
        passwordMismatch: 'Passwords do not match',
        passwordTooShort: 'Password must be at least 8 characters',
        passwordRequireDigit: 'Password must contain at least one digit',
        passwordRequireLowercase: 'Password must contain at least one lowercase letter',
        passwordRequireUppercase: 'Password must contain at least one uppercase letter',
        passwordRequireNonAlphanumeric: 'Password must contain at least one special character',
        passwordRequirements: 'Password must be at least 8 characters and contain uppercase, lowercase, digit, and special character',
        failed: 'Password reset failed. Please try again.',
        success: 'Password reset successful!',
        loginRedirect: 'Password reset successful! Please log in with your new password.',
      },
      setPassword: {
        title: 'Set Your Password',
        email: 'Email Address',
        newPassword: 'New Password',
        confirmPassword: 'Confirm Password',
        setPassword: 'Set Password',
        setting: 'Setting...',
        backToLogin: 'Back to Login',
        missingToken: 'Missing set password token',
        missingEmail: 'Missing email address',
        required: 'Password and confirmation are required',
        passwordMismatch: 'Passwords do not match',
        passwordTooShort: 'Password must be at least 8 characters',
        passwordRequireDigit: 'Password must contain at least one digit',
        passwordRequireLowercase: 'Password must contain at least one lowercase letter',
        passwordRequireUppercase: 'Password must contain at least one uppercase letter',
        passwordRequireNonAlphanumeric: 'Password must contain at least one special character',
        passwordRequirements: 'Password must be at least 8 characters and contain uppercase, lowercase, digit, and special character',
        failed: 'Password set failed. Please try again.',
        success: 'Password set successful!',
        loginRedirect: 'Password set successful! Please log in with your new password.',
      },
      firstAccess: {
        firstAccess: 'First access? Set your password!',
        emailRequired: 'Enter your email first to set your password',
        sending: 'Sending...',
        success: 'Password set link sent! Check your email.',
        error: 'An error occurred. Please try again.',
      },
      register: {
        title: 'Create Account',
        name: 'Full Name',
        email: 'Email',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        create: 'Create Account',
        creating: 'Creating Account...',
        haveAccount: 'Already have an account? Sign in',
        required: 'All fields are required',
        passwordMismatch: 'Passwords do not match',
        passwordTooShort: 'Password must be at least 6 characters',
        failed: 'Registration failed. Please try again.',
        success: 'Account created successfully! Redirecting...',
      },
      common: {
        cancel: 'Cancel',
        close: 'Close',
        back: 'Back',
        next: 'Next',
        continue: 'Continue',
        submit: 'Submit',
        submitting: 'Submitting...',
        loading: 'Loading',
        language: 'Language',
        navigation: 'Navigation',
        notifications: 'Notifications',
        closed: 'Closed',
        error: 'Error',
        unauthorized: 'You do not have permission to view this page.',
        noStores: 'No stores available',
        retry: 'Retry',
        refresh: 'Refresh',
        pullToReload: 'Pull to refresh',
        releaseToReload: 'Release to refresh',
        reloadingApp: 'Refreshing…',
        saving: 'Saving...',
        save: 'Save',
        delete: 'Delete',
        confirm: 'Confirm',
        filters: 'Filters',
        creating: 'Creating...',
        optional: 'Optional',
        selected: 'Selected',
        notProvided: 'Not provided',
        viewImage: 'View image',
        active: 'Active',
        inactive: 'Inactive',
        seeLess: 'See Less',
        previousTabs: 'Previous tabs',
        nextTabs: 'Next tabs',
        sending: 'Sending...',
        resend: 'Resend link',
        resendIn: 'Resend link in {{seconds}}s',
        resendError: 'Could not resend the link. Please try again.',
        backToLogin: 'Back to Login',
        languages: {
          pt: 'Portuguese',
          en: 'English',
          es: 'Spanish'
        },
        datePicker: {
          ok: 'OK',
          cancel: 'Cancel',
          clear: 'Clear',
          today: 'Today',
          previousMonth: 'Previous month',
          nextMonth: 'Next month',
          toolbarTitle: 'Select Date',
          openDatePickerDialogue: 'Choose date, selected date is {{value}}',
          openDatePickerDialogueEmpty: 'Choose date',
          closeDatePickerDialogue: 'Close date picker'
        },
        dateRange: {
          duration: 'Duration',
          from: 'From',
          to: 'To',
          endOptional: 'Leave the end date empty for no end date.',
        },
        yes: 'Yes',
        no: 'No',
        actions: 'Actions',
        moreOptions: 'More Options',
        contactSupport: 'App Support',
        contactStoreChat: 'Chat with the store',
        contactSupportSubject: 'Salovare support',
        contactSupportHint: 'Email us at {{email}} if you need help.',
        appVersion: 'Version {{version}} · {{slot}}',
        rowsPerPage: 'Rows per page:',
        of: 'of',
        all: 'All',
        apply: 'Apply',
        clearAll: 'Clear All',
        noFiltersAvailable: 'No filters available',
        days: {
          sunday: 'Sunday',
          monday: 'Monday',
          tuesday: 'Tuesday',
          wednesday: 'Wednesday',
          thursday: 'Thursday',
          friday: 'Friday',
          saturday: 'Saturday',
          sun: 'Sun',
          mon: 'Mon',
          tue: 'Tue',
          wed: 'Wed',
          thu: 'Thu',
          fri: 'Fri',
          sat: 'Sat'
        },
        minutes: 'min',
        hours: 'hr'
      },
      supportChat: {
        title: 'App Support',
        storeTitle: 'Chat with the store',
        placeholder: 'Type a message',
        send: 'Send',
        guestEmail: 'Your email',
        guestEmailHint: 'We need an email so we can reply to you.',
        guestEmailRequired: 'Enter your email to start the chat.',
        waiting: 'We received your message and will reply here.',
        closed: 'This chat is closed. Send a new message to reopen it.',
        closedPlaceholder: 'Send a message to reopen this chat',
        empty: 'Tell us what you need help with. We usually reply here in this chat.',
        storeEmpty: 'Ask the store a question. They usually reply here in this chat.',
        you: 'You',
        support: 'Support',
        store: 'Store',
        supportTyping: 'Support is typing',
        storeTyping: '{{name}} is typing',
        visitorTyping: '{{name}} is typing',
        typing: 'Typing…',
        sendFailed: 'Could not send the message. Please try again.',
        loadFailed: 'Could not load support chats.',
        inbox: 'App Support',
        inboxSubtitle: 'Pending and active chats from store staff.',
        storeInbox: 'Customer chat',
        storeInboxSubtitle: 'Pending and active chats from your customers.',
        noConversations: 'No staff chats yet.',
        storeNoConversations: 'No customer chats yet.',
        selectConversation: 'Select a conversation to reply.',
        closeConversation: 'Close chat',
        relatedAppointment: 'Appointment {{date}} at {{time}}',
        status: {
          Waiting: 'Waiting',
          Open: 'Open',
          Closed: 'Closed',
        },
      },
      notFound: {
        title: 'Page Not Found',
        message: 'The page you are looking for does not exist or the store link is invalid.',
        suggestion: 'To schedule an appointment, please:',
        contactStore: 'Contact the store directly to get the correct booking link.',
        goToHome: 'Go to main page',
        redirectCountdown: 'You will be redirected to the main page in {{seconds}} seconds.',
        storeTitle: 'Store not found',
        storeMessage: 'The store you are looking for may have changed its link.',
        storeContact: 'Contact the store to get the booking link.',
      },
      shortLink: {
        redirecting: 'Redirecting…',
      },
      navigation: {
        dashboard: 'Dashboard',
        appointments: 'Appointments',
        waitlist: 'Waitlist',
        services: 'Services',
        products: 'Products',
        membershipPlans: 'Membership Plans',
        customerMemberships: 'Customer Memberships',
        customers: 'Customers',
        teamMembers: 'Team Members',
        profile: 'Profile',
        store: 'Store',
        stores: 'Stores',
        billing: 'Billing',
        importData: 'Import data',
        communication: 'Communication',
        paymentOperations: 'Payment Operations',
        storesManagement: 'Stores Management',
        support: 'Support',
        appSupport: 'App Support',
        storeChat: 'Customer chat',
        planDiscountVouchers: 'Plan Discount Vouchers',
        employeePayroll: 'Payroll',
        employeePaymentSettings: 'Employee Payments',
        storeRegistration: 'Register Store',
        settings: 'Settings',
        logout: 'Logout'
      },
      management: {
        addToHomeScreen: {
          bannerTitle: 'Add Salovare to Home Screen',
          subtitle: 'Open this store faster from your phone',
          action: 'Install as app',
          iosGuideTitle: 'Add to Home Screen',
          iosFallback: 'Tap Share in Safari, then Add to Home Screen.',
          androidUnavailable: 'Open this page in Chrome to install the app.',
        },
      },
      dataImport: {
        title: 'Import data from Fresha',
        subtitle: 'Import in order: customers, services, team members, then appointments.',
        languageNotice: 'Fresha exports may use Portuguese or English column headers. Both formats are supported.',
        backToCustomers: 'Back to customers',
        errors: {
          loadProgress: 'Failed to load import progress.',
        },
        steps: {
          customers: 'Customers',
          teamMembers: 'Team Members',
          services: 'Services',
          appointments: 'Appointments',
        },
        comingSoon: {
          step: '{{step}} import will be available in a next step.',
        },
        customers: {
          helper: {
            title: 'How to export customers from Fresha',
            intro: 'Generate the CSV in Fresha, then upload it here.',
            step1: 'Open the Fresha clients list:',
            openFresha: 'partners.fresha.com/clients/list',
            step2: 'Click Options → Export: CSV.',
            step3: 'Upload the downloaded CSV file below.',
          },
          uploadButton: 'Upload Fresha CSV',
          selectedFile: 'Selected file: {{fileName}}',
          summary: '{{total}} rows · {{ready}} ready · {{failed}} failed · {{warnings}} warnings',
          failedTitle: 'Rows that need fixing',
          moreFailures: '+{{count}} more failing rows',
          fixFailingRows: 'Fix failing rows',
          downloadFailingRows: 'Download failing rows',
          importReadyRows: 'Import ready rows',
          importReadyConfirmTitle: 'Import ready rows only?',
          importReadyConfirmMessage: 'The {{failed}} row(s) that still need fixing will be ignored. Only the {{ready}} ready row(s) will be imported.',
          importReadyConfirmAction: 'Import ready rows',
          warningsNotice: '{{count}} optional field warning(s). Name and email are valid, so those rows can still be imported.',
          importButton: 'Import customers',
          importing: 'Importing...',
          alreadyCompleted: 'Customers were already imported for this store. You can continue to Services.',
          importSuccess: 'Import finished: {{created}} created, {{assigned}} assigned, {{alreadyInStore}} already in store.',
          fields: {
            name: 'Name',
            email: 'Email',
            phone: 'Phone',
            countryCode: 'Country code',
            genre: 'Gender',
            birthDate: 'Birth date',
          },
          genre: {
            male: 'Male',
            female: 'Female',
          },
          errors: {
            emptyFile: 'The CSV file is empty.',
            parseFailed: 'Could not read the CSV file.',
            importFailed: 'Customer import failed.',
            nameRequired: 'Name is required',
            emailRequired: 'Email is required',
            emailInvalid: 'Email is invalid',
            contactRequired: 'Email or phone is required',
          },
          warnings: {
            invalidPhone: 'Phone could not be imported',
            invalidGenre: 'Gender could not be imported',
            invalidBirthDate: 'Birth date could not be imported',
          },
          fixDialog: {
            title: 'Fix failing rows',
            selectRow: 'Failing row',
            rowLabel: 'Row {{row}} · {{name}}',
            rawRow: 'Full CSV row',
            originalValue: 'Original value: {{value}}',
            apply: 'Apply fix',
            noFailures: 'There are no failing rows left.',
            requiredSection: 'Required fields',
            requiredSectionHelp: 'These fields must be fixed before this row can be imported.',
            optionalSection: 'Optional fields',
            optionalSectionHelp: 'You can fix these now or leave them empty. Invalid values are treated as warnings only.',
          },
          mergeDialog: {
            title: 'Resolve duplicate customers',
            description: 'Some rows share the same name, email or phone. For each match, choose whether this is one account or two different accounts.',
            selectGroup: 'Jump to group',
            groupProgress: 'Duplicate group {{current}} of {{total}}',
            groupOption: '{{current}}/{{total}} · {{label}}',
            matches: 'Matching rows',
            matchesHelp: 'Compare the full details of each matching row below.',
            accountQuestion: 'Is this one account or two different accounts?',
            merge: 'One account',
            keepSeparate: 'Two different accounts',
            emailMustMerge: 'Rows with the same email must be one account, because email is unique.',
            primaryRow: 'Keep data from',
            primaryRowHelp: 'Select the row whose details should be kept on the merged customer account.',
            rowTitle: 'Row #{{row}}',
            freshaClientId: 'Fresha client ID',
            confirm: 'Continue import',
            autoResolve: 'Auto-resolve all duplicates',
            autoResolveHint: 'Skip reviewing each group and apply the recommended rules automatically.',
            autoResolveDialog: {
              title: 'Auto-resolve duplicates?',
              intro: 'This will finish the import without asking you to review each duplicate one by one.',
              willHappen: 'Here is what will happen when the same person appears more than once in the file:',
              sameEmailTitle: 'Same email:',
              sameEmailBody: 'They will be combined into one customer. We will keep the details from the first row that appears in the file.',
              sameNamePhoneTitle: 'Same name and same phone number:',
              sameNamePhoneBody: 'They will also be combined into one customer, using the details from the first matching row.',
              sameNameOnlyTitle: 'Same name, but different (or missing) phone:',
              sameNameOnlyBody: 'They will stay as separate customers, because they may be different people who simply share a name.',
              samePhoneOnlyTitle: 'Same phone, but different name:',
              samePhoneOnlyBody: 'They will stay as separate customers. Two people can share a phone number.',
              footer: 'If you prefer to choose manually for each duplicate, cancel and continue reviewing group by group.',
              confirm: 'Auto-resolve and import',
            },
            groupTypes: {
              email: 'Same email ({{key}}) · {{count}} rows',
              name: 'Same name ({{key}}) · {{count}} rows',
              phone: 'Same phone ({{key}}) · {{count}} rows',
              email_name: 'Same email and name ({{key}}) · {{count}} rows',
              email_phone: 'Same email and phone ({{key}}) · {{count}} rows',
              name_phone: 'Same name and phone ({{key}}) · {{count}} rows',
              email_name_phone: 'Same email, name and phone ({{key}}) · {{count}} rows',
            },
          },
        },
        services: {
          helper: {
            title: 'How to export services from Fresha',
            intro: 'Generate the service list CSV in Fresha, then upload it here.',
            step1: 'Open the Fresha services list:',
            openFresha: 'partners.fresha.com/setup/services',
            step2: 'Click Options → Export: CSV.',
            step3: 'Upload the downloaded CSV file below.',
          },
          uploadButton: 'Upload Fresha CSV',
          selectedFile: 'Selected file: {{fileName}}',
          summary: '{{total}} rows · {{ready}} ready · {{failed}} failed · {{warnings}} warnings · {{categories}} categories',
          failedTitle: 'Rows that need fixing',
          moreFailures: '+{{count}} more failing rows',
          fixFailingRows: 'Fix failing rows',
          downloadFailingRows: 'Download failing rows',
          importReadyRows: 'Import ready rows',
          importReadyConfirmTitle: 'Import ready rows only?',
          importReadyConfirmMessage: 'The {{failed}} row(s) that still need fixing will be ignored. Only the {{ready}} ready row(s) will be imported.',
          importReadyConfirmAction: 'Import ready rows',
          warningsNotice: '{{count}} optional field warning(s). Required fields are valid, so those rows can still be imported.',
          importButton: 'Import services',
          importing: 'Importing...',
          alreadyCompleted: 'Services were already imported for this store. You can continue to Team Members.',
          importSuccess: 'Import finished: {{created}} services created, {{updated}} updated, {{categoriesCreated}} categories created, {{categoriesUpdated}} categories updated.',
          fields: {
            serviceId: 'Service ID',
            name: 'Name',
            duration: 'Duration (minutes)',
            price: 'Price',
            description: 'Description',
            disabled: 'Disabled for online booking',
          },
          errors: {
            emptyFile: 'The CSV file is empty.',
            parseFailed: 'Could not read the CSV file.',
            importFailed: 'Service import failed.',
            serviceIdRequired: 'Service ID is required',
            nameRequired: 'Name is required',
            durationRequired: 'Duration is required',
            durationInvalid: 'Duration is invalid',
          },
          warnings: {
            missingPrice: 'Price was missing and defaulted to 0',
            invalidPrice: 'Price could not be parsed and defaulted to 0',
          },
          fixDialog: {
            title: 'Fix failing rows',
            rowLabel: 'Row {{row}} · {{name}}',
            apply: 'Apply fix',
            noFailures: 'There are no failing rows left.',
          },
        },
        teamMembers: {
          helper: {
            title: 'How to export team members from Fresha',
            intro: 'Generate the employees CSV in Fresha (English headers), then upload it here.',
            step1: 'Open the Fresha team members list:',
            openFresha: 'partners.fresha.com/team/members',
            step2: 'Click Options → Export: CSV.',
            step3: 'Upload the downloaded CSV file below.',
            englishOnly: 'This step currently supports English CSV column headers only.',
          },
          uploadButton: 'Upload Fresha CSV',
          selectedFile: 'Selected file: {{fileName}}',
          summary: '{{total}} rows · {{ready}} ready · {{failed}} failed · {{warnings}} warnings',
          failedTitle: 'Rows that need fixing',
          moreFailures: '+{{count}} more failing rows',
          fixFailingRows: 'Fix failing rows',
          downloadFailingRows: 'Download failing rows',
          importReadyRows: 'Import ready rows',
          importReadyConfirmTitle: 'Import ready rows only?',
          importReadyConfirmMessage: 'The {{failed}} row(s) that still need fixing will be ignored. Only the {{ready}} ready row(s) will be imported.',
          importReadyConfirmAction: 'Import ready rows',
          warningsNotice: '{{count}} optional field warning(s). Name and email are valid, so those rows can still be imported.',
          importButton: 'Import team members',
          importing: 'Importing...',
          alreadyCompleted: 'Team members were already imported for this store. You can continue to Appointments.',
          importSuccess: 'Import finished: {{created}} created, {{skipped}} skipped (email already exists).',
          skippedEmails: 'Skipped emails: {{emails}}',
          skippedEmailsMore: ' (+{{count}} more)',
          fields: {
            name: 'Name',
            email: 'Email',
            jobTitle: 'Job Title',
            permission: 'Permission / Role',
            disabled: 'Disabled (archived)',
          },
          roles: {
            staff: 'Staff (Basic)',
            manager: 'Manager (High)',
            full: 'Full (Owner)',
          },
          errors: {
            emptyFile: 'The CSV file is empty.',
            parseFailed: 'Could not read the CSV file.',
            unsupportedHeaders: 'Could not recognize the CSV headers. Export the employees file in English.',
            importFailed: 'Team member import failed.',
            nameRequired: 'Name is required',
            emailRequired: 'Email is required',
            emailInvalid: 'Email is invalid',
          },
          warnings: {
            invalidPhone: 'Phone could not be imported',
            invalidBirthDate: 'Birth date could not be imported',
            unknownStatus: 'Status was unknown and treated as Active',
            unknownPermission: 'Permission was unknown and mapped to Staff',
          },
          fixDialog: {
            title: 'Fix failing rows',
            rowLabel: 'Row {{row}} · {{name}}',
            apply: 'Apply fix',
            noFailures: 'There are no failing rows left.',
          },
        },
        appointments: {
          helper: {
            title: 'How to export appointments from Fresha',
            intro: 'Generate the appointments CSV in Fresha, then upload it here.',
            step1: 'Open the Fresha calendar / appointments export:',
            openFresha: 'partners.fresha.com/calendar',
            step2: 'Export appointments as CSV (Portuguese or English headers).',
            step3: 'Upload the downloaded CSV file below.',
            languageNote: 'Names are matched to customers, services and team members in this store. Unmatched rows can be fixed or skipped before import.',
          },
          uploadButton: 'Upload Fresha CSV',
          selectedFile: 'Selected file: {{fileName}}',
          summary: '{{total}} rows · {{appointments}} appointments · {{ready}} ready · {{failed}} failed · {{warnings}} warnings',
          failedTitle: 'Rows that need fixing',
          moreFailures: '+{{count}} more failing rows',
          fixFailingRows: 'Fix failing rows',
          downloadFailingRows: 'Download failing rows',
          importReadyRows: 'Import ready rows',
          importReadyConfirmTitle: 'Import ready rows only?',
          importReadyConfirmMessage: 'The {{failed}} row(s) that still need fixing will be ignored. Only the {{ready}} ready row(s) will be imported.',
          importReadyConfirmAction: 'Import ready rows',
          warningsNotice: '{{count}} optional field warning(s). Required fields are valid, so those rows can still be imported.',
          resolving: 'Matching names...',
          importButton: 'Import appointments',
          importing: 'Importing...',
          alreadyCompleted: 'Appointments were already imported for this store.',
          importSuccess: 'Import finished: {{created}} appointment(s) created.',
          skippedNotice: '{{count}} row(s) skipped and will not be imported.',
          fields: {
            customer: 'Customer',
            teamMember: 'Team member',
            service: 'Service',
            category: 'Category',
            status: 'Status',
            scheduledDate: 'Scheduled date',
            periodFrom: 'Start time',
            periodTo: 'End time',
            price: 'Price',
          },
          status: {
            new: 'New / Confirmed',
            completed: 'Completed',
            cancelled: 'Cancelled',
            noShow: 'No-show',
          },
          resolve: {
            walkIn: 'Walk-in (anonymous customer)',
            skipRow: 'Skip this row',
            searchCustomer: 'Search customer...',
            searchService: 'Search service...',
            searchTeamMember: 'Search team member...',
            importedValues: 'CSV values — customer: {{customer}} · service: {{service}} · team member: {{teamMember}}',
          },
          errors: {
            emptyFile: 'The CSV file is empty.',
            parseFailed: 'Could not read the CSV file.',
            unsupportedHeaders: 'Could not recognize the CSV headers. Use a Fresha appointments export in Portuguese or English.',
            importFailed: 'Appointment import failed.',
            noReadyRows: 'There are no rows ready to import.',
            referenceRequired: 'Appointment reference is required',
            customerRequired: 'Customer is required',
            teamMemberRequired: 'Team member is required',
            serviceRequired: 'Service is required',
            customerNotFound: 'Customer was not found in this store',
            serviceNotFound: 'Service was not found in this store',
            teamMemberNotFound: 'Team member was not found in this store',
            statusRequired: 'Status is required',
            statusUnknown: 'Status is unknown',
            dateRequired: 'Scheduled date is invalid or missing',
            slotRequired: 'Appointment time slot is invalid or missing',
          },
          warnings: {
            invalidPrice: 'Price could not be imported; catalog price will be used',
            invalidDuration: 'Duration could not be interpreted',
            trailingSpaces: 'Trailing spaces were trimmed from names',
          },
          fixDialog: {
            title: 'Fix failing rows',
            rowLabel: 'Row {{row}} · {{name}}',
            progress: 'Row {{current}} of {{total}} · CSV row {{row}} · {{name}}',
            next: 'Next',
            apply: 'Apply fix',
            noFailures: 'There are no failing rows left.',
          },
        },
      },
      communicationSettingsPage: {
        title: 'Communication',
        subtitle: 'Configure email, SMS and push notifications sent to customers for appointment events',
        emailAlways: 'Email always sent',
        emailLabel: 'Email',
        smsLabel: 'SMS',
        pushLabel: 'Push',
        smsPriceWarning: 'SMS messages are charged per message at {{price}}.',
        smsDependsOnEmail: 'Enable email for this reminder first',
        smsDisabledHelp: 'SMS billing is not active. Enable it in Billing settings.',
        smsBillingBanner: {
          text: 'SMS is not active for this store. Enable SMS billing to send text message notifications to your customers.',
          link: 'Go to Billing settings',
        },
        smsSenderName: {
          label: 'SMS Sender Name',
          help: 'This name appears as the sender on SMS messages. Leave blank to use your store name (truncated to 15 characters). Letters, numbers and spaces only. Note: some carriers may truncate names longer than 11 characters.',
          placeholder: 'Store name',
          error: {
            invalid: 'Sender name must start with a letter or number and contain only letters, numbers and spaces.',
          },
        },
        confirmation: {
          title: 'Appointment Confirmation',
          description: 'Email is always sent when an appointment is booked. Optionally also send SMS and push notifications.',
        },
        cancellation: {
          title: 'Appointment Cancellation',
          description: 'Email is always sent when an appointment is cancelled. Optionally also send SMS and push notifications.',
        },
        reminders: {
          title: 'Appointment Reminders',
          description: 'Choose email, SMS and push notifications independently for each reminder window. SMS follows email — it can only be enabled when email is on.',
        },
        reminder7Day: '7 days before',
        reminder1Day: '1 day before',
        reminder2Hour: '2 hours before',
        googleReviewAsk: {
          title: 'Google review ask',
          description: 'Optional thank-you sent about 2 hours after a completed visit, asking the customer to leave a Google review. Choose email, SMS or push; SMS uses the same billing rules as other appointment texts.',
          missingUrl: 'Add a Google Review URL in store details before turning this on.',
          editStore: 'Edit store details',
        },
        success: {
          save: 'Communication settings saved.',
        },
        error: {
          load: 'Failed to load communication settings.',
          save: 'Failed to save communication settings.',
        },
        tabs: {
          notifications: 'Notifications',
          waitlist: 'Waitlist',
        },
        waitlist: {
          title: 'Waitlist',
          subtitle: 'Choose how the store handles customers waiting for a preferred time.',
          typeLabel: 'Waitlist type',
          type: {
            Manual: 'Manual',
            Auto: 'Auto',
          },
          typeHelp: {
            Manual: 'Staff are notified and pick who gets the opened slot. Customers are not emailed automatically.',
            Auto: 'Matching customers are emailed after a short delay so staff can take the slot first.',
          },
          priorityLabel: 'Waitlist priority',
          priority: {
            FirstInLine: 'First in line',
            HighestValue: 'Highest value',
            NotifyAll: 'Notify all',
          },
          priorityHelp: {
            FirstInLine: 'Offer the slot to the customer who joined first.',
            HighestValue: 'Offer the slot first to the customer with the highest estimated amount due.',
            NotifyAll: 'Email every matching customer at once. The first to book gets the slot.',
          },
        },
      },
      waitlistQueuePage: {
        title: 'Waitlist',
        subtitle: 'Customers waiting for a preferred appointment time',
        empty: 'No one is on the waitlist right now.',
        book: 'Book',
        remove: 'Remove',
        removeTitle: 'Remove from waitlist',
        removeMessage: 'Remove {{name}} from the waitlist?',
        success: {
          remove: 'Customer removed from the waitlist.',
        },
        error: {
          load: 'Failed to load the waitlist.',
          remove: 'Failed to remove this customer from the waitlist.',
        },
        columns: {
          customer: 'Customer',
          services: 'Services',
          preferences: 'Preferred times',
          joined: 'Joined',
          value: 'Estimated value',
          actions: 'Actions',
        },
      },
      waitlistOfferPage: {
        title: 'A slot opened for you',
        book: 'Book',
        decline: 'Decline',
        duration: '{{minutes}} min',
        success: 'Your appointment is booked.',
        declined: 'You declined this slot.',
        alreadyBooked: 'This time slot is no longer available.',
        claimHint: 'You can return to your appointments from the store menu.',
        error: {
          load: 'Could not load this waitlist offer.',
          claim: 'Could not book this slot. It may already be taken.',
        },
      },
      waitlistAlert: {
        joinedTitle: 'Customer joined the waitlist',
        slotOpenedTitle: 'A matching slot opened',
        customer: 'Customer',
        services: 'Services',
        duration: 'Duration',
        durationValue: '{{minutes}} min',
        value: 'Estimated value',
        viewWaitlist: 'View waitlist',
        book: 'Book',
      },
      appointmentConfirmationAlert: {
        title: 'On-request appointment',
        customer: 'Customer',
        confirm: 'Confirm',
        decline: 'Decline',
        viewAppointment: 'View on calendar',
        openToAny: 'Open to any available professional until someone confirms.',
      },
      stripeCheckout: {
        title: 'Complete your payment',
        error: 'Failed to load the payment form. Please try again.'
      },
      paymentSettingsPage: {
        title: 'Payment Settings',
        subtitle: 'Manage your Salovare subscription and customer payment settings',
        tabs: {
          subscription: 'Salovare Subscription',
          customerPayments: 'Customer Payments',
          notifications: 'Notifications'
        },
        error: {
          loading: 'Failed to load payment settings.',
          unauthorized: 'You do not have permission to manage payment settings for this store.'
        },
        subscription: {
          title: 'Salovare subscription',
          description: 'Manage how your store pays for Salovare. Upgrade to Pro or update your billing details from here.',
          loading: 'Loading subscription status...',
          statusActive: 'Subscription billing is set up',
          statusActiveDescription: 'Your billing details are on file. Use the billing portal to update your payment method or review invoices.',
          statusActionRequired: 'Billing attention required',
          billingDetailsOnFile: 'Billing details on file',
          billingDetailsPending: 'Billing details pending',
          trialPlan: 'Free trial',
          trialUntil: 'Free trial until {{date}}',
          priceAfterTrial: '{{price}}/month for the base subscription after {{date}}',
          nextBillingOn: 'Next billing on {{date}}',
          priceOnNextBilling: '{{price}}/month for the base subscription on {{date}}',
          discountOnNextPayment: '{{percent}}% plan discount on your next payment',
          discountApplied: '{{percent}}% plan discount applied',
          cancelInstructions: 'You can cancel anytime from the billing portal. You will not be charged after the current period ends if you cancel.',
          cancelAction: 'Cancel subscription',
          cancelTitle: 'Cancel subscription',
          cancelMessage: 'Your subscription will remain active until {{date}}. Your final invoice will include subscription and SMS usage incurred until that date.',
          cancelConfirm: 'Cancel at period end',
          cancelError: 'Failed to cancel subscription.',
          cancelScheduled: 'Cancellation scheduled. Your subscription remains active until {{date}}.',
          teamMemberSeats: '{{active}} active team members ({{included}} included)',
          additionalTeamMemberSeats: '{{count}} additional team member(s) at {{price}}/month each',
          billedAdditionalSeats: '{{count}} additional billed seat(s) at {{price}}/month each',
          additionalStores: '{{count}} additional store(s) at {{price}}/month each',
          seatBreakdownRow: '{{store}}: {{count}} extra professional(s)',
          unnamedStore: 'Unnamed store',
          estimatedMonthlyTotal: 'Estimated monthly total: {{price}}'
        },
        customerPayments: {
          error: {
            load: 'Failed to load customer payment settings.',
            save: 'Failed to save customer payment settings.'
          },
          success: {
            save: 'Customer payment settings saved.'
          },
          step1: {
            title: '1. Set up Stripe',
            ready: 'Stripe is connected and ready to accept payments.',
            refresh: 'Re-check payment methods',
            manageOnStripe: 'Manage on Stripe'
          },
          step2: {
            title: '2. Choose payment methods',
            description: 'These are the payment methods Stripe has made available for your store. Card is always accepted; turn on any others you want to offer customers.',
            empty: 'Finish setting up Stripe above to see the payment methods available for your store.'
          },
          step3: {
            title: '3. Enable online payment',
            needsOnboarding: 'Finish setting up Stripe above before enabling online payments.',
            services: 'Services',
            servicesHelp: 'Includes any products added to the booking.',
            memberships: 'Memberships'
          },
          methods: {
            card: 'Card & Google Pay',
            klarna: 'Klarna (installments)',
            mbWay: 'MB WAY',
            pix: 'Pix',
            scalapay: 'Scalapay (installments)',
            sequra: 'SeQura (installments)'
          },
          fees: {
            checkFee: 'Check fee',
            title: '{{method}} fee',
            exampleService: 'Haircut & Styling Service',
            processingFee: 'Online Payment Processing Fee ({{rate}})',
            youReceive: 'You Receive',
            disclaimer: 'Salovare adds no fee on top of Stripe. The exact rate can vary with the card issuer and currency conversion — see',
            stripePricingLink: 'Stripe pricing'
          }
        },
        sms: {
          title: 'SMS notifications',
          description: 'Send appointment confirmation and reminder SMS to customers at {{price}} per message, billed on your subscription cycle.',
          loading: 'Loading SMS settings...',
          enableLabel: 'Enable SMS notifications',
          enableHelp: 'Usage is billed monthly with a spending cap.',
          enableHelpTrialBlocked: 'SMS is not available on trial unless Salovare support enables it for your store.',
          enableHelpNeedsStripe: 'Connect Stripe billing first, then you can enable SMS on trial.',
          trialBlocked: 'SMS is not enabled during trial by default. Email {{email}} to request access.',
          trialNeedsStripe: 'SMS was approved for your trial. Connect Stripe billing first to enable it.',
          emailSupport: 'Email support',
          supportEmailSubject: 'Request SMS during free trial',
          supportEmailBody: 'Hello Salovare support,\n\nI would like to request SMS notifications during my free trial.\n\nStore ID: {{storeId}}\n\nThank you.',
          usageSummary: 'Current period usage: {{spent}} / {{limit}} (resets on {{date}})',
          limitReached: 'You have reached your SMS limit of {{limit}}. Pay the outstanding invoice in the billing portal to continue, or wait until {{date}} when the limit resets.',
          paymentFailed: 'SMS is disabled because a recent invoice payment failed. Update your payment method in the billing portal.',
          openBillingPortal: 'Open billing portal',
          error: {
            update: 'Failed to update SMS settings.'
          }
        }
      },
      paymentModes: {
        inStoreOnly: 'In-store only',
        onlineOnly: 'Online only',
        both: 'Online and in-store'
      },
      storePaymentMethods: {
        label: 'Payment method',
        placeholder: 'Select payment method',
        cash: 'Cash',
        card: 'Card',
        transfer: 'Transfer',
        mbWay: 'MB WAY',
        other: 'Other',
        online: 'Online'
      },
      appointmentCheckout: {
        amountDue: 'Amount due',
        amountReceived: 'Amount received',
        amountReceivedHint: 'You can collect less than the amount due.',
        membershipFee: 'Membership fee',
        totalCollected: 'Total collected: {{amount}}',
        remaining: 'Remaining: {{amount}}',
        discount: 'Discount: {{amount}}',
        addPayment: 'Add payment',
        removePayment: 'Remove payment',
        paymentLine: 'Payment {{number}}',
        receivedSummary: '{{amount}} will be recorded in cash flow.',
        errors: {
          amountRange: 'Amount received must be between 0 and the amount due.',
          paymentMethodRequired: 'Select a payment method.',
          duplicateMethod: 'Each payment method can only be used once.',
          amountRequired: 'Enter an amount greater than zero.',
          membershipCoverRequired: 'Collected amount must cover the membership fee.',
          fullAmountRequired: 'Collected amount must equal the amount due.'
        }
      },
      customerMembershipsManagement: {
        title: 'Customer Memberships',
        subtitle: 'View memberships, enroll customers in-store, and mark billing periods as paid.',
        error: {
          noStoreSelected: 'Select a store to manage customer memberships.',
          loading: 'Failed to load customer memberships.'
        },
        empty: {
          title: 'No customer memberships yet',
          subtitle: 'Enroll a customer in-store or wait for online subscriptions.'
        },
        table: {
          customer: 'Customer',
          plan: 'Plan',
          status: 'Status',
          channel: 'Payment channel',
          period: 'Current period',
          price: 'Price',
          actions: 'Actions',
          unnamedCustomer: 'Unnamed customer',
          periodRange: '{{start}} – {{end}}',
          noPeriod: 'No active period'
        },
        paymentChannel: {
          online: 'Online',
          inStore: 'In-store',
          unknown: 'Unknown'
        },
        enroll: {
          action: 'Enroll customer',
          title: 'Enroll in-store membership',
          description: 'Create an in-store membership for an existing customer. The first billing period must be marked as paid before entitlements are active.',
          customerId: 'Customer ID',
          customer: 'Customer',
          customerPlaceholder: 'Search by name or email',
          noCustomers: 'No customers found',
          plan: 'Membership plan',
          planPlaceholder: 'Select a plan',
          confirm: 'Enroll',
          enrolling: 'Enrolling...',
          success: 'Customer enrolled successfully.',
          error: 'Failed to enroll customer.',
          errors: {
            customerIdRequired: 'Customer ID is required.',
            customerRequired: 'Select a customer.',
            planRequired: 'Select a membership plan.',
            paymentMethodRequired: 'Select a payment method.'
          }
        },
        markPaid: {
          action: 'Mark period paid',
          payNextPeriod: 'Pay next period',
          title: 'Mark period paid',
          payNextTitle: 'Pay next period',
          description: 'Record payment for this billing period.',
          payNextDescription: 'Collect payment for the next billing period. Current entitlements stay until this period ends.',
          amount: 'Amount',
          marking: 'Marking...',
          success: 'Billing period marked as paid.',
          payNextSuccess: 'Next billing period paid.',
          error: 'Failed to mark billing period as paid.'
        },
        cancel: {
          action: 'Cancel membership',
          canceling: 'Canceling...',
          resume: 'Keep membership',
          resuming: 'Updating...',
          confirmTitle: 'Cancel membership',
          confirmMessage: 'The customer can keep using this membership until {{date}} — the last period already paid. After that, it will be blocked. Continue?',
          success: 'The membership stays active until {{date}}. After that, it will be cancelled.',
          error: 'Failed to cancel this membership.',
          resumeSuccess: 'This membership will continue as usual.',
          resumeError: 'Failed to keep this membership active.',
          cancelsOn: 'Cancels on {{date}}'
        }
      },
      customersManagement: {
        title: 'Customers',
        subtitle: 'View and add customers assigned to this store.',
        error: {
          noStoreSelected: 'Select a store to manage customers.',
          loading: 'Failed to load customers.'
        },
        empty: {
          title: 'No customers yet',
          subtitle: 'Add your first customer to start building your store directory.',
          noResultsTitle: 'No matching customers',
          noResultsSubtitle: 'Try a different name, email, or phone number.'
        },
        dropdown: {
          searchPlaceholder: 'Search by name, email or phone',
          noOptions: 'No results'
        },
        table: {
          customer: 'Customer',
          email: 'Email',
          phone: 'Phone',
          genre: 'Gender',
          birthDate: 'Birth date',
          addedOn: 'Added on'
        },
        genre: {
          male: 'Male',
          female: 'Female',
          other: 'Other'
        },
        create: {
          title: 'Add customer',
          subtitle: 'Check email or phone, then create a new customer or add an existing one to this store.',
          action: 'Create customer',
          assign: 'Add to store',
          email: 'Email',
          name: 'Name',
          phone: 'Phone',
          emailPlaceholder: 'Enter email address',
          namePlaceholder: 'Enter customer name',
          phonePlaceholder: 'Enter phone number',
          emailOptional: 'Optional if a phone number is provided',
          phoneOptional: 'Optional if an email is provided',
          emailCheck: {
            check: 'Check',
            available: 'This email is available. Enter the customer name below.',
            existingTitle: 'Customer already registered',
            existingMessage: '{{name}} ({{email}}) is already registered in Salovare. Do you want to add this customer to your store? Their existing profile will not be changed.',
            existingConfirm: 'Add to this store',
            existingConfirmed: '{{name}} is already registered. You can add them to this store without changing their profile.',
            alreadyInStore: 'This customer is already assigned to this store.',
            error: 'Could not verify this email. Please try again.'
          },
          phoneCheck: {
            available: 'This phone number is available. Enter the customer name below.',
            existingConfirmed: '{{name}} is already registered with this phone number. You can add them to this store without changing their profile.',
            alreadyInStore: 'This customer is already assigned to this store.',
            error: 'Could not verify this phone number. Please try again.',
            required: 'Please check this phone number before continuing.'
          },
          success: {
            created: 'Customer created successfully! Password setup email sent.',
            createdPhoneOnly: 'Customer created successfully. They can add an email the first time they sign in.',
            assigned: 'Customer added to this store successfully.'
          },
          error: {
            creation: 'Failed to add customer.'
          },
          validation: {
            nameRequired: 'Name is required.',
            emailRequired: 'Email is required.',
            emailInvalid: 'Please enter a valid email address.',
            emailCheckRequired: 'Please check this email before continuing.',
            contactRequired: 'Email or phone is required.',
            phoneRequired: 'Phone number is required when email is empty.'
          }
        },
        details: {
          title: 'Customer details',
          subtitle: 'Store-specific profile, care notes, and visit metrics.',
          edit: 'Edit',
          bookAppointment: 'Book appointment',
          storeOverride: 'Store override',
          usingAccount: 'Using account details',
          loading: 'Failed to load customer details.',
          notFound: 'This customer is not assigned to this store.',
          saveSuccess: 'Store customer profile saved.',
          saveError: 'Failed to save store customer profile.',
          metrics: {
            frequency: 'Visit frequency',
            lastVisit: 'Last visit {{date}}',
            noVisits: 'No completed visits yet',
            noShows: 'No-shows',
            lifetimeValue: 'Amount spent',
            lastThreeMonths: 'Last 3 months',
            productsSpend: 'Products spend'
          },
          contact: {
            title: 'Contact',
            name: 'Name',
            email: 'Email',
            phone: 'Phone'
          },
          upcoming: {
            title: 'Upcoming appointments',
            empty: 'No upcoming appointments',
            error: 'Failed to load upcoming appointments.'
          },
          consultation: {
            title: 'Care & preferences',
            empty: 'No care notes yet. Add allergies, medical conditions, medications, and pregnancy or nursing status so the team can treat this customer safely.',
            allergies: 'Allergies & sensitivities',
            medical: 'Medical conditions',
            medications: 'Medications',
            pregnancy: 'Pregnancy / nursing status'
          },
          editDialog: {
            title: 'Customer info',
            storeViewOnly: 'This change applies to this store only.',
            accountProfile: 'Saved to the customer profile.',
            customerProvided: 'Provided by the customer and cannot be changed.',
            selectGenre: 'Select gender',
            name: 'Name',
            namePlaceholder: 'Name',
            email: 'Email',
            emailPlaceholder: 'Email',
            phone: 'Phone',
            phonePlaceholder: 'Phone',
            allergiesHelper: 'Skin allergies, latex, nuts (common in massage oils), or hair dye reactions.',
            medicalHelper: 'Diabetes (affects healing in pedicures/microblading), epilepsy (flashing lights/lasers), or circulatory issues.',
            medicationsHelper: 'Blood thinners, Accutane/Retin-A (makes skin tear easily during waxing or facials).',
            pregnancyHelper: 'Certain essential oils, electrical currents, and chemicals are unsafe.',
            save: 'Save',
            emailInvalid: 'Please enter a valid email address.'
          }
        },
        import: {
          action: 'Import',
          title: 'Import customers',
          pickSubtitle: 'Upload a CSV or vCard file exported from your phone or email contacts.',
          uploadButton: 'Upload CSV or vCard',
          selectedFile: 'Selected: {{fileName}}',
          mappingTitle: 'Map columns',
          mappingSubtitle: 'Drag a file column onto a Salovare field. Mapping is used only for this import.',
          mappingSelectSubtitle: 'Choose a file column for each Salovare field. Mapping is used only for this import.',
          mappingNone: "Don't import",
          fileColumns: 'File columns',
          mapping: 'Mapping',
          salovareFields: 'Salovare fields',
          continue: 'Continue',
          fields: {
            name: 'Name',
            firstName: 'First name',
            lastName: 'Last name',
            email: 'Email',
            phone: 'Phone',
            birthDate: 'Birth date',
            genre: 'Gender'
          },
          errors: {
            emptyFile: 'The file has no contacts to import.',
            parseFailed: 'Could not read this file. Use a .csv or .vcf export.',
            unsupportedType: 'Use a .csv or .vcf file.',
            noRows: 'No contacts were found after mapping.',
            importFailed: 'Failed to import customers.'
          },
          success: 'Imported {{created}} new customers. {{assigned}} already existed and were added to this store. {{alreadyInStore}} were already in this store.'
        },
        fallback: {
          message: 'Please select a store from the top menu to manage customers.',
          goToStores: 'Go to Stores'
        }
      },
      employeePayments: {
        settings: {
          title: 'Employee Payment Settings',
          subtitle: 'Configure store margin and employee pay-day for services and memberships',
          readOnlyHint: 'You can view these settings but only managers can update them.',
          margin: {
            title: 'Store margin',
            description: 'Percentage the store keeps from subscription payments received each calendar month. The remainder forms the monthly subscription pool shared among team members based on service duration points from completed membership appointments. If no membership services were completed that month, the pool is split equally among active team members.',
            label: 'Store margin (%)'
          },
          payDay: {
            title: 'Employee pay-day',
            description: 'Day of each month when employee compensation is recognized. Subscription pool shares are paid on the first pay-day after the calendar month ends.',
            label: 'Pay-day of month',
            option: 'Day {{day}}'
          },
          error: {
            loading: 'Failed to load employee payment settings.',
            save: 'Failed to save employee payment settings.',
            marginRange: 'Store margin must be between 0 and 100.'
          },
          success: {
            saved: 'Employee payment settings saved successfully.'
          }
        },
        payroll: {
          title: 'Employee Payroll',
          subtitle: 'Preview compensation for all team members in the selected period',
          subtitleOwn: 'Preview your compensation for the selected period',
          referencePeriod: 'Reference period',
          teamMemberFilter: 'Team member',
          teamMemberFilterPlaceholder: 'Select team members',
          filters: {
            title: 'Filters',
            view: 'View report',
            edit: 'Edit filters',
            expand: 'Expand filters',
            collapse: 'Collapse filters',
            summary: '{{period}} · {{members}}',
            customRangeSummary: '{{from}} – {{to}}',
          },
          periodPresets: {
            nextMonth: 'Next month',
            today: 'Today',
            thisWeek: 'This week',
            lastWeek: 'Last week',
            thisMonth: 'This month',
            lastMonth: 'Last month',
            last3Months: 'Last 3 months',
            thisYear: 'This year',
            custom: 'Custom range',
          },
          year: 'Year',
          month: 'Month',
          empty: 'No payroll data found for this period.',
          emptyService: 'No service payroll data found for this period.',
          emptyMembership: 'No membership share payouts found for this period.',
          estimatedPeriodAlert: 'Figures for next month are estimates and update as appointments and membership payments are recorded.',
          sections: {
            cashFlow: 'Cash flow',
            service: 'Services & salaries',
            membership: 'Membership shares',
            membershipHint: 'Subscription pool shares are paid monthly on the configured membership pay day, always in the month after the reference month.',
          },
          amountTone: {
            keep: 'Larger is money kept',
            cost: 'Larger is money paid out',
          },
          profit: {
            title: 'Profit',
            amount: 'Profit',
            hint: 'Store retention after salaries and commissions, plus the subscription margin the store keeps, minus posted expenses for this period.',
            filtered: 'Profit is for the whole store. Clear the team member filter to see it.',
          },
          cashFlow: {
            hint: 'Money received by the store in this period. Team member filters do not apply.',
            totalReceived: 'Total received',
            cashier: 'Cashier',
            bank: 'Bank',
            other: 'Other',
            sliceDetail: '{{amount}} · {{percent}} of total',
            tapSlice: 'Tap a slice to see the amount and its share of the total.',
          },
          membershipTotals: {
            gross: 'Subscription gross',
            storeMargin: 'Store margin',
            barberPool: 'Team pool',
            totalSharesPaid: 'Total shares paid',
          },
          membershipTable: {
            referenceMonth: 'Reference month',
            shareAmount: 'Share amount',
            totalShare: 'Total share',
          },
          unknownMember: 'Team member',
          estimated: 'Estimated',
          minimumApplied: 'Minimum applied',
          noBreakdown: 'No revenue lines recorded for this period.',
          error: {
            loading: 'Failed to load payroll report.',
            customRangeRequired: 'Select both start and end dates for a custom range.',
            customRangeOrder: 'Start date cannot be after end date.',
            customRangeMax: 'Custom range cannot exceed one year.',
          },
          monthNames: {
            1: 'January',
            2: 'February',
            3: 'March',
            4: 'April',
            5: 'May',
            6: 'June',
            7: 'July',
            8: 'August',
            9: 'September',
            10: 'October',
            11: 'November',
            12: 'December'
          },
          totals: {
            grossBilled: 'Total service revenue',
            productsGrossSold: 'Product sales',
            productCommissionsPaid: 'Product commissions paid',
            salaries: 'Total payout',
            storeGain: 'Store retention after payroll',
          },
          table: {
            member: 'Team member',
            mode: 'Payment mode',
            commissionRate: 'Commission rate',
            fixedSalary: 'Fixed salary',
            commission: 'Commission',
            productsSold: 'Products sold',
            productCommission: 'Product commission',
            total: 'Total payout',
            details: 'Details',
            viewAppointments: 'View appointments',
            actions: 'Actions',
            editCompensation: 'Edit compensation settings'
          },
          breakdown: {
            source: 'Source',
            recognitionDate: 'Recognition date',
            serviceRevenue: 'Service revenue',
            extraBilled: 'Extra billed',
            employeeShare: 'Professional share',
          },
          sourceTypes: {
            appointmentService: 'Appointment service',
            appointmentAdditionalCost: 'Additional time cost',
            productSale: 'Product sale',
            membershipMonthlyShare: 'Subscription monthly share',
            membershipPeriodUsageShare: 'Subscription period usage share',
          },
          membershipPot: {
            title: 'Subscription pool — {{month}} {{year}}',
            subtitle: 'All subscription payments received in this calendar month, after store margin, shared by duration points.',
            gross: 'Subscription gross',
            storeMargin: 'Store margin',
            barberPool: 'Team pool',
            totalPoints: 'Total duration points: {{points}} min',
            member: 'Team member',
            points: 'Points (min)',
            sharePercent: 'Share %',
            shareAmount: 'Share amount',
            viewPot: 'View pool',
            error: {
              loading: 'Failed to load subscription pool details.',
            },
          },
          membershipPeriod: {
            title: 'Usage-based subscription payouts',
            subtitle: 'Closed billing periods paid on the store pay day, split by completed visits and original service prices.',
            gross: 'Subscription gross',
            storeMargin: 'Store margin',
            barberPool: 'Team pool',
            unallocated: 'Kept by store',
            empty: 'No closed membership periods were paid in this range.',
            periodDates: '{{from}} – {{to}}',
            member: 'Team member',
            redemptions: 'Visit share',
            shareAmount: 'Share amount',
            keptByStore: 'Kept by store',
            error: {
              loading: 'Failed to load usage-based membership payouts.',
            },
          },
          appointmentDetails: {
            title: 'Appointments — {{name}}',
            subtitle: '{{from}} to {{to}}',
            empty: 'No attributed appointments found for this period.',
            date: 'Date',
            customer: 'Customer',
            appointmentId: 'Appointment ID',
            services: 'Executed services',
            totalPrice: 'Total price',
            additionalCost: 'Additional time cost',
            employeeShare: 'Professional share',
            teamMemberShare: 'Team member share',
            storeMargin: 'Store margin retained',
            monthAppointmentCount: '{{count}} appointments',
            error: {
              loading: 'Failed to load appointment details.',
            },
          },
        },
        compensation: {
          title: 'Compensation Settings',
          subtitle: 'Configure how this team member is paid',
          editTitle: 'Compensation — {{name}}',
          paymentMode: 'Payment mode',
          fixedMonthlySalary: 'Fixed monthly salary',
          commissionRatePercent: 'Commission rate',
          minimumMonthlySalary: 'Minimum monthly salary',
          effectiveFrom: 'Effective from',
          effectiveFromHint: 'Past dates are allowed (up to 5 years). Saving recalculates payroll for paid appointments from this date.',
          payScheduleTitle: 'Pay schedule',
          payScheduleHint: 'Monthly pay is on the configured day of the following month. Weekly pay is on the configured weekday of the week after the Mon–Sun work week.',
          payCadence: 'Pay cadence',
          payDayOfMonth: 'Pay day of month',
          payDayOfWeek: 'Pay day of week',
          payDayOption: 'Day {{day}}',
          payCadenceOptions: {
            monthly: 'Monthly',
            weekly: 'Weekly',
          },
          deductions: {
            title: 'Deductions',
            discounts: 'Discounts',
            discountsHelpTitle: 'How discounts affect commission',
            discountsHelpAriaLabel: 'How discounts affect commission',
            discountsHelpOff: 'Leave this off if they should earn commission on the full service price, even when the customer paid less.',
            discountsHelpOn: 'Turn it on if they should earn commission only on what the customer actually paid.',
            discountsHelpExample: 'Example: a €30 haircut with a €10 discount. Off = commission on €30. On = commission on €20.',
          },
          weekdays: {
            monday: 'Monday',
            tuesday: 'Tuesday',
            wednesday: 'Wednesday',
            thursday: 'Thursday',
            friday: 'Friday',
            saturday: 'Saturday',
            sunday: 'Sunday',
          },
          readOnlyHint: 'You can view compensation settings but only managers can update them.',
          modes: {
            fixedSalaryOnly: 'Fixed salary only',
            commissionOnly: 'Commission only',
            mixed: 'Fixed salary + commission',
            commissionWithMinimum: 'Commission with minimum guarantee',
            unknown: 'Unknown'
          },
          error: {
            loading: 'Failed to load compensation settings.',
            save: 'Failed to save compensation settings.',
            fixedRequired: 'Fixed monthly salary is required for this payment mode.',
            commissionRange: 'Commission rate must be between 0 and 100.',
            minimumRequired: 'Minimum monthly salary is required for this payment mode.',
            effectiveFromRequired: 'Effective from date is required.',
            payDayOfMonthRange: 'Pay day of month must be between 1 and 28.',
            payDayOfWeekRequired: 'Pay day of week is required for weekly pay cadence.',
          },
          success: {
            saved: 'Compensation settings saved successfully.'
          }
        }
      },
      storesManagement: {
        title: 'Stores Management',
        subtitle: 'Manage stores and plan discount vouchers.',
        tabs: {
          stores: 'Stores',
          vouchers: 'Discount Vouchers',
        },
        stores: {
          description: 'Allow SMS during free trial for stores that requested it. Owners still need Stripe connected and must enable SMS themselves.',
          empty: 'No stores found.',
          allowSmsOnTrial: 'Allow SMS on trial',
          table: {
            store: 'Store',
            plan: 'Plan',
            status: 'Status',
            stripe: 'Stripe',
            actions: 'SMS trial',
          },
          plan: {
            trial: 'Trial',
            basesubscription: 'Base',
          },
          status: {
            active: 'Active',
            trialing: 'Trialing',
            pastdue: 'Past due',
            suspended: 'Suspended',
            expired: 'Expired',
            canceled: 'Cancelled',
            disabled: 'Disabled',
          },
          stripe: {
            connected: 'Connected',
            missing: 'Not connected',
          },
          error: {
            loading: 'Failed to load stores.',
            updateSms: 'Failed to update SMS trial access.',
          },
          delete: {
            action: 'Delete store',
            title: 'Delete store',
            message: 'This permanently deletes {{name}} and the services, appointments, products, memberships, and other data that belong only to this store. Team members and customers who also belong to another store are kept. Images and files for this store are deleted, and Salovare billing for this store is cancelled. A Stripe connected account, if one was created, stays open in Stripe.',
            confirmLabel: 'Type the store name to confirm',
            confirm: 'Delete store',
            error: 'Failed to delete the store.',
          },
        },
      },
      planDiscountVouchers: {
        title: 'Plan Discount Vouchers',
        subtitle: 'Create and track Salovare plan discounts for specific stores.',
        empty: 'No plan discount vouchers yet.',
        error: {
          loading: 'Failed to load plan discount vouchers.',
          disable: 'Failed to disable the voucher.',
        },
        actions: {
          create: 'New voucher',
          generate: 'Generate voucher',
          disable: 'Disable',
        },
        disable: {
          title: 'Disable voucher',
          message: 'Disable voucher {{code}}? It will no longer apply to future checkouts.',
        },
        status: {
          Active: 'Active',
          Scheduled: 'Scheduled',
          Expired: 'Expired',
          Disabled: 'Disabled',
        },
        table: {
          code: 'Code',
          store: 'Store',
          discount: 'Discount',
          startsAt: 'Starts',
          endsAt: 'Ends',
          status: 'Status',
          createdAt: 'Created',
          actions: 'Actions',
        },
        form: {
          title: 'Generate plan discount voucher',
          store: 'Store',
          selectStore: 'Select a store',
          discountPercent: 'Discount (%)',
          discountPlaceholder: 'e.g. 50%',
          startsAt: 'Start date',
          endsAt: 'End date',
          endsAtHelper: 'Leave the end date empty for no expiration.',
          errors: {
            storeRequired: 'Select a store.',
            discountRequired: 'Enter a discount percentage.',
            discountInvalid: 'Enter a discount between 1 and 100.',
            startRequired: 'Select a start date.',
            startInPast: 'Start date cannot be in the past.',
            dateRange: 'End date must be on or after the start date.',
            create: 'Failed to generate the voucher.',
          },
        },
      },
      paymentOperations: {
        title: 'Payment Operations',
        subtitle: 'Monitor payment health, webhook failures, and operational exceptions.',
        empty: 'No records found for the current filters.',
        error: {
          loading: 'Failed to load payment operations data.',
        },
        actions: {
          refresh: 'Refresh',
          reconcile: 'Run Stripe reconciliation',
          reconciling: 'Reconciling...',
        },
        reconciliation: {
          successDetail: 'Stripe reconciliation finished. Appointments: {{appointmentsRepaired}} repaired of {{appointmentsMismatched}} mismatched ({{appointmentsInspected}} inspected). Store subscriptions: {{storeSubscriptionsRepaired}} repaired of {{storeSubscriptionsMismatched}} mismatched ({{storeSubscriptionsInspected}} inspected). Memberships: {{customerMembershipsRepaired}} repaired of {{customerMembershipsMismatched}} mismatched ({{customerMembershipsInspected}} inspected).',
          error: 'Failed to run Stripe reconciliation.',
        },
        monitoring: {
          title: 'Payment monitoring',
          subtitle: 'Operational signals and alerting thresholds for Stripe-backed workflows.',
          enabled: 'Alerts enabled',
          disabled: 'Alerts disabled',
          evaluate: 'Run alert evaluation',
          evaluating: 'Evaluating...',
          evaluateSuccess: 'Monitoring evaluation finished. {{triggeredCount}} alert(s) triggered; {{emailsSent}} email(s) sent.',
          evaluateError: 'Failed to run payment monitoring evaluation.',
          configuration: 'Lookback {{lookbackMinutes}}m · Cooldown {{cooldownMinutes}}m · {{recipientCount}} recipient(s) configured in appsettings.',
          activeAlerts: 'Active alerts',
          noActiveAlerts: 'No payment monitoring thresholds are currently breached.',
          recentHistory: 'Recent alert emails sent',
          signals: {
            openFailedWebhooks: 'Open failed webhooks',
            recentFailedWebhooks: 'Recent webhook failures',
            staleProcessingWebhooks: 'Stale processing webhooks',
            recentPayoutFailures: 'Recent payout failures',
            recentPaymentFailureWebhooks: 'Recent payment-failure webhooks',
          },
        },
        summary: {
          failedWebhooks: 'Failed webhooks',
          staleWebhooks: 'Stale processing webhooks',
          appointmentExceptions: 'Appointment payment exceptions',
          storeSubscriptionExceptions: 'Store subscription exceptions',
          customerMembershipExceptions: 'Customer membership exceptions',
          paymentDisputesRequiringAction: 'Disputes requiring action',
        },
        tabs: {
          webhooks: 'Webhook events',
          appointments: 'Appointments',
          storeSubscriptions: 'Store subscriptions',
          customerMemberships: 'Customer memberships',
          disputes: 'Disputes',
          refunds: 'Refunds',
        },
        refunds: {
          issueRefund: 'Issue refund',
          issueTitle: 'Issue appointment refund',
          view: 'View refund',
          detailTitle: 'Refund details',
          appointmentId: 'Appointment ID',
          loadContext: 'Load',
          loadContextError: 'Failed to load refund context.',
          cannotRefund: 'This appointment cannot be refunded.',
          amount: 'Refund amount',
          amountHelp: 'Leave as the remaining balance for a full refund, or enter a lower amount for a partial refund.',
          reason: 'Refund reason',
          internalNote: 'Internal note',
          confirmIssue: 'Issue refund',
          issuing: 'Issuing...',
          issueSuccess: 'Refund issued successfully.',
          issueError: 'Failed to issue refund.',
          sync: 'Sync from Stripe',
          syncSuccess: 'Refund synchronized from Stripe.',
          syncError: 'Failed to synchronize refund from Stripe.',
          columns: {
            store: 'Store',
            amount: 'Amount',
            status: 'Status',
            reason: 'Reason',
            createdAt: 'Created at',
            refundId: 'Stripe refund ID',
          },
          reasons: {
            RequestedByCustomer: 'Requested by customer',
            Duplicate: 'Duplicate',
            Fraudulent: 'Fraudulent',
          },
          context: {
            store: 'Store',
            chargeAmount: 'Charge amount',
            refundedAmount: 'Already refunded',
            remainingAmount: 'Remaining refundable',
          },
        },
        disputes: {
          requiresActionOnly: 'Requires action only',
          manage: 'Manage dispute',
          detailTitle: 'Dispute details and evidence',
          saveDraft: 'Save draft',
          submitEvidence: 'Submit to Stripe',
          submitting: 'Submitting...',
          sync: 'Sync from Stripe',
          uploadAttachment: 'Upload attachment',
          attachments: 'Attachments',
          evidenceSubmitted: 'Evidence has already been submitted to Stripe.',
          saveEvidenceSuccess: 'Evidence draft saved.',
          saveEvidenceError: 'Failed to save evidence draft.',
          submitEvidenceSuccess: 'Evidence submitted to Stripe.',
          submitEvidenceError: 'Failed to submit evidence to Stripe.',
          syncSuccess: 'Dispute synchronized from Stripe.',
          syncError: 'Failed to synchronize dispute from Stripe.',
          uploadSuccess: 'Attachment uploaded.',
          uploadError: 'Failed to upload attachment.',
          columns: {
            amount: 'Amount',
            deadline: 'Evidence deadline',
            disputeId: 'Stripe dispute ID',
          },
          evidence: {
            customerCommunication: 'Customer communication',
            cancellationPolicy: 'Cancellation policy',
            refundPolicy: 'Refund policy',
            serviceDocumentation: 'Service documentation',
            uncategorizedText: 'Additional evidence',
            internalNotes: 'Internal notes (not sent to Stripe)',
          },
        },
        webhooks: {
          platform: 'Platform',
          viewDetails: 'View details',
          detailTitle: 'Webhook event details',
          retry: 'Retry processing',
          retrying: 'Retrying...',
          retrySuccess: 'Webhook processing has been requeued.',
          retryError: 'Failed to retry webhook processing.',
          filters: {
            status: 'Status',
            staleOnly: 'Stale processing only',
          },
          columns: {
            eventType: 'Event type',
            status: 'Status',
            externalEventId: 'External event ID',
            connectedAccount: 'Connected account',
            receivedAt: 'Received at',
            lastError: 'Last error',
          },
          detail: {
            correlationId: 'Correlation ID',
            traceId: 'Trace ID',
            deliveryCount: 'Delivery count',
          },
        },
        exceptions: {
          columns: {
            store: 'Store',
            date: 'Date',
            amount: 'Amount',
            status: 'Status',
            sessionId: 'Checkout session',
            subscriptionId: 'Subscription ID',
            endsAt: 'Ends at',
            customerId: 'Customer ID',
          },
        },
      },
      storeRegistrationSteps: {
        step1: {
          title: 'Basic Information',
          description: 'Enter your store name, description, and supported languages'
        },
        step2: {
          title: 'Contact Information',
          description: 'Provide email, phone, and website details'
        },
        step3: {
          title: 'Location Information',
          description: 'Enter your store address and location details'
        },
        step4: {
          title: 'Business Hours',
          description: 'Set your store opening hours'
        },
        step5: {
          title: 'Review & Submit',
          description: 'Review all information before submitting'
        }
      },
      storeRegistration: {
        title: 'Store Registration',
        seoTitle: 'Create your barbershop or salon | Salovare Free and Pro',
        seoDescription: 'Register your barbershop or salon on Salovare. Start free with one professional and one store. Pro from 9,90€/month. Appointments and online booking included.',
        subtitle: 'Create a new store',
        selfServiceTitle: 'Create Your Store',
        selfServiceSubtitle: 'Set up your store on Free and finish access from the password email',
        success: 'Store registered successfully!',
        error: 'Failed to register store. Please try again.',
        billingRequiredTitle: 'Billing setup required',
        billingRequiredMessage: 'Free includes one store. Upgrade to Pro before adding another store.',
        billingRequiredAction: 'Open payment settings',
        paidStoreWarning: 'This extra store is billed at {{price}}/month.',
        paidStoreWarningProrated: 'This extra store is billed about {{proratedPrice}} now until {{nextBillingDate}}, then {{price}}/month.',
        paidStoreConfirmTitle: 'Confirm extra store',
        paidStoreConfirmMessage: 'Billing starts at {{price}}/month. You will set up payment after registration.',
        paidStoreConfirmMessageProrated: 'We will reuse your existing payment method, charge about {{proratedPrice}} until {{nextBillingDate}}, then bill {{price}}/month on that date.',
        paidStoreConfirmAction: 'Create paid store',
        paidStoreCreatedMessage: 'Store created. Set up billing to activate the paid subscription.',
        paidStoreProvisionedMessage: 'Store created and billed with your existing payment method for the remaining period until the next billing date.',
        errors: {
          billingSetupRequired: 'Upgrade to Pro on an existing store before adding another store.',
          phoneAlreadyInUse: 'This phone number is already registered. Sign in if you already have an account, or use a different number.',
          emailAlreadyInUse: 'This email is already registered. Sign in if you already have an account, or use a different email.',
        },
        name: 'Store Name',
        description: 'Store Description',
        supportedLanguages: 'Supported Languages',
        about: 'About',
        ownerName: 'Owner Name',
        ownerEmail: 'Owner Email',
        ownerPhone: 'Owner Phone',
        email: 'Email',
        phone: 'Phone',
        storeEmail: 'Store Email',
        storePhone: 'Store Phone',
        website: 'Website',
        googleReviewUrl: 'Google Review URL',
        googleReviewUrlPlaceholder: 'https://g.page/r/.../review',
        googleReviewUrlHelp: 'Open your Google Business listing, copy the “Write a review” or share link, and paste it here. Customers receive a Salovare short link, not this URL.',
        ownerSectionTitle: 'Owner Information',
        storeContactSectionTitle: 'Store Contact Information',
        address: 'Address',
        door: 'Door',
        additionalInfo: 'Additional info',
        city: 'City',
        state: 'County',
        zipCode: 'Postcode',
        country: 'Country',
        basicInfo: {
          storeName: 'Store Name',
          storeNameLabel: 'Store Name',
          slug: 'Slug (URL)',
          currency: 'Currency',
          currencyHelper: 'Select the currency used for pricing in your store',
          selectCurrency: 'Select Currency',
          descriptionLabel: 'Description',
          descriptionPlaceholder: 'Describe your store, services, and what makes it special...',
          supportedLanguages: 'Supported Languages',
          supportedLanguagesHelper: 'Select the languages in which your store will be available. You must provide descriptions in all selected languages.',
          storeLanguage: 'Default Store Language',
          storeLanguageHelper: 'Choose the language customers should see when their preferred language is not available for this store.',
          selectStoreLanguage: 'Select Default Language',
          storeLanguageDisabledHelper: 'Select at least one supported language before choosing the default store language.',
          about: 'About',
          aboutHelper: 'Describe your store in each selected language',
          aboutLabel: 'About ({{language}})',
          aboutPlaceholder: 'Describe your store in {{language}}...',
          timeZone: 'Time Zone',
          timeZoneHelper: 'Choose the time zone used to evaluate opening hours and appointment availability for this store',
          selectTimeZone: 'Select Time Zone',
          typeToSearchTimeZone: 'Type to search a time zone',
          topTimeZones: 'Popular Timezones',
          allTimeZones: 'All Timezones',
          slotInterval: 'Time Slot Interval',
          slotIntervalHelper: 'Define the duration of each calendar time slot shown to customers when booking',
          selectSlotInterval: 'Select Interval',
          slotIntervalFive: '5 minutes',
          slotIntervalTen: '10 minutes',
          slotIntervalFifteen: '15 minutes',
          slotIntervalThirty: '30 minutes',
          slotIntervalSixty: '60 minutes'
        },
        contactInfo: {
          title: 'Contact Information',
          selfServiceDescription: 'Provide owner details and the public store contact information',
          email: 'Email',
          phone: 'Phone',
          website: 'Website'
        },
        locationInfo: {
          title: 'Location Information',
          address: 'Address',
          city: 'City',
          state: 'County',
          zipCode: 'Postcode',
          country: 'Country'
        },
        services: {
          title: 'Services',
          helper: 'Select the services your store offers',
          addService: 'Add Service',
          serviceName: 'Service Name',
          serviceDescription: 'Service Description',
          serviceDuration: 'Duration',
          servicePrice: 'Price',
          removeService: 'Remove Service',
          viewService: 'View Service',
          editService: 'Edit Service',
          saveChanges: 'Save Changes',
          services: 'Services',
          noServicesAdded: 'No services added',
          suggestions: 'Service Suggestions',
          selectSuggestion: 'Click a suggestion to auto-fill the form',
          defaultName: 'Name',
          defaultDescription: 'Description',
          serviceSuggestions: {
            mensHaircut: {
              name: 'Men\'s Haircut',
              description: 'Traditional haircut for men'
            },
            beardTrim: {
              name: 'Beard Trim',
              description: 'Trim and shape the beard'
            },
            haircutBeard: {
              name: 'Haircut + Beard',
              description: 'Combined haircut and beard service'
            },
            womensHaircut: {
              name: 'Women\'s Haircut',
              description: 'Haircut for women'
            },
            kidsHaircut: {
              name: 'Kids Haircut',
              description: 'Haircut for children'
            },
            eyebrow: {
              name: 'Eyebrow',
              description: 'Eyebrow shaping and grooming'
            },
            haircutEyebrow: {
              name: 'Haircut + Eyebrow',
              description: 'Combined haircut and eyebrow service'
            },
            haircutBeardEyebrow: {
              name: 'Haircut + Beard + Eyebrow',
              description: 'Complete grooming service with haircut, beard, and eyebrow'
            }
          }
        },
        businessHours: {
          title: 'Business Hours',
          helper: 'Set your store operating hours for each day of the week',
          applyToAllTitle: 'Apply to Other Days?',
          applyToAllMessage: 'You changed the {{field}} for {{day}} to {{value}}. Would you like to apply this change to all other days as well?',
          openTime: 'Opens at',
          closeTime: 'Closes at',
          to: 'to',
          applyToCurrentDay: 'Apply to {{day}} only',
          applyToAllDays: 'Apply to all days',
          addPeriod: 'Add period',
          removePeriod: 'Remove period'
        },
        review: {
          title: 'Review Your Store Information',
          subtitle: 'Please review all the information below before submitting',
          basicInfo: 'Basic Information',
          contactInfo: 'Contact Information',
          locationInfo: 'Location Information',
          services: 'Services',
          businessHours: 'Business Hours'
        },
        validation: {
          nameRequired: 'Store name is required',
          slugRequired: 'Store URL is required',
          slugTooShort: 'Store URL must be at least 3 characters',
          slugTooLong: 'Store URL cannot exceed 30 characters',
          slugInvalid: 'Use lowercase letters, numbers, and hyphens only. It cannot start or end with a hyphen.',
          slugReserved: 'Not available',
          slugTaken: 'Not available',
          slugCheckFailed: 'Could not check if this URL is available. Try again.',
          descriptionRequired: 'Store description is required',
          languagesRequired: 'At least one language must be selected',
          aboutRequired: 'About content is required for all selected languages',
          storeLanguageRequired: 'Default store language is required',
          storeLanguageMustBeSupported: 'Default store language must be one of the supported languages',
          ownerNameRequired: 'Owner name is required',
          ownerEmailRequired: 'Owner email is required',
          ownerPhoneRequired: 'Owner phone number is required',
          emailRequired: 'Email is required',
          storeEmailRequired: 'Store email is required',
          emailInvalid: 'Please enter a valid email address',
          phoneRequired: 'Phone number is required',
          storePhoneRequired: 'Store phone number is required',
          addressRequired: 'Address is required',
          cityRequired: 'City is required',
          stateRequired: 'County is required',
          zipCodeRequired: 'Postcode is required',
          countryRequired: 'Country is required',
          timeZoneRequired: 'Time zone is required',
          servicesRequired: 'At least one service must be selected',
          businessHoursRequired: 'At least one day must be enabled for business hours',
          serviceNameRequired: 'Service name is required for all selected languages',
          servicePriceRequired: 'Service price is required and must be greater than 0',
          openTimeAfterCloseTime: 'Opening time cannot be after or equal to closing time',
          periodsOverlap: 'Periods cannot overlap'
        },
        slug: {
          checking: 'Checking availability...',
          available: 'Available',
        }
      },
      landing: {
        nav: {
          features: 'Features',
          howItWorks: 'How it works',
          pricing: 'Pricing',
          faq: 'FAQ',
          signIn: 'Sign in',
          getStarted: 'Get started',
          goToControlPanel: 'Go to control panel',
          openMenu: 'Open menu',
        },
        hero: {
          eyebrow: 'Health & beauty software',
          title: 'Every appointment, every booking, every payout under control.',
          subtitle: 'Salovare runs the front desk, the online booking page, membership plans, service packages and team payroll for health and beauty businesses. Start free with a single professional and upgrade the day you hire.',
          ctaPrimary: 'Create your store',
          ctaSecondary: 'Sign in',
          specs: {
            free: { label: 'Free plan', value: 'One professional, no expiry' },
            pro: { label: 'Pro', value: '{{price}}/month' },
            card: { label: 'Credit card', value: 'Not required' },
            commitment: { label: 'Commitment', value: 'Cancel anytime' },
          },
          preview: {
            title: 'Today',
            meta: 'Front desk',
            room: 'Room {{number}}',
            services: {
              facial: 'Facial',
              massage: 'Massage',
              haircut: 'Haircut',
              manicure: 'Manicure',
            },
            statuses: {
              confirmed: 'Confirmed',
              membership: 'Membership',
              walkIn: 'Walk-in',
            },
          },
        },
        features: {
          overline: 'Platform',
          title: 'Built for the way a studio actually runs.',
          subtitle: 'Nine modules that cover the whole day: the agenda at the desk, the booking page online, memberships and packages for your regulars and payroll at month end.',
          appointments: {
            title: 'Staff agenda',
            description: 'See every professional\'s day at a glance. Book walk-ins at the desk, including slots outside opening hours when you need them.',
          },
          team: {
            title: 'Team, roles & time off',
            description: 'Invite staff, set who accepts online bookings, manage permissions, and block time off on the calendar.',
          },
          services: {
            title: 'Services & categories',
            description: 'Organize hair, skin, wellness and extras with durations, prices, and custom rates per professional.',
          },
          booking: {
            title: 'Online booking',
            description: 'Share a branded booking page so clients pick a service, a professional, and a time without calling the studio.',
          },
          memberships: {
            title: 'Membership Plans & Service Packages',
            description: 'Sell monthly membership plans and prepaid service packages, like 10 sessions. Remaining visits are tracked for you and used up as clients book.',
          },
          customers: {
            title: 'Client records',
            description: 'Keep walk-ins and regulars in one list, with appointment history ready when they come back.',
          },
          reminders: {
            title: 'Reminders that cut no-shows',
            description: 'Email confirmations and reminders are included on every plan. Pro adds SMS and WhatsApp, charged per message.',
          },
          payroll: {
            title: 'Payroll & commissions',
            description: 'Track what each professional earned from services, memberships and packages, with a payroll view built for the studio floor.',
          },
          dashboard: {
            title: 'Daily dashboard',
            description: 'Today\'s appointments, pending payments, monthly revenue, and active memberships in one place.',
          },
        },
        howItWorks: {
          overline: 'Onboarding',
          title: 'Open the doors in four steps.',
          subtitle: 'Registration is guided from the first screen. Most studios are taking bookings the same afternoon.',
          steps: {
            register: {
              title: 'Create your store',
              description: 'Sign up with your studio details, contact info, and business hours in a guided registration flow.',
            },
            setup: {
              title: 'Add services & hours',
              description: 'Configure your service menu, opening hours, and store profile for clients to discover.',
            },
            team: {
              title: 'Invite your team',
              description: 'Add team members, assign roles, and give each professional their own agenda access.',
            },
            book: {
              title: 'Book at the desk or online',
              description: 'Share your booking link for online reservations, and offer membership plans and service packages to your regulars.',
            },
          },
        },
        pricing: {
          overline: 'Plans',
          title: 'Free to start. Pro when you grow.',
          subtitle: 'Free covers a single professional for as long as you need it. Pro opens the rest of the platform — extra professionals, extra stores, online payments, stock, payroll and messaging.',
          plans: {
            free: {
              name: 'Free',
              tagline: 'For the solo professional',
              price: 'FREE',
              priceNote: 'No credit card, no expiry date.',
              note: 'Upgrade to Pro the day you hire. Nothing to migrate and nothing to set up again.',
              cta: 'Create your free store',
              values: {
                stores: 'Single store',
                professionals: 'Single professional',
                dashboard: 'Basic dashboard',
                payments: 'Payment in store',
              },
            },
            pro: {
              name: 'Pro',
              tagline: 'For growing teams and multiple locations',
              priceSuffix: '/month',
              priceNote: 'For the first {{months}} months, then {{price}}/month. Cancel anytime.',
              cta: 'Start free, upgrade in app',
              values: {
                stores: 'Multiple stores',
                professionals: 'Unlimited professionals',
                dashboard: 'Full dashboard',
                payments: 'Online and in-store payments',
              },
            },
          },
          features: {
            appointments: 'Appointments and online booking',
            emailReminders: 'Email reminders',
            realtimeChat: 'In-app client realtime chat',
            deposits: 'Deposits and prepayment at booking',
            installments: 'Pay in installments with Klarna',
            refunds: 'Cancellation policy with automatic refunds',
            productsStock: 'Products and stock',
            membershipPlans: 'Membership plans and service packages',
            teamPayroll: 'Team payroll',
            waitlistHours: 'Waitlist and additional hours',
            additionalCosts: 'Additional costs for specific days or periods',
            customPermissions: 'Custom permissions per team member',
            smsReminders: 'SMS reminders',
            whatsappReminders: 'WhatsApp reminders',
            customBranding: 'Custom branding colors',
            prioritySupport: 'Priority support',
            marketingAutomation: 'Automated marketing messages',
          },
          rates: {
            perMessage: '{{price}} per message',
          },
          comingSoon: 'Coming soon',
          addOns: {
            title: 'Add-ons',
            additionalStore: 'Additional store',
            additionalProfessional: 'Additional professional',
            perMonth: '{{price}}/month',
          },
          footnote: 'Prices in EUR. Pro is {{introPrice}}/month for the first {{months}} months, then {{regularPrice}}/month. Extra stores are {{extraStorePrice}}/month on the same invoice. Additional professionals are {{teamMemberPrice}}/month. SMS is {{smsPrice}} and WhatsApp {{whatsAppPrice}} per message. Membership plan and service package prices are set by each store.',
        },
        faq: {
          overline: 'Questions',
          title: 'Before you sign up.',
          subtitle: 'Straight answers on Free, Pro and how Salovare fits a studio floor.',
          items: {
            price: {
              question: 'How much does Salovare cost?',
              answer: 'Free is 0€ for a single store with a single professional, with no expiry date. Pro is {{introPrice}}/month for the first {{months}} months and {{regularPrice}}/month after that. Extra stores are {{extraStorePrice}}/month on the same invoice. Additional professionals are {{teamMemberPrice}}/month and messaging is charged per message.',
            },
            plans: {
              question: 'What is the difference between Free and Pro?',
              answer: 'Free covers appointments, online booking, email reminders, the basic dashboard and the in-app client chat for one professional in one store, with clients paying in store. Pro adds online payments with deposits, prepayment, installments and automatic refunds, products and stock, membership plans and service packages, team payroll, waitlist and additional hours, custom permissions per team member, SMS and WhatsApp reminders, custom branding, the full dashboard and priority support.',
            },
            messaging: {
              question: 'How do SMS and WhatsApp reminders work?',
              answer: 'They are available on Pro and billed per message on your subscription cycle: {{smsPrice}} per SMS and {{whatsAppPrice}} per WhatsApp message. Email reminders are included on every plan.',
            },
            healthBeauty: {
              question: 'Does it work for the health and beauty industry?',
              answer: 'Yes. Salovare is built for health and beauty businesses — salons, barbershops, spas, clinics and wellness studios: staff agendas, walk-ins, online booking, membership plans, service packages, reminders, and payroll.',
            },
            multiStore: {
              question: 'Can I manage more than one store?',
              answer: 'Yes, on Pro. Free includes a single store. Extra stores are {{extraStorePrice}}/month each on the same owner invoice. If payment stops, the oldest store stays on Free and the others are deactivated.',
            },
            creditCard: {
              question: 'Do I need a credit card to start?',
              answer: 'No. You can create your store on Free without adding a payment method. Add a card when you upgrade to Pro.',
            },
          },
        },
        seo: {
          title: 'Salovare | Health & beauty software — Free and Pro from 9,90€/month',
          description: 'Appointment software for the health and beauty industry. Start free with one professional and one store. Pro from 9,90€/month for extra seats, products, memberships, service packages and payroll.',
        },
        cta: {
          overline: 'Get started',
          title: 'Take your next booking on Salovare.',
          subtitle: 'Create your store in a few minutes. No credit card, no contract and no setup fee.',
          button: 'Create your store',
        },
        footer: {
          signIn: 'Team sign in',
          register: 'Create store',
          copyright: '© {{year}} Salovare. All rights reserved.',
        },
      },
      settings: 'Settings',
      home: {
        welcomeBack: 'Welcome back, {{name}}!',
        description: 'This is your home page where you\'ll soon see your dashboard with appointments, statistics, and quick actions. For now, enjoy this clean and modern interface designed specifically for your salon needs.',
      },
      dashboard: {
        title: 'Dashboard',
        subtitle: 'Overview of appointments, sales, and store performance',
        selectStore: 'Select a store to view the dashboard.',
        rebuild: 'Rebuild dashboard data',
        rebuildConfirmTitle: 'Rebuild dashboard data?',
        rebuildConfirmMessage: 'This re-aggregates the last 12 months of dashboard stats from appointments and revenue. Existing rollup data in that period will be replaced.',
        rebuildSuccess: 'Dashboard data rebuilt successfully.',
        rebuildError: 'Failed to rebuild dashboard data.',
        activityTypes: {
          appointmentCreated: 'New appointment for {{customerName}}',
          appointmentPaid: 'Payment received from {{customerName}}',
          appointmentCompleted: 'Appointment completed for {{customerName}}',
          appointmentCancelled: 'Appointment cancelled for {{customerName}}',
          appointmentNoShow: 'No-show recorded for {{customerName}}',
          revenueRecognized: 'Revenue recognized for {{customerName}}',
          membershipEnrolled: '{{customerName}} enrolled in {{planName}}',
          membershipCanceled: '{{customerName}} cancelled {{planName}}',
          membershipPaused: '{{customerName}} paused {{planName}}',
          productSold: 'Product sale for {{customerName}}',
          appointmentRefunded: 'Refund to {{customerName}}',
          productSaleVoided: 'Product sale reverted for {{customerName}}',
        },
      },
      managementAppointments: {
        title: 'Appointments',
        subtitle: 'Calendar view for the selected store and professional.',
        today: 'Today',
        professional: 'Professional',
        myAgenda: 'My agenda',
        selectStore: 'Select a store to view the agenda.',
        noPermission: 'You do not have permission to view the appointments agenda.',
          selectProfessionalToView: 'Select a professional to view the agenda.',
        loadError: 'Failed to load the appointments agenda.',
        unnamedService: 'Unnamed service',
        status: 'Status',
        customer: 'Customer',
        services: 'Services',
        service: 'Service',
        products: 'Products',
        product: 'Product',
        totalPrice: 'Total',
        additionalInfo: 'Additional info',
        timeRange: 'Time range',
        professionalsPerService: 'Professional per service',
        professionals: 'Professionals',
        noAdditionalInfo: 'No notes from the customer.',
        noConsultationNotes: 'No consultation notes yet.',
        openCustomerDetails: 'Open customer details',
        unnamedProfessional: 'Unassigned professional',
        detailsTitle: 'Appointment details',
        timeOff: 'Time off',
        timeOffDetailsTitle: 'Time off details',
        timeOffType: 'Type',
        markCompleted: 'Mark as completed',
        confirmOnRequest: 'Confirm request',
        declineOnRequest: 'Decline request',
        confirmingOnRequest: 'Confirming...',
        decliningOnRequest: 'Declining...',
        confirmOnRequestSuccess: 'Appointment confirmed.',
        declineOnRequestSuccess: 'Request declined.',
        onRequestRespondError: 'Failed to respond to the on-request appointment.',
        openToAnyProfessional: 'Any professional',
        selectProfessionalToConfirm: 'Select the professional who is responding',
        markCancelled: 'Mark as cancelled',
        markNoShow: 'Mark as no-show',
        completing: 'Completing...',
        cancelling: 'Cancelling...',
        markingNoShow: 'Recording no-show...',
        completeError: 'Failed to mark appointment as completed.',
        statusUpdateError: 'Failed to update appointment status.',
        paymentStatusLabel: 'Payment status',
        markPaid: 'Mark as paid',
        markingPaid: 'Marking...',
        markPaidError: 'Failed to mark appointment as paid.',
        changeToRegular: 'Change to regular appointment',
        changingToRegular: 'Changing...',
        changeToRegularError: 'Failed to change appointment to regular.',
        amountDue: 'Amount due',
        amountPaid: 'Amount paid',
        additionalCost: 'Additional time cost',
        coveredByMembership: 'Covered by membership',
        pendingMembershipEnrollment: 'Membership due in store',
        membershipPlanNote: 'Included with {{plan}}',
        membershipPriceNote: 'Pay {{plan}} membership in store',
        confirm: {
          completeTitle: 'Mark appointment as completed?',
          completeMessage: 'This will mark the appointment as completed and paid. This action cannot be undone.',
          completeEnrollTitle: 'Complete and enroll in membership?',
          completeEnrollMessage: 'This will complete the appointment and enroll the customer in {{plan}}. Amount due: {{amount}}. This action cannot be undone.',
          completeAndBookNext: 'Confirm and book next',
          cancelTitle: 'Mark appointment as cancelled?',
          cancelMessage: 'This frees the time slot. This action cannot be undone.',
          noShowTitle: 'Mark appointment as no-show?',
          noShowMessage: 'This records that the customer did not attend and frees the time slot. This action cannot be undone.',
          deleteTitle: 'Delete appointment?',
          deleteMessage: 'This appointment will be removed from the agenda and excluded from reports. This action cannot be undone.',
          markPaidTitle: 'Mark appointment as paid?',
          markPaidMessage: 'This will record the appointment as paid. This action cannot be undone.',
          changeToRegularTitle: 'Change to regular appointment?',
          changeToRegularMessage: 'This removes membership coverage from the appointment and charges regular service prices. This action cannot be undone.',
          confirmOnRequestTitle: 'Confirm this on-request appointment?',
          confirmOnRequestMessage: 'This confirms the extra-hour booking and assigns it to the selected professional.',
          declineOnRequestTitle: 'Decline this on-request appointment?',
          declineOnRequestMessage: 'If no other professional accepts, the booking will be cancelled and the customer will be notified.'
        },
        edit: {
          title: 'Edit appointment',
          action: 'Edit appointment',
          date: 'Date',
          submit: 'Save changes',
          submitting: 'Saving...',
          submitError: 'Failed to update appointment.',
          loadError: 'Failed to load appointment data.',
          validation: {
            dateRequired: 'Select a date.',
            startTimeRequired: 'Select a start time.'
          }
        },
        delete: {
          action: 'Delete appointment',
          error: 'Failed to delete appointment.'
        },
        paymentStatus: {
          paid: 'Paid',
          pendingPayment: 'Pending payment',
          unpaid: 'Unpaid'
        },
        membershipRenewalRisk: 'Membership renewal risk: the appointment date is on or after the current period end ({{date}}). If the membership is not renewed, the regular service price will apply.',
        views: {
          oneDay: '1 day',
          threeDays: '3 days',
          sevenDays: '7 days',
          oneDayShort: '1D',
          threeDaysShort: '3D',
          sevenDaysShort: '7D'
        },
        bookFab: 'Book appointment',
        book: {
          title: 'Book appointment',
          appointmentContext: 'Appointment time',
          startTime: 'Start time',
          selectStartTime: 'Select start time',
          tapToChangeTime: 'Tap to change',
          customerType: 'Customer',
          customerTypeExisting: 'Existing customer',
          customerTypeNew: 'New customer',
          customerTypeAnonymous: 'Anonymous / walk-in',
          customerSearchPlaceholder: 'Search by name, email or phone',
          guestName: 'Guest name (optional)',
          guestNamePlaceholder: 'Leave empty for walk-in',
          services: 'Services',
          products: 'Products',
          productsHelp: 'Add products to sell with this appointment. They are collected at checkout.',
          professionals: 'Professional per service',
          selectProfessional: 'Select professional',
          membership: 'Membership',
          membershipHelp: 'Choose an existing membership or start a plan. The plan starts when this appointment is completed and paid in store.',
          pendingEnrollmentHelp: 'The customer will be enrolled when this appointment is completed and paid. Until then the membership is pending.',
          planServicesLocked: 'Plan services are selected and cannot be removed. You can still add extra services.',
          noMembership: 'No membership covers the selected services.',
          noMembershipRedemption: 'No membership',
          subscribedTag: 'Subscribed',
          enrollInStoreTag: 'Enroll in store',
          notes: 'Notes',
          notesPlaceholder: 'Optional notes for this appointment',
          submit: 'Book appointment',
          submitting: 'Booking...',
          success: 'Appointment booked successfully.',
          submitError: 'Failed to book appointment.',
          loadError: 'Failed to load booking data.',
          noCustomerReadPermission: 'You do not have permission to search customers. Choose new or anonymous customer instead.',
          warnings: {
            closedDay: 'This store is normally closed on the selected date. You can still book it manually.',
            outsideHours: 'This time is outside the store opening hours. You can still book it manually.',
            slotConflict: 'This time overlaps with an existing appointment for the selected professional. You can still book it manually.'
          },
          validation: {
            servicesRequired: 'Select at least one service.',
            customerRequired: 'Select a customer.',
            professionalRequired: 'Assign a professional to every selected service.',
            dateRequired: 'Select a date.',
            startTimeRequired: 'Select a start time.'
          }
        }
      },
      notifications: {
        title: 'Notifications',
        newAppointment: 'New appointment booked for John Doe',
        appointmentCancelled: 'Appointment cancelled by Sarah Smith',
        systemMaintenance: 'System maintenance scheduled for tonight',
        twoMinutesAgo: '2 minutes ago',
        oneHourAgo: '1 hour ago',
        threeHoursAgo: '3 hours ago',
      },
      customerNotifications: {
        title: 'Notifications',
        empty: 'No notifications yet.',
        markAllRead: 'Mark all as read',
        seeAll: 'See all',
        page: {
          title: 'Notifications',
          subtitle: 'Messages and updates from this store',
          error: {
            loading: 'Failed to load notifications.',
          },
        },
      },
      staffNotifications: {
        title: 'Notifications',
        empty: 'No notifications yet.',
        markAllRead: 'Mark all as read',
        enableSounds: 'Enable notification sounds',
        seeAll: 'See all',
        page: {
          title: 'Notifications',
          subtitle: 'Latest in-app notifications for you',
          table: {
            title: 'Title',
            message: 'Message',
            dateTime: 'Date & time',
            status: 'Status',
          },
          status: {
            read: 'Read',
            unread: 'Unread',
          },
          error: {
            loading: 'Failed to load notifications.',
            noStoreSelected: 'No store selected.',
          },
        },
		newAppointment: {
          title: 'New appointment',
          body: 'A new appointment was booked for you.',
          bodyWithDetails: '{{customerName}} booked at {{date}} from {{begin}} to {{end}}. See details.',
        },
        cancelledAppointment: {
          title: 'Appointment cancelled',
          body: 'An appointment assigned to you was cancelled.',
        },
        supportChat: {
          title: 'New customer chat',
          body: 'A customer started a conversation with the store.',
          messageTitle: 'New chat message',
          messageBody: '{{customerName}} sent a message.'
        },
        platformSupport: {
          title: '{{storeName}}',
          body: '{{message}}',
        },
        waitlistJoined: {
          title: 'Customer joined the waitlist',
          body: 'A customer joined the waitlist with preferred dates and times.',
        },
        waitlistSlotOpened: {
          title: 'Matching waitlist slot opened',
          body: 'A slot opened that matches a waitlist customer.',
        },
        appointmentConfirmationRequested: {
          title: 'On-request appointment',
          body: 'A customer requested a time outside regular hours.',
          bodyWithDetails: '{{customerName}} requested {{date}} from {{begin}} to {{end}}. Confirm or decline.',
        },
        updatedAppointment: {
          title: 'Appointment updated',
          body: 'An appointment assigned to you was updated.',
          bodyWithDetails: '{{customerName}} on {{date}} from {{begin}} to {{end}} was updated.',
        },
        other: {
          title: 'Notification',
          body: 'You have a new notification.',
        },
      },
      pushNotifications: {
        enable: 'Enable push notifications',
        promptTitle: 'Turn on push notifications?',
        notNow: 'Not now',
        staffHint: 'Get alerts for new appointments, changes and cancellations even when Salovare is closed.',
        customerHint: 'Get alerts when an appointment is booked, changed, cancelled or coming up.',
        denied: 'Notifications are blocked in the browser. Allow them in the site settings to turn them on.',
        unsupported: 'This browser does not support push notifications.',
        unavailable: 'Push notifications are not available yet.',
        error: 'Could not update push notifications. Please try again.',
      },
      store: {
        clickToChange: 'Click to change store',
        currentStore: 'Current store',
        selectStore: 'Select Store',
        registrationTitle: 'Store Registration',
        registrationSubtitle: 'Create a new store',
        accessDenied: 'Access denied. You need permission to create stores.',
      },
      user: {
        clickToOpenMenu: 'Click to open menu',
        profile: 'Profile',
        logout: 'Logout',
        defaultName: 'User',
      },
      storePlan: {
        upgradeRequired: 'This feature is available on the Pro plan. Upgrade to unlock it.',
        upgradeCta: 'Upgrade to Pro',
        upgradeToPro: 'Upgrade to Pro',
        freePlanTitle: 'Free plan',
        freeBadge: 'Free',
        freePlanDescription: 'Your store is on Free. Upgrade to Pro for extra professionals, extra stores, products, memberships, payroll, waitlist, additional hours, and SMS.',
        lapsedTitle: 'Reactivate Pro',
        lapsedBadge: 'Pro ended',
        lapsedDescription: 'Your Pro subscription ended. Reactivate to restore extra professionals, extra stores, and Pro features. Your store stays online on Free.',
        reactivateAction: 'Reactivate Pro',
        reactivateTitle: 'Reactivate your account',
        reactivateDescription: 'Your Pro plan has ended. Reactivate to restore extra professionals, extra stores, and Pro-only features. Your store stays online.',
        paymentFailedTitle: 'Update your payment method',
        paymentFailedDescription: 'We could not process your last payment. Update your card in the billing portal to keep Pro.',
        paymentFailedAction: 'Update payment method',
        extraStoreTitle: 'Extra stores are a Pro feature',
        extraStoreMessage: 'Free includes one store. Upgrade to Pro to add another store.',
        extraMemberTitle: 'Extra professionals are a Pro feature',
        extraMemberMessage: 'Free includes one professional. Upgrade to Pro to add team members.',
        smsUpgradeTitle: 'SMS is included with Pro',
        smsUpgradeMessage: 'Upgrade to Pro to send appointment SMS and WhatsApp messages.',
        prioritySupportBadge: 'Pro',
        upgradeDialog: {
          eyebrow: 'Salovare Pro',
          title: 'Unlock the full platform',
          lapsedTitle: 'Bring Pro back',
          subtitle: 'Grow beyond a single professional with payments, team tools and client messaging.',
          priceNote: '{{introPrice}}/month for the first {{months}} months, then {{regularPrice}}/month. Cancel anytime.',
          askOwner: 'Ask the store owner to start the Pro plan.',
          crownAria: 'Pro feature. Open upgrade details.',
          crownTooltip: 'Pro feature',
          lockedPageTitle: 'This is a Pro feature',
          lockedPageSubtitle: 'Upgrade to unlock products, memberships, payroll and the rest of the Pro toolkit.',
        },
      },
      ownerProfile: {
        title: 'Profile',
        subtitle: 'Your personal details and time off',
        saved: 'Profile saved',
        tabs: {
          profile: 'Profile',
          timeOff: 'Time off',
        },
        error: {
          load: 'Could not load your profile.',
          save: 'Could not save your profile.',
        },
      },
      stores: {
        title: 'Stores',
        subtitle: 'Details and settings for the selected store',
        addNew: 'Add New Store',
        creationBlockedTitle: 'Billing setup required',
        creationBlockedMessage: 'Upgrade to Pro on your current store before adding another store.',
        creationBlockedAction: 'Open payment settings',
        totalStores: 'Total Stores',
        managedStores: 'Managed Stores',
        searchPlaceholder: 'Search stores...',
        filters: 'Filters',
        noResults: 'No stores found matching your search',
        noStores: 'No stores available',
        emptySelected: 'Select or create a store to get started.',
        tabs: {
          main: 'Main',
          images: 'Images',
          roles: 'Roles & Permissions',
          rolesShort: 'Roles',
          onRequestHours: 'Additional hours',
          onRequestHoursShort: 'Hours',
          additionalCosts: 'Additional costs',
          additionalCostsShort: 'Costs',
          waitlist: 'Waitlist',
          branding: 'Branding',
        },
        branding: {
          title: 'Brand colors',
          description: 'Choose up to four colors for your booking pages and this management area. Leave a color empty to keep the Salovare default.',
          roles: {
            primary: { label: 'Primary', help: 'Top bars, menus and dark surfaces.' },
            accent: { label: 'Accent', help: 'Buttons, links, highlights and selected items.' },
            text: { label: 'Text', help: 'Main text on light backgrounds.' },
            background: { label: 'Background', help: 'Page background.' },
          },
          hexPlaceholder: '#RRGGBB',
          invalidHex: 'Use a hex color such as #A15C43.',
          pickColor: 'Pick the {{role}} color',
          useDefault: 'Use default',
          defaultValue: 'Default: {{color}}',
          resetAll: 'Reset to Salovare colors',
          previewTitle: 'Preview',
          preview: {
            storeName: 'Your store',
            customer: 'Customer',
            management: 'Management',
            login: 'Login',
            previous: 'Previous',
            next: 'Next',
            customerTitle: 'Customer booking page',
            managementTitle: 'Management area',
            managementMenu: 'Navigation',
            managementCard: 'Today',
            loginTitle: 'Sign in to continue',
            bookButton: 'Book now',
            menuItem: 'Agenda',
            serviceName: 'Haircut & beard',
            serviceMeta: '45 min · with Ana',
            selected: 'Selected',
          },
          warnings: {
            textContrast: 'This text color is hard to read on the background, so a more readable shade will be used.',
            accentContrast: 'This accent is hard to see on the background, so a more readable shade will be used for links and icons.',
          },
          saved: 'Brand colors saved.',
          saveError: 'Could not save the brand colors.',
          upgradeTitle: 'Custom brand colors are a Pro feature',
          upgradeMessage: 'Upgrade to Pro to use your own colors on your booking pages and in the management area.',
          readOnly: 'You do not have permission to change the brand colors.',
          images: {
            title: 'Branding images',
            description: 'Add optional images for your management header, customer header and authentication pages. Images are resized automatically.',
            managementLogo: { label: 'Management logo', help: 'Shown in the management header on desktop and mobile.' },
            customerLogo: { label: 'Customer logo', help: 'Shown in the customer header on desktop and mobile.' },
            loginLogo: { label: 'Login logo', help: 'Shown on customer login and registration pages.' },
            loginBackground: { label: 'Login background', help: 'Shown behind customer authentication pages.' },
            favicon: { label: 'Browser icon', help: 'Shown in the browser tab for this store.' },
            saved: 'Branding image saved.',
            deleted: 'Branding image removed.',
            confirmDelete: 'Remove this branding image?',
            error: {
              uploading: 'Could not upload the branding image.',
              deleting: 'Could not remove the branding image.',
            },
          },
        },
          manager: 'Manager',
          teamMember: 'Team Member',
          active: 'Active',
          inactive: 'Inactive',
        actions: {
          view: 'View',
          edit: 'Edit',
          billing: 'Update payment details',
          teamMembers: 'Team Members',
          roles: 'Roles & Permissions',
          images: 'Manage Images',
          copyBookingLink: 'Copy booking link',
          delete: 'Delete',
          enable: 'Enable',
          disable: 'Disable',
          more: 'More Options'
        },
        table: {
          name: 'Store Name',
          role: 'Role',
          permissions: 'Permissions',
          selectRole: 'Select a role',
          optional: 'Optional',
          selected: 'Selected',
          status: 'Status',
          actions: 'Actions'
        },
        delete: {
          title: 'Delete Store',
          message: 'Are you sure you want to delete "{name}"? This action cannot be undone.'
        },
        feedback: {
          bookingLinkCopied: 'Booking link copied to clipboard'
        },
        authorizationHours: {
          helper: 'These extra hours sit outside regular opening. Customers request a booking and staff must confirm it.',
          save: 'Save additional hours',
          saved: 'Additional hours saved',
          overlapOpening: 'Additional hours cannot overlap opening hours.',
          overlapPeriods: 'Additional periods cannot overlap.',
          openingHoursLabel: 'Opening hours',
        },
        timePricing: {
          helper: 'Charge more for appointments that start on selected days or times, including extra hours. Percentage extra uses service prices, never the membership plan price.',
          shareTitle: 'Professional share',
          shareHelper: 'How much of the additional cost professionals receive on top of salary or commission. 0 keeps the extra in the store.',
          shareType: 'Share type',
          shareValue: 'Share value',
          rulesTitle: 'Day and time rules',
          addRule: 'Add rule',
          empty: 'No additional-cost rules. Customers will see regular prices only.',
          ruleLabel: 'Rule {{index}}',
          weekDays: 'Weekdays',
          allDay: 'All day',
          allDayHelper: 'Applies to the whole calendar day, including extra hours. Changing opening hours later does not change this rule.',
          from: 'From',
          to: 'To',
          presets: 'Fill from opening or additional hours',
          adjustmentType: 'Additional cost type',
          adjustmentValue: 'Value',
          save: 'Save additional costs',
          saved: 'Additional costs saved',
          adjustmentTypes: {
            Percentage: 'Percentage of service prices',
            FixedAmount: 'Fixed amount',
          },
          validation: {
            weekDays: 'Each rule needs at least one weekday.',
            amountNegative: 'Additional cost cannot be negative.',
            percentMax: 'Percentage cannot exceed 100.',
            periodRequired: 'Choose all day or a time window.',
            overlap: 'Rules cannot overlap on the same weekday.',
            shareNegative: 'Professional share cannot be negative.',
            sharePercent: 'Professional share percentage cannot exceed 100.',
          },
        },
        images: {
          title: 'Store Images',
          addImages: 'Add images',
          galleryTitle: 'Gallery',
          logoTitle: 'Logo',
          logoHelp: 'Used as the Home Screen app icon for customers.',
          appNameTitle: 'App name',
          appNamePlaceholder: 'e.g. Jhon Barber',
          appNameTooLong: 'Use at most {{max}} characters.',
          appNameSaved: 'App name saved.',
          saveAppName: 'Save',
          cropTitle: 'Crop logo',
          cropZoom: 'Zoom',
          confirmDeleteLogo: 'Remove this store logo?',
          upload: 'Upload Images',
          uploading: 'Uploading...',
          confirmDelete: 'Are you sure you want to delete this image?',
          noImages: 'No images available',
          maxImagesReached: 'Maximum of {{max}} images reached',
          reorderHelp: 'Drag to change the order customers see.',
          drag: 'Drag to reorder',
          moveEarlier: 'Move earlier',
          moveLater: 'Move later',
          error: {
            loading: 'Failed to load images',
            uploading: 'Failed to upload images',
            deleting: 'Failed to delete image',
            reorder: 'Failed to save image order',
            maxImages: 'Maximum of {{max}} images allowed. You currently have {{current}} images. You can add up to {{allowed}} more image(s).',
            noFile: 'No file provided.',
            fileEmpty: 'File is empty.',
            fileTooLarge: 'File size exceeds the maximum allowed limit of {{maxSize}} MB.',
            invalidFileType: 'File type not allowed. Accepted formats: {{formats}}',
            invalidMimeType: 'File type not allowed. Accepted MIME types: {{mimeTypes}}',
            fileValidationError: 'File {{index}} ({{fileName}}): {{error}}',
            cropFailed: 'Could not crop the image. Please try another file.',
            savingAppName: 'Failed to save the app name.',
          }
        },
        error: {
          loading: 'Failed to load stores',
          notFound: 'Store not found',
          updateStatus: 'Failed to update store status',
          updatePaymentSettings: 'Failed to update payment settings',
          copyBookingLink: 'Failed to copy booking link',
          bookingLinkUnavailable: 'Booking link is not available for this store',
          updateAuthorizationHours: 'Failed to save additional hours',
          updateTimePricing: 'Failed to save additional costs'
        },
        pagination: {
          rowsPerPage: 'Rows per page:',
          of: 'of'
        },
        billing: {
          title: 'Keep your Pro subscription active',
          description: 'Add or update payment details to keep Pro features.',
          issueTitle: 'Your store needs billing attention',
          issueDescription: 'We could not collect the latest subscription payment. Update your billing details to keep the store active.',
          action: 'Manage billing',
          openPortal: 'Manage billing',
          opening: 'Opening secure billing portal...',
          errors: {
            ownerOnly: 'Only store owners can access the billing portal.',
            open: 'Failed to open the billing portal.',
            invalidUrl: 'Billing portal URL is invalid.'
          }
        },
        checkout: {
          action: 'Subscribe now',
          opening: 'Redirecting to checkout...',
          errors: {
            ownerOnly: 'Only store owners can start a checkout session.',
            open: 'Failed to start checkout. Please try again.',
            invalidUrl: 'Checkout URL is invalid.'
          }
        },
        stripeConnect: {
          title: 'Customer payments (Stripe)',
          loading: 'Checking Stripe account status...',
          notStarted: {
            title: 'Set up payments for your store',
            description: 'Connect your store to Stripe so customers can pay online for appointments and memberships.'
          },
          pending: {
            title: 'Finish your Stripe account setup',
            description: 'Stripe still needs more information before this store can accept customer payments. Continue the secure Stripe form to submit the missing details.'
          },
          status: {
            NotStarted: 'Not started',
            Pending: 'Action required',
            Complete: 'Ready'
          },
          flags: {
            detailsSubmitted: 'Details submitted',
            cardPayments: {
              active: 'Card payments active',
              pending: 'Card payments pending',
              inactive: 'Card payments inactive'
            }
          },
          membershipBilling: {
            pending: {
              title: 'Card payments are still being activated',
              description: 'Stripe onboarding is complete, but card payments are not active yet. This usually resolves within a few minutes. Refresh the status, or continue Stripe setup if more information is required.'
            },
            inactive: {
              title: 'Complete card payment setup for memberships',
              description: 'Membership plans need active card payments on your Stripe account. Continue the secure Stripe setup to submit any missing details, then refresh the status here.'
            }
          },
          actions: {
            start: 'Set up Stripe payments',
            continue: 'Continue Stripe setup',
            refresh: 'Refresh status',
            opening: 'Opening Stripe...'
          },
          feedback: {
            complete: 'Stripe payments are now enabled for this store.',
            membershipBillingReady: 'Membership billing is now ready. You can create membership plans.'
          },
          errors: {
            ownerOnly: 'Only store owners can manage Stripe onboarding.',
            loadStatus: 'Failed to load Stripe onboarding status.',
            open: 'Failed to open Stripe onboarding.',
            refresh: 'Failed to refresh Stripe account status.',
            invalidUrl: 'Stripe onboarding URL is invalid.'
          }
        },
        access: {
          checking: 'Checking access to this store...',
          refresh: 'Refresh access',
          backToStores: 'Back to stores',
          billingAttentionTitle: 'Your store needs billing attention',
          switchStoreLabel: 'Switch to another store',
          ownerHint: 'Open the secure billing portal to add payment details and restore management access.',
          nonOwnerHint: 'Only the store owner can restore access in billing. Contact the owner to update payment details.',
          portalCardTitle: 'Restore access from billing',
          portalCardDescription: 'Once payment details or the subscription are updated, refresh access and Salovare will unlock the store again.',
          reasons: {
            trialExpired: {
              badge: 'Trial ended',
              title: 'Your free trial has ended',
              description: 'This store is paused until billing is updated.'
            },
            subscriptionCanceled: {
              badge: 'Subscription canceled',
              title: 'This store subscription was canceled',
              description: 'Reactivate billing to unlock the management area again.'
            },
            subscriptionExpired: {
              badge: 'Subscription expired',
              title: 'This store subscription has expired',
              description: 'Update billing to unlock the management area again.'
            },
            subscriptionSuspended: {
              badge: 'Subscription suspended',
              title: 'This store subscription is suspended',
              description: 'Resolve the overdue billing issue to unlock the management area again.'
            },
            paymentMethodRequired: {
              badge: 'Payment method required',
              title: 'Payment method required',
              description: 'Add a payment method to activate this store subscription and restore access.'
            },
            subscriptionPastDue: {
              badge: 'Payment failed',
              title: 'Subscription payment failed',
              description: 'The latest subscription payment failed. Update billing to restore access to this store.'
            },
            extraStoreDeactivated: {
              badge: 'Store deactivated',
              title: 'This extra store is deactivated',
              description: 'Pro billing has lapsed. Your oldest store stays on the Free plan. Restore billing to reopen this extra store.'
            }
          }
        },
        view: {
          subtitle: 'View store details and information',
          basicInfo: 'Basic Information',
          name: 'Store Name',
          slug: 'Slug (URL)',
          status: 'Status',
          about: 'About',
          supportedLanguages: 'Supported Languages',
          storeLanguage: 'Default Store Language',
          address: 'Address',
          street: 'Street',
          door: 'Door',
          additionalInfo: 'Additional Info',
          city: 'City',
          state: 'State',
          zipCode: 'Zip Code',
          country: 'Country',
          openingHours: 'Opening Hours',
          contact: 'Contact',
          timeZone: 'Time Zone',
          slotInterval: 'Time Slot Interval',
          images: 'Images',
          paymentSettings: 'Payment Settings',
          payOnBooking: 'Accept online payments at booking',
          payOnBookingDescription: 'When enabled, customers can pay for their appointment online at the time of booking.',
          stripeRequiredForOnlinePayments: 'Stripe payment integration must be configured before enabling online payments.',
          addImages: 'Add Images',
          upload: 'Upload Images',
          uploading: 'Uploading...',
          confirmDelete: 'Are you sure you want to delete this image?',
          noImages: 'No images available',
          maxImagesReached: 'Maximum of {{max}} images reached',
          error: {
            loading: 'Failed to load images',
            uploading: 'Failed to upload images',
            deleting: 'Failed to delete image',
            maxImages: 'Maximum of {{max}} images allowed. You currently have {{current}} images. You can add up to {{allowed}} more image(s).',
            noFile: 'No file provided.',
            fileEmpty: 'File is empty.',
            fileTooLarge: 'File size exceeds the maximum allowed limit of {{maxSize}} MB.',
            invalidFileType: 'File type not allowed. Accepted formats: {{formats}}',
            invalidMimeType: 'File type not allowed. Accepted MIME types: {{mimeTypes}}',
            fileValidationError: 'File {{index}} ({{fileName}}): {{error}}'
          }
        }
      },
      teamMembers: {
        title: 'Team Members',
        subtitle: 'Manage your store team members',
        addNew: 'Add New Team Member',
        totalTeamMembers: 'Total Team Members',
        searchPlaceholder: 'Search team members...',
        filters: 'Filters',
        noResults: 'No team members found matching your search',
        noTeamMembers: 'No team members available',
        actions: {
          view: 'View',
          edit: 'Edit',
          manageAgenda: 'Agenda',
          manageCompensation: 'Manage Compensation',
          enable: 'Enable',
          disable: 'Disable',
          more: 'More Options'
        },
        table: {
          name: 'Name',
          email: 'Email',
          manager: 'Manager',
          services: 'Services',
          status: 'Status',
          actions: 'Actions'
        },
        status: {
          active: 'Active',
          inactive: 'Inactive',
          acceptsBookings: 'Accepts bookings',
          noCustomerBookings: 'No customer bookings'
        },
        acceptsCustomerBookings: {
          label: 'Takes bookings',
          help: 'When enabled, this professional appears on the agenda, membership plans, and the customer booking page.',
          chargeNotice: 'Professionals who take bookings are charged.',
          currentPeriodNotice: 'This professional will still be charged for the current billing period and will not be charged in the next billing period.'
        },
        manager: 'Manager',
        teamMember: 'Team Member',
        services: {
          showMore: 'Show more',
          showLess: 'Show less',
          moreServices: '+{count} more'
        },
        registration: {
          title: 'Add New Team Member',
          subtitle: 'Create a new team member account for your store',
          personalInfo: 'Personal Information',
          name: 'Name',
          email: 'Email Address',
          phone: 'Phone Number',
          jobTitle: 'Job Title',
          jobTitlePlaceholder: 'e.g. Senior Stylist',
          genre: 'Gender',
          male: 'Male',
          female: 'Female',
          birthDate: 'Birth Date',
          language: 'Language',
          role: 'Access Type',
          permissions: 'Permissions',
          selectRole: 'Select access type',
          fromRole: 'From Access Type',
          additional: 'Additional',
          includesPermissions_one: 'Includes {{count}} permission',
          includesPermissions_other: 'Includes {{count}} permissions',
          rolePermissionsInfo: 'Permissions marked with "From Access Type" are included in the selected access type and cannot be modified. You can add additional permissions below.',
          storeAssignment: 'Store Assignment',
          currentStore: 'Current Store',
          isManager: 'Is Manager',
          services: 'Services',
          noServices: 'No services available for this store',
          create: 'Create',
          assign: 'Add to Store',
          emailCheck: {
            check: 'Check',
            available: 'This email is available. Complete the remaining details below.',
            existingTitle: 'Team member already registered',
            existingMessage: '{{name}} ({{email}}) is already registered in Salovare. Do you want to add this team member to your store? Their existing profile will not be changed.',
            existingConfirm: 'Add to this store',
            existingConfirmed: '{{name}} will be added to this store. Configure access type, permissions, and services below.',
            alreadyInStore: 'This team member is already assigned to this store.',
            error: 'Could not verify this email. Please try again.'
          },
          success: {
            created: 'Team member created successfully! Password setup email sent.',
            assigned: 'Team member added to this store successfully.'
          },
          error: {
            requiredFields: 'Name and email are required fields',
            loadingServices: 'Failed to load services',
            creation: 'Failed to create team member'
          },
          steps: {
            step1: {
              title: 'Personal Information',
              description: 'Enter the team member\'s personal details and contact information'
            },
            step2: {
              title: 'Access Type & Permissions',
              description: 'Assign an access type and configure permissions for this team member'
            },
            step3: {
              title: 'Services',
              description: 'Select the services this team member can provide'
            },
            step4: {
              title: 'Review',
              description: 'Review all information before creating the team member'
            }
          },
          validation: {
            nameRequired: 'Name is required',
            emailRequired: 'Email is required',
            emailInvalid: 'Please enter a valid email address',
            phoneRequired: 'Phone number is required',
            servicesRequired: 'Please select at least one service',
            emailCheckRequired: 'Please check this email before continuing.',
            alreadyInStore: 'This team member is already assigned to this store.',
            existingMemberConfirmationRequired: 'Please confirm that you want to add this existing team member to your store.'
          },
          namePlaceholder: 'Enter name',
          emailPlaceholder: 'Enter email address',
          phonePlaceholder: 'Enter phone number',
          selectGenre: 'Select gender',
          servicePrice: 'Price',
          serviceDuration: 'Duration',
          noServicesSelected: 'No services selected',
          defaultPrice: 'Default Price',
          defaultDuration: 'Default Duration',
          minutes: 'minutes',
          customSettings: 'Custom Settings',
          customPrice: 'Custom Price',
          customPriceHelp: 'Leave empty to use default price',
          price: 'Price',
          duration: 'Duration',
          custom: 'custom',
          default: 'default'
        },
        billing: {
          confirmTitle: 'Additional team member cost',
          confirmIntro: 'Adding a professional who takes bookings will incur an additional subscription cost of {{price}}/month per seat beyond the included member.',
          confirmAdd: 'Confirm and add member',
          confirmEnable: 'Confirm and enable member',
          immediateCharge: '{{amount}} will be charged now for the rest of this billing period (until {{date}}).',
          recurringCharge: 'Then {{amount}}/month from your next billing date ({{date}}).',
          recurringChargeNoDate: 'Then {{amount}}/month on each renewal.',
          removalPolicy: 'If you disable this member before {{date}}, you will still be charged for this seat until the end of the current billing period. It will not be charged in the next billing period.',
          removalPolicyNoDate: 'If you disable this member before the next billing date, you will still be charged for this seat until the end of the current billing period. It will not be charged in the next billing period.',
          trialNotice: 'This will add {{price}}/month after your trial ends. No charge during the trial.',
          reviewChargeNotice: 'Adding a professional who takes bookings will add {{price}}/month to your subscription.',
          assignExistingNotice: 'Adding this member will cost {{amount}}/month after billing is active. Removal takes effect from the next billing period ({{date}}).',
          assignExistingNoticeNoDate: 'Adding this member will cost {{amount}}/month after billing is active.',
          disabledNotice: 'This member was disabled. Billing for their seat continues until the next billing period.',
          syncFailed: 'Could not update your subscription for the additional team member. Check Payment Settings and try again.'
        },
        picture: {
          upload: 'Change picture',
          uploading: 'Uploading...',
          delete: 'Remove picture',
          deleting: 'Removing...',
          editProfilePicture: 'Edit profile picture',
          modalTitle: 'Profile picture',
          modalDescription: 'Change or remove this team member\'s profile picture.',
          confirmDelete: 'Remove this team member\'s profile picture?',
          uploadError: 'Failed to upload profile picture.',
          deleteError: 'Failed to delete profile picture.'
        },
        timeOff: {
          modalTitle: 'Manage Time Off',
          modalSubtitle: 'Schedule unavailability for {{name}}',
          scheduledTitle: 'Scheduled time off',
          addTitle: 'Add time off',
          empty: 'No time off scheduled in this period.',
          fullDay: 'Full day',
          add: 'Add time off',
          adding: 'Adding...',
          delete: 'Delete',
          confirmDeleteTitle: 'Delete time off',
          confirmDeleteMessage: 'Remove this time off entry?',
          types: {
            AnnualLeave: 'Annual leave',
            SickLeave: 'Sick leave',
            Other: 'Other'
          },
          fields: {
            date: 'Date',
            type: 'Type',
            fullDay: 'Full day',
            fullDayHelp: 'When enabled, the team member is unavailable for the entire day.',
            from: 'From',
            to: 'To',
            details: 'Details',
            detailsPlaceholder: 'Optional notes'
          },
          validation: {
            dateRequired: 'Date is required.',
            pastDate: 'Time off cannot be scheduled in the past.',
            invalidPeriod: 'Start time must be before end time.',
            pastPeriod: 'Time off cannot start in the past.'
          },
          success: {
            created: 'Time off added successfully.',
            deleted: 'Time off removed successfully.'
          },
          error: {
            loading: 'Failed to load time off entries.',
            create: 'Failed to add time off.',
            delete: 'Failed to remove time off.',
            conflict: 'This time off overlaps with an existing entry.',
            pastDate: 'Time off cannot be scheduled in the past.'
          }
        },
        agenda: {
          title: 'Agenda',
          subtitle: 'Manage working hours, special openings and time off for this professional.',
          tabs: {
            workingHours: 'Working hours',
            specialOpenings: 'Special openings',
            timeOff: 'Time off',
          },
          error: {
            loading: 'Failed to load the agenda.',
          },
        },
        workingHours: {
          title: 'Working hours',
          useStoreHours: 'Use store working hours',
          storeHoursLabel: 'Store hours',
          save: 'Save working hours',
          success: {
            saved: 'Working hours saved.',
          },
          error: {
            save: 'Failed to save working hours.',
          },
          validation: {
            overlapPeriods: 'Working periods cannot overlap.',
          },
        },
        specialOpenings: {
          calendarLabel: 'Select dates',
          calendarHelp: 'Tap dates to add or remove them. Outlined days already have a special opening.',
          defaultFrom: 'Default open',
          defaultTo: 'Default close',
          emptySelection: 'Select one or more dates on the calendar.',
          save: 'Save opening days',
          saving: 'Saving...',
          existingTitle: 'Saved special openings',
          existingEmpty: 'No special openings scheduled in this period.',
          confirmDeleteTitle: 'Delete special opening',
          confirmDeleteMessage: 'Remove this special opening? Clients will no longer see those slots for this professional.',
          validation: {
            datesRequired: 'Select at least one date.',
            invalidPeriod: 'Open time must be before close time.',
          },
          success: {
            saved: 'Special opening days saved.',
            deleted: 'Special opening removed.',
          },
          error: {
            loading: 'Failed to load special openings.',
            save: 'Failed to save special openings.',
            delete: 'Failed to remove special opening.',
          },
        },
        error: {
          loading: 'Failed to load team members',
          noStoreSelected: 'No store selected. Please select a store first.',
        },
        fallback: {
          message: 'Please select a store from the top menu to manage team members.',
          goToStores: 'Go to Stores'
        },
        update: {
          title: 'Update Team Member',
          subtitle: 'Modify team member information and settings',
          update: 'Update',
          discardChanges: 'Discard Changes',
          discardSuccess: 'Changes discarded. Form reset to original data.',
          discardError: 'Failed to discard changes.',
          success: {
            updated: 'Team member updated successfully!'
          },
          error: {
            loading: 'Failed to load team member information',
            update: 'Failed to update team member'
          },
          steps: {
            step1: {
              title: 'Personal Information',
              description: 'Update the team member\'s personal details and contact information'
            },
            step2: {
              title: 'Access Type & Permissions',
              description: 'Update the access type and permissions for this team member'
            },
            step3: {
              title: 'Services',
              description: 'Update the services this team member can provide'
            },
            step4: {
              title: 'Review',
              description: 'Review all information before updating the team member'
            }
          }
        }
      },
      servicesManagement: {
        title: 'Services',
        subtitle: 'Manage your store services',
        tabs: {
          services: 'Services',
          categories: 'Categories',
        },
        addNew: 'Add New Service',
        noServices: 'No services available',
        emptyHint: 'Create your first service to start assigning it to team members and appointments.',
        card: {
          noDescription: 'No description provided',
          primaryService: 'Primary service',
          secondaryService: 'Secondary service',
          duration: 'Duration',
          price: 'Price'
        },
        actions: {
          edit: 'Edit',
          delete: 'Delete'
        },
        fields: {
          name: 'Service Name',
          description: 'Service Description',
          type: 'Service Type',
          typeHelpTitle: 'What does service type mean?',
          typeHelpAriaLabel: 'What does service type mean?',
          typeHelpPrimary: 'Primary is the main service the customer came for. The appointment lasts as long as this service takes. Use it for things like a haircut, coloring, or a manicure.',
          typeHelpSecondary: 'Secondary is an extra done at the same time as a main service. It does not make the appointment longer. Use it for things like eyebrows or a beard trim done during the haircut.',
          typeHelpExample: 'Example: a 30-minute haircut (primary) plus eyebrows (secondary). The customer is booked for 30 minutes, not 30 plus the eyebrow time. If people should also be able to book that extra on its own, turn on “Allow standalone booking” below.',
          duration: 'Duration',
          price: 'Price',
          isActive: 'Active',
          isActiveHelp: 'Turn this off to create or save the service as inactive.',
          isStandalone: 'Allow standalone booking',
          isStandaloneHelp: 'Enable this when customers can book the service without a primary service.',
          teamMembers: 'Team members who can perform this service',
          teamMembersHelp: 'Customers can only book this service when at least one team member is assigned to it.',
          noTeamMembersAvailable: 'No team members available to assign.'
        },
        types: {
          primary: 'Primary',
          secondary: 'Secondary'
        },
        bookingRule: {
          primary: 'Primary service',
          standalone: 'Standalone',
          requiresPrimary: 'Requires primary service'
        },
        status: {
          active: 'Active',
          inactive: 'Inactive'
        },
        dialog: {
          createTitle: 'Add Service',
          editTitle: 'Edit Service',
          saveCreate: 'Create Service',
          saveEdit: 'Save Changes',
          steps: {
            details: 'Details',
            pricing: 'Pricing',
            consent: 'Consent forms',
          },
        },
        unassignedWarning: {
          title: 'No team member assigned',
          message: 'Customers will not be able to book this service because no team member is assigned to perform it.',
          confirmCreate: 'Create anyway',
          confirmSave: 'Save anyway'
        },
        delete: {
          title: 'Delete Service',
          message: 'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
          confirm: 'Delete',
          cancel: 'Cancel'
        },
        feedback: {
          created: 'Service created successfully!',
          updated: 'Service updated successfully!',
          deleted: 'Service deleted successfully!'
        },
        validation: {
          nameRequired: 'Service name is required for all supported languages',
          priceRequired: 'Price is required and must be greater than 0',
          durationRequired: 'Duration is required'
        },
        error: {
          loading: 'Failed to load services',
          create: 'Failed to create service',
          update: 'Failed to update service',
          delete: 'Failed to delete service',
          noStoreSelected: 'No store selected. Please select a store first.'
        },
        fallback: {
          message: 'Please select a store from the top menu to manage services.',
          goToStores: 'Go to Stores'
        }
      },
      productsManagement: {
        title: 'Products',
        subtitle: 'Manage your store products and sales',
        tabs: {
          products: 'Products',
          sales: 'Sales',
          settings: 'Commission',
          commission: 'Commission',
        },
        addNew: 'Add product',
        recordSale: 'Record sale',
        noProducts: 'No products yet',
        emptyHint: 'Create your first product to start selling it in store and during booking.',
        card: {
          noDescription: 'No description provided',
          price: 'Price',
          stock: 'In stock',
          outOfStock: 'Out of stock',
          lowStock: 'Low stock',
        },
        actions: {
          edit: 'Edit',
          delete: 'Delete',
          sell: 'Record sale',
        },
        fields: {
          name: 'Product name',
          description: 'Product description',
          price: 'Price',
          stock: 'Stock quantity',
          stockHelp: 'Leave empty to skip stock tracking. Sales are never blocked by stock.',
          isActive: 'Active',
          isActiveHelp: 'Turn this off to hide the product from new sales.',
          image: 'Product image',
          imageHelp: 'Optional. Images are resized before upload, same as store photos.',
          imageRemoveConfirm: 'Remove this product image?',
        },
        status: {
          active: 'Active',
          inactive: 'Inactive',
        },
        dialog: {
          createTitle: 'Add product',
          editTitle: 'Edit product',
          saveCreate: 'Create product',
          saveEdit: 'Save changes',
        },
        delete: {
          title: 'Delete product',
          message: 'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
          confirm: 'Delete',
          cancel: 'Cancel',
        },
        sale: {
          title: 'Record product sale',
          product: 'Product',
          quantity: 'Quantity',
          addLine: 'Add',
          lines: 'No products added yet',
          total: 'Total',
          professional: 'Professional',
          noProfessional: 'No professional',
          professionalLocked: 'You can only record sales as yourself. Assigning another professional requires extra permission.',
          customer: 'Customer',
          walkIn: 'Anonymous',
          notes: 'Notes',
          commissionHint: 'Estimated commission: {{amount}}',
          save: 'Record sale',
          validation: {
            noLines: 'Add at least one product',
            quantityInvalid: 'Quantity must be at least 1',
            amountInvalid: 'Payment amount is invalid',
          },
        },
        sales: {
          title: 'Sales',
          detailsTitle: 'Sale details',
          advancedSearch: 'Advanced search',
          hideAdvancedSearch: 'Hide advanced search',
          table: {
            date: 'Date',
            channel: 'Channel',
            customer: 'Customer',
            professional: 'Professional',
            items: 'Items',
            total: 'Total',
            collected: 'Collected',
            discount: 'Discount',
            commission: 'Commission',
            paymentMethod: 'Payment',
            status: 'Status',
            audit: 'Audit',
          },
          channels: {
            staffInStore: 'In store',
            customerSelfService: 'Online',
          },
          statuses: {
            pending: 'Pending',
            completed: 'Completed',
            voided: 'Reverted',
          },
          sellerAssignedBy: 'Seller assigned by {{name}}',
          customerLinesModifiedBy: 'Customer lines edited by {{name}}',
          void: 'Revert',
          voidConfirm: 'Revert this completed sale? Stock and commission will be reversed.',
          empty: 'No product sales in this period.',
        },
        checkout: {
          sectionTitle: 'Products',
          customerSelection: 'Customer selection',
          customerSelectionHint: 'The customer chose these items. They pay no commission unless a professional is credited.',
          addedInStore: 'Added in store',
          readOnlyHint: 'You need extra permission to change what the customer selected.',
        },
        settings: {
          title: 'Product commission',
          description: 'The same percentage applies to every professional. Changes affect only future sales.',
          commissionPercent: 'Commission percent',
          commissionPercentHelp: 'Enter a value between 0 and 100. This percentage is frozen on each completed sale.',
          selfServiceNote: 'Products a customer selects themselves pay no commission unless a manager credits a professional.',
          save: 'Save',
          saved: 'Product settings saved',
        },
        feedback: {
          created: 'Product created successfully!',
          updated: 'Product updated successfully!',
          deleted: 'Product deleted successfully!',
          saleRecorded: 'Product sale recorded.',
          saleVoided: 'Product sale reverted.',
          settingsSaved: 'Product settings saved.',
        },
        validation: {
          nameRequired: 'Product name is required for all supported languages',
          priceRequired: 'Price is required and must be greater than 0',
          stockInvalid: 'Stock quantity cannot be negative',
        },
        error: {
          loading: 'Failed to load products',
          create: 'Failed to create product',
          update: 'Failed to update product',
          delete: 'Failed to delete product',
          createSale: 'Failed to record product sale',
          voidSale: 'Failed to revert product sale',
          settings: 'Failed to save product settings',
          image: 'Failed to update product image',
          noStoreSelected: 'No store selected. Please select a store first.',
        },
        fallback: {
          message: 'Please select a store from the top menu to manage products.',
          goToStores: 'Go to Stores',
        },
      },
      serviceCategoriesManagement: {
        addNew: 'Add Category',
        noCategories: 'No categories yet',
        emptyHint: 'Create categories to organize services for your team and customer booking.',
        card: {
          unnamed: 'Unnamed category',
          serviceCount: '{{count}} services',
        },
        actions: {
          drag: 'Drag to reorder',
          moveUp: 'Move up',
          moveDown: 'Move down',
          edit: 'Edit',
          delete: 'Delete',
        },
        status: {
          active: 'Active',
          inactive: 'Inactive',
        },
        fields: {
          name: 'Name',
          description: 'Description',
          active: 'Active',
          services: 'Assigned services',
          noServicesAvailable: 'No services available to assign.',
        },
        dialog: {
          createTitle: 'Add Category',
          editTitle: 'Edit Category',
          saveCreate: 'Create Category',
          saveEdit: 'Save Changes',
        },
        delete: {
          title: 'Delete Category',
          message: 'Are you sure you want to delete "{{name}}"? Services will remain but will no longer be grouped under this category.',
          confirm: 'Delete',
        },
        feedback: {
          created: 'Category created successfully!',
          updated: 'Category updated successfully!',
          deleted: 'Category deleted successfully!',
        },
        validation: {
          nameRequired: 'Category name is required for all supported languages',
        },
        error: {
          loading: 'Failed to load categories',
          create: 'Failed to create category',
          update: 'Failed to update category',
          delete: 'Failed to delete category',
          reorder: 'Failed to save category order',
          noStoreSelected: 'No store selected. Please select a store first.',
        },
      },
      membershipPlansManagement: {
        title: 'Membership Plans',
        subtitle: 'Create and manage recurring membership plans for your customers',
        tabs: {
          plans: 'Plans',
          customerMemberships: 'Customer Memberships',
          customerMembershipsShort: 'Memberships',
          settings: 'Settings',
        },
        revenueSettings: {
          title: 'Membership revenue settings',
          subtitle: 'Store margin and pay day for subscription pool sharing. Applies to membership revenue only, not regular appointments.',
          storeMarginPercent: 'Store margin on subscriptions (%)',
          membershipSharePayDay: 'Membership share pay day',
          payDayOption: 'Day {{day}}',
          payDayHint: 'January subscription pool is paid in February on this day.',
          distributionModel: 'Distribution model',
          distributionModelOptions: {
            monthlyPot: 'Monthly pot',
            periodUsage: 'Usage-based',
          },
          distributionModelHints: {
            monthlyPot: 'On the store pay day, unpaid closed subscription periods are pooled and shared by duration minutes among team members who completed those visits.',
            periodUsage: 'On the store pay day, each unpaid closed billing period is paid separately. Team members receive a share of that period’s team pool equal to their visit share. When one visit includes services by more than one team member, the visit is split by the original service list prices (for example Haircut €10 + Beard Trim €20 on a €45 pool pays €15 and €30). Unused visits on limited plans stay with the store.',
          },
          distributionModelSwitchWarning: 'Takes effect on the next pay day ({{date}}). Unpaid closed subscription revenue will be split with the model selected that day. Amounts already paid are not reversed.',
          pendingUndistributed: '{{amount}} is waiting to be paid on {{date}} with the selected model.',
          error: {
            loading: 'Failed to load membership revenue settings.',
            save: 'Failed to save membership revenue settings.',
            marginRange: 'Store margin must be between 0 and 100.',
          },
          success: {
            saved: 'Membership revenue settings saved successfully.',
          },
        },
        addNew: 'Add Membership Plan',
        noPlans: 'No membership plans available',
        emptyHint: 'Create your first membership plan to offer recurring subscriptions to your customers.',
        card: {
          unnamed: 'Membership plan',
          price: 'Price',
          perMonth: '/month',
          includedServices: 'Included services',
          subscribers: 'Subscribers',
          customerCancellation: 'Customer can cancel'
        },
        actions: {
          edit: 'Edit',
          activate: 'Activate',
          deactivate: 'Deactivate'
        },
        fields: {
          name: 'Plan Name',
          price: 'Monthly Price',
          billingInterval: 'Billing Interval',
          entitlementType: 'Entitlement',
          allowanceQuantity: 'Uses per month',
          commitmentMonths: 'Minimum commitment (months)',
          commitmentMonthsPlaceholder: 'Optional',
          commitmentMonthsHelp: 'Leave empty if customers can cancel anytime after the first billing period.',
          allowCustomerCancellation: 'Allow customer cancellation',
          allowCustomerCancellationHelp: 'When enabled, customers can cancel their membership from the customer portal.',
          includedServices: 'Included Services',
          noServicesAvailable: 'No active services are available. Create services before adding a membership plan.',
          includedProfessionals: 'Professionals (optional)',
          includedProfessionalsHelp: 'Leave all unchecked to allow all professionals. Check specific ones to restrict this plan to those team members.',
          noProfessionalsAvailable: 'No active professionals found.'
        },
        billingInterval: {
          monthly: 'Monthly'
        },
        entitlement: {
          unlimited: 'Unlimited use',
          fixedAllowance: 'Fixed allowance per month',
          allowance: '{{count}} uses per month'
        },
        status: {
          active: 'Active',
          inactive: 'Inactive'
        },
        dialog: {
          createTitle: 'Add Membership Plan',
          editTitle: 'Edit Membership Plan',
          saveCreate: 'Create Plan',
          saveEdit: 'Save Changes'
        },
        deactivate: {
          title: 'Deactivate Membership Plan',
          message: 'Are you sure you want to deactivate "{{name}}"? Existing subscribers will keep their plan until it is canceled. New customers will not be able to subscribe.',
          confirm: 'Deactivate',
          cancel: 'Cancel'
        },
        activate: {
          title: 'Activate Membership Plan',
          message: 'Are you sure you want to activate "{{name}}"? Customers will be able to subscribe to this plan again.',
          confirm: 'Activate',
          cancel: 'Cancel'
        },
        feedback: {
          created: 'Membership plan created successfully!',
          updated: 'Membership plan updated successfully!',
          activated: 'Membership plan activated successfully!',
          deactivated: 'Membership plan deactivated successfully!'
        },
        validation: {
          nameRequired: 'Plan name is required for all supported languages',
          priceRequired: 'Price is required and must be greater than 0',
          allowanceRequired: 'Allowance quantity is required and must be greater than 0',
          commitmentRequired: 'Commitment months must be greater than 0 when specified',
          servicesRequired: 'Select at least one included service'
        },
        error: {
          loading: 'Failed to load membership plans',
          create: 'Failed to create membership plan',
          update: 'Failed to update membership plan',
          activate: 'Failed to activate membership plan',
          deactivate: 'Failed to deactivate membership plan',
          billingNotReady: 'Membership billing is not ready yet. Complete Stripe setup on the Billing page, then try again.',
          noStoreSelected: 'No store selected. Please select a store first.'
        },
        paymentModeAdjust: {
          title: 'Change membership payment mode',
          message: 'This store is set to online-only membership payments, but Stripe is not configured yet. To create this plan for in-store payments, the membership payment mode will be changed from Online only to Both.',
          confirm: 'Continue and create plan',
          error: 'Failed to update payment settings. Please try again.',
        },
        fallback: {
          message: 'Please select a store from the top menu to manage membership plans.',
          goToStores: 'Go to Stores'
        }
      },
      permissions: {
        infoAriaLabel: 'Permission details',
      },
      roles: {
        title: 'Roles & Permissions',
        subtitle: 'Manage roles and permissions for your store',
        addNew: 'Add New Role',
        noRoles: 'No roles found',
        searchPlaceholder: 'Search roles...',
        actions: {
          edit: 'Edit',
          delete: 'Delete',
          view: 'View'
        },
        card: {
          permissions: 'Permissions',
          permissionCount: '{count} permission{plural}',
          teamMembers: 'Team Members',
          teamMemberCount: '{count} member{plural}'
        },
        modal: {
          create: {
            title: 'Create New Role',
            subtitle: 'Define a new role with specific permissions'
          },
          edit: {
            title: 'Edit Role',
            subtitle: 'Update role name and permissions'
          },
          form: {
            name: 'Role Name',
            nameRequired: 'Role name is required for all supported languages',
            permissions: 'Permissions',
            permissionsRequired: 'At least one permission must be selected',
            selectAll: 'Select All',
            deselectAll: 'Deselect All',
            categories: {
              teamMembers: 'Team Members',
              stores: 'Stores',
              services: 'Services',
              appointments: 'Appointments',
              memberships: 'Memberships',
              customers: 'Customers',
              products: 'Products',
              stock: 'Stock',
              expenses: 'Expenses',
              employeePayments: 'Employee Payments',
              chat: 'Chat',
              other: 'Other'
            }
          },
          save: 'Save Role',
          saving: 'Saving...',
          cancel: 'Cancel',
          success: {
            created: 'Role created successfully!',
            updated: 'Role updated successfully!'
          },
          error: {
            create: 'Failed to create role',
            update: 'Failed to update role',
            delete: 'Failed to delete role',
            inUse: 'Cannot delete role that is currently assigned to team members'
          }
        },
        delete: {
          title: 'Delete Role',
          message: 'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
          confirm: 'Delete',
          cancel: 'Cancel',
          inUse: 'This role is currently assigned to team members and cannot be deleted.'
        },
        error: {
          loading: 'Failed to load roles',
          noStoreSelected: 'No store selected. Please select a store first.'
        }
      },
      storeUpdate: {
        title: 'Update Store',
        subtitle: 'Modify store information and settings',
        storeInfo: 'Store Information',
        editSection: 'Edit Store Information',
        form: {
          name: 'Store Name',
          status: 'Store Status'
        },
        enabled: 'Enabled',
        disabled: 'Disabled',
        enabledDescription: 'Store is active and visible to customers',
        disabledDescription: 'Store is inactive and hidden from customers',
        success: 'Store updated successfully',
        slugChangeTitle: 'Change store URL?',
        slugChangeMessage: 'The old customer booking link will stop working.',
        slugChangeConfirm: 'Change URL',
        discardChanges: 'Discard Changes',
        discardSuccess: 'Changes discarded. Form reset to original data.',
        discardError: 'Failed to discard changes.',
        error: {
          loading: 'Failed to load store information',
          saving: 'Failed to update store'
        }
      },
      customer: {
        navigation: {
          home: 'Home',
          appointments: 'Appointments',
          memberships: 'Memberships',
          profile: 'Profile',
          settings: 'Settings'
        },
        memberships: {
          title: 'Memberships',
          subtitle: 'Subscribe to a recurring plan and enjoy included services every month.',
          card: {
            unnamed: 'Membership plan',
            perMonth: '/month',
            commitment: 'Minimum commitment: {{count}} months',
            pendingCheckout: 'Complete checkout to activate this membership.',
            cancelsAtPeriodEnd: 'You can keep using this membership until {{date}}. After that, it will be cancelled.'
          },
          entitlement: {
            unlimited: 'Unlimited use',
            allowance: '{{count}} uses per month'
          },
          usage: {
            unlimited: 'Unlimited uses remaining this period',
            remaining: '{{remaining}} of {{total}} uses remaining this period'
          },
          actions: {
            subscribe: 'Subscribe',
            subscribing: 'Redirecting...',
            continueCheckout: 'Continue checkout',
            bookAppointment: 'Book appointment',
            bookWithMembership: 'Book with membership',
            cancel: 'Cancel membership',
            canceling: 'Canceling...',
            pause: 'Pause membership',
            pausing: 'Pausing...',
            resume: 'Resume membership',
            resuming: 'Resuming...',
            resumeCancellation: 'Keep membership',
            resumingCancellation: 'Updating...',
            manage: 'Manage'
          },
          cancel: {
            confirmTitle: 'Cancel membership',
            confirmMessage: 'You can keep using this membership until {{date}} — the last period already paid. After that, it will be cancelled. Continue?',
            success: 'Your membership stays active until {{date}}. After that, it will be cancelled.',
            error: 'Failed to cancel membership. Please try again.'
          },
          pause: {
            confirmTitle: 'Pause membership',
            confirmMessage: 'Billing will stop while your membership is paused. You can resume it at any time.',
            success: 'Your membership has been paused.',
            error: 'Failed to pause membership. Please try again.'
          },
          resume: {
            success: 'Your membership has been resumed.',
            error: 'Failed to resume membership. Please try again.'
          },
          resumeCancellation: {
            success: 'Your membership will continue renewing.',
            error: 'Failed to keep your membership active. Please try again.'
          },
          status: {
            pendingCheckout: 'Checkout pending',
            active: 'Active',
            trialing: 'Trialing',
            pastDue: 'Past due',
            paused: 'Paused',
            canceled: 'Canceled',
            expired: 'Expired'
          },
          current: {
            title: 'Your membership',
            pendingCheckout: 'Complete checkout to activate your membership.'
          },
          checkout: {
            success: 'Membership checkout completed. Your subscription will appear shortly.',
            processing: 'We are confirming your payment. Your subscription will appear here once it completes.',
            canceled: 'Membership checkout was canceled.',
            error: 'Failed to start membership checkout. Please try again.',
            invalidUrl: 'Checkout URL was not returned by the server.'
          },
          empty: {
            title: 'No membership plans available',
            subtitle: 'This store has not published any membership plans yet.'
          },
          inStoreOnlyMessage: 'Visit the store to enroll in this membership plan.',
          bothModeHint: 'You can subscribe online or enroll in-store with the team.',
          browseOnly: 'Membership enrollment is handled in-store. Browse plans below and start a booking — a team member will enroll you and collect the membership fee at the salon.',
          error: {
            loading: 'Failed to load membership plans.'
          }
        },
        menu: {
          profile: 'Profile',
          logout: 'Logout'
        },
        addToHomeScreen: {
          bannerTitle: 'Install as app',
          subtitle: 'Add this store to your Home Screen',
          action: 'Install as app',
          iosGuideTitle: 'Add to Home Screen',
          iosFallback: 'Tap Share in Safari, then Add to Home Screen.',
          androidUnavailable: 'Open this page in Chrome to install the app.',
        },
        appointments: {
          title: 'Book an appointment',
          comingSoon: 'Appointment booking feature coming soon!',
          bookNow: 'Book now',
          myAppointments: {
            title: 'My Appointments',
            subtitle: 'Your latest appointments.',
            loadError: 'Failed to load appointments.',
            emptyTitle: 'You do not have appointments yet.',
            emptySubtitle: 'Start by creating your first booking.',
            services: 'Services',
            professional: 'Professional',
            unnamedService: 'Unnamed service',
            unnamedProfessional: 'Unknown professional',
            amountDue: 'Amount due',
            additionalCost: 'Additional time cost',
            coveredByMembership: 'Covered by membership',
            pendingMembershipEnrollment: 'Membership due in store',
            membershipPlanNote: 'Included with {{plan}}',
            membershipPriceNote: 'Pay {{plan}} membership in store',
            payNow: 'Pay now',
            paying: 'Redirecting...',
            cancel: 'Cancel appointment',
            cancelTitle: 'Cancel appointment?',
            cancelMessage: 'This appointment will be cancelled and the time slot will become available again.',
            cancelConfirm: 'Cancel appointment',
            cancelSuccess: 'Appointment cancelled successfully.',
            cancelError: 'Failed to cancel the appointment. Please try again.',
            cancelTooLate: 'Appointments cannot be cancelled within 2 hours of the start time.',
            awaitingConfirmationNote: 'The shop still needs to confirm this extra-hour time. You will be notified when it is accepted or declined.'
          },
          payment: {
            success: 'Payment completed successfully.',
            canceled: 'Payment was canceled.',
            failed: 'Payment failed! Click "Pay now" to try again.',
            failedNotBooked: 'Payment was not completed, so the appointment was not booked.',
            processing: 'Payment is processing. This will update shortly.',
            expired: 'Payment session expired! Click "Pay now" to try again.',
            checkoutError: 'Failed to start checkout. Please try again.'
          },
          status: {
            pending: 'Pending',
            awaitingConfirmation: 'Awaiting confirmation',
            awaitingPayment: 'Awaiting payment',
            cancelled: 'Cancelled',
            confirmed: 'Confirmed',
            completed: 'Completed',
            noShow: 'No-show',
            unknown: 'Unknown'
          },
          booking: {
            stepServices: 'Services',
            categoryAll: 'All',
            stepProfessionals: 'Professionals',
            stepCalendar: 'Date & Time',
            stepReview: 'Review & Confirm',
            selectServices: 'Select services',
            selectAtLeastOne: 'Select at least one service',
            servicesSelected: '{{count}} selected',
            next: 'Next',
            back: 'Back',
            confirm: 'Confirm',
            anyProfessional: 'Any professional',
            specificProfessional: 'Specific professional',
            professionalPerService: 'Professional per service',
            selectProfessional: 'Select professional',
            selectProfessionalForService: 'Select professional for {{service}}',
            professionalNotAble: 'This professional cannot perform one or more selected services.',
            specificProfessionalHelper: 'Use this option only when one professional can perform every selected service.',
            specificProfessionalUnavailable: 'No single professional can perform all selected services right now. Use Professional per service instead.',
            incompatibleProfessionalTitle: '{{name}} cannot perform all selected services',
            incompatibleProfessionalBody: '{{name}} cannot perform the following services:',
            selectAnotherProfessional: 'Select another professional',
            selectDifferentPerService: 'Different professional per service',
            suggestion: 'Suggested assignment: {{service1}} with {{name1}} and {{service2}} with {{name2}}. You can change the assignment below.',
            suggestionSingle: 'All selected services can be done by one professional.',
            selectDate: 'Select date',
            selectTime: 'Select time',
            pickDate: 'Pick a date',
            morning: 'Morning',
            afternoon: 'Afternoon',
            evening: 'Evening',
            noSlots: 'No available slots for this date.',
            chooseProfessional: 'Choose professional',
            multipleAvailable: 'More than one professional is available at this time. Who would you like?',
            selectTimeForProfessional: 'Select time for {{professional}}',
            noSlotsForProfessional: 'No slots available for this professional on the selected date.',
            selectTimeMobileHint: 'Pick one time per professional. Later services unlock after the previous one ends.',
            professionalsReady: '{{selected}} of {{total}} professionals ready',
            selectedTimeLabel: 'Selected: {{time}}',
            availableFrom: 'From {{time}}',
            selectPreviousTimeFirst: 'Select the previous time first',
            requiresCompanionService: 'Select at least one primary or standalone service to continue with this booking.',
            selectCompanionProfessionalFirst: 'Select the professional for the companion primary or standalone service first.',
            companionProfessionalOnly: 'Only already selected companion professionals are available for this service.',
            summary: 'Summary',
            dateTime: 'Date & time',
            services: 'Services',
            professional: 'Professional',
            totalPrice: 'Total',
            paymentMethod: 'Payment',
            payInStore: 'In store',
            payOnline: 'Online',
            payWithMembershipUnlimited: 'Use membership: {{plan}}',
            payWithMembershipAllowance: 'Use membership: {{plan}} ({{remaining}} left)',
            membershipSuccess: 'Appointment booked with your membership!',
            detailsPlaceholder: 'Additional details (optional)',
            success: 'Appointment booked successfully!',
            awaitingConfirmationSuccess: 'Request sent. We will confirm this extra-hour time with the shop.',
            requiresConfirmationHint: 'This time is outside regular opening hours. The shop must confirm before the booking is held.',
            onRequestSlot: 'On request',
            additionalCost: 'Additional time cost',
            products: 'Products',
            addProducts: 'Add products',
            addProductsHint: 'Would you like to add products to this appointment?',
            noThanks: 'No, thanks',
            editProducts: 'Edit',
            noProductsSelected: 'No products selected',
            productsTotal: 'Products total',
            onRequestAnyProfessionalHint: 'Any available professional can accept this extra-hour request.',
            error: 'Failed to book appointment. Please try again.',
            paymentNotCompleted: 'Payment was not completed, so the appointment was not booked. You can try again.',
            paymentReleaseFailed: 'Payment was not completed. If this time still appears under My Appointments, cancel it so the slot is freed.',
            membershipPlans: 'Membership plans',
            yourMemberships: 'Your memberships',
            selectedMembershipPlan: 'Selected membership plan',
            discoverMembershipPrompt: 'Interested in a membership?',
            seeMembershipPlans: 'See plans',
            membershipPlanServices: '{{count}} included services',
            membershipPlanCovered: 'Covered',
            membershipPlanActive: 'Active membership',
            membershipPlanNotEnrolled: 'Enroll in-store',
            coveredByMembership: 'Covered by membership',
            dueInStore: 'Due in store',
            membershipExtrasHint: 'Plan services stay selected. You can add extra paid services.',
            enrollInStoreNote: 'A team member will enroll you in this plan in-store. Pay {{price}} at the salon for this membership period.',
            planHowItWorksUnlimited: 'How it works: pay the monthly membership fee and use the included services as often as you like during each billing period.',
            planHowItWorksAllowance: 'How it works: pay the monthly membership fee and use the included services up to {{count}} times during each billing period.',
            coveredServiceTitle: 'Service covered by membership',
            coveredServiceBody: '"{{service}}" is included in your "{{plan}}" membership. Would you like to book via your membership or pay the regular price?',
            goWithMembership: 'Use membership',
            keepRegular: 'Regular price',
            renewalRiskTitle: 'Membership renewal reminder',
            renewalRiskBody: 'Your current membership period ends on {{date}}. If your membership is not renewed by the appointment date, the regular service price will apply. You can still continue booking.',
            continueAnyway: 'Continue',
            waitlist: {
              prompt: 'Can’t find your perfect slot?',
              cta: 'Join the waitlist',
              title: 'Join the waitlist',
              subtitle: 'Choose preferred dates and times. We will contact you if a matching slot opens.',
              join: 'Join waitlist',
              addRow: 'Add another date',
              removeRow: 'Remove date',
              timePlaceholder: 'Select a time window',
              any: 'Any',
              from: 'From',
              to: 'To',
              success: 'You joined the waitlist. We will notify you if a matching slot opens.',
              error: {
                preferences: 'Select at least one date and time.',
                services: 'Select services before joining the waitlist.',
                save: 'Could not join the waitlist. Please try again.',
              },
            },
          }
        },
        profile: {
          title: 'My Profile',
          subtitle: 'Manage your profile picture, phone number, language, and account.',
          name: 'Name',
          comingSoon: 'Profile management coming soon!',
          phone: {
            label: 'Phone number',
            placeholder: 'Enter phone number',
            helper: 'Used for appointment reminders by SMS.',
            saveSuccess: 'Phone number saved.',
            saveError: 'Failed to save phone number.',
            invalid: 'Please enter a valid phone number.'
          },
          picture: {
            upload: 'Upload Picture',
            uploading: 'Uploading...',
            delete: 'Delete Picture',
            deleting: 'Deleting...',
            uploadSuccess: 'Profile picture uploaded successfully.',
            uploadError: 'Failed to upload profile picture.',
            deleteSuccess: 'Profile picture deleted successfully.',
            deleteError: 'Failed to delete profile picture.',
            confirmDelete: 'Are you sure you want to delete your profile picture?'
          }
        },
        settings: {
          title: 'Settings',
          subtitle: 'Customize your preferences for this store.',
          comingSoon: 'Settings feature coming soon!'
        },
        store: {
          notFound: 'Store not found',
          location: 'Location',
          openingHours: 'Opening Hours',
          closed: 'Closed',
          open: 'Open',
          openUntil: 'Open until',
          opensAt: 'Opens at',
          opensTomorrow: 'Opens tomorrow',
          opensOn: 'Opens on',
          at: 'at',
          additionalHours: 'Additional hours',
          additionalHoursUntil: 'Additional hours until',
          additionalHoursFrom: 'Additional hours from',
          contact: 'Contact',
          getDirections: 'Get Directions',
          noImages: 'No images available',
          noServices: 'No services available',
          noTeamMembers: 'No team members available',
          noAbout: 'No information available',
          tabs: {
            overview: 'Overview',
            location: 'Location',
            images: 'Images',
            services: 'Services',
            products: 'Products',
            team: 'Team',
            about: 'About'
          }
        },
        products: {
          title: 'Products',
          subtitle: 'Add retail products when you book.',
          addWhenBooking: 'Add when booking',
          outOfStock: 'Out of stock',
        }
      },
    },
  },
  pt: {
    translation: {
      brand: 'Salovare',
      language: 'Idioma',
      languages: {
        en: 'English',
        pt: 'Português',
        es: 'Español'
      },
      login: {
        email: 'Endereço de Email',
        password: 'Senha',
        signIn: 'Entrar',
        signingIn: 'Entrando...',
        required: 'Email e senha são obrigatórios',
        invalid: 'Credenciais inválidas',
        serverError: 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.',
        adminQuestion: 'Admin?',
        teamMemberQuestion: 'Membro da Equipa?',
        or: 'OU',
        noAccount: 'Não tem uma conta? Registre-se',
        googleFailed: 'Falha no login com Google. Tente novamente.',
        registerStorePrompt: 'Ainda não tem conta?',
        registerStoreCta: 'Começar',
        storeRegistrationSuccess: 'Loja criada. Verifique seu email para definir a senha e concluir a configuração da conta.',
        backToLogin: 'Voltar ao Login',
        continue: 'Continuar',
        continuing: 'Continuando...',
        emailRequired: 'O email é obrigatório',
        emailInvalid: 'Introduza um endereço de email válido',
        emailCheckFailed: 'Não foi possível verificar este email agora. Tente novamente.',
        passwordRequired: 'A senha é obrigatória',
        changeEmail: 'Usar outro email',
        setPasswordPrompt: 'A sua conta existe, mas ainda não definiu uma senha. Podemos enviar um link por email para criar uma.',
        sendSetPasswordLink: 'Enviar link para definir senha',
        phoneOnlyCta: 'Registado só com telefone?',
        claim: {
          phone: 'Número de telefone',
          phoneRequired: 'O número de telefone é obrigatório',
          sendCode: 'Enviar código de verificação',
          sendingCode: 'A enviar código...',
          codeSent: 'Se este telefone estiver registado sem email, foi enviado um código de verificação.',
          code: 'Código de verificação',
          codeRequired: 'Introduza o código de verificação',
          verify: 'Verificar código',
          verifying: 'A verificar...',
          complete: 'Enviar link para definir senha',
          completing: 'A enviar...',
          success: 'Email para definir senha enviado. Verifique a sua caixa de entrada.',
          back: 'Voltar ao login com email',
          error: 'Não foi possível continuar. Tente novamente.',
        },
      },
      googleAuth: {
        continueWithGoogle: 'Continuar com Google',
        continueWithGoogleSignUp: 'Registar com Google',
      },
      forgotPassword: {
        forgotPassword: 'Esqueceu sua senha? Vamos dar um jeito nisso!',
        emailRequired: 'Digite seu email primeiro para redefinir a senha',
        sending: 'Enviando...',
        success: 'Link de redefinição enviado! Verifique seu email.',
        error: 'Ocorreu um erro. Tente novamente.',
      },
      resetPassword: {
        title: 'Redefinir Sua Senha',
        email: 'Endereço de Email',
        newPassword: 'Nova Senha',
        confirmPassword: 'Confirmar Senha',
        resetPassword: 'Redefinir Senha',
        resetting: 'Redefinindo...',
        backToLogin: 'Voltar ao Login',
        missingToken: 'Token de redefinição ausente',
        missingEmail: 'Endereço de email ausente',
        required: 'Senha e confirmação são obrigatórias',
        passwordMismatch: 'As senhas não coincidem',
        passwordTooShort: 'A senha deve ter pelo menos 8 caracteres',
        passwordRequireDigit: 'A senha deve conter pelo menos um dígito',
        passwordRequireLowercase: 'A senha deve conter pelo menos uma letra minúscula',
        passwordRequireUppercase: 'A senha deve conter pelo menos uma letra maiúscula',
        passwordRequireNonAlphanumeric: 'A senha deve conter pelo menos um caractere especial',
        passwordRequirements: 'A senha deve ter pelo menos 8 caracteres e conter maiúscula, minúscula, dígito e caractere especial',
        failed: 'Falha na redefinição da senha. Tente novamente.',
        success: 'Senha redefinida com sucesso!',
        loginRedirect: 'Senha redefinida com sucesso! Faça login com sua nova senha.',
      },
      setPassword: {
        title: 'Definir Sua Senha',
        email: 'Endereço de Email',
        newPassword: 'Nova Senha',
        confirmPassword: 'Confirmar Senha',
        setPassword: 'Definir Senha',
        setting: 'Definindo...',
        backToLogin: 'Voltar ao Login',
        missingToken: 'Token de definição de senha ausente',
        missingEmail: 'Endereço de email ausente',
        required: 'Senha e confirmação são obrigatórias',
        passwordMismatch: 'As senhas não coincidem',
        passwordTooShort: 'A senha deve ter pelo menos 8 caracteres',
        passwordRequireDigit: 'A senha deve conter pelo menos um dígito',
        passwordRequireLowercase: 'A senha deve conter pelo menos uma letra minúscula',
        passwordRequireUppercase: 'A senha deve conter pelo menos uma letra maiúscula',
        passwordRequireNonAlphanumeric: 'A senha deve conter pelo menos um caractere especial',
        passwordRequirements: 'A senha deve ter pelo menos 8 caracteres e conter maiúscula, minúscula, dígito e caractere especial',
        failed: 'Falha na definição da senha. Tente novamente.',
        success: 'Senha definida com sucesso!',
        loginRedirect: 'Senha definida com sucesso! Faça login com sua nova senha.',
      },
      firstAccess: {
        firstAccess: 'Primeiro acesso? Defina sua senha!',
        emailRequired: 'Digite seu email primeiro para definir sua senha',
        sending: 'Enviando...',
        success: 'Link de definição de senha enviado! Verifique seu email.',
        error: 'Ocorreu um erro. Tente novamente.',
      },
      register: {
        title: 'Criar Conta',
        name: 'Nome Completo',
        email: 'Email',
        password: 'Senha',
        confirmPassword: 'Confirmar Senha',
        create: 'Criar Conta',
        creating: 'Criando Conta...',
        haveAccount: 'Já tem uma conta? Entrar',
        required: 'Todos os campos são obrigatórios',
        passwordMismatch: 'As senhas não coincidem',
        passwordTooShort: 'A senha deve ter pelo menos 6 caracteres',
        failed: 'Falha no registro. Tente novamente.',
        success: 'Conta criada com sucesso! Redirecionando...',
      },
      common: {
        cancel: 'Cancelar',
        close: 'Fechar',
        back: 'Voltar',
        next: 'Próximo',
        continue: 'Continuar',
        submit: 'Enviar',
        submitting: 'Enviando...',
        loading: 'Carregando',
        language: 'Idioma',
        navigation: 'Navegação',
        notifications: 'Notificações',
        closed: 'Fechado',
        error: 'Erro',
        unauthorized: 'Não tem permissão para ver esta página.',
        noStores: 'Nenhuma loja disponível',
        retry: 'Tentar Novamente',
        refresh: 'Atualizar',
        pullToReload: 'Puxe para atualizar',
        releaseToReload: 'Solte para atualizar',
        reloadingApp: 'Atualizando…',
        saving: 'Salvando...',
        save: 'Salvar',
        delete: 'Excluir',
        confirm: 'Confirmar',
        filters: 'Filtros',
        creating: 'Criando...',
        optional: 'Opcional',
        selected: 'Selecionado',
        notProvided: 'Não fornecido',
        viewImage: 'Ver imagem',
        active: 'Ativo',
        inactive: 'Inativo',
        seeMore: 'Ver Mais',
        seeLess: 'Ver Menos',
        previousTabs: 'Separadores anteriores',
        nextTabs: 'Separadores seguintes',
        sending: 'Enviando...',
        resend: 'Reenviar link',
        resendIn: 'Reenviar link em {{seconds}}s',
        resendError: 'Não foi possível reenviar o link. Tente novamente.',
        backToLogin: 'Voltar ao Login',
        languages: {
          pt: 'Português',
          en: 'Inglês',
          es: 'Espanhol'
        },
        datePicker: {
          ok: 'OK',
          cancel: 'Cancelar',
          clear: 'Limpar',
          today: 'Hoje',
          previousMonth: 'Mês anterior',
          nextMonth: 'Próximo mês',
          toolbarTitle: 'Selecionar Data',
          openDatePickerDialogue: 'Escolher data, data selecionada é {{value}}',
          openDatePickerDialogueEmpty: 'Escolher data',
          closeDatePickerDialogue: 'Fechar seletor de data'
        },
        dateRange: {
          duration: 'Duração',
          from: 'De',
          to: 'Até',
          endOptional: 'Deixe a data de fim vazia para sem data de fim.',
        },
        yes: 'Sim',
        no: 'Não',
        actions: 'Ações',
        moreOptions: 'Mais Opções',
        contactSupport: 'Suporte da app',
        contactStoreChat: 'Falar com a loja',
        contactSupportSubject: 'Suporte Salovare',
        contactSupportHint: 'Envie um email para {{email}} se precisar de ajuda.',
        appVersion: 'Versão {{version}} · {{slot}}',
        rowsPerPage: 'Linhas por página:',
        of: 'de',
        all: 'Todos',
        apply: 'Aplicar',
        clearAll: 'Limpar Tudo',
        noFiltersAvailable: 'Nenhum filtro disponível',
        days: {
          monday: 'Segunda',
          tuesday: 'Terça',
          wednesday: 'Quarta',
          thursday: 'Quinta',
          friday: 'Sexta',
          saturday: 'Sábado',
          sunday: 'Domingo'
        },
        minutes: 'min',
        hours: 'h'
      },
      supportChat: {
        title: 'Suporte da app',
        storeTitle: 'Chat com a loja',
        placeholder: 'Escreva uma mensagem',
        send: 'Enviar',
        guestEmail: 'O seu email',
        guestEmailHint: 'Precisamos de um email para lhe responder.',
        guestEmailRequired: 'Introduza o seu email para iniciar o chat.',
        waiting: 'Recebemos a sua mensagem e responderemos aqui.',
        closed: 'Este chat está fechado. Envie uma nova mensagem para o reabrir.',
        closedPlaceholder: 'Envie uma mensagem para reabrir este chat',
        empty: 'Diga-nos em que precisa de ajuda. Respondemos aqui neste chat.',
        storeEmpty: 'Pergunte à loja o que precisar. A resposta costuma chegar aqui neste chat.',
        you: 'Você',
        support: 'Suporte',
        store: 'Loja',
        supportTyping: 'O suporte está a escrever',
        storeTyping: '{{name}} está a escrever',
        visitorTyping: '{{name}} está a escrever',
        typing: 'A escrever…',
        sendFailed: 'Não foi possível enviar a mensagem. Tente novamente.',
        loadFailed: 'Não foi possível carregar os chats de suporte.',
        inbox: 'Suporte da app',
        inboxSubtitle: 'Chats pendentes e ativos da equipa das lojas.',
        storeInbox: 'Chat com clientes',
        storeInboxSubtitle: 'Chats pendentes e ativos dos seus clientes.',
        noConversations: 'Ainda não há chats da equipa.',
        storeNoConversations: 'Ainda não há chats de clientes.',
        selectConversation: 'Selecione uma conversa para responder.',
        closeConversation: 'Fechar chat',
        relatedAppointment: 'Agendamento {{date}} às {{time}}',
        status: {
          Waiting: 'A aguardar',
          Open: 'Aberto',
          Closed: 'Fechado',
        },
      },
      notFound: {
        title: 'Página Não Encontrada',
        message: 'A página que procura não existe ou o link da loja é inválido.',
        suggestion: 'Para agendar um horário, por favor:',
        contactStore: 'Contacte a loja diretamente para obter o link de agendamento correto.',
        goToHome: 'Ir para a página principal',
        redirectCountdown: 'Será redirecionado para a página principal em {{seconds}} segundos.',
        storeTitle: 'Loja não encontrada',
        storeMessage: 'A loja que procura pode ter alterado o link.',
        storeContact: 'Contacte a loja para obter o link de agendamento.',
      },
      shortLink: {
        redirecting: 'A redirecionar…',
      },
      navigation: {
        dashboard: 'Painel',
        appointments: 'Agendamentos',
        waitlist: 'Lista de espera',
        services: 'Serviços',
        products: 'Produtos',
        membershipPlans: 'Planos de Assinatura',
        customerMemberships: 'Assinaturas de Clientes',
        customers: 'Clientes',
        teamMembers: 'Membros da Equipa',
        profile: 'Perfil',
        store: 'Loja',
        stores: 'Lojas',
        billing: 'Faturação',
        importData: 'Importar dados',
        communication: 'Comunicação',
        paymentOperations: 'Operações de Pagamento',
        storesManagement: 'Gestão de Lojas',
        support: 'Suporte',
        appSupport: 'Suporte da app',
        storeChat: 'Chat com clientes',
        planDiscountVouchers: 'Vouchers de Desconto do Plano',
        employeePayroll: 'Folha de Pagamento',
        employeePaymentSettings: 'Pagamentos de Funcionários',
        storeRegistration: 'Registrar Loja',
        settings: 'Configurações',
        logout: 'Sair'
      },
      management: {
        addToHomeScreen: {
          bannerTitle: 'Adicionar a Salovare ao ecrã inicial',
          subtitle: 'Abra esta loja mais depressa a partir do telemóvel',
          action: 'Instalar como aplicação',
          iosGuideTitle: 'Adicionar ao ecrã inicial',
          iosFallback: 'Toque em Partilhar no Safari e depois em Adicionar ao Ecrã Início.',
          androidUnavailable: 'Abra esta página no Chrome para instalar a aplicação.',
        },
      },
      dataImport: {
        title: 'Importar dados do Fresha',
        subtitle: 'Importe nesta ordem: clientes, serviços, membros da equipa e depois agendamentos.',
        languageNotice: 'As exportações do Fresha podem usar cabeçalhos em português ou inglês. Ambos os formatos são suportados.',
        backToCustomers: 'Voltar aos clientes',
        errors: {
          loadProgress: 'Falha ao carregar o progresso da importação.',
        },
        steps: {
          customers: 'Clientes',
          teamMembers: 'Membros da Equipa',
          services: 'Serviços',
          appointments: 'Agendamentos',
        },
        comingSoon: {
          step: 'A importação de {{step}} estará disponível no próximo passo.',
        },
        customers: {
          helper: {
            title: 'Como exportar clientes do Fresha',
            intro: 'Gere o CSV no Fresha e depois carregue-o aqui.',
            step1: 'Abra a lista de clientes do Fresha:',
            openFresha: 'partners.fresha.com/clients/list',
            step2: 'Clique em Opções → Exportar: CSV.',
            step3: 'Carregue o ficheiro CSV descarregado abaixo.',
          },
          uploadButton: 'Carregar CSV do Fresha',
          selectedFile: 'Ficheiro selecionado: {{fileName}}',
          summary: '{{total}} linhas · {{ready}} prontas · {{failed}} com erro · {{warnings}} avisos',
          failedTitle: 'Linhas que precisam de correção',
          moreFailures: '+{{count}} linhas com erro',
          fixFailingRows: 'Corrigir linhas com erro',
          downloadFailingRows: 'Descarregar linhas com erro',
          importReadyRows: 'Importar linhas válidas',
          importReadyConfirmTitle: 'Importar apenas as linhas válidas?',
          importReadyConfirmMessage: 'As {{failed}} linha(s) que ainda precisam de correção serão ignoradas. Apenas as {{ready}} linha(s) válidas serão importadas.',
          importReadyConfirmAction: 'Importar linhas válidas',
          warningsNotice: '{{count}} aviso(s) em campos opcionais. Nome e email estão válidos, por isso essas linhas ainda podem ser importadas.',
          importButton: 'Importar clientes',
          importing: 'A importar...',
          alreadyCompleted: 'Os clientes já foram importados para esta loja. Pode continuar para Serviços.',
          importSuccess: 'Importação concluída: {{created}} criados, {{assigned}} associados, {{alreadyInStore}} já na loja.',
          fields: {
            name: 'Nome',
            email: 'Email',
            phone: 'Telefone',
            countryCode: 'Indicativo',
            genre: 'Género',
            birthDate: 'Data de nascimento',
          },
          genre: {
            male: 'Masculino',
            female: 'Feminino',
          },
          errors: {
            emptyFile: 'O ficheiro CSV está vazio.',
            parseFailed: 'Não foi possível ler o ficheiro CSV.',
            importFailed: 'A importação de clientes falhou.',
            nameRequired: 'O nome é obrigatório',
            emailRequired: 'O email é obrigatório',
            emailInvalid: 'O email é inválido',
            contactRequired: 'O email ou o telefone é obrigatório',
          },
          warnings: {
            invalidPhone: 'O telefone não pôde ser importado',
            invalidGenre: 'O género não pôde ser importado',
            invalidBirthDate: 'A data de nascimento não pôde ser importada',
          },
          fixDialog: {
            title: 'Corrigir linhas com erro',
            selectRow: 'Linha com erro',
            rowLabel: 'Linha {{row}} · {{name}}',
            rawRow: 'Linha CSV completa',
            originalValue: 'Valor original: {{value}}',
            apply: 'Aplicar correção',
            noFailures: 'Já não há linhas com erro.',
            requiredSection: 'Campos obrigatórios',
            requiredSectionHelp: 'Estes campos têm de ser corrigidos antes de importar esta linha.',
            optionalSection: 'Campos opcionais',
            optionalSectionHelp: 'Pode corrigi-los agora ou deixá-los vazios. Valores inválidos são apenas avisos.',
          },
          mergeDialog: {
            title: 'Resolver clientes duplicados',
            description: 'Algumas linhas partilham o mesmo nome, email ou telefone. Em cada correspondência, escolha se é uma única conta ou duas contas diferentes.',
            selectGroup: 'Ir para o grupo',
            groupProgress: 'Grupo de duplicados {{current}} de {{total}}',
            groupOption: '{{current}}/{{total}} · {{label}}',
            matches: 'Linhas correspondentes',
            matchesHelp: 'Compare os detalhes completos de cada linha correspondente abaixo.',
            accountQuestion: 'Isto é uma única conta ou duas contas diferentes?',
            merge: 'Uma única conta',
            keepSeparate: 'Duas contas diferentes',
            emailMustMerge: 'Linhas com o mesmo email têm de ser uma única conta, porque o email é único.',
            primaryRow: 'Manter dados de',
            primaryRowHelp: 'Selecione a linha cujos dados devem ser mantidos na conta de cliente juntada.',
            rowTitle: 'Linha #{{row}}',
            freshaClientId: 'ID de cliente Fresha',
            confirm: 'Continuar importação',
            autoResolve: 'Resolver todos os duplicados automaticamente',
            autoResolveHint: 'Ignore a revisão grupo a grupo e aplique as regras recomendadas automaticamente.',
            autoResolveDialog: {
              title: 'Resolver duplicados automaticamente?',
              intro: 'Isto vai concluir a importação sem pedir para rever cada duplicado um a um.',
              willHappen: 'Eis o que acontece quando a mesma pessoa aparece mais do que uma vez no ficheiro:',
              sameEmailTitle: 'Mesmo email:',
              sameEmailBody: 'Serão juntados num único cliente. Mantemos os dados da primeira linha que aparece no ficheiro.',
              sameNamePhoneTitle: 'Mesmo nome e mesmo telefone:',
              sameNamePhoneBody: 'Também serão juntados num único cliente, usando os dados da primeira linha correspondente.',
              sameNameOnlyTitle: 'Mesmo nome, mas telefone diferente (ou em falta):',
              sameNameOnlyBody: 'Ficam como clientes separados, porque podem ser pessoas diferentes que partilham o nome.',
              samePhoneOnlyTitle: 'Mesmo telefone, mas nome diferente:',
              samePhoneOnlyBody: 'Ficam como clientes separados. Duas pessoas podem partilhar o mesmo número de telefone.',
              footer: 'Se preferir escolher manualmente cada duplicado, cancele e continue a rever grupo a grupo.',
              confirm: 'Resolver e importar',
            },
            groupTypes: {
              email: 'Mesmo email ({{key}}) · {{count}} linhas',
              name: 'Mesmo nome ({{key}}) · {{count}} linhas',
              phone: 'Mesmo telefone ({{key}}) · {{count}} linhas',
              email_name: 'Mesmo email e nome ({{key}}) · {{count}} linhas',
              email_phone: 'Mesmo email e telefone ({{key}}) · {{count}} linhas',
              name_phone: 'Mesmo nome e telefone ({{key}}) · {{count}} linhas',
              email_name_phone: 'Mesmo email, nome e telefone ({{key}}) · {{count}} linhas',
            },
          },
        },
        services: {
          helper: {
            title: 'Como exportar serviços do Fresha',
            intro: 'Gere o CSV da lista de serviços no Fresha e depois carregue-o aqui.',
            step1: 'Abra a lista de serviços do Fresha:',
            openFresha: 'partners.fresha.com/setup/services',
            step2: 'Clique em Opções → Exportar: CSV.',
            step3: 'Carregue o ficheiro CSV descarregado abaixo.',
          },
          uploadButton: 'Carregar CSV do Fresha',
          selectedFile: 'Ficheiro selecionado: {{fileName}}',
          summary: '{{total}} linhas · {{ready}} prontas · {{failed}} com erro · {{warnings}} avisos · {{categories}} categorias',
          failedTitle: 'Linhas que precisam de correção',
          moreFailures: '+{{count}} linhas com erro',
          fixFailingRows: 'Corrigir linhas com erro',
          downloadFailingRows: 'Descarregar linhas com erro',
          importReadyRows: 'Importar linhas válidas',
          importReadyConfirmTitle: 'Importar apenas as linhas válidas?',
          importReadyConfirmMessage: 'As {{failed}} linha(s) que ainda precisam de correção serão ignoradas. Apenas as {{ready}} linha(s) válidas serão importadas.',
          importReadyConfirmAction: 'Importar linhas válidas',
          warningsNotice: '{{count}} aviso(s) em campos opcionais. Os campos obrigatórios estão válidos, por isso essas linhas ainda podem ser importadas.',
          importButton: 'Importar serviços',
          importing: 'A importar...',
          alreadyCompleted: 'Os serviços já foram importados para esta loja. Pode continuar para Membros da Equipa.',
          importSuccess: 'Importação concluída: {{created}} serviços criados, {{updated}} atualizados, {{categoriesCreated}} categorias criadas, {{categoriesUpdated}} categorias atualizadas.',
          fields: {
            serviceId: 'ID do serviço',
            name: 'Nome',
            duration: 'Duração (minutos)',
            price: 'Preço',
            description: 'Descrição',
            disabled: 'Desativado para reserva online',
          },
          errors: {
            emptyFile: 'O ficheiro CSV está vazio.',
            parseFailed: 'Não foi possível ler o ficheiro CSV.',
            importFailed: 'A importação de serviços falhou.',
            serviceIdRequired: 'O ID do serviço é obrigatório',
            nameRequired: 'O nome é obrigatório',
            durationRequired: 'A duração é obrigatória',
            durationInvalid: 'A duração é inválida',
          },
          warnings: {
            missingPrice: 'O preço estava em falta e foi definido como 0',
            invalidPrice: 'O preço não pôde ser interpretado e foi definido como 0',
          },
          fixDialog: {
            title: 'Corrigir linhas com erro',
            rowLabel: 'Linha {{row}} · {{name}}',
            apply: 'Aplicar correção',
            noFailures: 'Já não há linhas com erro.',
          },
        },
        teamMembers: {
          helper: {
            title: 'Como exportar membros da equipa do Fresha',
            intro: 'Gere o CSV de colaboradores no Fresha (cabeçalhos em inglês) e carregue-o aqui.',
            step1: 'Abra a lista de membros da equipa do Fresha:',
            openFresha: 'partners.fresha.com/team/members',
            step2: 'Clique em Opções → Exportar: CSV.',
            step3: 'Carregue o ficheiro CSV descarregado abaixo.',
            englishOnly: 'Este passo atualmente suporta apenas cabeçalhos de CSV em inglês.',
          },
          uploadButton: 'Carregar CSV do Fresha',
          selectedFile: 'Ficheiro selecionado: {{fileName}}',
          summary: '{{total}} linhas · {{ready}} prontas · {{failed}} com erro · {{warnings}} avisos',
          failedTitle: 'Linhas que precisam de correção',
          moreFailures: '+{{count}} linhas com erro',
          fixFailingRows: 'Corrigir linhas com erro',
          downloadFailingRows: 'Descarregar linhas com erro',
          importReadyRows: 'Importar linhas válidas',
          importReadyConfirmTitle: 'Importar apenas as linhas válidas?',
          importReadyConfirmMessage: 'As {{failed}} linha(s) que ainda precisam de correção serão ignoradas. Apenas as {{ready}} linha(s) válidas serão importadas.',
          importReadyConfirmAction: 'Importar linhas válidas',
          warningsNotice: '{{count}} aviso(s) em campos opcionais. Nome e email estão válidos, por isso essas linhas ainda podem ser importadas.',
          importButton: 'Importar membros da equipa',
          importing: 'A importar...',
          alreadyCompleted: 'Os membros da equipa já foram importados para esta loja. Pode continuar para Agendamentos.',
          importSuccess: 'Importação concluída: {{created}} criados, {{skipped}} ignorados (email já existe).',
          skippedEmails: 'Emails ignorados: {{emails}}',
          skippedEmailsMore: ' (+{{count}} mais)',
          fields: {
            name: 'Nome',
            email: 'Email',
            jobTitle: 'Cargo',
            permission: 'Permissão / Função',
            disabled: 'Desativado (arquivado)',
          },
          roles: {
            staff: 'Funcionário (Basic)',
            manager: 'Gerente (High)',
            full: 'Completo (Owner)',
          },
          errors: {
            emptyFile: 'O ficheiro CSV está vazio.',
            parseFailed: 'Não foi possível ler o ficheiro CSV.',
            unsupportedHeaders: 'Não foi possível reconhecer os cabeçalhos do CSV. Exporte o ficheiro de colaboradores em inglês.',
            importFailed: 'A importação de membros da equipa falhou.',
            nameRequired: 'O nome é obrigatório',
            emailRequired: 'O email é obrigatório',
            emailInvalid: 'O email é inválido',
          },
          warnings: {
            invalidPhone: 'O telefone não pôde ser importado',
            invalidBirthDate: 'A data de nascimento não pôde ser importada',
            unknownStatus: 'O estado era desconhecido e foi tratado como Active',
            unknownPermission: 'A permissão era desconhecida e foi mapeada para Funcionário',
          },
          fixDialog: {
            title: 'Corrigir linhas com erro',
            rowLabel: 'Linha {{row}} · {{name}}',
            apply: 'Aplicar correção',
            noFailures: 'Já não há linhas com erro.',
          },
        },
        appointments: {
          helper: {
            title: 'Como exportar agendamentos do Fresha',
            intro: 'Gere o CSV de agendamentos no Fresha e carregue-o aqui.',
            step1: 'Abra a exportação de calendário / agendamentos do Fresha:',
            openFresha: 'partners.fresha.com/calendar',
            step2: 'Exporte os agendamentos em CSV (cabeçalhos em português ou inglês).',
            step3: 'Carregue o ficheiro CSV transferido abaixo.',
            languageNote: 'Os nomes são correspondidos aos clientes, serviços e membros da equipa desta loja. Linhas sem correspondência podem ser corrigidas ou ignoradas antes da importação.',
          },
          uploadButton: 'Carregar CSV do Fresha',
          selectedFile: 'Ficheiro selecionado: {{fileName}}',
          summary: '{{total}} linhas · {{appointments}} agendamentos · {{ready}} prontas · {{failed}} com erro · {{warnings}} avisos',
          failedTitle: 'Linhas que precisam de correção',
          moreFailures: '+{{count}} linhas com erro',
          fixFailingRows: 'Corrigir linhas com erro',
          downloadFailingRows: 'Descarregar linhas com erro',
          importReadyRows: 'Importar linhas prontas',
          importReadyConfirmTitle: 'Importar apenas as linhas prontas?',
          importReadyConfirmMessage: 'As {{failed}} linha(s) que ainda precisam de correção serão ignoradas. Apenas as {{ready}} linha(s) prontas serão importadas.',
          importReadyConfirmAction: 'Importar linhas prontas',
          warningsNotice: '{{count}} aviso(s) em campos opcionais. Os campos obrigatórios são válidos, por isso essas linhas ainda podem ser importadas.',
          resolving: 'A corresponder nomes...',
          importButton: 'Importar agendamentos',
          importing: 'A importar...',
          alreadyCompleted: 'Os agendamentos já foram importados para esta loja.',
          importSuccess: 'Importação concluída: {{created}} agendamento(s) criado(s).',
          skippedNotice: '{{count}} linha(s) ignorada(s) e não serão importadas.',
          fields: {
            customer: 'Cliente',
            teamMember: 'Colaborador',
            service: 'Serviço',
            category: 'Categoria',
            status: 'Situação',
            scheduledDate: 'Data agendada',
            periodFrom: 'Hora de início',
            periodTo: 'Hora de fim',
            price: 'Preço',
          },
          status: {
            new: 'Novo / Confirmado',
            completed: 'Concluído',
            cancelled: 'Cancelado',
            noShow: 'Não compareceu',
          },
          resolve: {
            walkIn: 'Walk-in (cliente anónimo)',
            skipRow: 'Ignorar esta linha',
            searchCustomer: 'Pesquisar cliente...',
            searchService: 'Pesquisar serviço...',
            searchTeamMember: 'Pesquisar colaborador...',
            importedValues: 'Valores do CSV — cliente: {{customer}} · serviço: {{service}} · colaborador: {{teamMember}}',
          },
          errors: {
            emptyFile: 'O ficheiro CSV está vazio.',
            parseFailed: 'Não foi possível ler o ficheiro CSV.',
            unsupportedHeaders: 'Não foi possível reconhecer os cabeçalhos do CSV. Use uma exportação de agendamentos do Fresha em português ou inglês.',
            importFailed: 'A importação de agendamentos falhou.',
            noReadyRows: 'Não há linhas prontas para importar.',
            referenceRequired: 'A referência do agendamento é obrigatória',
            customerRequired: 'O cliente é obrigatório',
            teamMemberRequired: 'O colaborador é obrigatório',
            serviceRequired: 'O serviço é obrigatório',
            customerNotFound: 'O cliente não foi encontrado nesta loja',
            serviceNotFound: 'O serviço não foi encontrado nesta loja',
            teamMemberNotFound: 'O colaborador não foi encontrado nesta loja',
            statusRequired: 'A situação é obrigatória',
            statusUnknown: 'A situação é desconhecida',
            dateRequired: 'A data agendada é inválida ou está em falta',
            slotRequired: 'O horário do agendamento é inválido ou está em falta',
          },
          warnings: {
            invalidPrice: 'O preço não pôde ser importado; será usado o preço do catálogo',
            invalidDuration: 'A duração não pôde ser interpretada',
            trailingSpaces: 'Espaços no fim dos nomes foram removidos',
          },
          fixDialog: {
            title: 'Corrigir linhas com erro',
            rowLabel: 'Linha {{row}} · {{name}}',
            progress: 'Linha {{current}} de {{total}} · Linha CSV {{row}} · {{name}}',
            next: 'Seguinte',
            apply: 'Aplicar correção',
            noFailures: 'Já não há linhas com erro.',
          },
        },
      },
      communicationSettingsPage: {
        title: 'Comunicação',
        subtitle: 'Configure notificações por e-mail, SMS e push enviadas aos clientes para eventos de agendamento',
        emailAlways: 'E-mail sempre enviado',
        emailLabel: 'E-mail',
        smsLabel: 'SMS',
        pushLabel: 'Push',
        smsPriceWarning: 'As mensagens SMS são cobradas por mensagem, ao preço de {{price}}.',
        smsDependsOnEmail: 'Ative o e-mail para este lembrete primeiro',
        smsDisabledHelp: 'A cobrança de SMS não está ativa. Ative-a nas configurações de Faturação.',
        smsBillingBanner: {
          text: 'O SMS não está ativo para esta loja. Ative a cobrança de SMS para enviar notificações por mensagem de texto aos seus clientes.',
          link: 'Ir para as configurações de Faturação',
        },
        smsSenderName: {
          label: 'Nome do Remetente SMS',
          help: 'Este nome aparece como remetente nas mensagens SMS. Deixe em branco para usar o nome da loja (truncado a 15 caracteres). Apenas letras, números e espaços. Nota: algumas operadoras podem truncar nomes com mais de 11 caracteres.',
          placeholder: 'Nome da loja',
          error: {
            invalid: 'O nome do remetente deve começar com uma letra ou número e conter apenas letras, números e espaços.',
          },
        },
        confirmation: {
          title: 'Confirmação de Agendamento',
          description: 'O e-mail é sempre enviado quando um agendamento é criado. Opcionalmente, envie também SMS e notificações push.',
        },
        cancellation: {
          title: 'Cancelamento de Agendamento',
          description: 'O e-mail é sempre enviado quando um agendamento é cancelado. Opcionalmente, envie também SMS e notificações push.',
        },
        reminders: {
          title: 'Lembretes de Agendamento',
          description: 'Escolha de forma independente notificações por e-mail, SMS e push para cada janela de lembrete. O SMS segue o e-mail — só pode ser ativado quando o e-mail estiver ativo.',
        },
        reminder7Day: '7 dias antes',
        reminder1Day: '1 dia antes',
        reminder2Hour: '2 horas antes',
        googleReviewAsk: {
          title: 'Pedido de avaliação no Google',
          description: 'Opcional. Enviado cerca de 2 horas após uma visita concluída, a pedir ao cliente uma avaliação no Google. Escolha e-mail, SMS ou push; o SMS usa as mesmas regras de faturação das outras mensagens de marcação.',
          missingUrl: 'Adicione o URL de avaliação no Google nos detalhes da loja antes de ativar isto.',
          editStore: 'Editar detalhes da loja',
        },
        success: {
          save: 'Configurações de comunicação guardadas.',
        },
        error: {
          load: 'Falha ao carregar as configurações de comunicação.',
          save: 'Falha ao guardar as configurações de comunicação.',
        },
        tabs: {
          notifications: 'Notificações',
          waitlist: 'Lista de espera',
        },
        waitlist: {
          title: 'Lista de espera',
          subtitle: 'Escolha como a loja trata clientes à espera de um horário preferido.',
          typeLabel: 'Tipo de lista de espera',
          type: {
            Manual: 'Manual',
            Auto: 'Automática',
          },
          typeHelp: {
            Manual: 'A equipa é notificada e escolhe quem fica com o horário. Os clientes não recebem e-mail automaticamente.',
            Auto: 'Os clientes correspondentes recebem e-mail após um breve intervalo para a equipa poder usar o horário primeiro.',
          },
          priorityLabel: 'Prioridade da lista de espera',
          priority: {
            FirstInLine: 'Por ordem de chegada',
            HighestValue: 'Maior valor',
            NotifyAll: 'Notificar todos',
          },
          priorityHelp: {
            FirstInLine: 'Oferece o horário ao cliente que entrou primeiro.',
            HighestValue: 'Oferece o horário primeiro ao cliente com o maior valor estimado.',
            NotifyAll: 'Envia e-mail a todos os clientes correspondentes. O primeiro a marcar fica com o horário.',
          },
        },
      },
      waitlistQueuePage: {
        title: 'Lista de espera',
        subtitle: 'Clientes à espera de um horário preferido',
        empty: 'Ninguém está na lista de espera neste momento.',
        book: 'Marcar',
        remove: 'Remover',
        removeTitle: 'Remover da lista de espera',
        removeMessage: 'Remover {{name}} da lista de espera?',
        success: {
          remove: 'Cliente removido da lista de espera.',
        },
        error: {
          load: 'Falha ao carregar a lista de espera.',
          remove: 'Falha ao remover este cliente da lista de espera.',
        },
        columns: {
          customer: 'Cliente',
          services: 'Serviços',
          preferences: 'Horários preferidos',
          joined: 'Entrou',
          value: 'Valor estimado',
          actions: 'Ações',
        },
      },
      waitlistOfferPage: {
        title: 'Abriu um horário para si',
        book: 'Marcar',
        decline: 'Recusar',
        duration: '{{minutes}} min',
        success: 'O seu agendamento está marcado.',
        declined: 'Recusou este horário.',
        alreadyBooked: 'Este horário já não está disponível.',
        claimHint: 'Pode voltar aos seus agendamentos no menu da loja.',
        error: {
          load: 'Não foi possível carregar esta oferta da lista de espera.',
          claim: 'Não foi possível marcar este horário. Pode já estar ocupado.',
        },
      },
      waitlistAlert: {
        joinedTitle: 'Cliente entrou na lista de espera',
        slotOpenedTitle: 'Abriu um horário correspondente',
        customer: 'Cliente',
        services: 'Serviços',
        duration: 'Duração',
        durationValue: '{{minutes}} min',
        value: 'Valor estimado',
        viewWaitlist: 'Ver lista de espera',
        book: 'Marcar',
      },
      appointmentConfirmationAlert: {
        title: 'Marcação sob pedido',
        customer: 'Cliente',
        confirm: 'Confirmar',
        decline: 'Recusar',
        viewAppointment: 'Ver na agenda',
        openToAny: 'Fica aberto a qualquer profissional disponível até alguém confirmar.',
      },
      stripeCheckout: {
        title: 'Concluir pagamento',
        error: 'Falha ao carregar o formulário de pagamento. Tente novamente.'
      },
      paymentSettingsPage: {
        title: 'Configurações de Pagamento',
        subtitle: 'Gerir a subscrição Salovare e as configurações de pagamento dos clientes',
        tabs: {
          subscription: 'Subscrição Salovare',
          customerPayments: 'Pagamentos de Clientes',
          notifications: 'Notificações'
        },
        error: {
          loading: 'Falha ao carregar as configurações de pagamento.',
          unauthorized: 'Não tem permissão para gerir as configurações de pagamento desta loja.'
        },
        subscription: {
          title: 'Subscrição Salovare',
          description: 'Gerir como a sua loja paga o Salovare. Faça upgrade para Pro ou atualize os dados de faturação aqui.',
          loading: 'A carregar estado da subscrição...',
          statusActive: 'Faturação da subscrição configurada',
          statusActiveDescription: 'Os seus dados de faturação estão registados. Use o portal de faturação para atualizar o método de pagamento ou rever faturas.',
          statusActionRequired: 'Atenção de faturação necessária',
          billingDetailsOnFile: 'Dados de faturação registados',
          billingDetailsPending: 'Dados de faturação pendentes',
          trialPlan: 'Período experimental',
          trialUntil: 'Período experimental até {{date}}',
          priceAfterTrial: '{{price}}/mês pela subscrição base após {{date}}',
          nextBillingOn: 'Próxima faturação em {{date}}',
          priceOnNextBilling: '{{price}}/mês pela subscrição base em {{date}}',
          discountOnNextPayment: 'Desconto de {{percent}}% no plano no próximo pagamento',
          discountApplied: 'Desconto de {{percent}}% no plano aplicado',
          cancelInstructions: 'Pode cancelar a qualquer momento no portal de faturação. Não será cobrado após o fim do período atual se cancelar.',
          cancelAction: 'Cancelar subscrição',
          cancelTitle: 'Cancelar subscrição',
          cancelMessage: 'A sua subscrição permanecerá ativa até {{date}}. A fatura final incluirá a subscrição e a utilização de SMS até essa data.',
          cancelConfirm: 'Cancelar no fim do período',
          cancelError: 'Falha ao cancelar a subscrição.',
          cancelScheduled: 'Cancelamento agendado. A subscrição permanece ativa até {{date}}.',
          teamMemberSeats: '{{active}} membros ativos ({{included}} incluído(s))',
          additionalTeamMemberSeats: '{{count}} membro(s) adicional(is) a {{price}}/mês cada',
          billedAdditionalSeats: '{{count}} lugar(es) adicional(is) faturado(s) a {{price}}/mês cada',
          additionalStores: '{{count}} loja(s) adicional(is) a {{price}}/mês cada',
          seatBreakdownRow: '{{store}}: {{count}} profissional(is) extra',
          unnamedStore: 'Loja sem nome',
          estimatedMonthlyTotal: 'Total mensal estimado: {{price}}'
        },
        customerPayments: {
          error: {
            load: 'Falha ao carregar as configurações de pagamento de clientes.',
            save: 'Falha ao guardar as configurações de pagamento de clientes.'
          },
          success: {
            save: 'Configurações de pagamento de clientes guardadas.'
          },
          step1: {
            title: '1. Configurar o Stripe',
            ready: 'O Stripe está ligado e pronto para aceitar pagamentos.',
            refresh: 'Verificar métodos de pagamento',
            manageOnStripe: 'Gerir no Stripe'
          },
          step2: {
            title: '2. Escolher métodos de pagamento',
            description: 'Estes são os métodos de pagamento que o Stripe disponibilizou para a sua loja. O cartão está sempre ativo; ative os restantes que quiser oferecer aos clientes.',
            empty: 'Termine a configuração do Stripe acima para ver os métodos de pagamento disponíveis para a sua loja.'
          },
          step3: {
            title: '3. Ativar pagamento online',
            needsOnboarding: 'Termine a configuração do Stripe acima antes de ativar pagamentos online.',
            services: 'Serviços',
            servicesHelp: 'Inclui quaisquer produtos adicionados ao agendamento.',
            memberships: 'Assinaturas'
          },
          methods: {
            card: 'Cartão e Google Pay',
            klarna: 'Klarna (pagamento em prestações)',
            mbWay: 'MB WAY',
            pix: 'Pix',
            scalapay: 'Scalapay (pagamento em prestações)',
            sequra: 'SeQura (pagamento em prestações)'
          },
          fees: {
            checkFee: 'Ver taxa',
            title: 'Taxa {{method}}',
            exampleService: 'Serviço de corte e styling',
            processingFee: 'Taxa de processamento de pagamento online ({{rate}})',
            youReceive: 'Recebe',
            disclaimer: 'A Salovare não cobra qualquer taxa adicional além do Stripe. A taxa exata pode variar com o emissor do cartão e a conversão de moeda — consulte os',
            stripePricingLink: 'preços do Stripe'
          }
        },
        sms: {
          title: 'Notificações SMS',
          description: 'Envie SMS de confirmação e lembrete de agendamentos aos clientes a {{price}} por mensagem, faturados no ciclo da subscrição.',
          loading: 'A carregar definições de SMS...',
          enableLabel: 'Ativar notificações SMS',
          enableHelp: 'A utilização é faturada mensalmente com um limite de gastos.',
          enableHelpTrialBlocked: 'O SMS não está disponível no período experimental, a menos que o suporte da Salovare o ative para a sua loja.',
          enableHelpNeedsStripe: 'Ligue primeiro a faturação Stripe e depois pode ativar o SMS no período experimental.',
          trialBlocked: 'O SMS não está ativado por defeito durante o período experimental. Envie um email para {{email}} para pedir acesso.',
          trialNeedsStripe: 'O SMS foi aprovado para o seu período experimental. Ligue primeiro a faturação Stripe para o ativar.',
          emailSupport: 'Enviar email ao suporte',
          supportEmailSubject: 'Pedido de SMS durante o período experimental',
          supportEmailBody: 'Olá suporte Salovare,\n\nGostaria de pedir notificações SMS durante o meu período experimental gratuito.\n\nID da loja: {{storeId}}\n\nObrigado.',
          usageSummary: 'Utilização do período atual: {{spent}} / {{limit}} (reinicia em {{date}})',
          limitReached: 'Atingiu o limite de SMS de {{limit}}. Pague a fatura pendente no portal de faturação para continuar, ou aguarde até {{date}} para o reinício do limite.',
          paymentFailed: 'SMS está desativado porque um pagamento de fatura falhou. Atualize o método de pagamento no portal de faturação.',
          openBillingPortal: 'Abrir portal de faturação',
          error: {
            update: 'Falha ao atualizar as definições de SMS.'
          }
        }
      },
      paymentModes: {
        inStoreOnly: 'Apenas na loja',
        onlineOnly: 'Apenas online',
        both: 'Online e na loja'
      },
      storePaymentMethods: {
        label: 'Método de pagamento',
        placeholder: 'Selecione o método de pagamento',
        cash: 'Numerário',
        card: 'Cartão',
        transfer: 'Transferência',
        mbWay: 'MB WAY',
        other: 'Outro',
        online: 'Online'
      },
      appointmentCheckout: {
        amountDue: 'Valor a pagar',
        amountReceived: 'Valor recebido',
        amountReceivedHint: 'Pode receber menos do que o valor a pagar.',
        membershipFee: 'Mensalidade da assinatura',
        totalCollected: 'Total recebido: {{amount}}',
        remaining: 'Em falta: {{amount}}',
        discount: 'Desconto: {{amount}}',
        addPayment: 'Adicionar pagamento',
        removePayment: 'Remover pagamento',
        paymentLine: 'Pagamento {{number}}',
        receivedSummary: '{{amount}} será registado no fluxo de caixa.',
        errors: {
          amountRange: 'O valor recebido deve estar entre 0 e o valor a pagar.',
          paymentMethodRequired: 'Selecione um método de pagamento.',
          duplicateMethod: 'Cada método de pagamento só pode ser usado uma vez.',
          amountRequired: 'Introduza um valor maior do que zero.',
          membershipCoverRequired: 'O valor recebido deve cobrir a mensalidade da assinatura.',
          fullAmountRequired: 'O valor recebido deve ser igual ao valor a pagar.'
        }
      },
      customerMembershipsManagement: {
        title: 'Assinaturas de Clientes',
        subtitle: 'Veja assinaturas, inscreva clientes na loja e marque períodos de faturação como pagos.',
        error: {
          noStoreSelected: 'Selecione uma loja para gerir assinaturas de clientes.',
          loading: 'Falha ao carregar assinaturas de clientes.'
        },
        empty: {
          title: 'Ainda não há assinaturas de clientes',
          subtitle: 'Inscreva um cliente na loja ou aguarde subscrições online.'
        },
        table: {
          customer: 'Cliente',
          plan: 'Plano',
          status: 'Estado',
          channel: 'Canal de pagamento',
          period: 'Período atual',
          price: 'Preço',
          actions: 'Ações',
          unnamedCustomer: 'Cliente sem nome',
          periodRange: '{{start}} – {{end}}',
          noPeriod: 'Sem período ativo'
        },
        paymentChannel: {
          online: 'Online',
          inStore: 'Na loja',
          unknown: 'Desconhecido'
        },
        enroll: {
          action: 'Inscrever cliente',
          title: 'Inscrever assinatura na loja',
          description: 'Crie uma assinatura na loja para um cliente existente. O primeiro período de faturação deve ser marcado como pago antes de os direitos ficarem ativos.',
          customerId: 'ID do cliente',
          customer: 'Cliente',
          customerPlaceholder: 'Pesquisar por nome ou email',
          noCustomers: 'Nenhum cliente encontrado',
          plan: 'Plano de assinatura',
          planPlaceholder: 'Selecione um plano',
          confirm: 'Inscrever',
          enrolling: 'A inscrever...',
          success: 'Cliente inscrito com sucesso.',
          error: 'Falha ao inscrever cliente.',
          errors: {
            customerIdRequired: 'O ID do cliente é obrigatório.',
            customerRequired: 'Selecione um cliente.',
            planRequired: 'Selecione um plano de assinatura.',
            paymentMethodRequired: 'Selecione um método de pagamento.'
          }
        },
        markPaid: {
          action: 'Marcar período pago',
          payNextPeriod: 'Pagar próximo período',
          title: 'Marcar período pago',
          payNextTitle: 'Pagar próximo período',
          description: 'Registe o pagamento deste período de faturação.',
          payNextDescription: 'Receba o pagamento do próximo período. Os direitos atuais mantêm-se até este período terminar.',
          amount: 'Valor',
          marking: 'A marcar...',
          success: 'Período de faturação marcado como pago.',
          payNextSuccess: 'Próximo período de faturação pago.',
          error: 'Falha ao marcar período de faturação como pago.'
        },
        cancel: {
          action: 'Cancelar assinatura',
          canceling: 'A cancelar...',
          resume: 'Manter assinatura',
          resuming: 'A atualizar...',
          confirmTitle: 'Cancelar assinatura',
          confirmMessage: 'O cliente pode continuar a usar esta assinatura até {{date}} — o último período já pago. Depois dessa data, fica bloqueada. Deseja continuar?',
          success: 'A assinatura permanece ativa até {{date}}. Depois dessa data, será cancelada.',
          error: 'Falha ao cancelar esta assinatura.',
          resumeSuccess: 'Esta assinatura vai continuar normalmente.',
          resumeError: 'Falha ao manter esta assinatura ativa.',
          cancelsOn: 'Cancela em {{date}}'
        }
      },
      customersManagement: {
        title: 'Clientes',
        subtitle: 'Visualize e adicione clientes atribuídos a esta loja.',
        error: {
          noStoreSelected: 'Selecione uma loja para gerir clientes.',
          loading: 'Falha ao carregar clientes.'
        },
        empty: {
          title: 'Ainda não há clientes',
          subtitle: 'Adicione o primeiro cliente para começar a construir o diretório da loja.',
          noResultsTitle: 'Nenhum cliente correspondente',
          noResultsSubtitle: 'Tente outro nome, email ou número de telefone.'
        },
        dropdown: {
          searchPlaceholder: 'Pesquisar por nome, email ou telefone',
          noOptions: 'Sem resultados'
        },
        table: {
          customer: 'Cliente',
          email: 'Email',
          phone: 'Telefone',
          genre: 'Género',
          birthDate: 'Data de nascimento',
          addedOn: 'Adicionado em'
        },
        genre: {
          male: 'Masculino',
          female: 'Feminino',
          other: 'Outro'
        },
        create: {
          title: 'Adicionar cliente',
          subtitle: 'Verifique o email ou o telefone e depois crie um novo cliente ou adicione um existente a esta loja.',
          action: 'Criar cliente',
          assign: 'Adicionar à loja',
          email: 'Email',
          name: 'Nome',
          phone: 'Telefone',
          emailPlaceholder: 'Introduza o endereço de email',
          namePlaceholder: 'Introduza o nome do cliente',
          phonePlaceholder: 'Introduza o número de telefone',
          emailOptional: 'Opcional se for indicado um número de telefone',
          phoneOptional: 'Opcional se for indicado um email',
          emailCheck: {
            check: 'Verificar',
            available: 'Este email está disponível. Introduza o nome do cliente abaixo.',
            existingTitle: 'Cliente já registado',
            existingMessage: '{{name}} ({{email}}) já está registado no Salovare. Deseja adicionar este cliente à sua loja? O perfil existente não será alterado.',
            existingConfirm: 'Adicionar a esta loja',
            existingConfirmed: '{{name}} já está registado. Pode adicioná-lo a esta loja sem alterar o perfil.',
            alreadyInStore: 'Este cliente já está atribuído a esta loja.',
            error: 'Não foi possível verificar este email. Tente novamente.'
          },
          phoneCheck: {
            available: 'Este número de telefone está disponível. Introduza o nome do cliente abaixo.',
            existingConfirmed: '{{name}} já está registado com este número de telefone. Pode adicioná-lo a esta loja sem alterar o perfil.',
            alreadyInStore: 'Este cliente já está atribuído a esta loja.',
            error: 'Não foi possível verificar este número de telefone. Tente novamente.',
            required: 'Verifique este número de telefone antes de continuar.'
          },
          success: {
            created: 'Cliente criado com sucesso! Email de definição de palavra-passe enviado.',
            createdPhoneOnly: 'Cliente criado com sucesso. Pode adicionar um email na primeira vez que iniciar sessão.',
            assigned: 'Cliente adicionado a esta loja com sucesso.'
          },
          error: {
            creation: 'Falha ao adicionar cliente.'
          },
          validation: {
            nameRequired: 'O nome é obrigatório.',
            emailRequired: 'O email é obrigatório.',
            emailInvalid: 'Introduza um endereço de email válido.',
            emailCheckRequired: 'Verifique este email antes de continuar.',
            contactRequired: 'O email ou o telefone é obrigatório.',
            phoneRequired: 'O número de telefone é obrigatório quando o email está vazio.'
          }
        },
        details: {
          title: 'Detalhes do cliente',
          subtitle: 'Perfil específico da loja, notas de cuidados e métricas de visitas.',
          edit: 'Editar',
          bookAppointment: 'Agendar',
          storeOverride: 'Substituição da loja',
          usingAccount: 'A usar dados da conta',
          loading: 'Falha ao carregar os detalhes do cliente.',
          notFound: 'Este cliente não está atribuído a esta loja.',
          saveSuccess: 'Perfil de cliente da loja guardado.',
          saveError: 'Falha ao guardar o perfil de cliente da loja.',
          metrics: {
            frequency: 'Frequência de visitas',
            lastVisit: 'Última visita {{date}}',
            noVisits: 'Ainda sem visitas concluídas',
            noShows: 'Faltas',
            lifetimeValue: 'Valor gasto',
            lastThreeMonths: 'Últimos 3 meses',
            productsSpend: 'Gastos em produtos'
          },
          contact: {
            title: 'Contacto',
            name: 'Nome',
            email: 'Email',
            phone: 'Telefone'
          },
          upcoming: {
            title: 'Próximas marcações',
            empty: 'Não há marcações próximas',
            error: 'Falha ao carregar as próximas marcações.'
          },
          consultation: {
            title: 'Cuidados e preferências',
            empty: 'Ainda sem notas de cuidados. Adicione alergias, condições médicas, medicamentos e estado de gravidez ou amamentação para a equipa tratar este cliente em segurança.',
            allergies: 'Alergias e sensibilidades',
            medical: 'Condições médicas',
            medications: 'Medicamentos',
            pregnancy: 'Gravidez / amamentação'
          },
          editDialog: {
            title: 'Informação do cliente',
            storeViewOnly: 'Esta alteração aplica-se apenas a esta loja.',
            accountProfile: 'Guardado no perfil do cliente.',
            customerProvided: 'Definido pelo cliente e não pode ser alterado.',
            selectGenre: 'Selecionar género',
            name: 'Nome',
            namePlaceholder: 'Nome',
            email: 'Email',
            emailPlaceholder: 'Email',
            phone: 'Telefone',
            phonePlaceholder: 'Telefone',
            allergiesHelper: 'Alergias de pele, látex, frutos secos (comuns em óleos de massagem) ou reações a tintas capilares.',
            medicalHelper: 'Diabetes (afeta a cicatrização em pedicures/microblading), epilepsia (luzes/lasers) ou problemas circulatórios.',
            medicationsHelper: 'Anticoagulantes, Accutane/Retin-A (a pele rasga-se facilmente em depilação ou faciais).',
            pregnancyHelper: 'Alguns óleos essenciais, correntes elétricas e químicos não são seguros.',
            save: 'Guardar',
            emailInvalid: 'Introduza um endereço de email válido.'
          }
        },
        import: {
          action: 'Importar',
          title: 'Importar clientes',
          pickSubtitle: 'Carregue um ficheiro CSV ou vCard exportado do telemóvel ou dos contactos de email.',
          uploadButton: 'Carregar CSV ou vCard',
          selectedFile: 'Selecionado: {{fileName}}',
          mappingTitle: 'Mapear colunas',
          mappingSubtitle: 'Arraste uma coluna do ficheiro para um campo da Salovare. O mapeamento só é usado nesta importação.',
          mappingSelectSubtitle: 'Escolha uma coluna do ficheiro para cada campo da Salovare. O mapeamento só é usado nesta importação.',
          mappingNone: 'Não importar',
          fileColumns: 'Colunas do ficheiro',
          mapping: 'Mapeamento',
          salovareFields: 'Campos da Salovare',
          continue: 'Continuar',
          fields: {
            name: 'Nome',
            firstName: 'Primeiro nome',
            lastName: 'Apelido',
            email: 'Email',
            phone: 'Telefone',
            birthDate: 'Data de nascimento',
            genre: 'Género'
          },
          errors: {
            emptyFile: 'O ficheiro não tem contactos para importar.',
            parseFailed: 'Não foi possível ler este ficheiro. Use uma exportação .csv ou .vcf.',
            unsupportedType: 'Use um ficheiro .csv ou .vcf.',
            noRows: 'Não foram encontrados contactos após o mapeamento.',
            importFailed: 'Falha ao importar clientes.'
          },
          success: 'Importados {{created}} clientes novos. {{assigned}} já existiam e foram adicionados a esta loja. {{alreadyInStore}} já estavam nesta loja.'
        },
        fallback: {
          message: 'Selecione uma loja no menu superior para gerir clientes.',
          goToStores: 'Ir para Lojas'
        }
      },
      employeePayments: {
        settings: {
          title: 'Definições de Pagamentos de Funcionários',
          subtitle: 'Configure a margem da loja e o dia de pagamento para serviços e assinaturas',
          readOnlyHint: 'Pode visualizar estas definições, mas apenas gestores podem atualizá-las.',
          margin: {
            title: 'Margem da loja',
            description: 'Percentagem que a loja retém dos pagamentos de assinaturas recebidos em cada mês civil. O restante forma o pote mensal de assinaturas, partilhado entre os membros da equipa com base nos pontos de duração dos serviços de assinaturas concluídos. Se não houver serviços de assinatura concluídos nesse mês, o pote é dividido igualmente entre os membros ativos.',
            label: 'Margem da loja (%)'
          },
          payDay: {
            title: 'Dia de pagamento',
            description: 'Dia de cada mês em que a compensação dos funcionários é reconhecida. As partes do pote de assinaturas são pagas no primeiro dia de pagamento após o fim do mês civil.',
            label: 'Dia de pagamento do mês',
            option: 'Dia {{day}}'
          },
          error: {
            loading: 'Falha ao carregar definições de pagamentos de funcionários.',
            save: 'Falha ao guardar definições de pagamentos de funcionários.',
            marginRange: 'A margem da loja deve estar entre 0 e 100.'
          },
          success: {
            saved: 'Definições de pagamentos de funcionários guardadas com sucesso.'
          }
        },
        payroll: {
          title: 'Folha de Pagamento',
          subtitle: 'Pré-visualize a compensação de todos os membros da equipa no período selecionado',
          subtitleOwn: 'Pré-visualize a sua compensação no período selecionado',
          referencePeriod: 'Período de referência',
          teamMemberFilter: 'Membro da equipa',
          teamMemberFilterPlaceholder: 'Selecione membros da equipa',
          filters: {
            title: 'Filtros',
            view: 'Ver relatório',
            edit: 'Editar filtros',
            expand: 'Expandir filtros',
            collapse: 'Recolher filtros',
            summary: '{{period}} · {{members}}',
            customRangeSummary: '{{from}} – {{to}}',
          },
          periodPresets: {
            nextMonth: 'Próximo mês',
            today: 'Hoje',
            thisWeek: 'Esta semana',
            lastWeek: 'Semana passada',
            thisMonth: 'Este mês',
            lastMonth: 'Mês passado',
            last3Months: 'Últimos 3 meses',
            thisYear: 'Este ano',
            custom: 'Intervalo personalizado',
          },
          year: 'Ano',
          month: 'Mês',
          empty: 'Não existem dados de folha de pagamento para este período.',
          emptyService: 'Não existem dados de folha de pagamento de serviços para este período.',
          emptyMembership: 'Não existem pagamentos de partes de assinaturas para este período.',
          estimatedPeriodAlert: 'Os valores do próximo mês são estimativas e atualizam-se à medida que são registados agendamentos e pagamentos de assinaturas.',
          sections: {
            cashFlow: 'Fluxo de caixa',
            service: 'Serviços e salários',
            membership: 'Partes de assinaturas',
            membershipHint: 'As partes do pote de assinaturas são pagas mensalmente no dia configurado, sempre no mês seguinte ao mês de referência.',
          },
          amountTone: {
            keep: 'Um valor alto é dinheiro retido',
            cost: 'Um valor alto é dinheiro pago',
          },
          profit: {
            title: 'Lucro',
            amount: 'Lucro',
            hint: 'Retenção da loja após salários e comissões, mais a margem das assinaturas que a loja fica, menos as despesas lançadas neste período.',
            filtered: 'O lucro é da loja inteira. Limpe o filtro de membros da equipa para o ver.',
          },
          cashFlow: {
            hint: 'Dinheiro recebido pela loja neste período. O filtro de membro da equipa não se aplica.',
            totalReceived: 'Total recebido',
            cashier: 'Caixa',
            bank: 'Banco',
            other: 'Outro',
            sliceDetail: '{{amount}} · {{percent}} do total',
            tapSlice: 'Toque numa fatia para ver o valor e a percentagem do total.',
          },
          membershipTotals: {
            gross: 'Bruto de assinaturas',
            storeMargin: 'Margem da loja',
            barberPool: 'Pote da equipa',
            totalSharesPaid: 'Total de partes pagas',
          },
          membershipTable: {
            referenceMonth: 'Mês de referência',
            shareAmount: 'Valor da parte',
            totalShare: 'Total da parte',
          },
          unknownMember: 'Membro da equipa',
          estimated: 'Estimativa',
          minimumApplied: 'Mínimo aplicado',
          noBreakdown: 'Não existem linhas de receita registadas para este período.',
          error: {
            loading: 'Falha ao carregar relatório de folha de pagamento.',
            customRangeRequired: 'Selecione as datas de início e fim para o intervalo personalizado.',
            customRangeOrder: 'A data de início não pode ser posterior à data de fim.',
            customRangeMax: 'O intervalo personalizado não pode exceder um ano.',
          },
          monthNames: {
            1: 'Janeiro',
            2: 'Fevereiro',
            3: 'Março',
            4: 'Abril',
            5: 'Maio',
            6: 'Junho',
            7: 'Julho',
            8: 'Agosto',
            9: 'Setembro',
            10: 'Outubro',
            11: 'Novembro',
            12: 'Dezembro'
          },
          totals: {
            grossBilled: 'Receita total de serviços',
            productsGrossSold: 'Vendas de produtos',
            productCommissionsPaid: 'Comissões de produtos pagas',
            salaries: 'Pagamento total',
            storeGain: 'Retenção da loja após folha',
          },
          table: {
            member: 'Membro da equipa',
            mode: 'Modo de pagamento',
            commissionRate: 'Taxa de comissão',
            fixedSalary: 'Salário fixo',
            commission: 'Comissão',
            productsSold: 'Produtos vendidos',
            productCommission: 'Comissão de produtos',
            total: 'Pagamento total',
            details: 'Detalhes',
            viewAppointments: 'Ver agendamentos',
            actions: 'Ações',
            editCompensation: 'Editar definições de compensação'
          },
          breakdown: {
            source: 'Origem',
            recognitionDate: 'Data de reconhecimento',
            serviceRevenue: 'Receita de serviço',
            extraBilled: 'Extra faturado',
            employeeShare: 'Parte do profissional',
          },
          sourceTypes: {
            appointmentService: 'Serviço de agendamento',
            appointmentAdditionalCost: 'Custo adicional de horário',
            productSale: 'Venda de produto',
            membershipMonthlyShare: 'Parte mensal de assinaturas',
            membershipPeriodUsageShare: 'Parte de uso do período da assinatura',
          },
          membershipPot: {
            title: 'Pote de assinaturas — {{month}} {{year}}',
            subtitle: 'Todos os pagamentos de assinaturas recebidos neste mês civil, após a margem da loja, partilhados por pontos de duração.',
            gross: 'Bruto de assinaturas',
            storeMargin: 'Margem da loja',
            barberPool: 'Pote da equipa',
            totalPoints: 'Total de pontos de duração: {{points}} min',
            member: 'Membro da equipa',
            points: 'Pontos (min)',
            sharePercent: 'Parte %',
            shareAmount: 'Valor da parte',
            viewPot: 'Ver pote',
            error: {
              loading: 'Falha ao carregar detalhes do pote de assinaturas.',
            },
          },
          membershipPeriod: {
            title: 'Pagamentos de assinaturas com base no uso',
            subtitle: 'Períodos de faturação encerrados pagos no dia de pagamento da loja, repartidos por visitas concluídas e preços originais dos serviços.',
            gross: 'Bruto de assinaturas',
            storeMargin: 'Margem da loja',
            barberPool: 'Pote da equipa',
            unallocated: 'Retido pela loja',
            empty: 'Não existem períodos de assinatura encerrados pagos neste intervalo.',
            periodDates: '{{from}} – {{to}}',
            member: 'Membro da equipa',
            redemptions: 'Parte das visitas',
            shareAmount: 'Valor da parte',
            keptByStore: 'Retido pela loja',
            error: {
              loading: 'Falha ao carregar pagamentos de assinaturas com base no uso.',
            },
          },
          appointmentDetails: {
            title: 'Agendamentos — {{name}}',
            subtitle: '{{from}} a {{to}}',
            empty: 'Não existem agendamentos atribuídos para este período.',
            date: 'Data',
            customer: 'Cliente',
            appointmentId: 'ID do agendamento',
            services: 'Serviços executados',
            totalPrice: 'Preço total',
            additionalCost: 'Custo adicional de horário',
            employeeShare: 'Parte do profissional',
            teamMemberShare: 'Parte do membro',
            storeMargin: 'Margem retida pela loja',
            monthAppointmentCount: '{{count}} agendamentos',
            error: {
              loading: 'Falha ao carregar detalhes dos agendamentos.',
            },
          },
        },
        compensation: {
          title: 'Definições de Compensação',
          subtitle: 'Configure como este membro da equipa é pago',
          editTitle: 'Compensação — {{name}}',
          paymentMode: 'Modo de pagamento',
          fixedMonthlySalary: 'Salário mensal fixo',
          commissionRatePercent: 'Taxa de comissão',
          minimumMonthlySalary: 'Salário mínimo mensal',
          effectiveFrom: 'Efetivo a partir de',
          effectiveFromHint: 'Datas no passado são permitidas (até 5 anos). Guardar recalcula o pagamento das marcações pagas a partir desta data.',
          payScheduleTitle: 'Calendário de pagamento',
          payScheduleHint: 'Pagamento mensal no dia configurado do mês seguinte. Pagamento semanal no dia da semana configurado na semana seguinte à semana de trabalho (seg–dom).',
          payCadence: 'Periodicidade de pagamento',
          payDayOfMonth: 'Dia de pagamento do mês',
          payDayOfWeek: 'Dia de pagamento da semana',
          payDayOption: 'Dia {{day}}',
          payCadenceOptions: {
            monthly: 'Mensal',
            weekly: 'Semanal',
          },
          deductions: {
            title: 'Deduções',
            discounts: 'Descontos',
            discountsHelpTitle: 'Como os descontos afetam a comissão',
            discountsHelpAriaLabel: 'Como os descontos afetam a comissão',
            discountsHelpOff: 'Deixe desligado se esta pessoa deve receber comissão sobre o preço cheio do serviço, mesmo quando o cliente pagou menos.',
            discountsHelpOn: 'Ligue se a comissão deve ser só sobre o valor que o cliente realmente pagou.',
            discountsHelpExample: 'Exemplo: um corte de 30 € com 10 € de desconto. Desligado = comissão sobre 30 €. Ligado = comissão sobre 20 €.',
          },
          weekdays: {
            monday: 'Segunda-feira',
            tuesday: 'Terça-feira',
            wednesday: 'Quarta-feira',
            thursday: 'Quinta-feira',
            friday: 'Sexta-feira',
            saturday: 'Sábado',
            sunday: 'Domingo',
          },
          readOnlyHint: 'Pode visualizar as definições de compensação, mas apenas gestores podem atualizá-las.',
          modes: {
            fixedSalaryOnly: 'Apenas salário fixo',
            commissionOnly: 'Apenas comissão',
            mixed: 'Salário fixo + comissão',
            commissionWithMinimum: 'Comissão com garantia mínima',
            unknown: 'Desconhecido'
          },
          error: {
            loading: 'Falha ao carregar definições de compensação.',
            save: 'Falha ao guardar definições de compensação.',
            fixedRequired: 'Salário mensal fixo é obrigatório para este modo de pagamento.',
            commissionRange: 'A taxa de comissão deve estar entre 0 e 100.',
            minimumRequired: 'Salário mínimo mensal é obrigatório para este modo de pagamento.',
            effectiveFromRequired: 'A data de início é obrigatória.',
            payDayOfMonthRange: 'O dia de pagamento do mês deve estar entre 1 e 28.',
            payDayOfWeekRequired: 'O dia de pagamento da semana é obrigatório para pagamento semanal.',
          },
          success: {
            saved: 'Definições de compensação guardadas com sucesso.'
          }
        }
      },
      storesManagement: {
        title: 'Gestão de Lojas',
        subtitle: 'Gerir lojas e vouchers de desconto do plano.',
        tabs: {
          stores: 'Lojas',
          vouchers: 'Vouchers de Desconto',
        },
        stores: {
          description: 'Permitir SMS durante o período experimental às lojas que o pediram. Os proprietários ainda precisam de Stripe ligado e de ativar o SMS.',
          empty: 'Nenhuma loja encontrada.',
          allowSmsOnTrial: 'Permitir SMS no trial',
          table: {
            store: 'Loja',
            plan: 'Plano',
            status: 'Estado',
            stripe: 'Stripe',
            actions: 'SMS no trial',
          },
          plan: {
            trial: 'Trial',
            basesubscription: 'Base',
          },
          status: {
            active: 'Ativo',
            trialing: 'Em trial',
            pastdue: 'Em atraso',
            suspended: 'Suspenso',
            expired: 'Expirado',
            canceled: 'Cancelado',
            disabled: 'Desativado',
          },
          stripe: {
            connected: 'Ligado',
            missing: 'Não ligado',
          },
          error: {
            loading: 'Falha ao carregar lojas.',
            updateSms: 'Falha ao atualizar o acesso SMS no trial.',
          },
          delete: {
            action: 'Eliminar loja',
            title: 'Eliminar loja',
            message: 'Isto elimina permanentemente {{name}} e os serviços, marcações, produtos, planos de membro e restantes dados que pertencem apenas a esta loja. Profissionais e clientes que também pertençam a outra loja são mantidos. As imagens e ficheiros desta loja são eliminados e a faturação Salovare desta loja é cancelada. Uma conta Stripe ligada, se tiver sido criada, permanece aberta no Stripe.',
            confirmLabel: 'Escreva o nome da loja para confirmar',
            confirm: 'Eliminar loja',
            error: 'Falha ao eliminar a loja.',
          },
        },
      },
      planDiscountVouchers: {
        title: 'Vouchers de Desconto do Plano',
        subtitle: 'Crie e acompanhe descontos do plano Salovare para lojas específicas.',
        empty: 'Ainda não existem vouchers de desconto do plano.',
        error: {
          loading: 'Falha ao carregar vouchers de desconto do plano.',
          disable: 'Falha ao desativar o voucher.',
        },
        actions: {
          create: 'Novo voucher',
          generate: 'Gerar voucher',
          disable: 'Desativar',
        },
        disable: {
          title: 'Desativar voucher',
          message: 'Desativar o voucher {{code}}? Deixará de ser aplicado em futuros checkouts.',
        },
        status: {
          Active: 'Ativo',
          Scheduled: 'Agendado',
          Expired: 'Expirado',
          Disabled: 'Desativado',
        },
        table: {
          code: 'Código',
          store: 'Loja',
          discount: 'Desconto',
          startsAt: 'Início',
          endsAt: 'Fim',
          status: 'Estado',
          createdAt: 'Criado',
          actions: 'Ações',
        },
        form: {
          title: 'Gerar voucher de desconto do plano',
          store: 'Loja',
          selectStore: 'Selecione uma loja',
          discountPercent: 'Desconto (%)',
          discountPlaceholder: 'ex.: 50%',
          startsAt: 'Data de início',
          endsAt: 'Data de fim',
          endsAtHelper: 'Deixe a data de fim vazia para não expirar.',
          errors: {
            storeRequired: 'Selecione uma loja.',
            discountRequired: 'Introduza uma percentagem de desconto.',
            discountInvalid: 'Introduza um desconto entre 1 e 100.',
            startRequired: 'Selecione a data de início.',
            startInPast: 'A data de início não pode estar no passado.',
            dateRange: 'A data de fim deve ser igual ou posterior à data de início.',
            create: 'Falha ao gerar o voucher.',
          },
        },
      },
      paymentOperations: {
        title: 'Operações de Pagamento',
        subtitle: 'Monitorize a saúde dos pagamentos, falhas de webhook e exceções operacionais.',
        empty: 'Nenhum registo encontrado para os filtros atuais.',
        error: {
          loading: 'Falha ao carregar dados de operações de pagamento.',
        },
        actions: {
          refresh: 'Atualizar',
          reconcile: 'Executar reconciliação Stripe',
          reconciling: 'A reconciliar...',
        },
        reconciliation: {
          successDetail: 'Reconciliação Stripe concluída. Marcações: {{appointmentsRepaired}} reparadas de {{appointmentsMismatched}} divergências ({{appointmentsInspected}} inspecionadas). Assinaturas da loja: {{storeSubscriptionsRepaired}} reparadas de {{storeSubscriptionsMismatched}} divergências ({{storeSubscriptionsInspected}} inspecionadas). Assinaturas do cliente: {{customerMembershipsRepaired}} reparadas de {{customerMembershipsMismatched}} divergências ({{customerMembershipsInspected}} inspecionadas).',
          error: 'Falha ao executar a reconciliação Stripe.',
        },
        monitoring: {
          title: 'Monitorização de pagamentos',
          subtitle: 'Sinais operacionais e limites de alerta para fluxos Stripe.',
          enabled: 'Alertas ativos',
          disabled: 'Alertas desativados',
          evaluate: 'Executar avaliação de alertas',
          evaluating: 'A avaliar...',
          evaluateSuccess: 'Avaliação de monitorização concluída. {{triggeredCount}} alerta(s) acionado(s); {{emailsSent}} email(s) enviado(s).',
          evaluateError: 'Falha ao executar a avaliação de monitorização.',
          configuration: 'Janela {{lookbackMinutes}}m · Cooldown {{cooldownMinutes}}m · {{recipientCount}} destinatário(s) configurado(s) no appsettings.',
          activeAlerts: 'Alertas ativos',
          noActiveAlerts: 'Nenhum limite de monitorização está atualmente ultrapassado.',
          recentHistory: 'Emails de alerta enviados recentemente',
          signals: {
            openFailedWebhooks: 'Webhooks falhados em aberto',
            recentFailedWebhooks: 'Falhas recentes de webhook',
            staleProcessingWebhooks: 'Webhooks em processamento obsoleto',
            recentPayoutFailures: 'Falhas recentes de payout',
            recentPaymentFailureWebhooks: 'Webhooks recentes de falha de pagamento',
          },
        },
        summary: {
          failedWebhooks: 'Webhooks com falha',
          staleWebhooks: 'Webhooks em processamento obsoleto',
          appointmentExceptions: 'Exceções de pagamento de marcações',
          storeSubscriptionExceptions: 'Exceções de assinatura da loja',
          customerMembershipExceptions: 'Exceções de assinatura do cliente',
          paymentDisputesRequiringAction: 'Disputas que requerem ação',
        },
        tabs: {
          webhooks: 'Eventos de webhook',
          appointments: 'Marcações',
          storeSubscriptions: 'Assinaturas da loja',
          customerMemberships: 'Assinaturas do cliente',
          disputes: 'Disputas',
          refunds: 'Reembolsos',
        },
        refunds: {
          issueRefund: 'Emitir reembolso',
          issueTitle: 'Emitir reembolso de marcação',
          view: 'Ver reembolso',
          detailTitle: 'Detalhes do reembolso',
          appointmentId: 'ID da marcação',
          loadContext: 'Carregar',
          loadContextError: 'Falha ao carregar o contexto de reembolso.',
          cannotRefund: 'Esta marcação não pode ser reembolsada.',
          amount: 'Valor do reembolso',
          amountHelp: 'Deixe o saldo restante para reembolso total ou introduza um valor inferior para reembolso parcial.',
          reason: 'Motivo do reembolso',
          internalNote: 'Nota interna',
          confirmIssue: 'Emitir reembolso',
          issuing: 'A emitir...',
          issueSuccess: 'Reembolso emitido com sucesso.',
          issueError: 'Falha ao emitir o reembolso.',
          sync: 'Sincronizar do Stripe',
          syncSuccess: 'Reembolso sincronizado a partir do Stripe.',
          syncError: 'Falha ao sincronizar o reembolso a partir do Stripe.',
          columns: {
            store: 'Loja',
            amount: 'Valor',
            status: 'Estado',
            reason: 'Motivo',
            createdAt: 'Criado em',
            refundId: 'ID de reembolso Stripe',
          },
          reasons: {
            RequestedByCustomer: 'Pedido pelo cliente',
            Duplicate: 'Duplicado',
            Fraudulent: 'Fraudulento',
          },
          context: {
            store: 'Loja',
            chargeAmount: 'Valor da cobrança',
            refundedAmount: 'Já reembolsado',
            remainingAmount: 'Restante reembolsável',
          },
        },
        disputes: {
          requiresActionOnly: 'Apenas com ação necessária',
          manage: 'Gerir disputa',
          detailTitle: 'Detalhes da disputa e evidências',
          saveDraft: 'Guardar rascunho',
          submitEvidence: 'Submeter ao Stripe',
          submitting: 'A submeter...',
          sync: 'Sincronizar do Stripe',
          uploadAttachment: 'Carregar anexo',
          attachments: 'Anexos',
          evidenceSubmitted: 'As evidências já foram submetidas ao Stripe.',
          saveEvidenceSuccess: 'Rascunho de evidências guardado.',
          saveEvidenceError: 'Falha ao guardar o rascunho de evidências.',
          submitEvidenceSuccess: 'Evidências submetidas ao Stripe.',
          submitEvidenceError: 'Falha ao submeter evidências ao Stripe.',
          syncSuccess: 'Disputa sincronizada a partir do Stripe.',
          syncError: 'Falha ao sincronizar a disputa a partir do Stripe.',
          uploadSuccess: 'Anexo carregado.',
          uploadError: 'Falha ao carregar o anexo.',
          columns: {
            amount: 'Valor',
            deadline: 'Prazo de evidências',
            disputeId: 'ID da disputa Stripe',
          },
          evidence: {
            customerCommunication: 'Comunicação com o cliente',
            cancellationPolicy: 'Política de cancelamento',
            refundPolicy: 'Política de reembolso',
            serviceDocumentation: 'Documentação do serviço',
            uncategorizedText: 'Evidência adicional',
            internalNotes: 'Notas internas (não enviadas ao Stripe)',
          },
        },
        webhooks: {
          platform: 'Plataforma',
          viewDetails: 'Ver detalhes',
          detailTitle: 'Detalhes do evento de webhook',
          retry: 'Repetir processamento',
          retrying: 'A repetir...',
          retrySuccess: 'O processamento do webhook foi reenfileirado.',
          retryError: 'Falha ao repetir o processamento do webhook.',
          filters: {
            status: 'Estado',
            staleOnly: 'Apenas processamento obsoleto',
          },
          columns: {
            eventType: 'Tipo de evento',
            status: 'Estado',
            externalEventId: 'ID externo do evento',
            connectedAccount: 'Conta conectada',
            receivedAt: 'Recebido em',
            lastError: 'Último erro',
          },
          detail: {
            correlationId: 'ID de correlação',
            traceId: 'ID de rastreio',
            deliveryCount: 'Contagem de entregas',
          },
        },
        exceptions: {
          columns: {
            store: 'Loja',
            date: 'Data',
            amount: 'Valor',
            status: 'Estado',
            sessionId: 'Sessão de checkout',
            subscriptionId: 'ID da assinatura',
            endsAt: 'Termina em',
            customerId: 'ID do cliente',
          },
        },
      },
      storeRegistrationSteps: {
        step1: {
          title: 'Informações Básicas',
          description: 'Digite o nome da sua loja, descrição e idiomas suportados'
        },
        step2: {
          title: 'Informações de Contato',
          description: 'Forneça detalhes de email, telefone e website'
        },
        step3: {
          title: 'Informações de Localização',
          description: 'Digite o endereço da sua loja e detalhes de localização'
        },
        step4: {
          title: 'Horário de Funcionamento',
          description: 'Defina o horário de funcionamento da sua loja'
        },
        step5: {
          title: 'Revisar e Enviar',
          description: 'Revise todas as informações antes de enviar'
        }
      },
      storeRegistration: {
        title: 'Registro de Loja',
        seoTitle: 'Crie a sua barbearia ou salão | Salovare Grátis e Pro',
        seoDescription: 'Registe a sua barbearia ou salão no Salovare. Comece grátis com um profissional e uma loja. Pro a partir de 9,90€/mês. Agendamentos e reservas online incluídos.',
        subtitle: 'Criar uma nova loja',
        selfServiceTitle: 'Crie a Sua Loja',
        selfServiceSubtitle: 'Configure a sua loja no plano Grátis e conclua o acesso a partir do email de palavra-passe',
        success: 'Loja registrada com sucesso!',
        error: 'Falha ao registrar loja. Tente novamente.',
        billingRequiredTitle: 'Configuração de faturação necessária',
        billingRequiredMessage: 'O plano Grátis inclui uma loja. Faça upgrade para Pro antes de adicionar outra loja.',
        billingRequiredAction: 'Abrir configurações de pagamento',
        paidStoreWarning: 'Esta loja extra é faturada a {{price}}/mês.',
        paidStoreWarningProrated: 'Esta loja extra é faturada cerca de {{proratedPrice}} agora até {{nextBillingDate}}, depois {{price}}/mês.',
        paidStoreConfirmTitle: 'Confirmar loja extra',
        paidStoreConfirmMessage: 'A faturação começa a {{price}}/mês. Configurará o pagamento após o registo.',
        paidStoreConfirmMessageProrated: 'Vamos reutilizar o método de pagamento existente, cobrar cerca de {{proratedPrice}} até {{nextBillingDate}} e depois {{price}}/mês nessa data.',
        paidStoreConfirmAction: 'Criar loja paga',
        paidStoreCreatedMessage: 'Loja criada. Configure a faturação para ativar a subscrição paga.',
        paidStoreProvisionedMessage: 'Loja criada e faturada com o seu método de pagamento existente pelo período restante até a próxima data de faturação.',
        errors: {
          billingSetupRequired: 'Faça upgrade para Pro numa loja existente antes de adicionar outra loja.',
          phoneAlreadyInUse: 'Este número de telefone já está registado. Inicie sessão se já tiver conta, ou use outro número.',
          emailAlreadyInUse: 'Este email já está registado. Inicie sessão se já tiver conta, ou use outro email.',
        },
        name: 'Nome da Loja',
        description: 'Descrição da Loja',
        supportedLanguages: 'Idiomas Suportados',
        about: 'Sobre',
        ownerName: 'Nome do Responsável',
        ownerEmail: 'Email do Responsável',
        ownerPhone: 'Telefone do Responsável',
        email: 'E-mail',
        phone: 'Telefone',
        storeEmail: 'Email da Loja',
        storePhone: 'Telefone da Loja',
        website: 'Website',
        googleReviewUrl: 'URL de avaliação no Google',
        googleReviewUrlPlaceholder: 'https://g.page/r/.../review',
        googleReviewUrlHelp: 'Abra a ficha do Google Business, copie o link “Escrever uma avaliação” ou de partilha, e cole-o aqui. Os clientes recebem um link curto da Salovare, não este URL.',
        ownerSectionTitle: 'Informações do Responsável',
        storeContactSectionTitle: 'Informações de Contato da Loja',
        address: 'Morada',
        door: 'Porta',
        additionalInfo: 'Complemento',
        city: 'Cidade',
        state: 'Distrito',
        zipCode: 'Código Postal',
        country: 'País',
        basicInfo: {
          storeName: 'Nome da Loja',
          storeNameLabel: 'Nome da Loja',
          slug: 'Slug (URL)',
          currency: 'Moeda',
          currencyHelper: 'Selecione a moeda usada para preços na sua loja',
          selectCurrency: 'Selecionar Moeda',
          descriptionLabel: 'Descrição',
          descriptionPlaceholder: 'Descreva sua loja, serviços e o que a torna especial...',
          supportedLanguages: 'Idiomas Suportados',
          supportedLanguagesHelper: 'Selecione os idiomas em que sua loja estará disponível. Você deve fornecer descrições em todos os idiomas selecionados.',
          storeLanguage: 'Idioma Padrão da Loja',
          storeLanguageHelper: 'Escolha o idioma que os clientes verão quando o idioma preferido deles não estiver disponível para esta loja.',
          selectStoreLanguage: 'Selecionar Idioma Padrão',
          storeLanguageDisabledHelper: 'Selecione pelo menos um idioma suportado antes de escolher o idioma padrão da loja.',
          about: 'Sobre',
          aboutHelper: 'Descreva sua loja em cada idioma selecionado',
          aboutLabel: 'Sobre ({{language}})',
          aboutPlaceholder: 'Descreva sua loja em {{language}}...',
          timeZone: 'Fuso Horário',
          timeZoneHelper: 'Escolha o fuso horário usado para avaliar o horário de funcionamento e a disponibilidade de agendamentos desta loja',
          selectTimeZone: 'Selecionar Fuso Horário',
          typeToSearchTimeZone: 'Digite para pesquisar um fuso horário',
          topTimeZones: 'Fusos Horários Populares',
          allTimeZones: 'Todos os Fusos Horários',
          slotInterval: 'Intervalo de Horário',
          slotIntervalHelper: 'Define a duração de cada intervalo de horário exibido aos clientes ao fazer uma reserva',
          selectSlotInterval: 'Selecionar Intervalo',
          slotIntervalFive: '5 minutos',
          slotIntervalTen: '10 minutos',
          slotIntervalFifteen: '15 minutos',
          slotIntervalThirty: '30 minutos',
          slotIntervalSixty: '60 minutos'
        },
        contactInfo: {
          title: 'Informações de Contato',
          selfServiceDescription: 'Informe os dados do responsável e o contacto público da loja',
          email: 'Email',
          phone: 'Telefone',
          website: 'Website'
        },
        locationInfo: {
          title: 'Informações de Localização',
          address: 'Morada',
          door: 'Porta',
          additionalInfo: 'Complemento',
          city: 'Cidade',
          state: 'Distrito',
          zipCode: 'Código Postal',
          country: 'País'
        },
        services: {
          title: 'Serviços',
          helper: 'Selecione os serviços que sua loja oferece',
          addService: 'Adicionar Serviço',
          serviceName: 'Nome do Serviço',
          serviceDescription: 'Descrição do Serviço',
          serviceDuration: 'Duração',
          servicePrice: 'Preço',
          removeService: 'Remover Serviço',
          viewService: 'Ver Serviço',
          editService: 'Editar Serviço',
          saveChanges: 'Salvar Alterações',
          services: 'Serviços',
          noServicesAdded: 'Nenhum serviço adicionado',
          suggestions: 'Sugestões de Serviços',
          selectSuggestion: 'Clique numa sugestão para preencher automaticamente o formulário',
          defaultName: 'Nome',
          defaultDescription: 'Descrição',
          serviceSuggestions: {
            mensHaircut: {
              name: 'Corte de Cabelo Masculino',
              description: 'Corte de cabelo tradicional para homens'
            },
            beardTrim: {
              name: 'Barba',
              description: 'Aparar e modelar a barba'
            },
            haircutBeard: {
              name: 'Corte + Barba',
              description: 'Corte de cabelo e barba combinados'
            },
            womensHaircut: {
              name: 'Corte Feminino',
              description: 'Corte de cabelo para mulheres'
            },
            kidsHaircut: {
              name: 'Corte Infantil',
              description: 'Corte de cabelo para crianças'
            },
            eyebrow: {
              name: 'Sobrancelha',
              description: 'Design e modelagem de sobrancelhas'
            },
            haircutEyebrow: {
              name: 'Corte + Sobrancelha',
              description: 'Corte de cabelo e sobrancelha combinados'
            },
            haircutBeardEyebrow: {
              name: 'Corte + Barba + Sobrancelha',
              description: 'Serviço completo de cuidado pessoal com corte, barba e sobrancelha'
            }
          }
        },
        businessHours: {
          title: 'Horário de Funcionamento',
          helper: 'Defina o horário de funcionamento da sua loja para cada dia da semana',
          applyToAllTitle: 'Aplicar a todos os dias?',
          applyToAllMessage: 'Alterou o {{field}} para {{day}} para {{value}}. Gostaria de aplicar esta alteração a todos os outros dias também?',
          openTime: 'Abre às',
          closeTime: 'Fecha às',
          to: 'para',
          applyToCurrentDay: 'Aplicar apenas ao {{day}}',
          applyToAllDays: 'Aplicar a todos os dias',
          addPeriod: 'Adicionar período',
          removePeriod: 'Remover período'
        },
        review: {
          title: 'Revise as Informações da Sua Loja',
          subtitle: 'Por favor, revise todas as informações abaixo antes de enviar',
          basicInfo: 'Informações Básicas',
          contactInfo: 'Informações de Contato',
          locationInfo: 'Informações de Localização',
          services: 'Serviços',
          businessHours: 'Horário de Funcionamento'
        },
        validation: {
          nameRequired: 'Nome da loja é obrigatório',
          slugRequired: 'O URL da loja é obrigatório',
          slugTooShort: 'O URL da loja deve ter pelo menos 3 caracteres',
          slugTooLong: 'O URL da loja não pode ter mais de 30 caracteres',
          slugInvalid: 'Use apenas letras minúsculas, números e hífens. Não pode começar nem terminar com hífen.',
          slugReserved: 'Não disponível',
          slugTaken: 'Não disponível',
          slugCheckFailed: 'Não foi possível verificar se este URL está disponível. Tente novamente.',
          descriptionRequired: 'Descrição da loja é obrigatória',
          languagesRequired: 'Pelo menos um idioma deve ser selecionado',
          aboutRequired: 'Campo Sobre é obrigatório para todos os idiomas selecionados',
          storeLanguageRequired: 'O idioma padrão da loja é obrigatório',
          storeLanguageMustBeSupported: 'O idioma padrão da loja deve estar entre os idiomas suportados',
          ownerNameRequired: 'O nome do responsável é obrigatório',
          ownerEmailRequired: 'O email do responsável é obrigatório',
          ownerPhoneRequired: 'O telefone do responsável é obrigatório',
          emailRequired: 'Email é obrigatório',
          storeEmailRequired: 'O email da loja é obrigatório',
          emailInvalid: 'Por favor, digite um endereço de email válido',
          phoneRequired: 'Número de telefone é obrigatório',
          storePhoneRequired: 'O telefone da loja é obrigatório',
          addressRequired: 'Endereço é obrigatório',
          cityRequired: 'Cidade é obrigatória',
          stateRequired: 'Distrito é obrigatório',
          zipCodeRequired: 'Código Postal é obrigatório',
          countryRequired: 'País é obrigatório',
          timeZoneRequired: 'Fuso horário é obrigatório',
          servicesRequired: 'Pelo menos um serviço deve ser selecionado',
          businessHoursRequired: 'Pelo menos um dia deve estar habilitado para o horário de funcionamento',
          serviceNameRequired: 'Nome do serviço é obrigatório para todos os idiomas selecionados',
          servicePriceRequired: 'Preço do serviço é obrigatório e deve ser maior que 0',
          openTimeAfterCloseTime: 'Horário de abertura não pode ser após ou igual ao horário de fechamento',
          periodsOverlap: 'Os períodos não podem se sobrepor'
        },
        slug: {
          checking: 'A verificar disponibilidade...',
          available: 'Disponível',
        }
      },
      landing: {
        nav: {
          features: 'Funcionalidades',
          howItWorks: 'Como funciona',
          pricing: 'Preços',
          faq: 'FAQ',
          signIn: 'Entrar',
          getStarted: 'Começar',
          goToControlPanel: 'Ir para o painel',
          openMenu: 'Abrir menu',
        },
        hero: {
          eyebrow: 'Software para saúde e beleza',
          title: 'Cada marcação, cada reserva, cada pagamento sob controlo.',
          subtitle: 'O Salovare gere o balcão, a página de reservas online, os planos de assinatura, os pacotes de serviços e os pagamentos da equipa para negócios de saúde e beleza. Comece grátis com um profissional e faça upgrade no dia em que contratar.',
          ctaPrimary: 'Criar a minha loja',
          ctaSecondary: 'Entrar',
          specs: {
            free: { label: 'Plano Grátis', value: 'Um profissional, sem prazo' },
            pro: { label: 'Pro', value: '{{price}}/mês' },
            card: { label: 'Cartão de crédito', value: 'Não é necessário' },
            commitment: { label: 'Compromisso', value: 'Cancele quando quiser' },
          },
          preview: {
            title: 'Hoje',
            meta: 'Balcão',
            room: 'Sala {{number}}',
            services: {
              facial: 'Facial',
              massage: 'Massagem',
              haircut: 'Corte',
              manicure: 'Manicure',
            },
            statuses: {
              confirmed: 'Confirmado',
              membership: 'Assinatura',
              walkIn: 'Walk-in',
            },
          },
        },
        features: {
          overline: 'Plataforma',
          title: 'Feito para a forma como um estúdio funciona.',
          subtitle: 'Nove módulos que cobrem o dia todo: a agenda no balcão, a página de reservas online, as assinaturas e os pacotes para os clientes habituais e os pagamentos no fim do mês.',
          appointments: {
            title: 'Agenda da equipa',
            description: 'Veja o dia de cada profissional de relance. Marque walk-ins no balcão, inclusive fora do horário de abertura quando precisar.',
          },
          team: {
            title: 'Equipa, funções e folgas',
            description: 'Convide colaboradores, defina quem aceita reservas online, gerencie permissões e bloqueie folgas no calendário.',
          },
          services: {
            title: 'Serviços e categorias',
            description: 'Organize cabelo, pele, bem-estar e extras com durações, preços e valores personalizados por profissional.',
          },
          booking: {
            title: 'Reservas online',
            description: 'Partilhe uma página com a sua marca para o cliente escolher serviço, profissional e horário sem ligar para o estúdio.',
          },
          memberships: {
            title: 'Planos de Assinatura e Pacotes de Serviços',
            description: 'Venda planos de assinatura mensais e pacotes de serviços pré-pagos, como 10 sessões. As visitas restantes são contadas por si e descontadas à medida que o cliente marca.',
          },
          customers: {
            title: 'Ficha de clientes',
            description: 'Guarde walk-ins e clientes habituais numa só lista, com o histórico de agendamentos pronto quando voltarem.',
          },
          reminders: {
            title: 'Lembretes que evitam faltas',
            description: 'Confirmações e lembretes por email estão incluídos em todos os planos. O Pro acrescenta SMS e WhatsApp, cobrados por mensagem.',
          },
          payroll: {
            title: 'Folha e comissões',
            description: 'Acompanhe o que cada profissional ganhou em serviços, assinaturas e pacotes, com uma vista de pagamentos pensada para o chão de estúdio.',
          },
          dashboard: {
            title: 'Painel do dia',
            description: 'Agendamentos de hoje, pagamentos pendentes, receita do mês e assinaturas ativas num só lugar.',
          },
        },
        howItWorks: {
          overline: 'Onboarding',
          title: 'Abra as portas em quatro passos.',
          subtitle: 'O registo é guiado desde o primeiro ecrã. A maioria dos estúdios começa a aceitar reservas na mesma tarde.',
          steps: {
            register: {
              title: 'Crie a sua loja',
              description: 'Registe-se com os dados da loja, contactos e horário de funcionamento num fluxo guiado.',
            },
            setup: {
              title: 'Adicione serviços e horários',
              description: 'Configure o menu de serviços, horários de abertura e perfil da loja para os clientes descobrirem.',
            },
            team: {
              title: 'Convide a equipa',
              description: 'Adicione membros da equipa, atribua funções e dê a cada profissional acesso à sua agenda.',
            },
            book: {
              title: 'Reserve no balcão ou online',
              description: 'Partilhe o link de reservas para marcações online e ofereça planos de assinatura e pacotes de serviços aos clientes habituais.',
            },
          },
        },
        pricing: {
          overline: 'Planos',
          title: 'Grátis para começar. Pro quando crescer.',
          subtitle: 'O Grátis cobre um profissional pelo tempo que precisar. O Pro abre o resto da plataforma — profissionais extra, lojas extra, pagamentos online, stock, pagamentos da equipa e mensagens.',
          plans: {
            free: {
              name: 'Grátis',
              tagline: 'Para quem trabalha sozinho',
              price: 'GRÁTIS',
              priceNote: 'Sem cartão de crédito e sem prazo de validade.',
              note: 'Faça upgrade para Pro no dia em que contratar. Não há nada para migrar nem para configurar de novo.',
              cta: 'Criar loja grátis',
              values: {
                stores: 'Loja única',
                professionals: 'Profissional único',
                dashboard: 'Painel básico',
                payments: 'Pagamento na loja',
              },
            },
            pro: {
              name: 'Pro',
              tagline: 'Para equipas em crescimento e várias lojas',
              priceSuffix: '/mês',
              priceNote: 'Nos primeiros {{months}} meses; depois {{price}}/mês. Cancele quando quiser.',
              cta: 'Comece grátis e faça upgrade na app',
              values: {
                stores: 'Várias lojas',
                professionals: 'Profissionais ilimitados',
                dashboard: 'Painel completo',
                payments: 'Pagamentos online e na loja',
              },
            },
          },
          features: {
            appointments: 'Agendamentos e reservas online',
            emailReminders: 'Lembretes por email',
            realtimeChat: 'Chat em tempo real com clientes na app',
            deposits: 'Sinal e pré-pagamento na marcação',
            installments: 'Pagamento em prestações com Klarna',
            refunds: 'Política de cancelamento com reembolsos automáticos',
            productsStock: 'Produtos e stock',
            membershipPlans: 'Planos de assinatura e pacotes de serviços',
            teamPayroll: 'Pagamentos da equipa',
            waitlistHours: 'Lista de espera e horários extra',
            additionalCosts: 'Custos adicionais em dias ou períodos específicos',
            customPermissions: 'Permissões personalizadas por membro da equipa',
            smsReminders: 'Lembretes por SMS',
            whatsappReminders: 'Lembretes por WhatsApp',
            customBranding: 'Cores da marca personalizadas',
            prioritySupport: 'Suporte prioritário',
            marketingAutomation: 'Mensagens de marketing automáticas',
          },
          rates: {
            perMessage: '{{price}} por mensagem',
          },
          comingSoon: 'Em breve',
          addOns: {
            title: 'Complementos',
            additionalStore: 'Loja adicional',
            additionalProfessional: 'Profissional adicional',
            perMonth: '{{price}}/mês',
          },
          footnote: 'Preços em EUR. O Pro custa {{introPrice}}/mês nos primeiros {{months}} meses e depois {{regularPrice}}/mês. Lojas extra custam {{extraStorePrice}}/mês na mesma fatura. Profissionais adicionais custam {{teamMemberPrice}}/mês. O SMS custa {{smsPrice}} e o WhatsApp {{whatsAppPrice}} por mensagem. Os preços dos planos de assinatura e dos pacotes de serviços são definidos por cada loja.',
        },
        faq: {
          overline: 'Perguntas',
          title: 'Antes de se registar.',
          subtitle: 'Respostas diretas sobre o Grátis, o Pro e como o Salovare funciona no dia a dia do estúdio.',
          items: {
            price: {
              question: 'Quanto custa o Salovare?',
              answer: 'O Grátis é 0€ para uma loja com um profissional, sem prazo de validade. O Pro é {{introPrice}}/mês nos primeiros {{months}} meses e {{regularPrice}}/mês depois disso. Lojas extra custam {{extraStorePrice}}/mês na mesma fatura. Profissionais adicionais custam {{teamMemberPrice}}/mês e as mensagens são cobradas por mensagem.',
            },
            plans: {
              question: 'Qual é a diferença entre Grátis e Pro?',
              answer: 'O Grátis inclui agendamentos, reservas online, lembretes por email, o painel básico e o chat com clientes na app para um profissional numa loja, com os clientes a pagar na loja. O Pro acrescenta pagamentos online com sinal, pré-pagamento, prestações e reembolsos automáticos, produtos e stock, planos de assinatura e pacotes de serviços, pagamentos da equipa, lista de espera e horários extra, permissões personalizadas por membro da equipa, lembretes por SMS e WhatsApp, marca personalizada, o painel completo e suporte prioritário.',
            },
            messaging: {
              question: 'Como funcionam os lembretes por SMS e WhatsApp?',
              answer: 'Estão disponíveis no Pro e são cobrados por mensagem no ciclo da subscrição: {{smsPrice}} por SMS e {{whatsAppPrice}} por mensagem de WhatsApp. Os lembretes por email estão incluídos em todos os planos.',
            },
            healthBeauty: {
              question: 'Serve para a área da saúde e beleza?',
              answer: 'Sim. O Salovare foi feito para negócios de saúde e beleza — salões, barbearias, spas, clínicas e estúdios de bem-estar: agenda da equipa, walk-ins, reservas online, planos de assinatura, pacotes de serviços, lembretes e folha de pagamentos.',
            },
            multiStore: {
              question: 'Posso gerir mais do que uma loja?',
              answer: 'Sim, no Pro. O Grátis inclui uma loja. Lojas extra custam {{extraStorePrice}}/mês cada uma na mesma fatura do dono. Se o pagamento parar, a loja mais antiga fica no Grátis e as outras são desativadas.',
            },
            creditCard: {
              question: 'Preciso de cartão de crédito para começar?',
              answer: 'Não. Pode criar a loja no plano Grátis sem adicionar um método de pagamento. Adicione um cartão quando fizer upgrade para Pro.',
            },
          },
        },
        seo: {
          title: 'Salovare | Software para saúde e beleza — Grátis e Pro a partir de 9,90€/mês',
          description: 'Software de agendamentos para a área da saúde e beleza. Comece grátis com um profissional e uma loja. Pro a partir de 9,90€/mês para lugares extra, produtos, assinaturas, pacotes de serviços e folha de pagamentos.',
        },
        cta: {
          overline: 'Comece agora',
          title: 'Receba a próxima reserva no Salovare.',
          subtitle: 'Crie a sua loja em poucos minutos. Sem cartão de crédito, sem contrato e sem custos de instalação.',
          button: 'Criar a minha loja',
        },
        footer: {
          signIn: 'Entrar na equipa',
          register: 'Criar loja',
          copyright: '© {{year}} Salovare. Todos os direitos reservados.',
        },
      },
      settings: 'Configurações',
      home: {
        welcomeBack: 'Bem-vindo de volta, {{name}}!',
        description: 'Esta é sua página inicial onde em breve você verá seu painel com agendamentos, estatísticas e ações rápidas. Por enquanto, aproveite esta interface limpa e moderna projetada especificamente para as necessidades do seu salão.',
      },
      dashboard: {
        title: 'Painel',
        subtitle: 'Visão geral de agendamentos, vendas e desempenho da loja',
        selectStore: 'Selecione uma loja para ver o painel.',
        rebuild: 'Reconstruir dados do painel',
        rebuildConfirmTitle: 'Reconstruir dados do painel?',
        rebuildConfirmMessage: 'Isto reagrega os últimos 12 meses de estatísticas do painel a partir de agendamentos e receita. Os dados agregados existentes nesse período serão substituídos.',
        rebuildSuccess: 'Dados do painel reconstruídos com sucesso.',
        rebuildError: 'Falha ao reconstruir os dados do painel.',
        activityTypes: {
          appointmentCreated: 'Novo agendamento para {{customerName}}',
          appointmentPaid: 'Pagamento recebido de {{customerName}}',
          appointmentCompleted: 'Agendamento concluído para {{customerName}}',
          appointmentCancelled: 'Agendamento cancelado para {{customerName}}',
          appointmentNoShow: 'Não compareceu: {{customerName}}',
          revenueRecognized: 'Receita reconhecida para {{customerName}}',
          membershipEnrolled: '{{customerName}} aderiu a {{planName}}',
          membershipCanceled: '{{customerName}} cancelou {{planName}}',
          membershipPaused: '{{customerName}} pausou {{planName}}',
          productSold: 'Venda de produto para {{customerName}}',
          appointmentRefunded: 'Reembolso para {{customerName}}',
          productSaleVoided: 'Venda de produto revertida para {{customerName}}',
        },
      },
      managementAppointments: {
        title: 'Agendamentos',
        subtitle: 'Vista de calendário para a loja e profissional selecionados.',
        today: 'Hoje',
        professional: 'Profissional',
        myAgenda: 'Minha agenda',
        selectStore: 'Selecione uma loja para ver a agenda.',
        noPermission: 'Não tem permissão para ver a agenda de agendamentos.',
          selectProfessionalToView: 'Selecione um profissional para ver a agenda.',
        loadError: 'Falha ao carregar a agenda de agendamentos.',
        unnamedService: 'Serviço sem nome',
        status: 'Estado',
        customer: 'Cliente',
        services: 'Serviços',
        service: 'Serviço',
        products: 'Produtos',
        product: 'Produto',
        totalPrice: 'Total',
        additionalInfo: 'Informação adicional',
        timeRange: 'Intervalo horário',
        professionalsPerService: 'Profissional por serviço',
        professionals: 'Profissionais',
        noAdditionalInfo: 'Sem notas do cliente.',
        noConsultationNotes: 'Ainda sem notas de consulta.',
        openCustomerDetails: 'Abrir detalhes do cliente',
        unnamedProfessional: 'Profissional não atribuído',
        detailsTitle: 'Detalhes do agendamento',
        timeOff: 'Folga',
        timeOffDetailsTitle: 'Detalhes da folga',
        timeOffType: 'Tipo',
        markCompleted: 'Marcar como concluído',
        confirmOnRequest: 'Confirmar pedido',
        declineOnRequest: 'Recusar pedido',
        confirmingOnRequest: 'A confirmar...',
        decliningOnRequest: 'A recusar...',
        confirmOnRequestSuccess: 'Agendamento confirmado.',
        declineOnRequestSuccess: 'Pedido recusado.',
        onRequestRespondError: 'Falha ao responder à marcação sob pedido.',
        openToAnyProfessional: 'Qualquer profissional',
        selectProfessionalToConfirm: 'Selecione o profissional que está a responder',
        markCancelled: 'Marcar como cancelado',
        markNoShow: 'Marcar como não compareceu',
        completing: 'A concluir...',
        cancelling: 'A cancelar...',
        markingNoShow: 'A marcar não compareceu...',
        completeError: 'Falha ao marcar o agendamento como concluído.',
        statusUpdateError: 'Falha ao atualizar o estado do agendamento.',
        paymentStatusLabel: 'Estado do pagamento',
        markPaid: 'Marcar como pago',
        markingPaid: 'A marcar...',
        markPaidError: 'Falha ao marcar agendamento como pago.',
        changeToRegular: 'Alterar para agendamento regular',
        changingToRegular: 'A alterar...',
        changeToRegularError: 'Falha ao alterar o agendamento para regular.',
        amountDue: 'Valor a pagar',
        amountPaid: 'Valor pago',
        additionalCost: 'Custo adicional de horário',
        coveredByMembership: 'Coberto pela assinatura',
        pendingMembershipEnrollment: 'Assinatura a pagar no salão',
        membershipPlanNote: 'Incluído em {{plan}}',
        membershipPriceNote: 'Pague a assinatura {{plan}} no salão',
        confirm: {
          completeTitle: 'Marcar agendamento como concluído?',
          completeMessage: 'Isto marca o agendamento como concluído e pago. Esta ação não pode ser revertida.',
          completeEnrollTitle: 'Concluir e inscrever na assinatura?',
          completeEnrollMessage: 'Isto conclui o agendamento e inscreve o cliente em {{plan}}. Valor a pagar: {{amount}}. Esta ação não pode ser revertida.',
          completeAndBookNext: 'Confirmar e agendar próximo',
          cancelTitle: 'Marcar agendamento como cancelado?',
          cancelMessage: 'Isto liberta o horário. Esta ação não pode ser revertida.',
          noShowTitle: 'Marcar agendamento como não compareceu?',
          noShowMessage: 'Isto regista que o cliente não compareceu e liberta o horário. Esta ação não pode ser revertida.',
          deleteTitle: 'Eliminar agendamento?',
          deleteMessage: 'O agendamento será removido da agenda e excluído dos relatórios. Esta ação não pode ser revertida.',
          markPaidTitle: 'Marcar agendamento como pago?',
          markPaidMessage: 'Isto regista o agendamento como pago. Esta ação não pode ser revertida.',
          changeToRegularTitle: 'Alterar para agendamento regular?',
          changeToRegularMessage: 'Isto remove a cobertura da assinatura do agendamento e aplica os preços regulares dos serviços. Esta ação não pode ser revertida.',
          confirmOnRequestTitle: 'Confirmar esta marcação sob pedido?',
          confirmOnRequestMessage: 'Isto confirma o horário adicional e atribui-o ao profissional selecionado.',
          declineOnRequestTitle: 'Recusar esta marcação sob pedido?',
          declineOnRequestMessage: 'Se nenhum outro profissional aceitar, a marcação será cancelada e o cliente será notificado.'
        },
        edit: {
          title: 'Editar agendamento',
          action: 'Editar agendamento',
          date: 'Data',
          submit: 'Guardar alterações',
          submitting: 'A guardar...',
          submitError: 'Falha ao atualizar o agendamento.',
          loadError: 'Falha ao carregar dados do agendamento.',
          validation: {
            dateRequired: 'Selecione uma data.',
            startTimeRequired: 'Selecione uma hora de início.'
          }
        },
        delete: {
          action: 'Eliminar agendamento',
          error: 'Falha ao eliminar o agendamento.'
        },
        paymentStatus: {
          paid: 'Pago',
          pendingPayment: 'Pagamento pendente',
          unpaid: 'Não pago'
        },
        membershipRenewalRisk: 'Risco de renovação de assinatura: a data do agendamento está na data ou após o fim do período atual ({{date}}). Se a assinatura não for renovada, o preço regular do serviço será aplicado.',
        views: {
          oneDay: '1 dia',
          threeDays: '3 dias',
          sevenDays: '7 dias',
          oneDayShort: '1D',
          threeDaysShort: '3D',
          sevenDaysShort: '7D'
        },
        bookFab: 'Registar agendamento',
        book: {
          title: 'Registar agendamento',
          appointmentContext: 'Horário do agendamento',
          startTime: 'Hora de início',
          selectStartTime: 'Selecionar hora de início',
          tapToChangeTime: 'Toque para alterar',
          customerType: 'Cliente',
          customerTypeExisting: 'Cliente existente',
          customerTypeNew: 'Novo cliente',
          customerTypeAnonymous: 'Anónimo / walk-in',
          customerSearchPlaceholder: 'Pesquisar por nome, email ou telefone',
          guestName: 'Nome do convidado (opcional)',
          guestNamePlaceholder: 'Deixe vazio para walk-in',
          services: 'Serviços',
          products: 'Produtos',
          productsHelp: 'Adicione produtos para vender com este agendamento. São cobrados no pagamento.',
          professionals: 'Profissional por serviço',
          selectProfessional: 'Selecionar profissional',
          membership: 'Assinatura',
          membershipHelp: 'Escolha uma assinatura existente ou inicie um plano. O plano começa quando este agendamento for concluído e pago na loja.',
          pendingEnrollmentHelp: 'O cliente será inscrito quando este agendamento for concluído e pago. Até lá a assinatura fica pendente.',
          planServicesLocked: 'Os serviços do plano estão selecionados e não podem ser removidos. Ainda pode adicionar serviços extra.',
          noMembership: 'Nenhuma assinatura cobre os serviços selecionados.',
          noMembershipRedemption: 'Sem assinatura',
          subscribedTag: 'Subscrito',
          enrollInStoreTag: 'Inscrever na loja',
          notes: 'Notas',
          notesPlaceholder: 'Notas opcionais para este agendamento',
          submit: 'Registar agendamento',
          submitting: 'A registar...',
          success: 'Agendamento registado com sucesso.',
          submitError: 'Falha ao registar agendamento.',
          loadError: 'Falha ao carregar dados de agendamento.',
          noCustomerReadPermission: 'Não tem permissão para pesquisar clientes. Escolha novo cliente ou anónimo.',
          warnings: {
            closedDay: 'Esta loja está normalmente fechada na data selecionada. Mesmo assim pode registar o agendamento manualmente.',
            outsideHours: 'Este horário está fora do horário de funcionamento da loja. Mesmo assim pode registar o agendamento manualmente.',
            slotConflict: 'Este horário sobrepõe-se a um agendamento existente do profissional selecionado. Mesmo assim pode registar o agendamento manualmente.'
          },
          validation: {
            servicesRequired: 'Selecione pelo menos um serviço.',
            customerRequired: 'Selecione um cliente.',
            professionalRequired: 'Atribua um profissional a cada serviço selecionado.',
            dateRequired: 'Selecione uma data.',
            startTimeRequired: 'Selecione uma hora de início.'
          }
        }
      },
      notifications: {
        title: 'Notificações',
        newAppointment: 'Novo agendamento para John Doe',
        appointmentCancelled: 'Agendamento cancelado por Sarah Smith',
        systemMaintenance: 'Manutenção do sistema agendada para hoje à noite',
        twoMinutesAgo: '2 minutos atrás',
        oneHourAgo: '1 hora atrás',
        threeHoursAgo: '3 horas atrás',
      },
      customerNotifications: {
        title: 'Notificações',
        empty: 'Ainda não há notificações.',
        markAllRead: 'Marcar todas como lidas',
        seeAll: 'Ver todas',
        page: {
          title: 'Notificações',
          subtitle: 'Mensagens e atualizações desta loja',
          error: {
            loading: 'Não foi possível carregar as notificações.',
          },
        },
      },
      staffNotifications: {
        title: 'Notificações',
        empty: 'Ainda não há notificações.',
        markAllRead: 'Marcar todas como lidas',
        enableSounds: 'Ativar sons de notificação',
        seeAll: 'Ver todas',
        page: {
          title: 'Notificações',
          subtitle: 'Últimas notificações in-app para si',
          table: {
            title: 'Título',
            message: 'Mensagem',
            dateTime: 'Data e hora',
            status: 'Estado',
          },
          status: {
            read: 'Lida',
            unread: 'Não lida',
          },
          error: {
            loading: 'Falha ao carregar as notificações.',
            noStoreSelected: 'Nenhuma loja selecionada.',
          },
        },
		newAppointment: {
          title: 'Novo agendamento',
          body: 'Foi reservado um novo agendamento para si.',
          bodyWithDetails: '{{customerName}} marcou para {{date}} das {{begin}} às {{end}}. Ver detalhes.',
        },
        cancelledAppointment: {
          title: 'Agendamento cancelado',
          body: 'Um agendamento atribuído a si foi cancelado.',
        },
        supportChat: {
          title: 'Novo chat de cliente',
          body: 'Um cliente iniciou uma conversa com a loja.',
          messageTitle: 'Nova mensagem no chat',
          messageBody: '{{customerName}} enviou uma mensagem.'
        },
        platformSupport: {
          title: '{{storeName}}',
          body: '{{message}}',
        },
        waitlistJoined: {
          title: 'Cliente entrou na lista de espera',
          body: 'Um cliente entrou na lista de espera com datas e horários preferidos.',
        },
        waitlistSlotOpened: {
          title: 'Abriu um horário da lista de espera',
          body: 'Abriu um horário que corresponde a um cliente da lista de espera.',
        },
        appointmentConfirmationRequested: {
          title: 'Marcação sob pedido',
          body: 'Um cliente pediu um horário fora do horário regular.',
          bodyWithDetails: '{{customerName}} pediu {{date}} das {{begin}} às {{end}}. Confirme ou recuse.',
        },
        updatedAppointment: {
          title: 'Marcação alterada',
          body: 'Uma marcação atribuída a si foi alterada.',
          bodyWithDetails: 'A marcação de {{customerName}} em {{date}} das {{begin}} às {{end}} foi alterada.',
        },
        other: {
          title: 'Notificação',
          body: 'Tem uma nova notificação.',
        },
      },
      pushNotifications: {
        enable: 'Ativar notificações push',
        promptTitle: 'Ativar notificações push?',
        notNow: 'Agora não',
        staffHint: 'Receba alertas de novas marcações, alterações e cancelamentos mesmo com o Salovare fechado.',
        customerHint: 'Receba alertas quando uma marcação for criada, alterada, cancelada ou estiver a chegar.',
        denied: 'As notificações estão bloqueadas no navegador. Autorize-as nas definições do site para as ativar.',
        unsupported: 'Este navegador não suporta notificações push.',
        unavailable: 'As notificações push ainda não estão disponíveis.',
        error: 'Não foi possível atualizar as notificações push. Tente novamente.',
      },
      store: {
        clickToChange: 'Clique para trocar de loja',
        currentStore: 'Loja atual',
        selectStore: 'Selecionar Loja',
        registrationTitle: 'Registro de Loja',
        registrationSubtitle: 'Criar uma nova loja',
        accessDenied: 'Acesso negado. Você precisa de permissão para criar lojas.',
      },
      user: {
        clickToOpenMenu: 'Clique para abrir menu',
        profile: 'Perfil',
        logout: 'Sair',
        defaultName: 'Usuário',
      },
      storePlan: {
        upgradeRequired: 'Esta funcionalidade está disponível no plano Pro. Faça o upgrade para a desbloquear.',
        upgradeCta: 'Upgrade para Pro',
        upgradeToPro: 'Upgrade para Pro',
        freePlanTitle: 'Plano gratuito',
        freeBadge: 'Grátis',
        freePlanDescription: 'A sua loja está no plano Grátis. Faça upgrade para Pro para profissionais extra, lojas extra, produtos, assinaturas, folha de pagamentos, lista de espera, horários extra e SMS.',
        lapsedTitle: 'Reativar Pro',
        lapsedBadge: 'Pro terminou',
        lapsedDescription: 'A sua subscrição Pro terminou. Reative para recuperar profissionais extra, lojas extra e funcionalidades Pro. A loja permanece online no plano Grátis.',
        reactivateAction: 'Reativar Pro',
        reactivateTitle: 'Reative a sua conta',
        reactivateDescription: 'O seu plano Pro terminou. Reative para recuperar profissionais extra, lojas extra e funcionalidades Pro. A loja permanece online.',
        paymentFailedTitle: 'Atualize o método de pagamento',
        paymentFailedDescription: 'Não foi possível processar o último pagamento. Atualize o cartão no portal de faturação para manter o Pro.',
        paymentFailedAction: 'Atualizar pagamento',
        extraStoreTitle: 'Lojas extra são uma funcionalidade Pro',
        extraStoreMessage: 'O plano Grátis inclui uma loja. Faça upgrade para Pro para adicionar outra loja.',
        extraMemberTitle: 'Profissionais extra são uma funcionalidade Pro',
        extraMemberMessage: 'O plano Grátis inclui um profissional. Faça upgrade para Pro para adicionar membros da equipa.',
        smsUpgradeTitle: 'O SMS está incluído no Pro',
        smsUpgradeMessage: 'Faça upgrade para Pro para enviar SMS e WhatsApp de agendamentos.',
        prioritySupportBadge: 'Pro',
        upgradeDialog: {
          eyebrow: 'Salovare Pro',
          title: 'Desbloqueie a plataforma completa',
          lapsedTitle: 'Recupere o Pro',
          subtitle: 'Cresça além de um único profissional com pagamentos, ferramentas de equipa e mensagens.',
          priceNote: '{{introPrice}}/mês nos primeiros {{months}} meses, depois {{regularPrice}}/mês. Cancele quando quiser.',
          askOwner: 'Peça ao dono da loja para começar o plano Pro.',
          crownAria: 'Funcionalidade Pro. Abrir detalhes do upgrade.',
          crownTooltip: 'Funcionalidade Pro',
          lockedPageTitle: 'Isto é uma funcionalidade Pro',
          lockedPageSubtitle: 'Faça upgrade para desbloquear produtos, assinaturas, folha de pagamentos e o resto das ferramentas Pro.',
        },
      },
      ownerProfile: {
        title: 'Perfil',
        subtitle: 'Os seus dados pessoais e folgas',
        saved: 'Perfil guardado',
        tabs: {
          profile: 'Perfil',
          timeOff: 'Folgas',
        },
        error: {
          load: 'Não foi possível carregar o perfil.',
          save: 'Não foi possível guardar o perfil.',
        },
      },
      stores: {
        title: 'Lojas',
        subtitle: 'Detalhes e definições da loja selecionada',
        addNew: 'Adicionar Nova Loja',
        creationBlockedTitle: 'Configuração de faturação necessária',
        creationBlockedMessage: 'Faça upgrade para Pro na sua loja atual antes de adicionar outra loja.',
        creationBlockedAction: 'Abrir configurações de pagamento',
        totalStores: 'Total de Lojas',
        managedStores: 'Lojas Gerenciadas',
        searchPlaceholder: 'Pesquisar lojas...',
        filters: 'Filtros',
        noResults: 'Nenhuma loja encontrada para sua pesquisa',
        noStores: 'Nenhuma loja disponível',
        emptySelected: 'Selecione ou crie uma loja para começar.',
        tabs: {
          main: 'Principal',
          images: 'Imagens',
          roles: 'Funções e Permissões',
          rolesShort: 'Funções',
          onRequestHours: 'Horário adicional',
          onRequestHoursShort: 'Horário',
          additionalCosts: 'Custos adicionais',
          additionalCostsShort: 'Custos',
          waitlist: 'Lista de espera',
          branding: 'Marca',
        },
        branding: {
          title: 'Cores da marca',
          description: 'Escolha até quatro cores para as suas páginas de reserva e para esta área de gestão. Deixe uma cor vazia para manter a cor padrão do Salovare.',
          roles: {
            primary: { label: 'Principal', help: 'Barras superiores, menus e superfícies escuras.' },
            accent: { label: 'Destaque', help: 'Botões, links, destaques e itens selecionados.' },
            text: { label: 'Texto', help: 'Texto principal sobre fundos claros.' },
            background: { label: 'Fundo', help: 'Fundo das páginas.' },
          },
          hexPlaceholder: '#RRGGBB',
          invalidHex: 'Use uma cor hexadecimal, por exemplo #A15C43.',
          pickColor: 'Escolher a cor {{role}}',
          useDefault: 'Usar padrão',
          defaultValue: 'Padrão: {{color}}',
          resetAll: 'Repor as cores do Salovare',
          previewTitle: 'Pré-visualização',
          preview: {
            storeName: 'A sua loja',
            customer: 'Cliente',
            management: 'Gestão',
            login: 'Início de sessão',
            previous: 'Anterior',
            next: 'Seguinte',
            customerTitle: 'Página de reservas do cliente',
            managementTitle: 'Área de gestão',
            managementMenu: 'Navegação',
            managementCard: 'Hoje',
            loginTitle: 'Inicie sessão para continuar',
            bookButton: 'Reservar agora',
            menuItem: 'Agenda',
            serviceName: 'Corte e barba',
            serviceMeta: '45 min · com a Ana',
            selected: 'Selecionado',
          },
          warnings: {
            textContrast: 'Esta cor de texto é difícil de ler sobre o fundo, por isso será usado um tom mais legível.',
            accentContrast: 'Esta cor de destaque é difícil de ver sobre o fundo, por isso será usado um tom mais legível em links e ícones.',
          },
          saved: 'Cores da marca guardadas.',
          saveError: 'Não foi possível guardar as cores da marca.',
          upgradeTitle: 'As cores da marca personalizadas são uma funcionalidade Pro',
          upgradeMessage: 'Faça upgrade para Pro para usar as suas cores nas páginas de reserva e na área de gestão.',
          readOnly: 'Não tem permissão para alterar as cores da marca.',
          images: {
            title: 'Imagens da marca',
            description: 'Adicione imagens opcionais para o cabeçalho da gestão, o cabeçalho do cliente e as páginas de autenticação. As imagens são redimensionadas automaticamente.',
            managementLogo: { label: 'Logótipo da gestão', help: 'Mostrado no cabeçalho da gestão em computadores e telemóveis.' },
            customerLogo: { label: 'Logótipo do cliente', help: 'Mostrado no cabeçalho do cliente em computadores e telemóveis.' },
            loginLogo: { label: 'Logótipo de início de sessão', help: 'Mostrado nas páginas de início de sessão e registo do cliente.' },
            loginBackground: { label: 'Fundo de início de sessão', help: 'Mostrado por trás das páginas de autenticação do cliente.' },
            favicon: { label: 'Ícone do navegador', help: 'Mostrado no separador do navegador desta loja.' },
            saved: 'Imagem da marca guardada.',
            deleted: 'Imagem da marca removida.',
            confirmDelete: 'Remover esta imagem da marca?',
            error: {
              uploading: 'Não foi possível carregar a imagem da marca.',
              deleting: 'Não foi possível remover a imagem da marca.',
            },
          },
        },
          manager: 'Gerente',
          teamMember: 'Membro da Equipa',
          active: 'Ativo',
          inactive: 'Inativo',
        actions: {
          view: 'Visualizar',
          edit: 'Editar',
          billing: 'Atualizar dados de pagamento',
          teamMembers: 'Membros da Equipa',
          roles: 'Funções e Permissões',
          images: 'Gerir Imagens',
          copyBookingLink: 'Copiar link de marcação',
          delete: 'Excluir',
          enable: 'Habilitar',
          disable: 'Desabilitar',
          more: 'Mais Opções'
        },
        table: {
          name: 'Nome da Loja',
          role: 'Função',
          permissions: 'Permissões',
          selectRole: 'Selecione uma função',
          optional: 'Opcional',
          selected: 'Selecionado',
          fromRole: 'Da Função',
          additional: 'Adicional',
          includesPermissions_one: 'Inclui {{count}} permissão',
          includesPermissions_other: 'Inclui {{count}} permissões',
          rolePermissionsInfo: 'As permissões marcadas com "Da Função" estão incluídas na função selecionada e não podem ser modificadas. Pode adicionar permissões adicionais abaixo.',
          status: 'Status',
          actions: 'Ações'
        },
        delete: {
          title: 'Excluir Loja',
          message: 'Tem certeza de que deseja excluir "{name}"? Esta ação não pode ser desfeita.'
        },
        feedback: {
          bookingLinkCopied: 'Link de marcação copiado para a área de transferência'
        },
        authorizationHours: {
          helper: 'Horário extra fora do funcionamento normal. Os clientes pedem a marcação e a equipa confirma.',
          save: 'Guardar horário adicional',
          saved: 'Horário adicional guardado',
          overlapOpening: 'O horário adicional não pode sobrepor o horário de funcionamento.',
          overlapPeriods: 'Os períodos adicionais não podem sobrepor-se.',
          openingHoursLabel: 'Horário de funcionamento',
        },
        timePricing: {
          helper: 'Cobre mais por marcações que comecem nos dias ou horários escolhidos, incluindo horário extra. A percentagem usa os preços dos serviços, nunca o preço da assinatura.',
          shareTitle: 'Parte dos profissionais',
          shareHelper: 'Quanto do custo adicional os profissionais recebem além do salário ou comissão. 0 fica todo na loja.',
          shareType: 'Tipo de parte',
          shareValue: 'Valor da parte',
          rulesTitle: 'Regras por dia e hora',
          addRule: 'Adicionar regra',
          empty: 'Sem regras de custo adicional. Os clientes veem só o preço normal.',
          ruleLabel: 'Regra {{index}}',
          weekDays: 'Dias da semana',
          allDay: 'O dia todo',
          allDayHelper: 'Aplica-se a todo o dia, incluindo horário extra. Alterar o horário de funcionamento mais tarde não muda esta regra.',
          from: 'De',
          to: 'Até',
          presets: 'Preencher com horário de funcionamento ou adicional',
          adjustmentType: 'Tipo de custo adicional',
          adjustmentValue: 'Valor',
          save: 'Guardar custos adicionais',
          saved: 'Custos adicionais guardados',
          adjustmentTypes: {
            Percentage: 'Percentagem dos preços dos serviços',
            FixedAmount: 'Valor fixo',
          },
          validation: {
            weekDays: 'Cada regra precisa de pelo menos um dia da semana.',
            amountNegative: 'O custo adicional não pode ser negativo.',
            percentMax: 'A percentagem não pode ultrapassar 100.',
            periodRequired: 'Escolha o dia todo ou um horário.',
            overlap: 'As regras não podem sobrepor-se no mesmo dia.',
            shareNegative: 'A parte dos profissionais não pode ser negativa.',
            sharePercent: 'A percentagem da parte não pode ultrapassar 100.',
          },
        },
        images: {
          title: 'Imagens da Loja',
          addImages: 'Adicionar imagens',
          galleryTitle: 'Galeria',
          logoTitle: 'Logótipo',
          logoHelp: 'Usado como ícone da aplicação no ecrã inicial dos clientes.',
          appNameTitle: 'Nome da aplicação',
          appNamePlaceholder: 'ex. Jhon Barber',
          appNameTooLong: 'Use no máximo {{max}} caracteres.',
          appNameSaved: 'Nome da aplicação guardado.',
          saveAppName: 'Guardar',
          cropTitle: 'Recortar logótipo',
          cropZoom: 'Zoom',
          confirmDeleteLogo: 'Remover este logótipo da loja?',
          upload: 'Carregar Imagens',
          uploading: 'A carregar...',
          confirmDelete: 'Tem a certeza de que deseja eliminar esta imagem?',
          noImages: 'Nenhuma imagem disponível',
          maxImagesReached: 'Máximo de {{max}} imagens atingido',
          reorderHelp: 'Arraste para alterar a ordem que os clientes veem.',
          drag: 'Arraste para reordenar',
          moveEarlier: 'Mover para antes',
          moveLater: 'Mover para depois',
          error: {
            loading: 'Falha ao carregar imagens',
            uploading: 'Falha ao carregar imagens',
            deleting: 'Falha ao eliminar imagem',
            reorder: 'Falha ao guardar a ordem das imagens',
            maxImages: 'Máximo de {{max}} imagens permitidas. Atualmente tem {{current}} imagens. Pode adicionar até {{allowed}} imagem(ns) mais.',
            noFile: 'Nenhum ficheiro fornecido.',
            fileEmpty: 'O ficheiro está vazio.',
            fileTooLarge: 'O tamanho do ficheiro excede o limite máximo permitido de {{maxSize}} MB.',
            invalidFileType: 'Tipo de ficheiro não permitido. Formatos aceitos: {{formats}}',
            invalidMimeType: 'Tipo de ficheiro não permitido. Tipos MIME aceitos: {{mimeTypes}}',
            fileValidationError: 'Ficheiro {{index}} ({{fileName}}): {{error}}',
            cropFailed: 'Não foi possível recortar a imagem. Tente outro ficheiro.',
            savingAppName: 'Falha ao guardar o nome da aplicação.',
          }
        },
        error: {
          loading: 'Falha ao carregar lojas',
          notFound: 'Loja não encontrada',
          updateStatus: 'Falha ao atualizar o status da loja',
          updatePaymentSettings: 'Falha ao atualizar as configurações de pagamento',
          copyBookingLink: 'Falha ao copiar o link de marcação',
          bookingLinkUnavailable: 'O link de marcação não está disponível para esta loja',
          updateAuthorizationHours: 'Falha ao guardar o horário adicional',
          updateTimePricing: 'Falha ao guardar os custos adicionais'
        },
        pagination: {
          rowsPerPage: 'Linhas por página:',
          of: 'de'
        },
        billing: {
          title: 'Mantenha a sua subscrição Pro ativa',
          description: 'Adicione ou atualize os dados de pagamento para manter as funcionalidades Pro.',
          issueTitle: 'A sua loja precisa de atenção na faturação',
          issueDescription: 'Não foi possível cobrar o pagamento mais recente da subscrição. Atualize a faturação para manter a loja ativa.',
          action: 'Gerir faturação',
          openPortal: 'Gerir faturação',
          opening: 'A abrir o portal de faturação seguro...',
          errors: {
            ownerOnly: 'Apenas os proprietários da loja podem aceder ao portal de faturação.',
            open: 'Falha ao abrir o portal de faturação.',
            invalidUrl: 'A URL do portal de faturação é inválida.'
          }
        },
        checkout: {
          action: 'Subscrever agora',
          opening: 'A redirecionar para o checkout...',
          errors: {
            ownerOnly: 'Apenas os proprietários da loja podem iniciar uma sessão de checkout.',
            open: 'Falha ao iniciar o checkout. Por favor tente novamente.',
            invalidUrl: 'A URL do checkout é inválida.'
          }
        },
        stripeConnect: {
          title: 'Pagamentos de clientes (Stripe)',
          loading: 'A verificar o estado da conta Stripe...',
          notStarted: {
            title: 'Configure os pagamentos da sua loja',
            description: 'Ligue a sua loja ao Stripe para que os clientes possam pagar online agendamentos e assinaturas.'
          },
          pending: {
            title: 'Conclua a configuração da conta Stripe',
            description: 'O Stripe ainda precisa de mais informações antes desta loja poder aceitar pagamentos de clientes. Continue o formulário seguro do Stripe para enviar os dados em falta.'
          },
          status: {
            NotStarted: 'Não iniciado',
            Pending: 'Ação necessária',
            Complete: 'Pronto'
          },
          flags: {
            detailsSubmitted: 'Detalhes enviados',
            cardPayments: {
              active: 'Pagamentos com cartão ativos',
              pending: 'Pagamentos com cartão pendentes',
              inactive: 'Pagamentos com cartão inativos'
            }
          },
          membershipBilling: {
            pending: {
              title: 'Os pagamentos com cartão ainda estão a ser ativados',
              description: 'O onboarding Stripe está concluído, mas os pagamentos com cartão ainda não estão ativos. Isto costuma resolver-se em poucos minutos. Atualize o estado ou continue a configuração Stripe se forem necessários mais dados.'
            },
            inactive: {
              title: 'Conclua a configuração de pagamentos com cartão para assinaturas',
              description: 'Os planos de assinatura precisam de pagamentos com cartão ativos na sua conta Stripe. Continue a configuração segura do Stripe para enviar os dados em falta e depois atualize o estado aqui.'
            }
          },
          actions: {
            start: 'Configurar pagamentos Stripe',
            continue: 'Continuar configuração Stripe',
            refresh: 'Atualizar estado',
            opening: 'A abrir o Stripe...'
          },
          feedback: {
            complete: 'Os pagamentos Stripe estão agora ativos para esta loja.',
            membershipBillingReady: 'A faturação de assinaturas está pronta. Já pode criar planos de assinatura.'
          },
          errors: {
            ownerOnly: 'Apenas os proprietários da loja podem gerir o onboarding Stripe.',
            loadStatus: 'Falha ao carregar o estado do onboarding Stripe.',
            open: 'Falha ao abrir o onboarding Stripe.',
            refresh: 'Falha ao atualizar o estado da conta Stripe.',
            invalidUrl: 'A URL de onboarding Stripe é inválida.'
          }
        },
        access: {
          checking: 'A verificar o acesso a esta loja...',
          refresh: 'Atualizar acesso',
          backToStores: 'Voltar às lojas',
          billingAttentionTitle: 'A sua loja precisa de atenção de faturação',
          switchStoreLabel: 'Mudar para outra loja',
          ownerHint: 'Abra o portal seguro de faturação para adicionar os dados de pagamento e restaurar o acesso à gestão.',
          nonOwnerHint: 'Apenas o proprietário da loja pode restaurar o acesso na faturação. Contacte o proprietário para atualizar os dados de pagamento.',
          portalCardTitle: 'Restaurar o acesso pela faturação',
          portalCardDescription: 'Depois de atualizar os dados de pagamento ou a subscrição, atualize o acesso e o Salovare voltará a desbloquear a loja.',
          reasons: {
            trialExpired: {
              badge: 'Teste terminado',
              title: 'O seu teste gratuito terminou',
              description: 'Esta loja está pausada até a faturação ser atualizada.'
            },
            subscriptionCanceled: {
              badge: 'Subscrição cancelada',
              title: 'A subscrição desta loja foi cancelada',
              description: 'Reative a faturação para desbloquear novamente a área de gestão.'
            },
            subscriptionExpired: {
              badge: 'Subscrição expirada',
              title: 'A subscrição desta loja expirou',
              description: 'Atualize a faturação para desbloquear novamente a área de gestão.'
            },
            subscriptionSuspended: {
              badge: 'Subscrição suspensa',
              title: 'A subscrição desta loja está suspensa',
              description: 'Resolva o problema de faturação em atraso para desbloquear novamente a área de gestão.'
            },
            paymentMethodRequired: {
              badge: 'Método de pagamento necessário',
              title: 'Método de pagamento necessário',
              description: 'Adicione um método de pagamento para ativar a subscrição desta loja e restaurar o acesso.'
            },
            subscriptionPastDue: {
              badge: 'Pagamento falhou',
              title: 'Pagamento da subscrição falhou',
              description: 'O último pagamento da subscrição falhou. Atualize a faturação para restaurar o acesso a esta loja.'
            },
            extraStoreDeactivated: {
              badge: 'Loja desativada',
              title: 'Esta loja extra está desativada',
              description: 'A faturação Pro falhou. A loja mais antiga permanece no plano Grátis. Restaure a faturação para reabrir esta loja extra.'
            }
          }
        },
        view: {
          subtitle: 'Visualizar detalhes e informações da loja',
          basicInfo: 'Informações Básicas',
          name: 'Nome da Loja',
          slug: 'Slug (URL)',
          status: 'Status',
          about: 'Sobre',
          supportedLanguages: 'Idiomas Suportados',
          storeLanguage: 'Idioma Padrão da Loja',
          address: 'Morada',
          street: 'Rua',
          door: 'Porta',
          additionalInfo: 'Informação Adicional',
          city: 'Cidade',
          state: 'Distrito',
          zipCode: 'Código Postal',
          country: 'País',
          openingHours: 'Horário de Funcionamento',
          contact: 'Contacto',
          timeZone: 'Fuso Horário',
          slotInterval: 'Intervalo de Horário',
          images: 'Imagens',
          paymentSettings: 'Configurações de Pagamento',
          payOnBooking: 'Aceitar pagamentos online na reserva',
          payOnBookingDescription: 'Quando ativado, os clientes podem pagar pelo agendamento online no momento da reserva.',
          stripeRequiredForOnlinePayments: 'A integração de pagamento Stripe deve ser configurada antes de ativar os pagamentos online.',
          addImages: 'Adicionar Imagens',
          upload: 'Carregar Imagens',
          uploading: 'A carregar...',
          confirmDelete: 'Tem a certeza de que deseja eliminar esta imagem?',
          noImages: 'Nenhuma imagem disponível',
          maxImagesReached: 'Máximo de {{max}} imagens atingido',
          error: {
            loading: 'Falha ao carregar imagens',
            uploading: 'Falha ao carregar imagens',
            deleting: 'Falha ao eliminar imagem',
            maxImages: 'Máximo de {{max}} imagens permitidas. Atualmente tem {{current}} imagens. Pode adicionar até {{allowed}} imagem(ns) mais.',
            noFile: 'Nenhum ficheiro fornecido.',
            fileEmpty: 'O ficheiro está vazio.',
            fileTooLarge: 'O tamanho do ficheiro excede o limite máximo permitido de {{maxSize}} MB.',
            invalidFileType: 'Tipo de ficheiro não permitido. Formatos aceitos: {{formats}}',
            invalidMimeType: 'Tipo de ficheiro não permitido. Tipos MIME aceitos: {{mimeTypes}}',
            fileValidationError: 'Ficheiro {{index}} ({{fileName}}): {{error}}'
          }
        }
      },
      teamMembers: {
        title: 'Membros da Equipa',
        subtitle: 'Gerencie os membros da equipa da sua loja',
        addNew: 'Adicionar Novo Membro da Equipa',
        totalTeamMembers: 'Total de Membros da Equipa',
        searchPlaceholder: 'Pesquisar membros da equipa...',
        filters: 'Filtros',
        noResults: 'Nenhum membro da equipa encontrado para sua pesquisa',
        noTeamMembers: 'Nenhum membro da equipa disponível',
        actions: {
          view: 'Visualizar',
          edit: 'Editar',
          manageAgenda: 'Agenda',
          manageCompensation: 'Gerir Compensação',
          enable: 'Habilitar',
          disable: 'Desabilitar',
          more: 'Mais Opções'
        },
        table: {
          name: 'Nome',
          email: 'Email',
          manager: 'Gerente',
          services: 'Serviços',
          status: 'Status',
          actions: 'Ações'
        },
        status: {
          active: 'Ativo',
          inactive: 'Inativo',
          acceptsBookings: 'Aceita marcações',
          noCustomerBookings: 'Sem marcações de clientes'
        },
        acceptsCustomerBookings: {
          label: 'Atende marcações',
          help: 'Quando ativo, este profissional aparece na agenda, nos planos de assinatura e na página de marcação do cliente.',
          chargeNotice: 'Os profissionais que atendem marcações são cobrados.',
          currentPeriodNotice: 'Este profissional será cobrado no período de faturação atual e não será cobrado no próximo período de faturação.'
        },
        manager: 'Gerente',
        teamMember: 'Membro da Equipa',
        services: {
          showMore: 'Mostrar mais',
          showLess: 'Mostrar menos',
          moreServices: '+{count} mais'
        },
        registration: {
          title: 'Adicionar Novo Membro da Equipa',
          subtitle: 'Criar uma nova conta de membro da equipa para sua loja',
          personalInfo: 'Informações Pessoais',
          name: 'Nome',
          email: 'Endereço de Email',
          phone: 'Número de Telefone',
          jobTitle: 'Cargo',
          jobTitlePlaceholder: 'ex. Estilista Sénior',
          genre: 'Gênero',
          male: 'Masculino',
          female: 'Feminino',
          birthDate: 'Data de Nascimento',
          language: 'Idioma',
          role: 'Perfil',
          permissions: 'Permissões',
          selectRole: 'Selecionar perfil',
          fromRole: 'Do Perfil',
          additional: 'Adicional',
          includesPermissions_one: 'Inclui {{count}} permissão',
          includesPermissions_other: 'Inclui {{count}} permissões',
          rolePermissionsInfo: 'As permissões marcadas com "Do Perfil" estão incluídas no perfil selecionado e não podem ser modificadas. Pode adicionar permissões adicionais abaixo.',
          storeAssignment: 'Atribuição da Loja',
          currentStore: 'Loja Atual',
          isManager: 'É Gerente',
          services: 'Serviços',
          noServices: 'Nenhum serviço disponível para esta loja',
          create: 'Criar',
          assign: 'Adicionar à Loja',
          emailCheck: {
            check: 'Verificar',
            available: 'Este email está disponível. Complete os dados restantes abaixo.',
            existingTitle: 'Membro da equipa já registado',
            existingMessage: '{{name}} ({{email}}) já está registado no Salovare. Deseja adicionar este membro da equipa à sua loja? O perfil existente não será alterado.',
            existingConfirm: 'Adicionar a esta loja',
            existingConfirmed: '{{name}} será adicionado a esta loja. Configure o perfil, permissões e serviços abaixo.',
            alreadyInStore: 'Este membro da equipa já está atribuído a esta loja.',
            error: 'Não foi possível verificar este email. Tente novamente.'
          },
          success: {
            created: 'Membro da Equipa criado com sucesso! Email de configuração de senha enviado.',
            assigned: 'Membro da equipa adicionado a esta loja com sucesso.'
          },
          error: {
            requiredFields: 'Nome e email são campos obrigatórios',
            loadingServices: 'Falha ao carregar serviços',
            creation: 'Falha ao criar membro da equipa'
          },
          steps: {
            step1: {
              title: 'Informações Pessoais',
              description: 'Insira os dados pessoais e informações de contato do membro da equipa'
            },
            step2: {
              title: 'Perfil e Permissões',
              description: 'Atribua um perfil e configure as permissões para este membro da equipa'
            },
            step3: {
              title: 'Serviços',
              description: 'Selecione os serviços que este membro da equipa pode prestar'
            },
            step4: {
              title: 'Revisão',
              description: 'Revise todas as informações antes de criar o membro da equipa'
            }
          },
          validation: {
            nameRequired: 'Nome é obrigatório',
            emailRequired: 'Email é obrigatório',
            emailInvalid: 'Por favor, digite um email válido',
            phoneRequired: 'Número de telefone é obrigatório',
            servicesRequired: 'Por favor, selecione pelo menos um serviço',
            emailCheckRequired: 'Verifique este email antes de continuar.',
            alreadyInStore: 'Este membro da equipa já está atribuído a esta loja.',
            existingMemberConfirmationRequired: 'Confirme que deseja adicionar este membro da equipa existente à sua loja.'
          },
          namePlaceholder: 'Digite o nome',
          emailPlaceholder: 'Digite o endereço de email',
          phonePlaceholder: 'Digite o número de telefone',
          selectGenre: 'Selecione o gênero',
          servicePrice: 'Preço',
          serviceDuration: 'Duração',
          noServicesSelected: 'Nenhum serviço selecionado',
          defaultPrice: 'Preço Padrão',
          defaultDuration: 'Duração Padrão',
          minutes: 'minutos',
          customSettings: 'Configurações Personalizadas',
          customPrice: 'Preço Personalizado',
          customPriceHelp: 'Deixe vazio para usar o preço padrão',
          price: 'Preço',
          duration: 'Duração',
          custom: 'personalizado',
          default: 'padrão'
        },
        billing: {
          confirmTitle: 'Custo de membro adicional',
          confirmIntro: 'Adicionar um profissional que atende marcações implica um custo adicional de subscrição de {{price}}/mês por lugar além do membro incluído.',
          confirmAdd: 'Confirmar e adicionar membro',
          confirmEnable: 'Confirmar e ativar membro',
          immediateCharge: 'Será cobrado {{amount}} agora pelo resto deste período de faturação (até {{date}}).',
          recurringCharge: 'Depois {{amount}}/mês a partir da próxima data de faturação ({{date}}).',
          recurringChargeNoDate: 'Depois {{amount}}/mês em cada renovação.',
          removalPolicy: 'Se desativar este membro antes de {{date}}, continuará a ser cobrado por este lugar até ao fim do período de faturação atual. Não será cobrado no próximo período de faturação.',
          removalPolicyNoDate: 'Se desativar este membro antes da próxima data de faturação, continuará a ser cobrado por este lugar até ao fim do período de faturação atual. Não será cobrado no próximo período de faturação.',
          trialNotice: 'Isto adicionará {{price}}/mês após o fim do período experimental. Sem cobrança durante o período experimental.',
          reviewChargeNotice: 'Adicionar um profissional que atende marcações adicionará {{price}}/mês à sua subscrição.',
          assignExistingNotice: 'Adicionar este membro custará {{amount}}/mês após a faturação estar ativa. A remoção só tem efeito a partir do próximo período de faturação ({{date}}).',
          assignExistingNoticeNoDate: 'Adicionar este membro custará {{amount}}/mês após a faturação estar ativa.',
          disabledNotice: 'Este membro foi desativado. A faturação do lugar continua até ao próximo período de faturação.',
          syncFailed: 'Não foi possível atualizar a subscrição para o membro adicional. Verifique os Detalhes de Pagamento e tente novamente.'
        },
        picture: {
          upload: 'Alterar foto',
          uploading: 'A carregar...',
          delete: 'Remover foto',
          deleting: 'A remover...',
          editProfilePicture: 'Editar foto de perfil',
          modalTitle: 'Foto de perfil',
          modalDescription: 'Alterar ou remover a foto de perfil deste membro da equipa.',
          confirmDelete: 'Remover a foto de perfil deste membro da equipa?',
          uploadError: 'Falha ao carregar foto de perfil.',
          deleteError: 'Falha ao eliminar foto de perfil.'
        },
        timeOff: {
          modalTitle: 'Gerenciar Folgas',
          modalSubtitle: 'Agendar indisponibilidade para {{name}}',
          scheduledTitle: 'Folgas agendadas',
          addTitle: 'Adicionar folga',
          empty: 'Nenhuma folga agendada neste período.',
          fullDay: 'Dia inteiro',
          add: 'Adicionar folga',
          adding: 'A adicionar...',
          delete: 'Eliminar',
          confirmDeleteTitle: 'Eliminar folga',
          confirmDeleteMessage: 'Remover esta folga?',
          types: {
            AnnualLeave: 'Férias',
            SickLeave: 'Baixa médica',
            Other: 'Outro'
          },
          fields: {
            date: 'Data',
            type: 'Tipo',
            fullDay: 'Dia inteiro',
            fullDayHelp: 'Quando ativado, o membro da equipa fica indisponível durante todo o dia.',
            from: 'De',
            to: 'Até',
            details: 'Detalhes',
            detailsPlaceholder: 'Notas opcionais'
          },
          validation: {
            dateRequired: 'A data é obrigatória.',
            pastDate: 'Não é possível agendar folgas no passado.',
            invalidPeriod: 'A hora de início deve ser anterior à hora de fim.',
            pastPeriod: 'A folga não pode começar no passado.'
          },
          success: {
            created: 'Folga adicionada com sucesso.',
            deleted: 'Folga removida com sucesso.'
          },
          error: {
            loading: 'Falha ao carregar folgas.',
            create: 'Falha ao adicionar folga.',
            delete: 'Falha ao remover folga.',
            conflict: 'Esta folga sobrepõe-se a uma entrada existente.',
            pastDate: 'Não é possível agendar folgas no passado.'
          }
        },
        agenda: {
          title: 'Agenda',
          subtitle: 'Gerencie o horário de trabalho, as aberturas especiais e as folgas deste profissional.',
          tabs: {
            workingHours: 'Horário de trabalho',
            specialOpenings: 'Aberturas especiais',
            timeOff: 'Folgas',
          },
          error: {
            loading: 'Falha ao carregar a agenda.',
          },
        },
        workingHours: {
          title: 'Horário de trabalho',
          useStoreHours: 'Usar o horário de funcionamento da loja',
          storeHoursLabel: 'Horário da loja',
          save: 'Guardar horário de trabalho',
          success: {
            saved: 'Horário de trabalho guardado.',
          },
          error: {
            save: 'Falha ao guardar o horário de trabalho.',
          },
          validation: {
            overlapPeriods: 'Os períodos de trabalho não podem sobrepor-se.',
          },
        },
        specialOpenings: {
          calendarLabel: 'Selecionar datas',
          calendarHelp: 'Toque nas datas para adicionar ou remover. Os dias com contorno já têm uma abertura especial.',
          defaultFrom: 'Abertura predefinida',
          defaultTo: 'Encerramento predefinido',
          emptySelection: 'Selecione uma ou mais datas no calendário.',
          save: 'Guardar dias de abertura',
          saving: 'A guardar...',
          existingTitle: 'Aberturas especiais guardadas',
          existingEmpty: 'Nenhuma abertura especial agendada neste período.',
          confirmDeleteTitle: 'Eliminar abertura especial',
          confirmDeleteMessage: 'Remover esta abertura especial? Os clientes deixam de ver esses horários para este profissional.',
          validation: {
            datesRequired: 'Selecione pelo menos uma data.',
            invalidPeriod: 'A hora de abertura deve ser anterior à de encerramento.',
          },
          success: {
            saved: 'Dias extra de abertura guardados.',
            deleted: 'Abertura especial removida.',
          },
          error: {
            loading: 'Falha ao carregar aberturas especiais.',
            save: 'Falha ao guardar aberturas especiais.',
            delete: 'Falha ao remover abertura especial.',
          },
        },
        error: {
          loading: 'Falha ao carregar membros da equipa',
          noStoreSelected: 'Nenhuma loja selecionada. Por favor, selecione uma loja primeiro.',
        },
        fallback: {
          message: 'Por favor, selecione uma loja no menu superior para gerenciar membros da equipa.',
          goToStores: 'Ir para Lojas'
        },
        update: {
          title: 'Atualizar Membro da Equipa',
          subtitle: 'Modifique as informações e configurações do membro da equipa',
          update: 'Atualizar',
          discardChanges: 'Descartar Alterações',
          discardSuccess: 'Alterações descartadas. Formulário restaurado aos dados originais.',
          discardError: 'Falha ao descartar alterações.',
          success: {
            updated: 'Membro da Equipa atualizado com sucesso!'
          },
          error: {
            loading: 'Falha ao carregar informações do membro da equipa',
            update: 'Falha ao atualizar membro da equipa'
          },
          steps: {
            step1: {
              title: 'Informações Pessoais',
              description: 'Atualize os dados pessoais e informações de contato do membro da equipa'
            },
            step2: {
              title: 'Perfil e Permissões',
              description: 'Atualize o perfil e permissões para este membro da equipa'
            },
            step3: {
              title: 'Serviços',
              description: 'Atualize os serviços que este membro da equipa pode prestar'
            },
            step4: {
              title: 'Revisão',
              description: 'Revise todas as informações antes de atualizar o membro da equipa'
            }
          }
        }
      },
      servicesManagement: {
        title: 'Serviços',
        subtitle: 'Gerencie os serviços da sua loja',
        tabs: {
          services: 'Serviços',
          categories: 'Categorias',
        },
        addNew: 'Adicionar Novo Serviço',
        noServices: 'Nenhum serviço disponível',
        emptyHint: 'Crie seu primeiro serviço para começar a atribuí-lo aos membros da equipa e aos agendamentos.',
        card: {
          noDescription: 'Nenhuma descrição fornecida',
          primaryService: 'Serviço principal',
          secondaryService: 'Serviço secundário',
          duration: 'Duração',
          price: 'Preço'
        },
        actions: {
          edit: 'Editar',
          delete: 'Excluir'
        },
        fields: {
          name: 'Nome do Serviço',
          description: 'Descrição do Serviço',
          type: 'Tipo de Serviço',
          typeHelpTitle: 'O que significa o tipo de serviço?',
          typeHelpAriaLabel: 'O que significa o tipo de serviço?',
          typeHelpPrimary: 'Principal é o serviço que o cliente veio fazer. O agendamento dura o tempo deste serviço. Use para coisas como corte, coloração ou manicure.',
          typeHelpSecondary: 'Secundário é um extra feito ao mesmo tempo que um serviço principal. Não aumenta o tempo do agendamento. Use para coisas como sobrancelhas ou barba feitas durante o corte.',
          typeHelpExample: 'Exemplo: um corte de 30 minutos (principal) mais sobrancelhas (secundário). O cliente fica marcado por 30 minutos, não 30 mais o tempo das sobrancelhas. Se as pessoas também puderem marcar só esse extra, ative “Permitir agendamento isolado” abaixo.',
          duration: 'Duração',
          price: 'Preço',
          isActive: 'Ativo',
          isActiveHelp: 'Desative isto para criar ou salvar o serviço como inativo.',
          isStandalone: 'Permitir agendamento isolado',
          isStandaloneHelp: 'Ative isto quando os clientes puderem reservar o serviço sem um serviço principal.',
          teamMembers: 'Membros da equipa que podem realizar este serviço',
          teamMembersHelp: 'Os clientes só conseguem reservar este serviço se pelo menos um membro da equipa estiver atribuído a ele.',
          noTeamMembersAvailable: 'Não há membros da equipa disponíveis para atribuir.'
        },
        types: {
          primary: 'Principal',
          secondary: 'Secundário'
        },
        bookingRule: {
          primary: 'Serviço principal',
          standalone: 'Independente',
          requiresPrimary: 'Requer serviço principal'
        },
        status: {
          active: 'Ativo',
          inactive: 'Inativo'
        },
        dialog: {
          createTitle: 'Adicionar Serviço',
          editTitle: 'Editar Serviço',
          saveCreate: 'Criar Serviço',
          saveEdit: 'Salvar Alterações',
          steps: {
            details: 'Detalhes',
            pricing: 'Preços',
            consent: 'Consentimento',
          },
        },
        unassignedWarning: {
          title: 'Nenhum membro da equipa atribuído',
          message: 'Os clientes não conseguirão reservar este serviço porque nenhum membro da equipa está atribuído para o realizar.',
          confirmCreate: 'Criar mesmo assim',
          confirmSave: 'Guardar mesmo assim'
        },
        delete: {
          title: 'Excluir Serviço',
          message: 'Tem certeza de que deseja excluir "{{name}}"? Esta ação não pode ser desfeita.',
          confirm: 'Excluir',
          cancel: 'Cancelar'
        },
        feedback: {
          created: 'Serviço criado com sucesso!',
          updated: 'Serviço atualizado com sucesso!',
          deleted: 'Serviço excluído com sucesso!'
        },
        validation: {
          nameRequired: 'O nome do serviço é obrigatório para todos os idiomas suportados',
          priceRequired: 'O preço é obrigatório e deve ser maior que 0',
          durationRequired: 'A duração é obrigatória'
        },
        error: {
          loading: 'Falha ao carregar serviços',
          create: 'Falha ao criar serviço',
          update: 'Falha ao atualizar serviço',
          delete: 'Falha ao excluir serviço',
          noStoreSelected: 'Nenhuma loja selecionada. Por favor, selecione uma loja primeiro.'
        },
        fallback: {
          message: 'Por favor, selecione uma loja no menu superior para gerenciar serviços.',
          goToStores: 'Ir para Lojas'
        }
      },
      productsManagement: {
        title: 'Produtos',
        subtitle: 'Gerencie os produtos e as vendas da sua loja',
        tabs: {
          products: 'Produtos',
          sales: 'Vendas',
          settings: 'Comissão',
          commission: 'Comissão',
        },
        addNew: 'Adicionar produto',
        recordSale: 'Registar venda',
        noProducts: 'Ainda não há produtos',
        emptyHint: 'Crie o primeiro produto para o vender na loja e durante a reserva.',
        card: {
          noDescription: 'Nenhuma descrição fornecida',
          price: 'Preço',
          stock: 'Em stock',
          outOfStock: 'Esgotado',
          lowStock: 'Stock baixo',
        },
        actions: {
          edit: 'Editar',
          delete: 'Eliminar',
          sell: 'Registar venda',
        },
        fields: {
          name: 'Nome do produto',
          description: 'Descrição do produto',
          price: 'Preço',
          stock: 'Quantidade em stock',
          stockHelp: 'Deixe vazio para não controlar stock. As vendas nunca são bloqueadas por stock.',
          isActive: 'Ativo',
          isActiveHelp: 'Desative para esconder o produto de novas vendas.',
          image: 'Imagem do produto',
          imageHelp: 'Opcional. As imagens são redimensionadas antes do envio, como as fotos da loja.',
          imageRemoveConfirm: 'Remover esta imagem do produto?',
        },
        status: {
          active: 'Ativo',
          inactive: 'Inativo',
        },
        dialog: {
          createTitle: 'Adicionar produto',
          editTitle: 'Editar produto',
          saveCreate: 'Criar produto',
          saveEdit: 'Guardar alterações',
        },
        delete: {
          title: 'Eliminar produto',
          message: 'Tem a certeza de que pretende eliminar "{{name}}"? Esta ação não pode ser revertida.',
          confirm: 'Eliminar',
          cancel: 'Cancelar',
        },
        sale: {
          title: 'Registar venda de produtos',
          product: 'Produto',
          quantity: 'Quantidade',
          addLine: 'Adicionar',
          lines: 'Ainda não foram adicionados produtos',
          total: 'Total',
          professional: 'Profissional',
          noProfessional: 'Sem profissional',
          professionalLocked: 'Só pode registar vendas em seu nome. Atribuir outro profissional exige permissão extra.',
          customer: 'Cliente',
          walkIn: 'Anónimo',
          notes: 'Notas',
          commissionHint: 'Comissão estimada: {{amount}}',
          save: 'Registar venda',
          validation: {
            noLines: 'Adicione pelo menos um produto',
            quantityInvalid: 'A quantidade deve ser pelo menos 1',
            amountInvalid: 'O valor do pagamento é inválido',
          },
        },
        sales: {
          title: 'Vendas',
          detailsTitle: 'Detalhes da venda',
          advancedSearch: 'Pesquisa avançada',
          hideAdvancedSearch: 'Ocultar pesquisa avançada',
          table: {
            date: 'Data',
            channel: 'Canal',
            customer: 'Cliente',
            professional: 'Profissional',
            items: 'Itens',
            total: 'Total',
            collected: 'Recebido',
            discount: 'Desconto',
            commission: 'Comissão',
            paymentMethod: 'Pagamento',
            status: 'Estado',
            audit: 'Auditoria',
          },
          channels: {
            staffInStore: 'Na loja',
            customerSelfService: 'Online',
          },
          statuses: {
            pending: 'Pendente',
            completed: 'Concluída',
            voided: 'Revertida',
          },
          sellerAssignedBy: 'Vendedor atribuído por {{name}}',
          customerLinesModifiedBy: 'Linhas do cliente editadas por {{name}}',
          void: 'Reverter',
          voidConfirm: 'Reverter esta venda concluída? O stock e a comissão serão revertidos.',
          empty: 'Não há vendas de produtos neste período.',
        },
        checkout: {
          sectionTitle: 'Produtos',
          customerSelection: 'Escolha do cliente',
          customerSelectionHint: 'O cliente escolheu estes itens. Não pagam comissão a menos que um profissional seja creditado.',
          addedInStore: 'Adicionados na loja',
          readOnlyHint: 'Precisa de permissão extra para alterar o que o cliente selecionou.',
        },
        settings: {
          title: 'Comissão de produtos',
          description: 'A mesma percentagem aplica-se a todos os profissionais. As alterações só afetam vendas futuras.',
          commissionPercent: 'Percentagem de comissão',
          commissionPercentHelp: 'Introduza um valor entre 0 e 100. Esta percentagem fica congelada em cada venda concluída.',
          selfServiceNote: 'Produtos que o cliente escolhe sozinho não pagam comissão, a menos que um gestor credite um profissional.',
          save: 'Guardar',
          saved: 'Definições de produtos guardadas',
        },
        feedback: {
          created: 'Produto criado com sucesso!',
          updated: 'Produto atualizado com sucesso!',
          deleted: 'Produto eliminado com sucesso!',
          saleRecorded: 'Venda de produtos registada.',
          saleVoided: 'Venda de produtos revertida.',
          settingsSaved: 'Definições de produtos guardadas.',
        },
        validation: {
          nameRequired: 'O nome do produto é obrigatório em todos os idiomas suportados',
          priceRequired: 'O preço é obrigatório e deve ser maior que 0',
          stockInvalid: 'A quantidade em stock não pode ser negativa',
        },
        error: {
          loading: 'Falha ao carregar produtos',
          create: 'Falha ao criar produto',
          update: 'Falha ao atualizar produto',
          delete: 'Falha ao eliminar produto',
          createSale: 'Falha ao registar venda de produtos',
          voidSale: 'Falha ao reverter venda de produtos',
          settings: 'Falha ao guardar definições de produtos',
          image: 'Falha ao atualizar a imagem do produto',
          noStoreSelected: 'Nenhuma loja selecionada. Por favor, selecione uma loja primeiro.',
        },
        fallback: {
          message: 'Por favor, selecione uma loja no menu superior para gerenciar produtos.',
          goToStores: 'Ir para Lojas',
        },
      },
      serviceCategoriesManagement: {
        addNew: 'Adicionar Categoria',
        noCategories: 'Ainda não há categorias',
        emptyHint: 'Crie categorias para organizar serviços para a sua equipa e reserva de clientes.',
        card: {
          unnamed: 'Categoria sem nome',
          serviceCount: '{{count}} serviços',
        },
        actions: {
          drag: 'Arrastar para reordenar',
          moveUp: 'Mover para cima',
          moveDown: 'Mover para baixo',
          edit: 'Editar',
          delete: 'Eliminar',
        },
        status: {
          active: 'Ativa',
          inactive: 'Inativa',
        },
        fields: {
          name: 'Nome',
          description: 'Descrição',
          active: 'Ativa',
          services: 'Serviços atribuídos',
          noServicesAvailable: 'Não há serviços disponíveis para atribuir.',
        },
        dialog: {
          createTitle: 'Adicionar Categoria',
          editTitle: 'Editar Categoria',
          saveCreate: 'Criar Categoria',
          saveEdit: 'Guardar Alterações',
        },
        delete: {
          title: 'Eliminar Categoria',
          message: 'Tem a certeza de que deseja eliminar "{{name}}"? Os serviços permanecem, mas deixam de estar agrupados nesta categoria.',
          confirm: 'Eliminar',
        },
        feedback: {
          created: 'Categoria criada com sucesso!',
          updated: 'Categoria atualizada com sucesso!',
          deleted: 'Categoria eliminada com sucesso!',
        },
        validation: {
          nameRequired: 'O nome da categoria é obrigatório para todos os idiomas suportados',
        },
        error: {
          loading: 'Falha ao carregar categorias',
          create: 'Falha ao criar categoria',
          update: 'Falha ao atualizar categoria',
          delete: 'Falha ao eliminar categoria',
          reorder: 'Falha ao guardar a ordem das categorias',
          noStoreSelected: 'Nenhuma loja selecionada. Por favor, selecione uma loja primeiro.',
        },
      },
      membershipPlansManagement: {
        title: 'Planos de Assinatura',
        subtitle: 'Crie e gerencie planos de assinatura recorrentes para os seus clientes',
        tabs: {
          plans: 'Planos',
          customerMemberships: 'Assinaturas de Clientes',
          customerMembershipsShort: 'Assinaturas',
          settings: 'Configurações',
        },
        revenueSettings: {
          title: 'Definições de receita de assinaturas',
          subtitle: 'Margem da loja e dia de pagamento para partilha do pote de assinaturas. Aplica-se apenas a receita de assinaturas, não a agendamentos regulares.',
          storeMarginPercent: 'Margem da loja em assinaturas (%)',
          membershipSharePayDay: 'Dia de pagamento das partes de assinaturas',
          payDayOption: 'Dia {{day}}',
          payDayHint: 'O pote de janeiro é pago em fevereiro neste dia.',
          distributionModel: 'Modelo de distribuição',
          distributionModelOptions: {
            monthlyPot: 'Pote mensal',
            periodUsage: 'Com base no uso',
          },
          distributionModelHints: {
            monthlyPot: 'No dia de pagamento da loja, os períodos de assinatura já encerrados e ainda não pagos entram num pote e são partilhados por minutos de duração entre os profissionais que completaram essas visitas.',
            periodUsage: 'No dia de pagamento da loja, cada período de faturação encerrado e ainda não pago é pago à parte. Cada profissional recebe a parte do pote daquele período correspondente às suas visitas. Se uma visita incluir serviços de mais do que um profissional, a visita é dividida pelo preço de lista original (por exemplo Corte €10 + Barba €20 num pote de €45 paga €15 e €30). Visitas não usadas em planos limitados ficam na loja.',
          },
          distributionModelSwitchWarning: 'Entra em vigor no próximo dia de pagamento ({{date}}). A receita de assinaturas encerrada e ainda não paga será dividida com o modelo escolhido nesse dia. Valores já pagos não são revertidos.',
          pendingUndistributed: '{{amount}} aguarda pagamento em {{date}} com o modelo selecionado.',
          error: {
            loading: 'Falha ao carregar definições de receita de assinaturas.',
            save: 'Falha ao guardar definições de receita de assinaturas.',
            marginRange: 'A margem da loja deve estar entre 0 e 100.',
          },
          success: {
            saved: 'Definições de receita de assinaturas guardadas com sucesso.',
          },
        },
        addNew: 'Adicionar Plano',
        noPlans: 'Nenhum plano de assinatura disponível',
        emptyHint: 'Crie o seu primeiro plano para oferecer assinaturas recorrentes aos clientes.',
        card: {
          unnamed: 'Plano de assinatura',
          price: 'Preço',
          perMonth: '/mês',
          includedServices: 'Serviços incluídos',
          subscribers: 'Assinantes',
          customerCancellation: 'Cliente pode cancelar'
        },
        actions: {
          edit: 'Editar',
          activate: 'Ativar',
          deactivate: 'Desativar'
        },
        fields: {
          name: 'Nome do Plano',
          price: 'Preço Mensal',
          billingInterval: 'Intervalo de Cobrança',
          entitlementType: 'Benefício',
          allowanceQuantity: 'Usos por mês',
          commitmentMonths: 'Compromisso mínimo (meses)',
          commitmentMonthsPlaceholder: 'Opcional',
          commitmentMonthsHelp: 'Deixe vazio se os clientes puderem cancelar a qualquer momento após o primeiro período de cobrança.',
          allowCustomerCancellation: 'Permitir cancelamento pelo cliente',
          allowCustomerCancellationHelp: 'Quando ativado, os clientes podem cancelar a assinatura no portal do cliente.',
          includedServices: 'Serviços Incluídos',
          noServicesAvailable: 'Não há serviços ativos disponíveis. Crie serviços antes de adicionar um plano.',
          includedProfessionals: 'Profissionais (opcional)',
          includedProfessionalsHelp: 'Deixe todos desmarcados para permitir todos os profissionais. Marque profissionais específicos para restringir este plano a esses membros.',
          noProfessionalsAvailable: 'Nenhum profissional ativo encontrado.'
        },
        billingInterval: {
          monthly: 'Mensal'
        },
        entitlement: {
          unlimited: 'Uso ilimitado',
          fixedAllowance: 'Limite fixo por mês',
          allowance: '{{count}} usos por mês'
        },
        status: {
          active: 'Ativo',
          inactive: 'Inativo'
        },
        dialog: {
          createTitle: 'Adicionar Plano de Assinatura',
          editTitle: 'Editar Plano de Assinatura',
          saveCreate: 'Criar Plano',
          saveEdit: 'Salvar Alterações'
        },
        deactivate: {
          title: 'Desativar Plano de Assinatura',
          message: 'Tem certeza de que deseja desativar "{{name}}"? Os assinantes atuais manterão o plano até cancelarem. Novos clientes não poderão assinar.',
          confirm: 'Desativar',
          cancel: 'Cancelar'
        },
        activate: {
          title: 'Ativar Plano de Assinatura',
          message: 'Tem certeza de que deseja ativar "{{name}}"? Os clientes poderão assinar este plano novamente.',
          confirm: 'Ativar',
          cancel: 'Cancelar'
        },
        feedback: {
          created: 'Plano de assinatura criado com sucesso!',
          updated: 'Plano de assinatura atualizado com sucesso!',
          activated: 'Plano de assinatura ativado com sucesso!',
          deactivated: 'Plano de assinatura desativado com sucesso!'
        },
        validation: {
          nameRequired: 'O nome do plano é obrigatório em todos os idiomas suportados',
          priceRequired: 'O preço é obrigatório e deve ser maior que 0',
          allowanceRequired: 'A quantidade de usos é obrigatória e deve ser maior que 0',
          commitmentRequired: 'Os meses de compromisso devem ser maiores que 0 quando especificados',
          servicesRequired: 'Selecione pelo menos um serviço incluído'
        },
        error: {
          loading: 'Falha ao carregar planos de assinatura',
          create: 'Falha ao criar plano de assinatura',
          update: 'Falha ao atualizar plano de assinatura',
          activate: 'Falha ao ativar plano de assinatura',
          deactivate: 'Falha ao desativar plano de assinatura',
          billingNotReady: 'A faturação de assinaturas ainda não está pronta. Conclua a configuração do Stripe na página Detalhes de pagamento e tente novamente.',
          noStoreSelected: 'Nenhuma loja selecionada. Selecione uma loja primeiro.'
        },
        paymentModeAdjust: {
          title: 'Alterar modo de pagamento de assinaturas',
          message: 'Esta loja está configurada apenas para pagamentos online de assinaturas, mas o Stripe ainda não está configurado. Para criar este plano com pagamentos na loja, o modo de pagamento de assinaturas será alterado de Apenas online para Ambos.',
          confirm: 'Continuar e criar plano',
          error: 'Falha ao atualizar as configurações de pagamento. Tente novamente.',
        },
        fallback: {
          message: 'Selecione uma loja no menu superior para gerenciar planos de assinatura.',
          goToStores: 'Ir para Lojas'
        }
      },
      permissions: {
        infoAriaLabel: 'Detalhes da permissão',
      },
      roles: {
        title: 'Funções e Permissões',
        subtitle: 'Gerencie funções e permissões da sua loja',
        addNew: 'Adicionar Nova Função',
        noRoles: 'Nenhuma função encontrada',
        searchPlaceholder: 'Pesquisar funções...',
        actions: {
          edit: 'Editar',
          delete: 'Excluir',
          view: 'Visualizar'
        },
        card: {
          permissions: 'Permissões',
          permissionCount: '{count} permissão{plural}',
          teamMembers: 'Membros da Equipa',
          teamMemberCount: '{count} membro{plural}'
        },
        modal: {
          create: {
            title: 'Criar Nova Função',
            subtitle: 'Defina uma nova função com permissões específicas'
          },
          edit: {
            title: 'Editar Função',
            subtitle: 'Atualize o nome e permissões da função'
          },
          form: {
            name: 'Nome da Função',
            nameRequired: 'O nome da função é obrigatório para todos os idiomas suportados',
            permissions: 'Permissões',
            permissionsRequired: 'Pelo menos uma permissão deve ser selecionada',
            selectAll: 'Selecionar Todas',
            deselectAll: 'Desselecionar Todas',
            categories: {
              teamMembers: 'Membros da Equipa',
              stores: 'Lojas',
              services: 'Serviços',
              appointments: 'Marcações',
              memberships: 'Assinaturas',
              customers: 'Clientes',
              products: 'Produtos',
              stock: 'Stock',
              expenses: 'Despesas',
              employeePayments: 'Pagamentos de Funcionários',
              chat: 'Chat',
              other: 'Outros'
            }
          },
          save: 'Salvar Função',
          saving: 'Salvando...',
          cancel: 'Cancelar',
          success: {
            created: 'Função criada com sucesso!',
            updated: 'Função atualizada com sucesso!'
          },
          error: {
            create: 'Falha ao criar função',
            update: 'Falha ao atualizar função',
            delete: 'Falha ao excluir função',
            inUse: 'Não é possível excluir função que está atualmente atribuída a membros da equipa'
          }
        },
        delete: {
          title: 'Excluir Função',
          message: 'Tem certeza de que deseja excluir "{{name}}"? Esta ação não pode ser desfeita.',
          confirm: 'Excluir',
          cancel: 'Cancelar',
          inUse: 'Esta função está atualmente atribuída a membros da equipa e não pode ser excluída.'
        },
        error: {
          loading: 'Falha ao carregar funções',
          noStoreSelected: 'Nenhuma loja selecionada. Por favor, selecione uma loja primeiro.'
        }
      },
      storeUpdate: {
        title: 'Atualizar Loja',
        subtitle: 'Modifique as informações e configurações da loja',
        storeInfo: 'Informações da Loja',
        editSection: 'Editar Informações da Loja',
        form: {
          name: 'Nome da Loja',
          status: 'Status da Loja'
        },
        enabled: 'Habilitado',
        disabled: 'Desabilitado',
        enabledDescription: 'Loja está ativa e visível para clientes',
        disabledDescription: 'Loja está inativa e oculta dos clientes',
        success: 'Loja atualizada com sucesso',
        slugChangeTitle: 'Alterar o URL da loja?',
        slugChangeMessage: 'O link antigo dos clientes vai deixar de funcionar.',
        slugChangeConfirm: 'Alterar URL',
        discardChanges: 'Descartar Alterações',
        discardSuccess: 'Alterações descartadas. Formulário restaurado aos dados originais.',
        discardError: 'Falha ao descartar alterações.',
        error: {
          loading: 'Falha ao carregar informações da loja',
          saving: 'Falha ao atualizar loja'
        }
      },
      customer: {
        navigation: {
          home: 'Início',
          appointments: 'Agendamentos',
          memberships: 'Assinaturas',
          profile: 'Perfil',
          settings: 'Configurações'
        },
        memberships: {
          title: 'Assinaturas',
          subtitle: 'Assine um plano recorrente e aproveite serviços incluídos todos os meses.',
          card: {
            unnamed: 'Plano de assinatura',
            perMonth: '/mês',
            commitment: 'Compromisso mínimo: {{count}} meses',
            pendingCheckout: 'Conclua o checkout para ativar esta assinatura.',
            cancelsAtPeriodEnd: 'Pode continuar a usar esta assinatura até {{date}}. Depois dessa data, será cancelada.'
          },
          entitlement: {
            unlimited: 'Uso ilimitado',
            allowance: '{{count}} usos por mês'
          },
          usage: {
            unlimited: 'Usos ilimitados restantes neste período',
            remaining: '{{remaining}} de {{total}} usos restantes neste período'
          },
          actions: {
            subscribe: 'Assinar',
            subscribing: 'Redirecionando...',
            continueCheckout: 'Continuar checkout',
            bookAppointment: 'Agendar',
            bookWithMembership: 'Agendar com assinatura',
            cancel: 'Cancelar assinatura',
            canceling: 'Cancelando...',
            pause: 'Pausar assinatura',
            pausing: 'Pausando...',
            resume: 'Retomar assinatura',
            resuming: 'Retomando...',
            resumeCancellation: 'Manter assinatura',
            resumingCancellation: 'Atualizando...',
            manage: 'Gerir'
          },
          cancel: {
            confirmTitle: 'Cancelar assinatura',
            confirmMessage: 'Pode continuar a usar esta assinatura até {{date}} — o último período já pago. Depois dessa data, será cancelada. Deseja continuar?',
            success: 'A assinatura permanece ativa até {{date}}. Depois dessa data, será cancelada.',
            error: 'Falha ao cancelar assinatura. Tente novamente.'
          },
          pause: {
            confirmTitle: 'Pausar assinatura',
            confirmMessage: 'A cobrança será interrompida enquanto a assinatura estiver pausada. Você pode retomá-la a qualquer momento.',
            success: 'Sua assinatura foi pausada.',
            error: 'Falha ao pausar assinatura. Tente novamente.'
          },
          resume: {
            success: 'Sua assinatura foi retomada.',
            error: 'Falha ao retomar assinatura. Tente novamente.'
          },
          resumeCancellation: {
            success: 'Sua assinatura continuará renovando.',
            error: 'Falha ao manter sua assinatura ativa. Tente novamente.'
          },
          status: {
            pendingCheckout: 'Checkout pendente',
            active: 'Ativa',
            trialing: 'Em teste',
            pastDue: 'Pagamento em atraso',
            paused: 'Pausada',
            canceled: 'Cancelada',
            expired: 'Expirada'
          },
          current: {
            title: 'Sua assinatura',
            pendingCheckout: 'Conclua o checkout para ativar sua assinatura.'
          },
          checkout: {
            success: 'Checkout da assinatura concluído. Sua assinatura aparecerá em breve.',
            processing: 'Estamos a confirmar o seu pagamento. A sua assinatura aparecerá aqui quando estiver concluído.',
            canceled: 'O checkout da assinatura foi cancelado.',
            error: 'Falha ao iniciar o checkout da assinatura. Tente novamente.',
            invalidUrl: 'A URL de checkout não foi retornada pelo servidor.'
          },
          empty: {
            title: 'Nenhum plano de assinatura disponível',
            subtitle: 'Esta loja ainda não publicou planos de assinatura.'
          },
          inStoreOnlyMessage: 'Visite a loja para se inscrever neste plano de assinatura.',
          bothModeHint: 'Pode subscrever online ou inscrever-se na loja com a equipa.',
          browseOnly: 'A inscrição em assinaturas é feita na loja. Veja os planos abaixo e inicie um agendamento — um membro da equipa irá inscrevê-lo e cobrar a mensalidade no salão.',
          error: {
            loading: 'Falha ao carregar planos de assinatura.'
          }
        },
        menu: {
          profile: 'Perfil',
          logout: 'Sair'
        },
        addToHomeScreen: {
          bannerTitle: 'Instalar como aplicação',
          subtitle: 'Adicione esta loja ao ecrã inicial',
          action: 'Instalar como aplicação',
          iosGuideTitle: 'Adicionar ao ecrã inicial',
          iosFallback: 'Toque em Partilhar no Safari e depois em Adicionar ao Ecrã Início.',
          androidUnavailable: 'Abra esta página no Chrome para instalar a aplicação.',
        },
        appointments: {
          title: 'Agendar',
          comingSoon: 'Funcionalidade de agendamento em breve!',
          bookNow: 'Reservar agora',
          myAppointments: {
            title: 'Meus Agendamentos',
            subtitle: 'Seus agendamentos mais recentes.',
            loadError: 'Falha ao carregar agendamentos.',
            emptyTitle: 'Você ainda não tem agendamentos.',
            emptySubtitle: 'Comece criando seu primeiro agendamento.',
            services: 'Serviços',
            professional: 'Profissional',
            unnamedService: 'Serviço sem nome',
            unnamedProfessional: 'Profissional desconhecido',
            amountDue: 'Valor a pagar',
            additionalCost: 'Custo adicional de horário',
            coveredByMembership: 'Coberto pela assinatura',
            pendingMembershipEnrollment: 'Assinatura a pagar no salão',
            membershipPlanNote: 'Incluído em {{plan}}',
            membershipPriceNote: 'Pague a assinatura {{plan}} no salão',
            payNow: 'Pagar agora',
            paying: 'A redirecionar...',
            cancel: 'Cancelar agendamento',
            cancelTitle: 'Cancelar agendamento?',
            cancelMessage: 'Este agendamento será cancelado e o horário ficará disponível novamente.',
            cancelConfirm: 'Cancelar agendamento',
            cancelSuccess: 'Agendamento cancelado com sucesso.',
            cancelError: 'Falha ao cancelar o agendamento. Tente novamente.',
            cancelTooLate: 'Não é possível cancelar agendamentos com menos de 2 horas de antecedência.',
            awaitingConfirmationNote: 'O salão ainda precisa de confirmar este horário adicional. Será notificado quando for aceite ou recusado.'
          },
          payment: {
            success: 'Pagamento concluído com sucesso.',
            canceled: 'Pagamento cancelado.',
            failed: 'Pagamento falhou! Clique em "Pagar agora" para tentar novamente.',
            failedNotBooked: 'O pagamento não foi concluído, por isso o agendamento não ficou reservado.',
            processing: 'Pagamento em processamento. Será atualizado em breve.',
            expired: 'Sessão de pagamento expirada! Clique em "Pagar agora" para tentar novamente.',
            checkoutError: 'Falha ao iniciar o pagamento. Tente novamente.'
          },
          status: {
            pending: 'Pendente',
            awaitingConfirmation: 'A aguardar confirmação',
            awaitingPayment: 'A aguardar pagamento',
            cancelled: 'Cancelado',
            confirmed: 'Confirmado',
            completed: 'Concluído',
            noShow: 'Não compareceu',
            unknown: 'Desconhecido'
          },
          booking: {
            stepServices: 'Serviços',
            stepProfessionals: 'Profissionais',
            stepCalendar: 'Data e Hora',
            stepReview: 'Rever e Confirmar',
            title: 'Continue a usar o Salovare após o período experimental',
            description: 'Adicione agora os seus dados de pagamento para continuar a usar o Salovare após o período experimental.',
            action: 'Adicionar dados de pagamento',
            openPortal: 'Adicionar dados de pagamento',
            opening: 'A abrir dados de pagamento seguros...',
            categoryAll: 'Todos',
            selectServices: 'Selecione os serviços',
            selectAtLeastOne: 'Selecione pelo menos um serviço',
            servicesSelected: '{{count}} selecionado(s)',
            next: 'Seguinte',
            back: 'Voltar',
            confirm: 'Confirmar',
            anyProfessional: 'Qualquer profissional',
            specificProfessional: 'Profissional específico',
            professionalPerService: 'Profissional por serviço',
            selectProfessional: 'Selecione o profissional',
            selectProfessionalForService: 'Selecione o profissional para {{service}}',
            professionalNotAble: 'Este profissional não consegue realizar um ou mais serviços selecionados.',
            specificProfessionalHelper: 'Use esta opção apenas quando um profissional conseguir realizar todos os serviços selecionados.',
            specificProfessionalUnavailable: 'Nenhum profissional consegue realizar todos os serviços selecionados neste momento. Use Profissional por serviço.',
            incompatibleProfessionalTitle: '{{name}} não consegue realizar todos os serviços selecionados',
            incompatibleProfessionalBody: '{{name}} não consegue realizar os seguintes serviços:',
            selectAnotherProfessional: 'Selecionar outro profissional',
            selectDifferentPerService: 'Profissional diferente por serviço',
            suggestion: 'Sugestão: {{service1}} com {{name1}} e {{service2}} com {{name2}}. Pode alterar abaixo.',
            suggestionSingle: 'Todos os serviços podem ser realizados por um único profissional.',
            selectDate: 'Selecione a data',
            selectTime: 'Selecione o horário',
            pickDate: 'Escolher data',
            morning: 'Manhã',
            afternoon: 'Tarde',
            evening: 'Noite',
            noSlots: 'Sem horários disponíveis para esta data.',
            chooseProfessional: 'Escolher profissional',
            multipleAvailable: 'Mais de um profissional está disponível. Quem prefere?',
            selectTimeForProfessional: 'Selecione o horário para {{professional}}',
            noSlotsForProfessional: 'Sem horários disponíveis para este profissional na data selecionada.',
            selectTimeMobileHint: 'Escolha um horário por profissional. Os serviços seguintes só são liberados após o fim do anterior.',
            professionalsReady: '{{selected}} de {{total}} profissionais com horário definido',
            selectedTimeLabel: 'Selecionado: {{time}}',
            availableFrom: 'A partir de {{time}}',
            selectPreviousTimeFirst: 'Selecione primeiro o horário anterior',
            requiresCompanionService: 'Selecione pelo menos um serviço principal ou secundário avulso para continuar com este agendamento.',
            selectCompanionProfessionalFirst: 'Selecione primeiro o profissional do serviço principal ou secundário avulso que acompanha este serviço.',
            companionProfessionalOnly: 'Somente os profissionais já selecionados para os serviços acompanhantes estão disponíveis para este serviço.',
            summary: 'Resumo',
            dateTime: 'Data e hora',
            services: 'Serviços',
            professional: 'Profissional',
            totalPrice: 'Total',
            paymentMethod: 'Pagamento',
            payInStore: 'Na loja',
            payOnline: 'Online',
            payWithMembershipUnlimited: 'Usar assinatura: {{plan}}',
            payWithMembershipAllowance: 'Usar assinatura: {{plan}} ({{remaining}} restantes)',
            membershipSuccess: 'Agendamento realizado com sua assinatura!',
            detailsPlaceholder: 'Detalhes adicionais (opcional)',
            success: 'Agendamento realizado com sucesso!',
            awaitingConfirmationSuccess: 'Pedido enviado. Vamos confirmar este horário adicional com o salão.',
            requiresConfirmationHint: 'Este horário está fora do horário regular. O salão precisa de confirmar antes de a marcação ficar reservada.',
            onRequestSlot: 'Sob pedido',
            additionalCost: 'Custo adicional de horário',
            products: 'Produtos',
            addProducts: 'Adicionar produtos',
            addProductsHint: 'Quer adicionar produtos a esta marcação?',
            noThanks: 'Não, obrigado',
            editProducts: 'Editar',
            noProductsSelected: 'Nenhum produto selecionado',
            productsTotal: 'Total de produtos',
            onRequestAnyProfessionalHint: 'Qualquer profissional disponível pode aceitar este pedido de horário adicional.',
            error: 'Falha ao agendar. Tente novamente.',
            paymentNotCompleted: 'O pagamento não foi concluído, por isso o agendamento não ficou reservado. Pode tentar novamente.',
            paymentReleaseFailed: 'O pagamento não foi concluído. Se este horário ainda aparecer em Meus Agendamentos, cancele-o para libertar o horário.',
            membershipPlans: 'Planos de assinatura',
            yourMemberships: 'As suas assinaturas',
            selectedMembershipPlan: 'Plano de assinatura selecionado',
            discoverMembershipPrompt: 'Interessado numa assinatura?',
            seeMembershipPlans: 'Ver planos',
            membershipPlanServices: '{{count}} serviço(s) incluído(s)',
            membershipPlanCovered: 'Coberto',
            membershipPlanActive: 'Assinatura ativa',
            membershipPlanNotEnrolled: 'Inscrever na loja',
            coveredByMembership: 'Coberto pela assinatura',
            dueInStore: 'A pagar na loja',
            membershipExtrasHint: 'Os serviços do plano permanecem selecionados. Pode adicionar serviços extra pagos.',
            enrollInStoreNote: 'Um membro da equipa irá inscrevê-lo neste plano na loja. Pague {{price}} no salão por este período de assinatura.',
            planHowItWorksUnlimited: 'Como funciona: pague a mensalidade e use os serviços incluídos as vezes que quiser em cada período de faturação.',
            planHowItWorksAllowance: 'Como funciona: pague a mensalidade e use os serviços incluídos até {{count}} vezes em cada período de faturação.',
            coveredServiceTitle: 'Serviço coberto pela assinatura',
            coveredServiceBody: '"{{service}}" está incluído na sua assinatura "{{plan}}". Gostaria de agendar através da assinatura ou pagar o preço regular?',
            goWithMembership: 'Usar assinatura',
            keepRegular: 'Preço regular',
            renewalRiskTitle: 'Lembrete de renovação de assinatura',
            renewalRiskBody: 'O seu período de assinatura atual termina a {{date}}. Se a assinatura não for renovada até à data do agendamento, o preço regular do serviço será aplicado. Pode continuar a agendar.',
            continueAnyway: 'Continuar',
            waitlist: {
              prompt: 'Não encontra o horário ideal?',
              cta: 'Entre na lista de espera',
              title: 'Entrar na lista de espera',
              subtitle: 'Escolha datas e horários preferidos. Contactamo-lo se abrir um horário correspondente.',
              join: 'Entrar na lista',
              addRow: 'Adicionar outra data',
              removeRow: 'Remover data',
              timePlaceholder: 'Selecione um horário',
              any: 'Qualquer',
              from: 'De',
              to: 'Até',
              success: 'Entrou na lista de espera. Avisamos se abrir um horário correspondente.',
              error: {
                preferences: 'Selecione pelo menos uma data e um horário.',
                services: 'Selecione serviços antes de entrar na lista de espera.',
                save: 'Não foi possível entrar na lista de espera. Tente novamente.',
              },
            },
          }
        },
        profile: {
          title: 'Meu Perfil',
          subtitle: 'Gerencie sua foto de perfil, número de telefone, idioma e conta.',
          name: 'Nome',
          comingSoon: 'Gerenciamento de perfil em breve!',
          phone: {
            label: 'Número de telefone',
            placeholder: 'Introduza o número de telefone',
            helper: 'Usado para lembretes de agendamento por SMS.',
            saveSuccess: 'Número de telefone guardado.',
            saveError: 'Falha ao guardar o número de telefone.',
            invalid: 'Introduza um número de telefone válido.'
          },
          picture: {
            upload: 'Carregar Foto',
            uploading: 'A carregar...',
            delete: 'Eliminar Foto',
            deleting: 'A eliminar...',
            uploadSuccess: 'Foto de perfil carregada com sucesso.',
            uploadError: 'Falha ao carregar foto de perfil.',
            deleteSuccess: 'Foto de perfil eliminada com sucesso.',
            deleteError: 'Falha ao eliminar foto de perfil.',
            confirmDelete: 'Tem a certeza de que deseja eliminar a sua foto de perfil?'
          }
        },
        settings: {
          title: 'Configurações',
          subtitle: 'Personalize suas preferências para esta loja.',
          comingSoon: 'Funcionalidade de configurações em breve!'
        },
        store: {
          notFound: 'Loja não encontrada',
          location: 'Localização',
          openingHours: 'Horário de Funcionamento',
          closed: 'Fechado',
          open: 'Aberto',
          openUntil: 'Aberto até',
          opensAt: 'Abre às',
          opensTomorrow: 'Abre amanhã',
          opensOn: 'Abre na',
          at: 'às',
          additionalHours: 'Horário adicional',
          additionalHoursUntil: 'Horário adicional até',
          additionalHoursFrom: 'Horário adicional a partir das',
          contact: 'Contato',
          getDirections: 'Obter Direções',
          noImages: 'Nenhuma imagem disponível',
          noServices: 'Nenhum serviço disponível',
          noTeamMembers: 'Nenhum membro da equipe disponível',
          noAbout: 'Nenhuma informação disponível',
          tabs: {
            overview: 'Visão Geral',
            location: 'Localização',
            images: 'Imagens',
            services: 'Serviços',
            products: 'Produtos',
            team: 'Equipe',
            about: 'Sobre'
          }
        },
        products: {
          title: 'Produtos',
          subtitle: 'Adicione produtos ao reservar.',
          addWhenBooking: 'Adicionar na reserva',
          outOfStock: 'Esgotado',
        }
      },
    },
  },
  es: {
    translation: {
      brand: 'Salovare',
      language: 'Idioma',
      languages: {
        en: 'English',
        pt: 'Português',
        es: 'Español'
      },
      login: {
        email: 'Correo electrónico',
        password: 'Contraseña',
        signIn: 'Iniciar sesión',
        signingIn: 'Iniciando...',
        required: 'Correo y contraseña son obligatorios',
        invalid: 'Credenciales inválidas',
        serverError: 'No se pudo conectar al servidor. Verifique su conexión e intente nuevamente.',
        adminQuestion: 'Admin?',
        teamMemberQuestion: '¿Miembro del Equipo?',
        or: 'O',
        noAccount: '¿No tienes una cuenta? Regístrate',
        googleFailed: 'Error al iniciar sesión con Google. Inténtalo de nuevo.',
        registerStorePrompt: '¿Aún no tienes cuenta?',
        registerStoreCta: 'Empezar',
        storeRegistrationSuccess: 'Tienda creada. Revisa tu correo para establecer tu contraseña y completar la configuración de tu cuenta.',
        backToLogin: 'Volver al Login',
        continue: 'Continuar',
        continuing: 'Continuando...',
        emailRequired: 'El correo es obligatorio',
        emailInvalid: 'Introduce una dirección de correo válida',
        emailCheckFailed: 'No se pudo verificar este correo ahora. Inténtalo de nuevo.',
        passwordRequired: 'La contraseña es obligatoria',
        changeEmail: 'Usar otro correo',
        setPasswordPrompt: 'Tu cuenta existe, pero aún no has establecido una contraseña. Podemos enviarte un enlace por correo para crear una.',
        sendSetPasswordLink: 'Enviar enlace para establecer contraseña',
        phoneOnlyCta: '¿Registrado solo con teléfono?',
        claim: {
          phone: 'Número de teléfono',
          phoneRequired: 'El número de teléfono es obligatorio',
          sendCode: 'Enviar código de verificación',
          sendingCode: 'Enviando código...',
          codeSent: 'Si este teléfono está registrado sin email, se ha enviado un código de verificación.',
          code: 'Código de verificación',
          codeRequired: 'Introduce el código de verificación',
          verify: 'Verificar código',
          verifying: 'Verificando...',
          complete: 'Enviar enlace para establecer contraseña',
          completing: 'Enviando...',
          success: 'Email para establecer contraseña enviado. Revisa tu bandeja de entrada.',
          back: 'Volver al inicio de sesión con email',
          error: 'No se pudo continuar. Inténtalo de nuevo.',
        },
      },
      googleAuth: {
        continueWithGoogle: 'Continuar con Google',
        continueWithGoogleSignUp: 'Registrarse con Google',
      },
      forgotPassword: {
        forgotPassword: '¿Olvidaste tu contraseña? ¡Vamos a arreglarlo!',
        emailRequired: 'Ingresa tu email primero para restablecer tu contraseña',
        sending: 'Enviando...',
        success: '¡Enlace de restablecimiento enviado! Revisa tu correo.',
        error: 'Ocurrió un error. Inténtalo de nuevo.',
      },
      resetPassword: {
        title: 'Restablecer Tu Contraseña',
        email: 'Correo Electrónico',
        newPassword: 'Nueva Contraseña',
        confirmPassword: 'Confirmar Contraseña',
        resetPassword: 'Restablecer Contraseña',
        resetting: 'Restableciendo...',
        backToLogin: 'Volver al Login',
        missingToken: 'Token de restablecimiento ausente',
        missingEmail: 'Dirección de correo ausente',
        required: 'Contraseña y confirmación son obligatorias',
        passwordMismatch: 'Las contraseñas no coinciden',
        passwordTooShort: 'La contraseña debe tener al menos 8 caracteres',
        passwordRequireDigit: 'La contraseña debe contener al menos un dígito',
        passwordRequireLowercase: 'La contraseña debe contener al menos una letra minúscula',
        passwordRequireUppercase: 'La contraseña debe contener al menos una letra mayúscula',
        passwordRequireNonAlphanumeric: 'La contraseña debe contener al menos un carácter especial',
        passwordRequirements: 'La contraseña debe tener al menos 8 caracteres y contener mayúscula, minúscula, dígito y carácter especial',
        failed: 'Falló el restablecimiento de la contraseña. Inténtalo de nuevo.',
        success: '¡Contraseña restablecida exitosamente!',
        loginRedirect: '¡Contraseña restablecida exitosamente! Inicia sesión con tu nueva contraseña.',
      },
      setPassword: {
        title: 'Establece Tu Contraseña',
        email: 'Correo Electrónico',
        newPassword: 'Nueva Contraseña',
        confirmPassword: 'Confirmar Contraseña',
        setPassword: 'Establecer Contraseña',
        setting: 'Estableciendo...',
        backToLogin: 'Volver al Login',
        missingToken: 'Token de establecimiento de contraseña ausente',
        missingEmail: 'Dirección de correo ausente',
        required: 'Contraseña y confirmación son obligatorias',
        passwordMismatch: 'Las contraseñas no coinciden',
        passwordTooShort: 'La contraseña debe tener al menos 8 caracteres',
        passwordRequireDigit: 'La contraseña debe contener al menos un dígito',
        passwordRequireLowercase: 'La contraseña debe contener al menos una letra minúscula',
        passwordRequireUppercase: 'La contraseña debe contener al menos una letra mayúscula',
        passwordRequireNonAlphanumeric: 'La contraseña debe contener al menos un carácter especial',
        passwordRequirements: 'La contraseña debe tener al menos 8 caracteres y contener mayúscula, minúscula, dígito y carácter especial',
        failed: 'Falló el establecimiento de la contraseña. Inténtalo de nuevo.',
        success: '¡Contraseña establecida exitosamente!',
        loginRedirect: '¡Contraseña establecida exitosamente! Inicia sesión con tu nueva contraseña.',
      },
      firstAccess: {
        firstAccess: '¿Primer acceso? ¡Establece tu contraseña!',
        emailRequired: 'Ingresa tu email primero para establecer tu contraseña',
        sending: 'Enviando...',
        success: '¡Enlace de establecimiento de contraseña enviado! Revisa tu correo.',
        error: 'Ocurrió un error. Inténtalo de nuevo.',
      },
      register: {
        title: 'Crear Cuenta',
        name: 'Nombre Completo',
        email: 'Email',
        password: 'Contraseña',
        confirmPassword: 'Confirmar Contraseña',
        create: 'Crear Cuenta',
        creating: 'Creando Cuenta...',
        haveAccount: '¿Ya tienes una cuenta? Inicia sesión',
        required: 'Todos los campos son obligatorios',
        passwordMismatch: 'Las contraseñas no coinciden',
        passwordTooShort: 'La contraseña debe tener al menos 6 caracteres',
        failed: 'Error en el registro. Inténtalo de nuevo.',
        success: '¡Cuenta creada exitosamente! Redirigiendo...',
      },
      common: {
        cancel: 'Cancelar',
        close: 'Cerrar',
        back: 'Atrás',
        next: 'Siguiente',
        continue: 'Continuar',
        submit: 'Enviar',
        submitting: 'Enviando...',
        loading: 'Cargando',
        language: 'Idioma',
        navigation: 'Navegación',
        notifications: 'Notificaciones',
        closed: 'Cerrado',
        error: 'Error',
        unauthorized: 'No tienes permiso para ver esta página.',
        noStores: 'No hay tiendas disponibles',
        retry: 'Reintentar',
        refresh: 'Actualizar',
        pullToReload: 'Desliza para actualizar',
        releaseToReload: 'Suelta para actualizar',
        reloadingApp: 'Actualizando…',
        saving: 'Guardando...',
        save: 'Guardar',
        delete: 'Eliminar',
        confirm: 'Confirmar',
        filters: 'Filtros',
        creating: 'Creando...',
        optional: 'Opcional',
        selected: 'Seleccionado',
        notProvided: 'No proporcionado',
        viewImage: 'Ver imagen',
        active: 'Activo',
        inactive: 'Inactivo',
        seeMore: 'Ver Más',
        seeLess: 'Ver Menos',
        previousTabs: 'Pestañas anteriores',
        nextTabs: 'Pestañas siguientes',
        sending: 'Enviando...',
        resend: 'Reenviar enlace',
        resendIn: 'Reenviar enlace en {{seconds}}s',
        resendError: 'No se pudo reenviar el enlace. Inténtalo de nuevo.',
        backToLogin: 'Volver al Inicio de Sesión',
        languages: {
          pt: 'Portugués',
          en: 'Inglés',
          es: 'Español'
        },
        datePicker: {
          ok: 'OK',
          cancel: 'Cancelar',
          clear: 'Limpiar',
          today: 'Hoy',
          previousMonth: 'Mes anterior',
          nextMonth: 'Próximo mes',
          toolbarTitle: 'Seleccionar Fecha',
          openDatePickerDialogue: 'Elegir fecha, fecha seleccionada es {{value}}',
          openDatePickerDialogueEmpty: 'Elegir fecha',
          closeDatePickerDialogue: 'Cerrar selector de fecha'
        },
        dateRange: {
          duration: 'Duración',
          from: 'Desde',
          to: 'Hasta',
          endOptional: 'Deje la fecha de fin vacía para sin fecha de fin.',
        },
        yes: 'Sí',
        no: 'No',
        actions: 'Acciones',
        moreOptions: 'Más Opciones',
        contactSupport: 'Soporte de la app',
        contactStoreChat: 'Hablar con la tienda',
        contactSupportSubject: 'Soporte Salovare',
        contactSupportHint: 'Escríbenos a {{email}} si necesitas ayuda.',
        appVersion: 'Versión {{version}} · {{slot}}',
        rowsPerPage: 'Filas por página:',
        of: 'de',
        all: 'Todos',
        apply: 'Aplicar',
        clearAll: 'Limpiar Todo',
        noFiltersAvailable: 'No hay filtros disponibles',
        days: {
          monday: 'Lunes',
          tuesday: 'Martes',
          wednesday: 'Miércoles',
          thursday: 'Jueves',
          friday: 'Viernes',
          saturday: 'Sábado',
          sunday: 'Domingo'
        },
        minutes: 'min',
        hours: 'h'
      },
      supportChat: {
        title: 'Soporte de la app',
        storeTitle: 'Chat con la tienda',
        placeholder: 'Escribe un mensaje',
        send: 'Enviar',
        guestEmail: 'Tu correo',
        guestEmailHint: 'Necesitamos un correo para responderte.',
        guestEmailRequired: 'Introduce tu correo para iniciar el chat.',
        waiting: 'Recibimos tu mensaje y responderemos aquí.',
        closed: 'Este chat está cerrado. Envía un nuevo mensaje para reabrirlo.',
        closedPlaceholder: 'Envía un mensaje para reabrir este chat',
        empty: 'Cuéntanos en qué necesitas ayuda. Respondemos aquí en este chat.',
        storeEmpty: 'Pregunta a la tienda lo que necesites. Suelen responder aquí en este chat.',
        you: 'Tú',
        support: 'Soporte',
        store: 'Tienda',
        supportTyping: 'Soporte está escribiendo',
        storeTyping: '{{name}} está escribiendo',
        visitorTyping: '{{name}} está escribiendo',
        typing: 'Escribiendo…',
        sendFailed: 'No se pudo enviar el mensaje. Inténtalo de nuevo.',
        loadFailed: 'No se pudieron cargar los chats de soporte.',
        inbox: 'Soporte de la app',
        inboxSubtitle: 'Chats pendientes y activos del equipo de las tiendas.',
        storeInbox: 'Chat con clientes',
        storeInboxSubtitle: 'Chats pendientes y activos de tus clientes.',
        noConversations: 'Todavía no hay chats del equipo.',
        storeNoConversations: 'Todavía no hay chats de clientes.',
        selectConversation: 'Selecciona una conversación para responder.',
        closeConversation: 'Cerrar chat',
        relatedAppointment: 'Cita {{date}} a las {{time}}',
        status: {
          Waiting: 'En espera',
          Open: 'Abierto',
          Closed: 'Cerrado',
        },
      },
      notFound: {
        title: 'Página No Encontrada',
        message: 'La página que buscas no existe o el enlace de la tienda es inválido.',
        suggestion: 'Para hacer una reserva, por favor:',
        contactStore: 'Contacta directamente con la tienda para obtener el enlace de reserva correcto.',
        goToHome: 'Ir a la página principal',
        redirectCountdown: 'Serás redirigido a la página principal en {{seconds}} segundos.',
        storeTitle: 'Tienda no encontrada',
        storeMessage: 'La tienda que buscas puede haber cambiado el enlace.',
        storeContact: 'Contacta con la tienda para obtener el enlace de reserva.',
      },
      shortLink: {
        redirecting: 'Redirigiendo…',
      },
      navigation: {
        dashboard: 'Panel',
        appointments: 'Reservas',
        waitlist: 'Lista de espera',
        services: 'Servicios',
        products: 'Productos',
        membershipPlans: 'Planes de Suscripción',
        customerMemberships: 'Membresías de Clientes',
        customers: 'Clientes',
        teamMembers: 'Miembros del Equipo',
        profile: 'Perfil',
        store: 'Tienda',
        stores: 'Tiendas',
        billing: 'Facturación',
        importData: 'Importar datos',
        communication: 'Comunicación',
        paymentOperations: 'Operaciones de Pago',
        storesManagement: 'Gestión de Tiendas',
        support: 'Soporte',
        appSupport: 'Soporte de la app',
        storeChat: 'Chat con clientes',
        planDiscountVouchers: 'Cupones de Descuento del Plan',
        employeePayroll: 'Nómina',
        employeePaymentSettings: 'Pagos de Empleados',
        storeRegistration: 'Registrar Tienda',
        settings: 'Configuración',
        logout: 'Cerrar Sesión'
      },
      management: {
        addToHomeScreen: {
          bannerTitle: 'Añadir Salovare a la pantalla de inicio',
          subtitle: 'Abre esta tienda más rápido desde el teléfono',
          action: 'Instalar como app',
          iosGuideTitle: 'Añadir a la pantalla de inicio',
          iosFallback: 'Toca Compartir en Safari y luego Añadir a pantalla de inicio.',
          androidUnavailable: 'Abre esta página en Chrome para instalar la app.',
        },
      },
      dataImport: {
        title: 'Importar datos de Fresha',
        subtitle: 'Importa en este orden: clientes, servicios, miembros del equipo y luego citas.',
        languageNotice: 'Las exportaciones de Fresha pueden usar encabezados en portugués o inglés. Ambos formatos son compatibles.',
        backToCustomers: 'Volver a clientes',
        errors: {
          loadProgress: 'No se pudo cargar el progreso de importación.',
        },
        steps: {
          customers: 'Clientes',
          teamMembers: 'Miembros del equipo',
          services: 'Servicios',
          appointments: 'Citas',
        },
        comingSoon: {
          step: 'La importación de {{step}} estará disponible en el siguiente paso.',
        },
        customers: {
          helper: {
            title: 'Cómo exportar clientes desde Fresha',
            intro: 'Genera el CSV en Fresha y luego súbelo aquí.',
            step1: 'Abre la lista de clientes de Fresha:',
            openFresha: 'partners.fresha.com/clients/list',
            step2: 'Haz clic en Opciones → Exportar: CSV.',
            step3: 'Sube el archivo CSV descargado abajo.',
          },
          uploadButton: 'Subir CSV de Fresha',
          selectedFile: 'Archivo seleccionado: {{fileName}}',
          summary: '{{total}} filas · {{ready}} listas · {{failed}} con error · {{warnings}} avisos',
          failedTitle: 'Filas que necesitan corrección',
          moreFailures: '+{{count}} filas con error',
          fixFailingRows: 'Corregir filas con error',
          downloadFailingRows: 'Descargar filas con error',
          importReadyRows: 'Importar filas válidas',
          importReadyConfirmTitle: '¿Importar solo las filas válidas?',
          importReadyConfirmMessage: 'Las {{failed}} fila(s) que aún necesitan corrección se ignorarán. Solo se importarán las {{ready}} fila(s) válidas.',
          importReadyConfirmAction: 'Importar filas válidas',
          warningsNotice: '{{count}} aviso(s) en campos opcionales. Nombre y email son válidos, así que esas filas aún se pueden importar.',
          importButton: 'Importar clientes',
          importing: 'Importando...',
          alreadyCompleted: 'Los clientes ya se importaron para esta tienda. Puedes continuar con Servicios.',
          importSuccess: 'Importación finalizada: {{created}} creados, {{assigned}} asignados, {{alreadyInStore}} ya en la tienda.',
          fields: {
            name: 'Nombre',
            email: 'Email',
            phone: 'Teléfono',
            countryCode: 'Código de país',
            genre: 'Género',
            birthDate: 'Fecha de nacimiento',
          },
          genre: {
            male: 'Masculino',
            female: 'Femenino',
          },
          errors: {
            emptyFile: 'El archivo CSV está vacío.',
            parseFailed: 'No se pudo leer el archivo CSV.',
            importFailed: 'La importación de clientes falló.',
            nameRequired: 'El nombre es obligatorio',
            emailRequired: 'El email es obligatorio',
            emailInvalid: 'El email no es válido',
            contactRequired: 'El email o el teléfono es obligatorio',
          },
          warnings: {
            invalidPhone: 'No se pudo importar el teléfono',
            invalidGenre: 'No se pudo importar el género',
            invalidBirthDate: 'No se pudo importar la fecha de nacimiento',
          },
          fixDialog: {
            title: 'Corregir filas con error',
            selectRow: 'Fila con error',
            rowLabel: 'Fila {{row}} · {{name}}',
            rawRow: 'Fila CSV completa',
            originalValue: 'Valor original: {{value}}',
            apply: 'Aplicar corrección',
            noFailures: 'Ya no hay filas con error.',
            requiredSection: 'Campos obligatorios',
            requiredSectionHelp: 'Estos campos deben corregirse antes de importar esta fila.',
            optionalSection: 'Campos opcionales',
            optionalSectionHelp: 'Puedes corregirlos ahora o dejarlos vacíos. Los valores inválidos solo generan avisos.',
          },
          mergeDialog: {
            title: 'Resolver clientes duplicados',
            description: 'Algunas filas comparten el mismo nombre, email o teléfono. En cada coincidencia, elige si es una sola cuenta o dos cuentas distintas.',
            selectGroup: 'Ir al grupo',
            groupProgress: 'Grupo de duplicados {{current}} de {{total}}',
            groupOption: '{{current}}/{{total}} · {{label}}',
            matches: 'Filas coincidentes',
            matchesHelp: 'Compara los detalles completos de cada fila coincidente abajo.',
            accountQuestion: '¿Es una sola cuenta o dos cuentas distintas?',
            merge: 'Una sola cuenta',
            keepSeparate: 'Dos cuentas distintas',
            emailMustMerge: 'Las filas con el mismo email deben ser una sola cuenta, porque el email es único.',
            primaryRow: 'Conservar datos de',
            primaryRowHelp: 'Selecciona la fila cuyos datos deben conservarse en la cuenta de cliente unida.',
            rowTitle: 'Fila #{{row}}',
            freshaClientId: 'ID de cliente Fresha',
            confirm: 'Continuar importación',
            autoResolve: 'Resolver todos los duplicados automáticamente',
            autoResolveHint: 'Omite la revisión grupo por grupo y aplica las reglas recomendadas automáticamente.',
            autoResolveDialog: {
              title: '¿Resolver duplicados automáticamente?',
              intro: 'Esto terminará la importación sin pedirte que revises cada duplicado uno por uno.',
              willHappen: 'Esto es lo que ocurrirá cuando la misma persona aparezca más de una vez en el archivo:',
              sameEmailTitle: 'Mismo email:',
              sameEmailBody: 'Se unirán en un solo cliente. Conservaremos los datos de la primera fila que aparece en el archivo.',
              sameNamePhoneTitle: 'Mismo nombre y mismo teléfono:',
              sameNamePhoneBody: 'También se unirán en un solo cliente, usando los datos de la primera fila coincidente.',
              sameNameOnlyTitle: 'Mismo nombre, pero teléfono diferente (o ausente):',
              sameNameOnlyBody: 'Se mantendrán como clientes separados, porque pueden ser personas distintas que simplemente comparten el nombre.',
              samePhoneOnlyTitle: 'Mismo teléfono, pero nombre diferente:',
              samePhoneOnlyBody: 'Se mantendrán como clientes separados. Dos personas pueden compartir el mismo número de teléfono.',
              footer: 'Si prefieres elegir manualmente cada duplicado, cancela y sigue revisando grupo por grupo.',
              confirm: 'Resolver e importar',
            },
            groupTypes: {
              email: 'Mismo email ({{key}}) · {{count}} filas',
              name: 'Mismo nombre ({{key}}) · {{count}} filas',
              phone: 'Mismo teléfono ({{key}}) · {{count}} filas',
              email_name: 'Mismo email y nombre ({{key}}) · {{count}} filas',
              email_phone: 'Mismo email y teléfono ({{key}}) · {{count}} filas',
              name_phone: 'Mismo nombre y teléfono ({{key}}) · {{count}} filas',
              email_name_phone: 'Mismo email, nombre y teléfono ({{key}}) · {{count}} filas',
            },
          },
        },
        services: {
          helper: {
            title: 'Cómo exportar servicios desde Fresha',
            intro: 'Genera el CSV de la lista de servicios en Fresha y luego súbelo aquí.',
            step1: 'Abre la lista de servicios de Fresha:',
            openFresha: 'partners.fresha.com/setup/services',
            step2: 'Haz clic en Opciones → Exportar: CSV.',
            step3: 'Sube el archivo CSV descargado abajo.',
          },
          uploadButton: 'Subir CSV de Fresha',
          selectedFile: 'Archivo seleccionado: {{fileName}}',
          summary: '{{total}} filas · {{ready}} listas · {{failed}} con error · {{warnings}} avisos · {{categories}} categorías',
          failedTitle: 'Filas que necesitan corrección',
          moreFailures: '+{{count}} filas con error',
          fixFailingRows: 'Corregir filas con error',
          downloadFailingRows: 'Descargar filas con error',
          importReadyRows: 'Importar filas válidas',
          importReadyConfirmTitle: '¿Importar solo las filas válidas?',
          importReadyConfirmMessage: 'Las {{failed}} fila(s) que aún necesitan corrección se ignorarán. Solo se importarán las {{ready}} fila(s) válidas.',
          importReadyConfirmAction: 'Importar filas válidas',
          warningsNotice: '{{count}} aviso(s) en campos opcionales. Los campos obligatorios son válidos, así que esas filas aún se pueden importar.',
          importButton: 'Importar servicios',
          importing: 'Importando...',
          alreadyCompleted: 'Los servicios ya se importaron para esta tienda. Puedes continuar con Miembros del equipo.',
          importSuccess: 'Importación finalizada: {{created}} servicios creados, {{updated}} actualizados, {{categoriesCreated}} categorías creadas, {{categoriesUpdated}} categorías actualizadas.',
          fields: {
            serviceId: 'ID del servicio',
            name: 'Nombre',
            duration: 'Duración (minutos)',
            price: 'Precio',
            description: 'Descripción',
            disabled: 'Desactivado para reserva online',
          },
          errors: {
            emptyFile: 'El archivo CSV está vacío.',
            parseFailed: 'No se pudo leer el archivo CSV.',
            importFailed: 'La importación de servicios falló.',
            serviceIdRequired: 'El ID del servicio es obligatorio',
            nameRequired: 'El nombre es obligatorio',
            durationRequired: 'La duración es obligatoria',
            durationInvalid: 'La duración no es válida',
          },
          warnings: {
            missingPrice: 'Faltaba el precio y se estableció en 0',
            invalidPrice: 'No se pudo interpretar el precio y se estableció en 0',
          },
          fixDialog: {
            title: 'Corregir filas con error',
            rowLabel: 'Fila {{row}} · {{name}}',
            apply: 'Aplicar corrección',
            noFailures: 'Ya no hay filas con error.',
          },
        },
        teamMembers: {
          helper: {
            title: 'Cómo exportar miembros del equipo desde Fresha',
            intro: 'Genera el CSV de empleados en Fresha (encabezados en inglés) y cárgalo aquí.',
            step1: 'Abre la lista de miembros del equipo de Fresha:',
            openFresha: 'partners.fresha.com/team/members',
            step2: 'Haz clic en Opciones → Exportar: CSV.',
            step3: 'Carga el archivo CSV descargado debajo.',
            englishOnly: 'Este paso actualmente solo admite encabezados CSV en inglés.',
          },
          uploadButton: 'Cargar CSV de Fresha',
          selectedFile: 'Archivo seleccionado: {{fileName}}',
          summary: '{{total}} filas · {{ready}} listas · {{failed}} con error · {{warnings}} avisos',
          failedTitle: 'Filas que necesitan corrección',
          moreFailures: '+{{count}} filas con error',
          fixFailingRows: 'Corregir filas con error',
          downloadFailingRows: 'Descargar filas con error',
          importReadyRows: 'Importar filas válidas',
          importReadyConfirmTitle: '¿Importar solo las filas válidas?',
          importReadyConfirmMessage: 'Las {{failed}} fila(s) que aún necesitan corrección se ignorarán. Solo se importarán las {{ready}} fila(s) válidas.',
          importReadyConfirmAction: 'Importar filas válidas',
          warningsNotice: '{{count}} aviso(s) en campos opcionales. El nombre y el email son válidos, por lo que esas filas aún se pueden importar.',
          importButton: 'Importar miembros del equipo',
          importing: 'Importando...',
          alreadyCompleted: 'Los miembros del equipo ya se importaron para esta tienda. Puedes continuar con Citas.',
          importSuccess: 'Importación finalizada: {{created}} creados, {{skipped}} omitidos (el email ya existe).',
          skippedEmails: 'Emails omitidos: {{emails}}',
          skippedEmailsMore: ' (+{{count}} más)',
          fields: {
            name: 'Nombre',
            email: 'Email',
            jobTitle: 'Cargo',
            permission: 'Permiso / Rol',
            disabled: 'Desactivado (archivado)',
          },
          roles: {
            staff: 'Personal (Basic)',
            manager: 'Gerente (High)',
            full: 'Completo (Owner)',
          },
          errors: {
            emptyFile: 'El archivo CSV está vacío.',
            parseFailed: 'No se pudo leer el archivo CSV.',
            unsupportedHeaders: 'No se reconocieron los encabezados del CSV. Exporta el archivo de empleados en inglés.',
            importFailed: 'La importación de miembros del equipo falló.',
            nameRequired: 'El nombre es obligatorio',
            emailRequired: 'El email es obligatorio',
            emailInvalid: 'El email no es válido',
          },
          warnings: {
            invalidPhone: 'No se pudo importar el teléfono',
            invalidBirthDate: 'No se pudo importar la fecha de nacimiento',
            unknownStatus: 'El estado era desconocido y se trató como Active',
            unknownPermission: 'El permiso era desconocido y se asignó a Personal',
          },
          fixDialog: {
            title: 'Corregir filas con error',
            rowLabel: 'Fila {{row}} · {{name}}',
            apply: 'Aplicar corrección',
            noFailures: 'Ya no hay filas con error.',
          },
        },
        appointments: {
          helper: {
            title: 'Cómo exportar citas de Fresha',
            intro: 'Genera el CSV de citas en Fresha y súbelo aquí.',
            step1: 'Abre la exportación de calendario / citas de Fresha:',
            openFresha: 'partners.fresha.com/calendar',
            step2: 'Exporta las citas como CSV (encabezados en portugués o inglés).',
            step3: 'Sube el archivo CSV descargado abajo.',
            languageNote: 'Los nombres se emparejan con clientes, servicios y miembros del equipo de esta tienda. Las filas sin coincidencia se pueden corregir u omitir antes de importar.',
          },
          uploadButton: 'Subir CSV de Fresha',
          selectedFile: 'Archivo seleccionado: {{fileName}}',
          summary: '{{total}} filas · {{appointments}} citas · {{ready}} listas · {{failed}} con error · {{warnings}} avisos',
          failedTitle: 'Filas que necesitan corrección',
          moreFailures: '+{{count}} filas con error',
          fixFailingRows: 'Corregir filas con error',
          downloadFailingRows: 'Descargar filas con error',
          importReadyRows: 'Importar filas listas',
          importReadyConfirmTitle: '¿Importar solo las filas listas?',
          importReadyConfirmMessage: 'Las {{failed}} fila(s) que aún necesitan corrección se ignorarán. Solo se importarán las {{ready}} fila(s) listas.',
          importReadyConfirmAction: 'Importar filas listas',
          warningsNotice: '{{count}} aviso(s) en campos opcionales. Los campos obligatorios son válidos, por lo que esas filas aún se pueden importar.',
          resolving: 'Emparejando nombres...',
          importButton: 'Importar citas',
          importing: 'Importando...',
          alreadyCompleted: 'Las citas ya se importaron para esta tienda.',
          importSuccess: 'Importación finalizada: {{created}} cita(s) creada(s).',
          skippedNotice: '{{count}} fila(s) omitida(s) y no se importarán.',
          fields: {
            customer: 'Cliente',
            teamMember: 'Colaborador',
            service: 'Servicio',
            category: 'Categoría',
            status: 'Estado',
            scheduledDate: 'Fecha programada',
            periodFrom: 'Hora de inicio',
            periodTo: 'Hora de fin',
            price: 'Precio',
          },
          status: {
            new: 'Nuevo / Confirmado',
            completed: 'Completado',
            cancelled: 'Cancelado',
            noShow: 'No asistió',
          },
          resolve: {
            walkIn: 'Walk-in (cliente anónimo)',
            skipRow: 'Omitir esta fila',
            searchCustomer: 'Buscar cliente...',
            searchService: 'Buscar servicio...',
            searchTeamMember: 'Buscar colaborador...',
            importedValues: 'Valores del CSV — cliente: {{customer}} · servicio: {{service}} · colaborador: {{teamMember}}',
          },
          errors: {
            emptyFile: 'El archivo CSV está vacío.',
            parseFailed: 'No se pudo leer el archivo CSV.',
            unsupportedHeaders: 'No se reconocieron los encabezados del CSV. Usa una exportación de citas de Fresha en portugués o inglés.',
            importFailed: 'La importación de citas falló.',
            noReadyRows: 'No hay filas listas para importar.',
            referenceRequired: 'La referencia de la cita es obligatoria',
            customerRequired: 'El cliente es obligatorio',
            teamMemberRequired: 'El colaborador es obligatorio',
            serviceRequired: 'El servicio es obligatorio',
            customerNotFound: 'El cliente no se encontró en esta tienda',
            serviceNotFound: 'El servicio no se encontró en esta tienda',
            teamMemberNotFound: 'El colaborador no se encontró en esta tienda',
            statusRequired: 'El estado es obligatorio',
            statusUnknown: 'El estado es desconocido',
            dateRequired: 'La fecha programada es inválida o falta',
            slotRequired: 'El horario de la cita es inválido o falta',
          },
          warnings: {
            invalidPrice: 'No se pudo importar el precio; se usará el precio del catálogo',
            invalidDuration: 'No se pudo interpretar la duración',
            trailingSpaces: 'Se eliminaron espacios al final de los nombres',
          },
          fixDialog: {
            title: 'Corregir filas con error',
            rowLabel: 'Fila {{row}} · {{name}}',
            progress: 'Fila {{current}} de {{total}} · Fila CSV {{row}} · {{name}}',
            next: 'Siguiente',
            apply: 'Aplicar corrección',
            noFailures: 'Ya no hay filas con error.',
          },
        },
      },
      communicationSettingsPage: {
        title: 'Comunicación',
        subtitle: 'Configura las notificaciones de correo, SMS y push enviadas a los clientes para eventos de cita',
        emailAlways: 'Correo siempre enviado',
        emailLabel: 'Correo',
        smsLabel: 'SMS',
        pushLabel: 'Push',
        smsPriceWarning: 'Los mensajes SMS se cobran por mensaje a {{price}}.',
        smsDependsOnEmail: 'Activa el correo para este recordatorio primero',
        smsDisabledHelp: 'La facturación de SMS no está activa. Actívala en la configuración de Facturación.',
        smsBillingBanner: {
          text: 'El SMS no está activo para esta tienda. Activa la facturación de SMS para enviar notificaciones por mensaje de texto a tus clientes.',
          link: 'Ir a la configuración de Facturación',
        },
        smsSenderName: {
          label: 'Nombre del Remitente SMS',
          help: 'Este nombre aparece como remitente en los mensajes SMS. Déjalo vacío para usar el nombre de la tienda (truncado a 15 caracteres). Solo letras, números y espacios. Nota: algunos operadores pueden truncar nombres de más de 11 caracteres.',
          placeholder: 'Nombre de la tienda',
          error: {
            invalid: 'El nombre del remitente debe comenzar con una letra o número y contener solo letras, números y espacios.',
          },
        },
        confirmation: {
          title: 'Confirmación de Cita',
          description: 'El correo siempre se envía cuando se reserva una cita. Opcionalmente, también envía SMS y notificaciones push.',
        },
        cancellation: {
          title: 'Cancelación de Cita',
          description: 'El correo siempre se envía cuando se cancela una cita. Opcionalmente, también envía SMS y notificaciones push.',
        },
        reminders: {
          title: 'Recordatorios de Cita',
          description: 'Elige de forma independiente las notificaciones de correo, SMS y push para cada ventana de recordatorio. El SMS sigue al correo — solo puede activarse cuando el correo está activo.',
        },
        reminder7Day: '7 días antes',
        reminder1Day: '1 día antes',
        reminder2Hour: '2 horas antes',
        googleReviewAsk: {
          title: 'Petición de reseña en Google',
          description: 'Opcional. Se envía unas 2 horas después de una visita completada, pidiendo al cliente una reseña en Google. Elige correo, SMS o push; el SMS usa las mismas reglas de facturación que los demás mensajes de cita.',
          missingUrl: 'Añade la URL de reseña de Google en los datos de la tienda antes de activar esto.',
          editStore: 'Editar datos de la tienda',
        },
        success: {
          save: 'Configuración de comunicación guardada.',
        },
        error: {
          load: 'Error al cargar la configuración de comunicación.',
          save: 'Error al guardar la configuración de comunicación.',
        },
        tabs: {
          notifications: 'Notificaciones',
          waitlist: 'Lista de espera',
        },
        waitlist: {
          title: 'Lista de espera',
          subtitle: 'Elige cómo la tienda gestiona a los clientes que esperan un horario preferido.',
          typeLabel: 'Tipo de lista de espera',
          type: {
            Manual: 'Manual',
            Auto: 'Automática',
          },
          typeHelp: {
            Manual: 'El equipo recibe un aviso y elige quién ocupa el hueco. Los clientes no reciben correo automáticamente.',
            Auto: 'Los clientes coincidentes reciben un correo tras una breve espera para que el equipo pueda usar el hueco primero.',
          },
          priorityLabel: 'Prioridad de la lista de espera',
          priority: {
            FirstInLine: 'Por orden de llegada',
            HighestValue: 'Mayor valor',
            NotifyAll: 'Avisar a todos',
          },
          priorityHelp: {
            FirstInLine: 'Ofrece el hueco al cliente que se apuntó primero.',
            HighestValue: 'Ofrece el hueco primero al cliente con el importe estimado más alto.',
            NotifyAll: 'Envía un correo a todos los clientes coincidentes. El primero en reservar se queda el hueco.',
          },
        },
      },
      waitlistQueuePage: {
        title: 'Lista de espera',
        subtitle: 'Clientes que esperan un horario preferido',
        empty: 'Nadie está en la lista de espera ahora.',
        book: 'Reservar',
        remove: 'Quitar',
        removeTitle: 'Quitar de la lista de espera',
        removeMessage: '¿Quitar a {{name}} de la lista de espera?',
        success: {
          remove: 'Cliente quitado de la lista de espera.',
        },
        error: {
          load: 'No se pudo cargar la lista de espera.',
          remove: 'No se pudo quitar a este cliente de la lista de espera.',
        },
        columns: {
          customer: 'Cliente',
          services: 'Servicios',
          preferences: 'Horarios preferidos',
          joined: 'Se apuntó',
          value: 'Valor estimado',
          actions: 'Acciones',
        },
      },
      waitlistOfferPage: {
        title: 'Se abrió un hueco para ti',
        book: 'Reservar',
        decline: 'Rechazar',
        duration: '{{minutes}} min',
        success: 'Tu cita está reservada.',
        declined: 'Rechazaste este hueco.',
        alreadyBooked: 'Este horario ya no está disponible.',
        claimHint: 'Puedes volver a tus citas desde el menú de la tienda.',
        error: {
          load: 'No se pudo cargar esta oferta de la lista de espera.',
          claim: 'No se pudo reservar este hueco. Puede que ya esté ocupado.',
        },
      },
      waitlistAlert: {
        joinedTitle: 'Un cliente se apuntó a la lista de espera',
        slotOpenedTitle: 'Se abrió un hueco coincidente',
        customer: 'Cliente',
        services: 'Servicios',
        duration: 'Duración',
        durationValue: '{{minutes}} min',
        value: 'Valor estimado',
        viewWaitlist: 'Ver lista de espera',
        book: 'Reservar',
      },
      appointmentConfirmationAlert: {
        title: 'Reserva bajo petición',
        customer: 'Cliente',
        confirm: 'Confirmar',
        decline: 'Rechazar',
        viewAppointment: 'Ver en la agenda',
        openToAny: 'Queda abierto a cualquier profesional disponible hasta que alguien confirme.',
      },
      stripeCheckout: {
        title: 'Completa tu pago',
        error: 'Error al cargar el formulario de pago. Inténtalo de nuevo.'
      },
      paymentSettingsPage: {
        title: 'Configuración de Pago',
        subtitle: 'Administra tu suscripción a Salovare y la configuración de pagos de clientes',
        tabs: {
          subscription: 'Suscripción Salovare',
          customerPayments: 'Pagos de Clientes',
          notifications: 'Notificaciones'
        },
        error: {
          loading: 'Error al cargar la configuración de pago.',
          unauthorized: 'No tienes permiso para administrar la configuración de pago de esta tienda.'
        },
        subscription: {
          title: 'Suscripción a Salovare',
          description: 'Administra cómo tu tienda paga Salovare. Mejora a Pro o actualiza tus datos de facturación desde aquí.',
          loading: 'Cargando estado de la suscripción...',
          statusActive: 'Facturación de suscripción configurada',
          statusActiveDescription: 'Tus datos de facturación están registrados. Usa el portal de facturación para actualizar tu método de pago o revisar facturas.',
          statusActionRequired: 'Se requiere atención de facturación',
          billingDetailsOnFile: 'Datos de facturación registrados',
          billingDetailsPending: 'Datos de facturación pendientes',
          trialPlan: 'Prueba gratuita',
          trialUntil: 'Prueba gratuita hasta {{date}}',
          priceAfterTrial: '{{price}}/mes por la suscripción base después del {{date}}',
          nextBillingOn: 'Próximo cobro el {{date}}',
          priceOnNextBilling: '{{price}}/mes por la suscripción base el {{date}}',
          discountOnNextPayment: 'Descuento del {{percent}}% en el plan en tu próximo pago',
          discountApplied: 'Descuento del {{percent}}% en el plan aplicado',
          cancelInstructions: 'Puedes cancelar en cualquier momento desde el portal de facturación. No se te cobrará después del período actual si cancelas.',
          cancelAction: 'Cancelar suscripción',
          cancelTitle: 'Cancelar suscripción',
          cancelMessage: 'Tu suscripción permanecerá activa hasta el {{date}}. La factura final incluirá la suscripción y el uso de SMS hasta esa fecha.',
          cancelConfirm: 'Cancelar al final del período',
          cancelError: 'Error al cancelar la suscripción.',
          cancelScheduled: 'Cancelación programada. Tu suscripción permanece activa hasta el {{date}}.',
          teamMemberSeats: '{{active}} miembros activos ({{included}} incluido(s))',
          additionalTeamMemberSeats: '{{count}} miembro(s) adicional(es) a {{price}}/mes cada uno',
          billedAdditionalSeats: '{{count}} plaza(s) adicional(es) facturada(s) a {{price}}/mes cada una',
          additionalStores: '{{count}} local(es) adicional(es) a {{price}}/mes cada uno',
          seatBreakdownRow: '{{store}}: {{count}} profesional(es) extra',
          unnamedStore: 'Local sin nombre',
          estimatedMonthlyTotal: 'Total mensual estimado: {{price}}'
        },
        customerPayments: {
          error: {
            load: 'Error al cargar la configuración de pagos de clientes.',
            save: 'Error al guardar la configuración de pagos de clientes.'
          },
          success: {
            save: 'Configuración de pagos de clientes guardada.'
          },
          step1: {
            title: '1. Configurar Stripe',
            ready: 'Stripe está conectado y listo para aceptar pagos.',
            refresh: 'Volver a comprobar los métodos de pago',
            manageOnStripe: 'Gestionar en Stripe'
          },
          step2: {
            title: '2. Elegir métodos de pago',
            description: 'Estos son los métodos de pago que Stripe ha puesto a disposición de tu tienda. La tarjeta siempre está activa; activa los demás que quieras ofrecer a los clientes.',
            empty: 'Termina de configurar Stripe arriba para ver los métodos de pago disponibles para tu tienda.'
          },
          step3: {
            title: '3. Activar pago en línea',
            needsOnboarding: 'Termina de configurar Stripe arriba antes de activar pagos en línea.',
            services: 'Servicios',
            servicesHelp: 'Incluye cualquier producto añadido a la reserva.',
            memberships: 'Membresías'
          },
          methods: {
            card: 'Tarjeta y Google Pay',
            klarna: 'Klarna (pago en cuotas)',
            mbWay: 'MB WAY',
            pix: 'Pix',
            scalapay: 'Scalapay (pago en cuotas)',
            sequra: 'SeQura (pago en cuotas)'
          },
          fees: {
            checkFee: 'Ver comisión',
            title: 'Comisión de {{method}}',
            exampleService: 'Servicio de corte y peinado',
            processingFee: 'Comisión de procesamiento de pago online ({{rate}})',
            youReceive: 'Recibes',
            disclaimer: 'Salovare no añade ninguna comisión sobre Stripe. La tarifa exacta puede variar según el emisor de la tarjeta y la conversión de moneda — consulta los',
            stripePricingLink: 'precios de Stripe'
          }
        },
        sms: {
          title: 'Notificaciones SMS',
          description: 'Envía SMS de confirmación y recordatorio de reservas a {{price}} por mensaje, facturados en el ciclo de suscripción.',
          loading: 'Cargando configuración de SMS...',
          enableLabel: 'Activar notificaciones SMS',
          enableHelp: 'El uso se factura mensualmente con un límite de gasto.',
          enableHelpTrialBlocked: 'SMS no está disponible durante la prueba a menos que el soporte de Salovare lo active para tu tienda.',
          enableHelpNeedsStripe: 'Conecta primero la facturación de Stripe y luego podrás activar SMS en la prueba.',
          trialBlocked: 'SMS no está activado por defecto durante la prueba. Envía un correo a {{email}} para solicitar acceso.',
          trialNeedsStripe: 'SMS fue aprobado para tu prueba. Conecta primero la facturación de Stripe para activarlo.',
          emailSupport: 'Enviar correo al soporte',
          supportEmailSubject: 'Solicitud de SMS durante la prueba gratuita',
          supportEmailBody: 'Hola soporte de Salovare,\n\nMe gustaría solicitar notificaciones SMS durante mi prueba gratuita.\n\nID de la tienda: {{storeId}}\n\nGracias.',
          usageSummary: 'Uso del período actual: {{spent}} / {{limit}} (se reinicia el {{date}})',
          limitReached: 'Has alcanzado tu límite de SMS de {{limit}}. Paga la factura pendiente en el portal de facturación para continuar, o espera hasta el {{date}} para que se reinicie el límite.',
          paymentFailed: 'SMS está desactivado porque falló el pago de una factura reciente. Actualiza tu método de pago en el portal de facturación.',
          openBillingPortal: 'Abrir portal de facturación',
          error: {
            update: 'Error al actualizar la configuración de SMS.'
          }
        }
      },
      paymentModes: {
        inStoreOnly: 'Solo en tienda',
        onlineOnly: 'Solo en línea',
        both: 'En línea y en tienda'
      },
      storePaymentMethods: {
        label: 'Método de pago',
        placeholder: 'Selecciona el método de pago',
        cash: 'Efectivo',
        card: 'Tarjeta',
        transfer: 'Transferencia',
        mbWay: 'MB WAY',
        other: 'Otro',
        online: 'En línea'
      },
      appointmentCheckout: {
        amountDue: 'Importe a pagar',
        amountReceived: 'Importe recibido',
        amountReceivedHint: 'Puedes cobrar menos que el importe a pagar.',
        membershipFee: 'Cuota de membresía',
        totalCollected: 'Total cobrado: {{amount}}',
        remaining: 'Pendiente: {{amount}}',
        discount: 'Descuento: {{amount}}',
        addPayment: 'Añadir pago',
        removePayment: 'Quitar pago',
        paymentLine: 'Pago {{number}}',
        receivedSummary: '{{amount}} se registrará en el flujo de caja.',
        errors: {
          amountRange: 'El importe recibido debe estar entre 0 y el importe a pagar.',
          paymentMethodRequired: 'Selecciona un método de pago.',
          duplicateMethod: 'Cada método de pago solo puede usarse una vez.',
          amountRequired: 'Introduce un importe mayor que cero.',
          membershipCoverRequired: 'El importe cobrado debe cubrir la cuota de membresía.',
          fullAmountRequired: 'El importe cobrado debe ser igual al importe a pagar.'
        }
      },
      customerMembershipsManagement: {
        title: 'Membresías de Clientes',
        subtitle: 'Consulta membresías, inscribe clientes en tienda y marca períodos de facturación como pagados.',
        error: {
          noStoreSelected: 'Selecciona una tienda para administrar membresías de clientes.',
          loading: 'Error al cargar membresías de clientes.'
        },
        empty: {
          title: 'Aún no hay membresías de clientes',
          subtitle: 'Inscribe un cliente en tienda o espera suscripciones en línea.'
        },
        table: {
          customer: 'Cliente',
          plan: 'Plan',
          status: 'Estado',
          channel: 'Canal de pago',
          period: 'Período actual',
          price: 'Precio',
          actions: 'Acciones',
          unnamedCustomer: 'Cliente sin nombre',
          periodRange: '{{start}} – {{end}}',
          noPeriod: 'Sin período activo'
        },
        paymentChannel: {
          online: 'En línea',
          inStore: 'En tienda',
          unknown: 'Desconocido'
        },
        enroll: {
          action: 'Inscribir cliente',
          title: 'Inscribir membresía en tienda',
          description: 'Crea una membresía en tienda para un cliente existente. El primer período de facturación debe marcarse como pagado antes de que los beneficios estén activos.',
          customerId: 'ID del cliente',
          customer: 'Cliente',
          customerPlaceholder: 'Buscar por nombre o email',
          noCustomers: 'No se encontraron clientes',
          plan: 'Plan de membresía',
          planPlaceholder: 'Selecciona un plan',
          confirm: 'Inscribir',
          enrolling: 'Inscribiendo...',
          success: 'Cliente inscrito correctamente.',
          error: 'Error al inscribir cliente.',
          errors: {
            customerIdRequired: 'El ID del cliente es obligatorio.',
            customerRequired: 'Selecciona un cliente.',
            planRequired: 'Selecciona un plan de membresía.',
            paymentMethodRequired: 'Selecciona un método de pago.'
          }
        },
        markPaid: {
          action: 'Marcar período pagado',
          payNextPeriod: 'Pagar siguiente período',
          title: 'Marcar período pagado',
          payNextTitle: 'Pagar siguiente período',
          description: 'Registra el pago de este período de facturación.',
          payNextDescription: 'Cobra el siguiente período. Los derechos actuales se mantienen hasta que termine este período.',
          amount: 'Importe',
          marking: 'Marcando...',
          success: 'Período de facturación marcado como pagado.',
          payNextSuccess: 'Siguiente período de facturación pagado.',
          error: 'Error al marcar período de facturación como pagado.'
        },
        cancel: {
          action: 'Cancelar membresía',
          canceling: 'Cancelando...',
          resume: 'Mantener membresía',
          resuming: 'Actualizando...',
          confirmTitle: 'Cancelar membresía',
          confirmMessage: 'El cliente puede seguir usando esta membresía hasta el {{date}} — el último período ya pagado. Después de esa fecha, quedará bloqueada. ¿Deseas continuar?',
          success: 'La membresía sigue activa hasta el {{date}}. Después de esa fecha, se cancelará.',
          error: 'No se pudo cancelar esta membresía.',
          resumeSuccess: 'Esta membresía continuará con normalidad.',
          resumeError: 'No se pudo mantener esta membresía activa.',
          cancelsOn: 'Se cancela el {{date}}'
        }
      },
      customersManagement: {
        title: 'Clientes',
        subtitle: 'Consulta y añade clientes asignados a esta tienda.',
        error: {
          noStoreSelected: 'Selecciona una tienda para gestionar clientes.',
          loading: 'Error al cargar clientes.'
        },
        empty: {
          title: 'Aún no hay clientes',
          subtitle: 'Añade tu primer cliente para empezar a construir el directorio de la tienda.',
          noResultsTitle: 'No hay clientes coincidentes',
          noResultsSubtitle: 'Prueba con otro nombre, email o número de teléfono.'
        },
        dropdown: {
          searchPlaceholder: 'Buscar por nombre, email o teléfono',
          noOptions: 'Sin resultados'
        },
        table: {
          customer: 'Cliente',
          email: 'Email',
          phone: 'Teléfono',
          genre: 'Género',
          birthDate: 'Fecha de nacimiento',
          addedOn: 'Añadido el'
        },
        genre: {
          male: 'Masculino',
          female: 'Femenino',
          other: 'Otro'
        },
        create: {
          title: 'Añadir cliente',
          subtitle: 'Verifica el email o el teléfono y luego crea un cliente nuevo o añade uno existente a esta tienda.',
          action: 'Crear cliente',
          assign: 'Añadir a la tienda',
          email: 'Email',
          name: 'Nombre',
          phone: 'Teléfono',
          emailPlaceholder: 'Introduce la dirección de email',
          namePlaceholder: 'Introduce el nombre del cliente',
          phonePlaceholder: 'Introduce el número de teléfono',
          emailOptional: 'Opcional si se indica un número de teléfono',
          phoneOptional: 'Opcional si se indica un email',
          emailCheck: {
            check: 'Verificar',
            available: 'Este email está disponible. Introduce el nombre del cliente abajo.',
            existingTitle: 'Cliente ya registrado',
            existingMessage: '{{name}} ({{email}}) ya está registrado en Salovare. ¿Quieres añadir este cliente a tu tienda? Su perfil existente no se modificará.',
            existingConfirm: 'Añadir a esta tienda',
            existingConfirmed: '{{name}} ya está registrado. Puedes añadirlo a esta tienda sin cambiar su perfil.',
            alreadyInStore: 'Este cliente ya está asignado a esta tienda.',
            error: 'No se pudo verificar este email. Inténtalo de nuevo.'
          },
          phoneCheck: {
            available: 'Este número de teléfono está disponible. Introduce el nombre del cliente abajo.',
            existingConfirmed: '{{name}} ya está registrado con este número de teléfono. Puedes añadirlo a esta tienda sin cambiar su perfil.',
            alreadyInStore: 'Este cliente ya está asignado a esta tienda.',
            error: 'No se pudo verificar este número de teléfono. Inténtalo de nuevo.',
            required: 'Verifica este número de teléfono antes de continuar.'
          },
          success: {
            created: 'Cliente creado correctamente. Se envió el email para establecer la contraseña.',
            createdPhoneOnly: 'Cliente creado correctamente. Puede añadir un email la primera vez que inicie sesión.',
            assigned: 'Cliente añadido a esta tienda correctamente.'
          },
          error: {
            creation: 'Error al añadir cliente.'
          },
          validation: {
            nameRequired: 'El nombre es obligatorio.',
            emailRequired: 'El email es obligatorio.',
            emailInvalid: 'Introduce una dirección de email válida.',
            emailCheckRequired: 'Verifica este email antes de continuar.',
            contactRequired: 'El email o el teléfono es obligatorio.',
            phoneRequired: 'El número de teléfono es obligatorio cuando el email está vacío.'
          }
        },
        details: {
          title: 'Detalles del cliente',
          subtitle: 'Perfil específico de la tienda, notas de cuidados y métricas de visitas.',
          edit: 'Editar',
          bookAppointment: 'Reservar cita',
          storeOverride: 'Datos de la tienda',
          usingAccount: 'Usando datos de la cuenta',
          loading: 'Error al cargar los detalles del cliente.',
          notFound: 'Este cliente no está asignado a esta tienda.',
          saveSuccess: 'Perfil de cliente de la tienda guardado.',
          saveError: 'Error al guardar el perfil de cliente de la tienda.',
          metrics: {
            frequency: 'Frecuencia de visitas',
            lastVisit: 'Última visita {{date}}',
            noVisits: 'Aún no hay visitas completadas',
            noShows: 'Ausencias',
            lifetimeValue: 'Total gastado',
            lastThreeMonths: 'Últimos 3 meses',
            productsSpend: 'Gasto en productos'
          },
          contact: {
            title: 'Contacto',
            name: 'Nombre',
            email: 'Email',
            phone: 'Teléfono'
          },
          upcoming: {
            title: 'Próximas citas',
            empty: 'No hay citas próximas',
            error: 'Error al cargar las próximas citas.'
          },
          consultation: {
            title: 'Cuidados y preferencias',
            empty: 'Aún no hay notas de cuidados. Añade alergias, condiciones médicas, medicamentos y estado de embarazo o lactancia para que el equipo trate a este cliente con seguridad.',
            allergies: 'Alergias y sensibilidades',
            medical: 'Condiciones médicas',
            medications: 'Medicamentos',
            pregnancy: 'Embarazo / lactancia'
          },
          editDialog: {
            title: 'Información del cliente',
            storeViewOnly: 'Este cambio se aplica solo a esta tienda.',
            accountProfile: 'Se guarda en el perfil del cliente.',
            customerProvided: 'Lo indicó el cliente y no se puede modificar.',
            selectGenre: 'Seleccionar género',
            name: 'Nombre',
            namePlaceholder: 'Nombre',
            email: 'Email',
            emailPlaceholder: 'Email',
            phone: 'Teléfono',
            phonePlaceholder: 'Teléfono',
            allergiesHelper: 'Alergias de piel, látex, frutos secos (comunes en aceites de masaje) o reacciones a tintes capilares.',
            medicalHelper: 'Diabetes (afecta la cicatrización en pedicuras/microblading), epilepsia (luces/láseres) o problemas circulatorios.',
            medicationsHelper: 'Anticoagulantes, Accutane/Retin-A (la piel se desgarra fácilmente en depilación o faciales).',
            pregnancyHelper: 'Algunos aceites esenciales, corrientes eléctricas y químicos no son seguros.',
            save: 'Guardar',
            emailInvalid: 'Introduce una dirección de email válida.'
          }
        },
        import: {
          action: 'Importar',
          title: 'Importar clientes',
          pickSubtitle: 'Sube un archivo CSV o vCard exportado del teléfono o de los contactos de email.',
          uploadButton: 'Subir CSV o vCard',
          selectedFile: 'Seleccionado: {{fileName}}',
          mappingTitle: 'Asignar columnas',
          mappingSubtitle: 'Arrastra una columna del archivo a un campo de Salovare. La asignación solo se usa en esta importación.',
          mappingSelectSubtitle: 'Elige una columna del archivo para cada campo de Salovare. La asignación solo se usa en esta importación.',
          mappingNone: 'No importar',
          fileColumns: 'Columnas del archivo',
          mapping: 'Asignación',
          salovareFields: 'Campos de Salovare',
          continue: 'Continuar',
          fields: {
            name: 'Nombre',
            firstName: 'Nombre',
            lastName: 'Apellido',
            email: 'Email',
            phone: 'Teléfono',
            birthDate: 'Fecha de nacimiento',
            genre: 'Género'
          },
          errors: {
            emptyFile: 'El archivo no tiene contactos para importar.',
            parseFailed: 'No se pudo leer este archivo. Usa una exportación .csv o .vcf.',
            unsupportedType: 'Usa un archivo .csv o .vcf.',
            noRows: 'No se encontraron contactos después de asignar las columnas.',
            importFailed: 'Error al importar clientes.'
          },
          success: 'Se importaron {{created}} clientes nuevos. {{assigned}} ya existían y se añadieron a esta tienda. {{alreadyInStore}} ya estaban en esta tienda.'
        },
        fallback: {
          message: 'Selecciona una tienda en el menú superior para gestionar clientes.',
          goToStores: 'Ir a Tiendas'
        }
      },
      employeePayments: {
        settings: {
          title: 'Ajustes de Pagos de Empleados',
          subtitle: 'Configura el margen de la tienda y el día de pago para servicios y membresías',
          readOnlyHint: 'Puedes ver estos ajustes, pero solo los gerentes pueden actualizarlos.',
          margin: {
            title: 'Margen de la tienda',
            description: 'Porcentaje que la tienda retiene de los pagos de membresía recibidos en cada mes calendario. El resto forma el pote mensual de membresías, compartido entre los miembros del equipo según los puntos de duración de los servicios de membresía completados. Si no hubo servicios de membresía completados ese mes, el pote se divide por igual entre los miembros activos.',
            label: 'Margen de la tienda (%)'
          },
          payDay: {
            title: 'Día de pago',
            description: 'Día de cada mes en que se reconoce la compensación de empleados. Las partes del pote de membresías se pagan en el primer día de pago después de que termine el mes calendario.',
            label: 'Día de pago del mes',
            option: 'Día {{day}}'
          },
          error: {
            loading: 'Error al cargar los ajustes de pagos de empleados.',
            save: 'Error al guardar los ajustes de pagos de empleados.',
            marginRange: 'El margen de la tienda debe estar entre 0 y 100.'
          },
          success: {
            saved: 'Ajustes de pagos de empleados guardados correctamente.'
          }
        },
        payroll: {
          title: 'Nómina de Empleados',
          subtitle: 'Previsualiza la compensación de todos los miembros del equipo en el período seleccionado',
          subtitleOwn: 'Previsualiza tu compensación en el período seleccionado',
          referencePeriod: 'Período de referencia',
          teamMemberFilter: 'Miembro del equipo',
          teamMemberFilterPlaceholder: 'Seleccione miembros del equipo',
          filters: {
            title: 'Filtros',
            view: 'Ver informe',
            edit: 'Editar filtros',
            expand: 'Expandir filtros',
            collapse: 'Contraer filtros',
            summary: '{{period}} · {{members}}',
            customRangeSummary: '{{from}} – {{to}}',
          },
          periodPresets: {
            nextMonth: 'Próximo mes',
            today: 'Hoy',
            thisWeek: 'Esta semana',
            lastWeek: 'Semana pasada',
            thisMonth: 'Este mes',
            lastMonth: 'Mes pasado',
            last3Months: 'Últimos 3 meses',
            thisYear: 'Este año',
            custom: 'Rango personalizado',
          },
          year: 'Año',
          month: 'Mes',
          empty: 'No hay datos de nómina para este período.',
          emptyService: 'No hay datos de nómina de servicios para este período.',
          emptyMembership: 'No hay pagos de partes de membresías para este período.',
          estimatedPeriodAlert: 'Las cifras del próximo mes son estimaciones y se actualizan a medida que se registran citas y pagos de membresías.',
          sections: {
            cashFlow: 'Flujo de caja',
            service: 'Servicios y salarios',
            membership: 'Partes de membresías',
            membershipHint: 'Las partes del pote de membresías se pagan mensualmente en el día configurado, siempre en el mes siguiente al mes de referencia.',
          },
          amountTone: {
            keep: 'Un importe alto es dinero retenido',
            cost: 'Un importe alto es dinero pagado',
          },
          profit: {
            title: 'Beneficio',
            amount: 'Beneficio',
            hint: 'Retención de la tienda tras salarios y comisiones, más el margen de suscripciones que se queda la tienda, menos los gastos registrados en este período.',
            filtered: 'El beneficio es de toda la tienda. Quite el filtro de miembros del equipo para verlo.',
          },
          cashFlow: {
            hint: 'Dinero recibido por la tienda en este período. El filtro de miembro del equipo no se aplica.',
            totalReceived: 'Total recibido',
            cashier: 'Caja',
            bank: 'Banco',
            other: 'Otro',
            sliceDetail: '{{amount}} · {{percent}} del total',
            tapSlice: 'Toque una porción para ver el importe y su parte del total.',
          },
          membershipTotals: {
            gross: 'Bruto de membresías',
            storeMargin: 'Margen de la tienda',
            barberPool: 'Pote del equipo',
            totalSharesPaid: 'Total de partes pagadas',
          },
          membershipTable: {
            referenceMonth: 'Mes de referencia',
            shareAmount: 'Importe de la parte',
            totalShare: 'Total de la parte',
          },
          unknownMember: 'Miembro del equipo',
          estimated: 'Estimado',
          minimumApplied: 'Mínimo aplicado',
          noBreakdown: 'No hay líneas de ingresos registradas para este período.',
          error: {
            loading: 'Error al cargar el informe de nómina.',
            customRangeRequired: 'Selecciona las fechas de inicio y fin para el rango personalizado.',
            customRangeOrder: 'La fecha de inicio no puede ser posterior a la fecha de fin.',
            customRangeMax: 'El rango personalizado no puede superar un año.',
          },
          monthNames: {
            1: 'Enero',
            2: 'Febrero',
            3: 'Marzo',
            4: 'Abril',
            5: 'Mayo',
            6: 'Junio',
            7: 'Julio',
            8: 'Agosto',
            9: 'Septiembre',
            10: 'Octubre',
            11: 'Noviembre',
            12: 'Diciembre'
          },
          totals: {
            grossBilled: 'Ingresos totales por servicios',
            productsGrossSold: 'Ventas de productos',
            productCommissionsPaid: 'Comisiones de productos pagadas',
            salaries: 'Pago total',
            storeGain: 'Retención de la tienda tras nómina',
          },
          table: {
            member: 'Miembro del equipo',
            mode: 'Modo de pago',
            commissionRate: 'Tasa de comisión',
            fixedSalary: 'Salario fijo',
            commission: 'Comisión',
            productsSold: 'Productos vendidos',
            productCommission: 'Comisión de productos',
            total: 'Pago total',
            details: 'Detalles',
            viewAppointments: 'Ver reservas',
            actions: 'Acciones',
            editCompensation: 'Editar ajustes de compensación'
          },
          breakdown: {
            source: 'Origen',
            recognitionDate: 'Fecha de reconocimiento',
            serviceRevenue: 'Ingresos por servicio',
            extraBilled: 'Extra facturado',
            employeeShare: 'Parte del profesional',
          },
          sourceTypes: {
            appointmentService: 'Servicio de reserva',
            appointmentAdditionalCost: 'Coste adicional de horario',
            productSale: 'Venta de producto',
            membershipMonthlyShare: 'Parte mensual de membresías',
            membershipPeriodUsageShare: 'Parte de uso del período de membresía',
          },
          membershipPot: {
            title: 'Pote de membresías — {{month}} {{year}}',
            subtitle: 'Todos los pagos de membresías recibidos en este mes calendario, después del margen de la tienda, compartidos por puntos de duración.',
            gross: 'Bruto de membresías',
            storeMargin: 'Margen de la tienda',
            barberPool: 'Pote del equipo',
            totalPoints: 'Total de puntos de duración: {{points}} min',
            member: 'Miembro del equipo',
            points: 'Puntos (min)',
            sharePercent: 'Parte %',
            shareAmount: 'Importe de la parte',
            viewPot: 'Ver pote',
            error: {
              loading: 'Error al cargar los detalles del pote de membresías.',
            },
          },
          membershipPeriod: {
            title: 'Pagos de membresías según el uso',
            subtitle: 'Períodos de facturación cerrados pagados el día de pago de la tienda, repartidos por visitas completadas y precios originales de los servicios.',
            gross: 'Bruto de membresías',
            storeMargin: 'Margen de la tienda',
            barberPool: 'Pote del equipo',
            unallocated: 'Retenido por la tienda',
            empty: 'No hay períodos de membresía cerrados pagados en este intervalo.',
            periodDates: '{{from}} – {{to}}',
            member: 'Miembro del equipo',
            redemptions: 'Parte de visitas',
            shareAmount: 'Importe de la parte',
            keptByStore: 'Retenido por la tienda',
            error: {
              loading: 'Error al cargar los pagos de membresías según el uso.',
            },
          },
          appointmentDetails: {
            title: 'Reservas — {{name}}',
            subtitle: '{{from}} a {{to}}',
            empty: 'No hay reservas atribuidas para este período.',
            date: 'Fecha',
            customer: 'Cliente',
            appointmentId: 'ID de reserva',
            services: 'Servicios ejecutados',
            totalPrice: 'Precio total',
            additionalCost: 'Coste adicional de horario',
            employeeShare: 'Parte del profesional',
            teamMemberShare: 'Parte del miembro',
            storeMargin: 'Margen retenido por la tienda',
            monthAppointmentCount: '{{count}} reservas',
            error: {
              loading: 'Error al cargar los detalles de las reservas.',
            },
          },
        },
        compensation: {
          title: 'Ajustes de Compensación',
          subtitle: 'Configura cómo se paga a este miembro del equipo',
          editTitle: 'Compensación — {{name}}',
          paymentMode: 'Modo de pago',
          fixedMonthlySalary: 'Salario mensual fijo',
          commissionRatePercent: 'Tasa de comisión',
          minimumMonthlySalary: 'Salario mínimo mensual',
          effectiveFrom: 'Efectivo desde',
          effectiveFromHint: 'Se permiten fechas pasadas (hasta 5 años). Guardar recalcula la nómina de citas pagadas desde esta fecha.',
          payScheduleTitle: 'Calendario de pago',
          payScheduleHint: 'Pago mensual el día configurado del mes siguiente. Pago semanal el día de la semana configurado en la semana siguiente a la semana laboral (lun–dom).',
          payCadence: 'Periodicidad de pago',
          payDayOfMonth: 'Día de pago del mes',
          payDayOfWeek: 'Día de pago de la semana',
          payDayOption: 'Día {{day}}',
          payCadenceOptions: {
            monthly: 'Mensual',
            weekly: 'Semanal',
          },
          deductions: {
            title: 'Deducciones',
            discounts: 'Descuentos',
            discountsHelpTitle: 'Cómo afectan los descuentos a la comisión',
            discountsHelpAriaLabel: 'Cómo afectan los descuentos a la comisión',
            discountsHelpOff: 'Déjelo desactivado si esta persona debe ganar comisión sobre el precio completo del servicio, aunque el cliente haya pagado menos.',
            discountsHelpOn: 'Actívelo si la comisión debe ser solo sobre lo que el cliente pagó de verdad.',
            discountsHelpExample: 'Ejemplo: un corte de 30 € con 10 € de descuento. Desactivado = comisión sobre 30 €. Activado = comisión sobre 20 €.',
          },
          weekdays: {
            monday: 'Lunes',
            tuesday: 'Martes',
            wednesday: 'Miércoles',
            thursday: 'Jueves',
            friday: 'Viernes',
            saturday: 'Sábado',
            sunday: 'Domingo',
          },
          readOnlyHint: 'Puedes ver los ajustes de compensación, pero solo los gerentes pueden actualizarlos.',
          modes: {
            fixedSalaryOnly: 'Solo salario fijo',
            commissionOnly: 'Solo comisión',
            mixed: 'Salario fijo + comisión',
            commissionWithMinimum: 'Comisión con garantía mínima',
            unknown: 'Desconocido'
          },
          error: {
            loading: 'Error al cargar los ajustes de compensación.',
            save: 'Error al guardar los ajustes de compensación.',
            fixedRequired: 'El salario mensual fijo es obligatorio para este modo de pago.',
            commissionRange: 'La tasa de comisión debe estar entre 0 y 100.',
            minimumRequired: 'El salario mínimo mensual es obligatorio para este modo de pago.',
            effectiveFromRequired: 'La fecha de inicio es obligatoria.',
            payDayOfMonthRange: 'El día de pago del mes debe estar entre 1 y 28.',
            payDayOfWeekRequired: 'El día de pago de la semana es obligatorio para pago semanal.',
          },
          success: {
            saved: 'Ajustes de compensación guardados correctamente.'
          }
        }
      },
      storesManagement: {
        title: 'Gestión de Tiendas',
        subtitle: 'Gestionar tiendas y cupones de descuento del plan.',
        tabs: {
          stores: 'Tiendas',
          vouchers: 'Cupones de Descuento',
        },
        stores: {
          description: 'Permitir SMS durante la prueba gratuita a las tiendas que lo solicitaron. Los propietarios aún necesitan Stripe conectado y deben activar el SMS.',
          empty: 'No se encontraron tiendas.',
          allowSmsOnTrial: 'Permitir SMS en prueba',
          table: {
            store: 'Tienda',
            plan: 'Plan',
            status: 'Estado',
            stripe: 'Stripe',
            actions: 'SMS en prueba',
          },
          plan: {
            trial: 'Prueba',
            basesubscription: 'Base',
          },
          status: {
            active: 'Activo',
            trialing: 'En prueba',
            pastdue: 'Vencido',
            suspended: 'Suspendido',
            expired: 'Expirado',
            canceled: 'Cancelado',
            disabled: 'Desactivado',
          },
          stripe: {
            connected: 'Conectado',
            missing: 'No conectado',
          },
          error: {
            loading: 'Error al cargar las tiendas.',
            updateSms: 'Error al actualizar el acceso SMS en prueba.',
          },
          delete: {
            action: 'Eliminar tienda',
            title: 'Eliminar tienda',
            message: 'Esto elimina de forma permanente {{name}} y los servicios, citas, productos, membresías y demás datos que pertenecen solo a esta tienda. Los profesionales y clientes que también pertenecen a otra tienda se conservan. Se eliminan las imágenes y archivos de esta tienda y se cancela la facturación de Salovare de esta tienda. Una cuenta de Stripe conectada, si se creó, permanece abierta en Stripe.',
            confirmLabel: 'Escribe el nombre de la tienda para confirmar',
            confirm: 'Eliminar tienda',
            error: 'Error al eliminar la tienda.',
          },
        },
      },
      planDiscountVouchers: {
        title: 'Cupones de Descuento del Plan',
        subtitle: 'Crea y supervisa descuentos del plan Salovare para tiendas específicas.',
        empty: 'Todavía no hay cupones de descuento del plan.',
        error: {
          loading: 'No se pudieron cargar los cupones de descuento del plan.',
          disable: 'No se pudo desactivar el cupón.',
        },
        actions: {
          create: 'Nuevo cupón',
          generate: 'Generar cupón',
          disable: 'Desactivar',
        },
        disable: {
          title: 'Desactivar cupón',
          message: '¿Desactivar el cupón {{code}}? Ya no se aplicará en futuros checkouts.',
        },
        status: {
          Active: 'Activo',
          Scheduled: 'Programado',
          Expired: 'Expirado',
          Disabled: 'Desactivado',
        },
        table: {
          code: 'Código',
          store: 'Tienda',
          discount: 'Descuento',
          startsAt: 'Inicio',
          endsAt: 'Fin',
          status: 'Estado',
          createdAt: 'Creado',
          actions: 'Acciones',
        },
        form: {
          title: 'Generar cupón de descuento del plan',
          store: 'Tienda',
          selectStore: 'Seleccione una tienda',
          discountPercent: 'Descuento (%)',
          discountPlaceholder: 'p. ej. 50%',
          startsAt: 'Fecha de inicio',
          endsAt: 'Fecha de fin',
          endsAtHelper: 'Deje la fecha de fin vacía para que no expire.',
          errors: {
            storeRequired: 'Seleccione una tienda.',
            discountRequired: 'Introduzca un porcentaje de descuento.',
            discountInvalid: 'Introduzca un descuento entre 1 y 100.',
            startRequired: 'Seleccione la fecha de inicio.',
            startInPast: 'La fecha de inicio no puede estar en el pasado.',
            dateRange: 'La fecha de fin debe ser igual o posterior a la fecha de inicio.',
            create: 'No se pudo generar el cupón.',
          },
        },
      },
      paymentOperations: {
        title: 'Operaciones de Pago',
        subtitle: 'Supervisa la salud de los pagos, fallos de webhook y excepciones operativas.',
        empty: 'No se encontraron registros para los filtros actuales.',
        error: {
          loading: 'Error al cargar los datos de operaciones de pago.',
        },
        actions: {
          refresh: 'Actualizar',
          reconcile: 'Ejecutar reconciliación Stripe',
          reconciling: 'Reconciliando...',
        },
        reconciliation: {
          successDetail: 'Reconciliación Stripe finalizada. Reservas: {{appointmentsRepaired}} reparadas de {{appointmentsMismatched}} discrepancias ({{appointmentsInspected}} inspeccionadas). Suscripciones de tienda: {{storeSubscriptionsRepaired}} reparadas de {{storeSubscriptionsMismatched}} discrepancias ({{storeSubscriptionsInspected}} inspeccionadas). Membresías: {{customerMembershipsRepaired}} reparadas de {{customerMembershipsMismatched}} discrepancias ({{customerMembershipsInspected}} inspeccionadas).',
          error: 'No se pudo ejecutar la reconciliación Stripe.',
        },
        monitoring: {
          title: 'Monitoreo de pagos',
          subtitle: 'Señales operativas y umbrales de alerta para flujos respaldados por Stripe.',
          enabled: 'Alertas activas',
          disabled: 'Alertas desactivadas',
          evaluate: 'Ejecutar evaluación de alertas',
          evaluating: 'Evaluando...',
          evaluateSuccess: 'Evaluación de monitoreo finalizada. {{triggeredCount}} alerta(s) activada(s); {{emailsSent}} correo(s) enviado(s).',
          evaluateError: 'No se pudo ejecutar la evaluación de monitoreo.',
          configuration: 'Ventana {{lookbackMinutes}}m · Enfriamiento {{cooldownMinutes}}m · {{recipientCount}} destinatario(s) configurado(s) en appsettings.',
          activeAlerts: 'Alertas activas',
          noActiveAlerts: 'Ningún umbral de monitoreo está actualmente superado.',
          recentHistory: 'Correos de alerta enviados recientemente',
          signals: {
            openFailedWebhooks: 'Webhooks fallidos abiertos',
            recentFailedWebhooks: 'Fallos recientes de webhook',
            staleProcessingWebhooks: 'Webhooks en procesamiento obsoleto',
            recentPayoutFailures: 'Fallos recientes de payout',
            recentPaymentFailureWebhooks: 'Webhooks recientes de fallo de pago',
          },
        },
        summary: {
          failedWebhooks: 'Webhooks fallidos',
          staleWebhooks: 'Webhooks en procesamiento obsoleto',
          appointmentExceptions: 'Excepciones de pago de reservas',
          storeSubscriptionExceptions: 'Excepciones de suscripción de tienda',
          customerMembershipExceptions: 'Excepciones de membresía del cliente',
          paymentDisputesRequiringAction: 'Disputas que requieren acción',
        },
        tabs: {
          webhooks: 'Eventos de webhook',
          appointments: 'Reservas',
          storeSubscriptions: 'Suscripciones de tienda',
          customerMemberships: 'Membresías de cliente',
          disputes: 'Disputas',
          refunds: 'Reembolsos',
        },
        refunds: {
          issueRefund: 'Emitir reembolso',
          issueTitle: 'Emitir reembolso de reserva',
          view: 'Ver reembolso',
          detailTitle: 'Detalles del reembolso',
          appointmentId: 'ID de reserva',
          loadContext: 'Cargar',
          loadContextError: 'No se pudo cargar el contexto de reembolso.',
          cannotRefund: 'Esta reserva no se puede reembolsar.',
          amount: 'Importe del reembolso',
          amountHelp: 'Deje el saldo restante para un reembolso total o introduzca un importe menor para un reembolso parcial.',
          reason: 'Motivo del reembolso',
          internalNote: 'Nota interna',
          confirmIssue: 'Emitir reembolso',
          issuing: 'Emitiendo...',
          issueSuccess: 'Reembolso emitido correctamente.',
          issueError: 'No se pudo emitir el reembolso.',
          sync: 'Sincronizar desde Stripe',
          syncSuccess: 'Reembolso sincronizado desde Stripe.',
          syncError: 'No se pudo sincronizar el reembolso desde Stripe.',
          columns: {
            store: 'Tienda',
            amount: 'Importe',
            status: 'Estado',
            reason: 'Motivo',
            createdAt: 'Creado el',
            refundId: 'ID de reembolso Stripe',
          },
          reasons: {
            RequestedByCustomer: 'Solicitado por el cliente',
            Duplicate: 'Duplicado',
            Fraudulent: 'Fraudulento',
          },
          context: {
            store: 'Tienda',
            chargeAmount: 'Importe del cobro',
            refundedAmount: 'Ya reembolsado',
            remainingAmount: 'Restante reembolsable',
          },
        },
        disputes: {
          requiresActionOnly: 'Solo requieren acción',
          manage: 'Gestionar disputa',
          detailTitle: 'Detalles de la disputa y evidencia',
          saveDraft: 'Guardar borrador',
          submitEvidence: 'Enviar a Stripe',
          submitting: 'Enviando...',
          sync: 'Sincronizar desde Stripe',
          uploadAttachment: 'Subir adjunto',
          attachments: 'Adjuntos',
          evidenceSubmitted: 'La evidencia ya se envió a Stripe.',
          saveEvidenceSuccess: 'Borrador de evidencia guardado.',
          saveEvidenceError: 'No se pudo guardar el borrador de evidencia.',
          submitEvidenceSuccess: 'Evidencia enviada a Stripe.',
          submitEvidenceError: 'No se pudo enviar la evidencia a Stripe.',
          syncSuccess: 'Disputa sincronizada desde Stripe.',
          syncError: 'No se pudo sincronizar la disputa desde Stripe.',
          uploadSuccess: 'Adjunto subido.',
          uploadError: 'No se pudo subir el adjunto.',
          columns: {
            amount: 'Importe',
            deadline: 'Fecha límite de evidencia',
            disputeId: 'ID de disputa Stripe',
          },
          evidence: {
            customerCommunication: 'Comunicación con el cliente',
            cancellationPolicy: 'Política de cancelación',
            refundPolicy: 'Política de reembolso',
            serviceDocumentation: 'Documentación del servicio',
            uncategorizedText: 'Evidencia adicional',
            internalNotes: 'Notas internas (no se envían a Stripe)',
          },
        },
        webhooks: {
          platform: 'Plataforma',
          viewDetails: 'Ver detalles',
          detailTitle: 'Detalles del evento de webhook',
          retry: 'Reintentar procesamiento',
          retrying: 'Reintentando...',
          retrySuccess: 'El procesamiento del webhook se volvió a encolar.',
          retryError: 'No se pudo reintentar el procesamiento del webhook.',
          filters: {
            status: 'Estado',
            staleOnly: 'Solo procesamiento obsoleto',
          },
          columns: {
            eventType: 'Tipo de evento',
            status: 'Estado',
            externalEventId: 'ID externo del evento',
            connectedAccount: 'Cuenta conectada',
            receivedAt: 'Recibido el',
            lastError: 'Último error',
          },
          detail: {
            correlationId: 'ID de correlación',
            traceId: 'ID de seguimiento',
            deliveryCount: 'Recuento de entregas',
          },
        },
        exceptions: {
          columns: {
            store: 'Tienda',
            date: 'Fecha',
            amount: 'Importe',
            status: 'Estado',
            sessionId: 'Sesión de checkout',
            subscriptionId: 'ID de suscripción',
            endsAt: 'Finaliza el',
            customerId: 'ID del cliente',
          },
        },
      },
      storeRegistrationSteps: {
        step1: {
          title: 'Información Básica',
          description: 'Ingresa el nombre de tu tienda, descripción e idiomas soportados'
        },
        step2: {
          title: 'Información de Contacto',
          description: 'Proporciona detalles de email, teléfono y sitio web'
        },
        step3: {
          title: 'Información de Ubicación',
          description: 'Ingresa la dirección de tu tienda y detalles de ubicación'
        },
        step4: {
          title: 'Horario de Trabajo',
          description: 'Define el horario de atención de tu tienda'
        },
        step5: {
          title: 'Revisar y Enviar',
          description: 'Revisa toda la información antes de enviar'
        }
      },
      storeRegistration: {
        title: 'Registro de Tienda',
        seoTitle: 'Crea tu barbería o salón | Salovare Gratis y Pro',
        seoDescription: 'Registra tu barbería o salón en Salovare. Empieza gratis con un profesional y un local. Pro desde 9,90€/mes. Citas y reservas online incluidas.',
        subtitle: 'Crear una nueva tienda',
        selfServiceTitle: 'Crea Tu Tienda',
        selfServiceSubtitle: 'Configura tu local en Gratis y completa el acceso desde el correo de contraseña',
        success: '¡Tienda registrada exitosamente!',
        error: 'Error al registrar tienda. Inténtalo de nuevo.',
        billingRequiredTitle: 'Configuración de facturación requerida',
        billingRequiredMessage: 'El plan Gratis incluye un local. Mejora a Pro antes de añadir otro local.',
        billingRequiredAction: 'Abrir configuración de pago',
        paidStoreWarning: 'Este local extra se factura a {{price}}/mes.',
        paidStoreWarningProrated: 'Este local extra se factura unos {{proratedPrice}} ahora hasta {{nextBillingDate}}, luego {{price}}/mes.',
        paidStoreConfirmTitle: 'Confirmar local extra',
        paidStoreConfirmMessage: 'La facturación empieza a {{price}}/mes. Configurarás el pago después del registro.',
        paidStoreConfirmMessageProrated: 'Reutilizaremos tu método de pago existente, cobraremos unos {{proratedPrice}} hasta {{nextBillingDate}} y luego {{price}}/mes en esa fecha.',
        paidStoreConfirmAction: 'Crear tienda de pago',
        paidStoreCreatedMessage: 'Tienda creada. Configura la facturación para activar la suscripción de pago.',
        paidStoreProvisionedMessage: 'Tienda creada y facturada con tu método de pago existente por el período restante hasta la próxima fecha de facturación.',
        errors: {
          billingSetupRequired: 'Mejora a Pro en un local existente antes de añadir otro local.',
          phoneAlreadyInUse: 'Este número de teléfono ya está registrado. Inicia sesión si ya tienes cuenta, o usa otro número.',
          emailAlreadyInUse: 'Este email ya está registrado. Inicia sesión si ya tienes cuenta, o usa otro email.',
        },
        name: 'Nombre de la Tienda',
        description: 'Descripción de la Tienda',
        supportedLanguages: 'Idiomas Soportados',
        about: 'Acerca de',
        ownerName: 'Nombre del Responsable',
        ownerEmail: 'Email del Responsable',
        ownerPhone: 'Teléfono del Responsable',
        email: 'Email',
        phone: 'Teléfono',
        storeEmail: 'Email de la Tienda',
        storePhone: 'Teléfono de la Tienda',
        website: 'Sitio Web',
        googleReviewUrl: 'URL de reseña de Google',
        googleReviewUrlPlaceholder: 'https://g.page/r/.../review',
        googleReviewUrlHelp: 'Abre tu ficha de Google Business, copia el enlace “Escribir una reseña” o de compartir, y pégalo aquí. Los clientes reciben un enlace corto de Salovare, no esta URL.',
        ownerSectionTitle: 'Información del Responsable',
        storeContactSectionTitle: 'Información de Contacto de la Tienda',
        address: 'Dirección',
        door: 'Puerta',
        additionalInfo: 'Complemento',
        city: 'Ciudad',
        state: 'Provincia',
        zipCode: 'Código Postal',
        country: 'País',
        basicInfo: {
          storeName: 'Nombre de la Tienda',
          storeNameLabel: 'Nombre de la Tienda',
          slug: 'Slug (URL)',
          currency: 'Moneda',
          currencyHelper: 'Selecciona la moneda utilizada para precios en tu tienda',
          selectCurrency: 'Seleccionar Moneda',
          descriptionLabel: 'Descripción',
          descriptionPlaceholder: 'Describe tu tienda, servicios y lo que la hace especial...',
          supportedLanguages: 'Idiomas Soportados',
          supportedLanguagesHelper: 'Selecciona los idiomas en los que tu tienda estará disponible. Debes proporcionar descripciones en todos los idiomas seleccionados.',
          storeLanguage: 'Idioma Predeterminado de la Tienda',
          storeLanguageHelper: 'Elige el idioma que verán los clientes cuando su idioma preferido no esté disponible para esta tienda.',
          selectStoreLanguage: 'Seleccionar Idioma Predeterminado',
          storeLanguageDisabledHelper: 'Selecciona al menos un idioma soportado antes de elegir el idioma predeterminado de la tienda.',
          about: 'Acerca de',
          aboutHelper: 'Describe tu tienda en cada idioma seleccionado',
          aboutLabel: 'Acerca de ({{language}})',
          aboutPlaceholder: 'Describe tu tienda en {{language}}...',
          timeZone: 'Zona Horaria',
          timeZoneHelper: 'Elige la zona horaria utilizada para evaluar el horario de atención y la disponibilidad de reservas de esta tienda',
          selectTimeZone: 'Seleccionar Zona Horaria',
          typeToSearchTimeZone: 'Escribe para buscar una zona horaria',
          topTimeZones: 'Zonas Horarias Populares',
          allTimeZones: 'Todas las Zonas Horarias',
          slotInterval: 'Intervalo de Horario',
          slotIntervalHelper: 'Define la duración de cada intervalo de horario mostrado a los clientes al reservar',
          selectSlotInterval: 'Seleccionar Intervalo',
          slotIntervalFive: '5 minutos',
          slotIntervalTen: '10 minutos',
          slotIntervalFifteen: '15 minutos',
          slotIntervalThirty: '30 minutos',
          slotIntervalSixty: '60 minutos'
        },
        contactInfo: {
          title: 'Información de Contacto',
          selfServiceDescription: 'Ingresa los datos del responsable y la información pública de contacto de la tienda',
          email: 'Email',
          phone: 'Teléfono',
          website: 'Sitio Web'
        },
        locationInfo: {
          title: 'Información de Ubicación',
          address: 'Dirección',
          door: 'Puerta',
          additionalInfo: 'Complemento',
          city: 'Ciudad',
          state: 'Provincia',
          zipCode: 'Código Postal',
          country: 'País'
        },
        services: {
          title: 'Servicios',
          helper: 'Selecciona los servicios que tu tienda ofrece',
          addService: 'Agregar Servicio',
          serviceName: 'Nombre del Servicio',
          serviceDescription: 'Descripción del Servicio',
          serviceDuration: 'Duración',
          servicePrice: 'Precio',
          removeService: 'Eliminar Servicio',
          viewService: 'Ver Servicio',
          editService: 'Editar Servicio',
          saveChanges: 'Guardar Cambios',
          services: 'Servicios',
          noServicesAdded: 'No se agregaron servicios',
          suggestions: 'Sugerencias de Servicios',
          selectSuggestion: 'Haz clic en una sugerencia para llenar automáticamente el formulario',
          defaultName: 'Nombre',
          defaultDescription: 'Descripción',
          serviceSuggestions: {
            mensHaircut: {
              name: 'Corte de Cabello Masculino',
              description: 'Corte de cabello tradicional para hombres'
            },
            beardTrim: {
              name: 'Barba',
              description: 'Recortar y dar forma a la barba'
            },
            haircutBeard: {
              name: 'Corte + Barba',
              description: 'Servicio combinado de corte y barba'
            },
            womensHaircut: {
              name: 'Corte Femenino',
              description: 'Corte de cabello para mujeres'
            },
            kidsHaircut: {
              name: 'Corte Infantil',
              description: 'Corte de cabello para niños'
            },
            eyebrow: {
              name: 'Cejas',
              description: 'Diseño y modelado de cejas'
            },
            haircutEyebrow: {
              name: 'Corte + Cejas',
              description: 'Servicio combinado de corte y cejas'
            },
            haircutBeardEyebrow: {
              name: 'Corte + Barba + Cejas',
              description: 'Servicio completo de cuidado personal con corte, barba y cejas'
            }
          }
        },
        businessHours: {
          title: 'Horario de Trabajo',
          helper: 'Establece el horario de trabajo de tu tienda para cada día de la semana',
          applyToAllTitle: '¿Aplicar a Otros Días?',
          applyToAllMessage: 'Cambiaste el {{field}} para {{day}} a {{value}}. ¿Te gustaría aplicar este cambio a todos los demás días también?',
          openTime: 'Abre a las',
          closeTime: 'Cierra a las',
          to: 'a',
          applyToCurrentDay: 'Aplicar solo a {{day}}',
          applyToAllDays: 'Aplicar a todos los días',
          addPeriod: 'Añadir período',
          removePeriod: 'Eliminar período'
        },
        review: {
          title: 'Revisa la Información de Tu Tienda',
          subtitle: 'Por favor, revisa toda la información a continuación antes de enviar',
          basicInfo: 'Información Básica',
          contactInfo: 'Información de Contacto',
          locationInfo: 'Información de Ubicación',
          services: 'Servicios',
          businessHours: 'Horario de Trabajo'
        },
        validation: {
          nameRequired: 'El nombre de la tienda es obligatorio',
          slugRequired: 'La URL de la tienda es obligatoria',
          slugTooShort: 'La URL de la tienda debe tener al menos 3 caracteres',
          slugTooLong: 'La URL de la tienda no puede superar los 30 caracteres',
          slugInvalid: 'Usa solo letras minúsculas, números y guiones. No puede empezar ni terminar con un guion.',
          slugReserved: 'No disponible',
          slugTaken: 'No disponible',
          slugCheckFailed: 'No se pudo comprobar si esta URL está disponible. Inténtalo de nuevo.',
          descriptionRequired: 'La descripción de la tienda es obligatoria',
          languagesRequired: 'Al menos un idioma debe ser seleccionado',
          aboutRequired: 'El contenido sobre es obligatorio para todos los idiomas seleccionados',
          storeLanguageRequired: 'El idioma predeterminado de la tienda es obligatorio',
          storeLanguageMustBeSupported: 'El idioma predeterminado de la tienda debe estar entre los idiomas soportados',
          ownerNameRequired: 'El nombre del responsable es obligatorio',
          ownerEmailRequired: 'El email del responsable es obligatorio',
          ownerPhoneRequired: 'El teléfono del responsable es obligatorio',
          emailRequired: 'El email es obligatorio',
          storeEmailRequired: 'El email de la tienda es obligatorio',
          emailInvalid: 'Por favor, ingresa una dirección de email válida',
          phoneRequired: 'El número de teléfono es obligatorio',
          storePhoneRequired: 'El teléfono de la tienda es obligatorio',
          addressRequired: 'La dirección es obligatoria',
          cityRequired: 'La ciudad es obligatoria',
          stateRequired: 'La provincia es obligatoria',
          zipCodeRequired: 'El código postal es obligatorio',
          countryRequired: 'El país es obligatorio',
          timeZoneRequired: 'La zona horaria es obligatoria',
          servicesRequired: 'Al menos un servicio debe ser seleccionado',
          businessHoursRequired: 'Al menos un día debe estar habilitado para el horario de trabajo',
          serviceNameRequired: 'El nombre del servicio es obligatorio para todos los idiomas seleccionados',
          servicePriceRequired: 'El precio del servicio es obligatorio y debe ser mayor que 0',
          openTimeAfterCloseTime: 'La hora de apertura no puede ser después o igual a la hora de cierre',
          periodsOverlap: 'Los períodos no pueden superponerse'
        },
        slug: {
          checking: 'Comprobando disponibilidad...',
          available: 'Disponible',
        }
      },
      landing: {
        nav: {
          features: 'Funcionalidades',
          howItWorks: 'Cómo funciona',
          pricing: 'Precios',
          faq: 'FAQ',
          signIn: 'Iniciar sesión',
          getStarted: 'Empezar',
          goToControlPanel: 'Ir al panel de control',
          openMenu: 'Abrir menú',
        },
        hero: {
          eyebrow: 'Software para salud y belleza',
          title: 'Cada cita, cada reserva, cada pago bajo control.',
          subtitle: 'Salovare gestiona la recepción, la página de reservas online, los planes de membresía, los paquetes de servicios y la nómina del equipo para negocios de salud y belleza. Empieza gratis con un profesional y mejora el día que contrates.',
          ctaPrimary: 'Crear mi local',
          ctaSecondary: 'Iniciar sesión',
          specs: {
            free: { label: 'Plan Gratis', value: 'Un profesional, sin caducidad' },
            pro: { label: 'Pro', value: '{{price}}/mes' },
            card: { label: 'Tarjeta de crédito', value: 'No hace falta' },
            commitment: { label: 'Compromiso', value: 'Cancela cuando quieras' },
          },
          preview: {
            title: 'Hoy',
            meta: 'Recepción',
            room: 'Sala {{number}}',
            services: {
              facial: 'Facial',
              massage: 'Masaje',
              haircut: 'Corte',
              manicure: 'Manicura',
            },
            statuses: {
              confirmed: 'Confirmada',
              membership: 'Membresía',
              walkIn: 'Walk-in',
            },
          },
        },
        features: {
          overline: 'Plataforma',
          title: 'Hecho para cómo funciona un estudio de verdad.',
          subtitle: 'Nueve módulos que cubren todo el día: la agenda en recepción, la página de reservas online, las membresías y los paquetes para tus clientes habituales y la nómina a fin de mes.',
          appointments: {
            title: 'Agenda del equipo',
            description: 'Ve el día de cada profesional de un vistazo. Reserva walk-ins en recepción, incluso fuera del horario de apertura cuando lo necesites.',
          },
          team: {
            title: 'Equipo, roles y ausencias',
            description: 'Invita al personal, define quién acepta reservas online, gestiona permisos y bloquea ausencias en el calendario.',
          },
          services: {
            title: 'Servicios y categorías',
            description: 'Organiza cabello, piel, bienestar y extras con duraciones, precios y tarifas personalizadas por profesional.',
          },
          booking: {
            title: 'Reservas online',
            description: 'Comparte una página con tu marca para que el cliente elija servicio, profesional y hora sin llamar al estudio.',
          },
          memberships: {
            title: 'Planes de Membresía y Paquetes de Servicios',
            description: 'Vende planes de membresía mensuales y paquetes de servicios prepagados, como 10 sesiones. Las visitas restantes se cuentan solas y se descuentan a medida que el cliente reserva.',
          },
          customers: {
            title: 'Ficha de clientes',
            description: 'Guarda walk-ins y clientes habituales en una sola lista, con el historial de reservas listo cuando vuelvan.',
          },
          reminders: {
            title: 'Recordatorios que evitan ausencias',
            description: 'Las confirmaciones y recordatorios por email están incluidos en todos los planes. Pro añade SMS y WhatsApp, cobrados por mensaje.',
          },
          payroll: {
            title: 'Nómina y comisiones',
            description: 'Controla lo que cada profesional ganó en servicios, membresías y paquetes, con una vista de pagos pensada para el salón.',
          },
          dashboard: {
            title: 'Panel del día',
            description: 'Reservas de hoy, pagos pendientes, ingresos del mes y membresías activas en un solo lugar.',
          },
        },
        howItWorks: {
          overline: 'Onboarding',
          title: 'Abre las puertas en cuatro pasos.',
          subtitle: 'El registro es guiado desde la primera pantalla. La mayoría de los estudios empieza a aceptar reservas esa misma tarde.',
          steps: {
            register: {
              title: 'Crea tu local',
              description: 'Regístrate con los datos de tu negocio, contacto y horario en un flujo guiado.',
            },
            setup: {
              title: 'Añade servicios y horarios',
              description: 'Configura tu menú de servicios, horarios de apertura y perfil del local para que los clientes te descubran.',
            },
            team: {
              title: 'Invita a tu equipo',
              description: 'Añade miembros del equipo, asigna roles y da a cada profesional acceso a su agenda.',
            },
            book: {
              title: 'Reserva en recepción o online',
              description: 'Comparte el enlace de reservas para citas online y ofrece planes de membresía y paquetes de servicios a tus clientes habituales.',
            },
          },
        },
        pricing: {
          overline: 'Planes',
          title: 'Gratis para empezar. Pro cuando crezcas.',
          subtitle: 'Gratis cubre un profesional todo el tiempo que necesites. Pro abre el resto de la plataforma — profesionales extra, locales extra, pagos online, stock, nómina y mensajería.',
          plans: {
            free: {
              name: 'Gratis',
              tagline: 'Para quien trabaja solo',
              price: 'GRATIS',
              priceNote: 'Sin tarjeta de crédito y sin fecha de caducidad.',
              note: 'Mejora a Pro el día que contrates. No hay nada que migrar ni que volver a configurar.',
              cta: 'Crear local gratis',
              values: {
                stores: 'Local único',
                professionals: 'Profesional único',
                dashboard: 'Panel básico',
                payments: 'Pago en el local',
              },
            },
            pro: {
              name: 'Pro',
              tagline: 'Para equipos que crecen y varios locales',
              priceSuffix: '/mes',
              priceNote: 'Durante los primeros {{months}} meses; después {{price}}/mes. Cancela cuando quieras.',
              cta: 'Empieza gratis y mejora en la app',
              values: {
                stores: 'Varios locales',
                professionals: 'Profesionales ilimitados',
                dashboard: 'Panel completo',
                payments: 'Pagos online y en el local',
              },
            },
          },
          features: {
            appointments: 'Citas y reservas online',
            emailReminders: 'Recordatorios por email',
            realtimeChat: 'Chat en tiempo real con clientes en la app',
            deposits: 'Señal y prepago al reservar',
            installments: 'Pago a plazos con Klarna',
            refunds: 'Política de cancelación con reembolsos automáticos',
            productsStock: 'Productos y stock',
            membershipPlans: 'Planes de membresía y paquetes de servicios',
            teamPayroll: 'Nómina del equipo',
            waitlistHours: 'Lista de espera y horario adicional',
            additionalCosts: 'Costes adicionales en días o periodos específicos',
            customPermissions: 'Permisos personalizados por miembro del equipo',
            smsReminders: 'Recordatorios por SMS',
            whatsappReminders: 'Recordatorios por WhatsApp',
            customBranding: 'Colores de marca personalizados',
            prioritySupport: 'Soporte prioritario',
            marketingAutomation: 'Mensajes de marketing automáticos',
          },
          rates: {
            perMessage: '{{price}} por mensaje',
          },
          comingSoon: 'Próximamente',
          addOns: {
            title: 'Complementos',
            additionalStore: 'Local adicional',
            additionalProfessional: 'Profesional adicional',
            perMonth: '{{price}}/mes',
          },
          footnote: 'Precios en EUR. Pro cuesta {{introPrice}}/mes durante los primeros {{months}} meses y después {{regularPrice}}/mes. Los locales extra cuestan {{extraStorePrice}}/mes en la misma factura. Los profesionales adicionales cuestan {{teamMemberPrice}}/mes. El SMS cuesta {{smsPrice}} y el WhatsApp {{whatsAppPrice}} por mensaje. Los precios de los planes de membresía y de los paquetes de servicios los define cada local.',
        },
        faq: {
          overline: 'Preguntas',
          title: 'Antes de registrarte.',
          subtitle: 'Respuestas claras sobre Gratis, Pro y cómo Salovare encaja en el día a día del estudio.',
          items: {
            price: {
              question: '¿Cuánto cuesta Salovare?',
              answer: 'Gratis es 0€ para un local con un profesional, sin fecha de caducidad. Pro es {{introPrice}}/mes durante los primeros {{months}} meses y {{regularPrice}}/mes después. Los locales extra cuestan {{extraStorePrice}}/mes en la misma factura. Los profesionales adicionales cuestan {{teamMemberPrice}}/mes y la mensajería se cobra por mensaje.',
            },
            plans: {
              question: '¿Cuál es la diferencia entre Gratis y Pro?',
              answer: 'Gratis incluye citas, reservas online, recordatorios por email, el panel básico y el chat con clientes en la app para un profesional en un local, con los clientes pagando en el local. Pro añade pagos online con señal, prepago, pago a plazos y reembolsos automáticos, productos y stock, planes de membresía y paquetes de servicios, nómina del equipo, lista de espera y horario adicional, permisos personalizados por miembro del equipo, recordatorios por SMS y WhatsApp, marca personalizada, el panel completo y soporte prioritario.',
            },
            messaging: {
              question: '¿Cómo funcionan los recordatorios por SMS y WhatsApp?',
              answer: 'Están disponibles en Pro y se cobran por mensaje en tu ciclo de suscripción: {{smsPrice}} por SMS y {{whatsAppPrice}} por mensaje de WhatsApp. Los recordatorios por email están incluidos en todos los planes.',
            },
            healthBeauty: {
              question: '¿Sirve para el sector de la salud y la belleza?',
              answer: 'Sí. Salovare está hecho para negocios de salud y belleza — salones, barberías, spas, clínicas y estudios de bienestar: agenda del equipo, walk-ins, reservas online, planes de membresía, paquetes de servicios, recordatorios y nómina.',
            },
            multiStore: {
              question: '¿Puedo gestionar más de un local?',
              answer: 'Sí, en Pro. Gratis incluye un local. Los locales extra cuestan {{extraStorePrice}}/mes cada uno en la misma factura del dueño. Si el pago se interrumpe, el local más antiguo permanece en Gratis y los demás se desactivan.',
            },
            creditCard: {
              question: '¿Necesito tarjeta de crédito para empezar?',
              answer: 'No. Puedes crear tu local en Gratis sin añadir un método de pago. Añade una tarjeta cuando pases a Pro.',
            },
          },
        },
        seo: {
          title: 'Salovare | Software para salud y belleza — Gratis y Pro desde 9,90€/mes',
          description: 'Software de citas para el sector de la salud y la belleza. Empieza gratis con un profesional y un local. Pro desde 9,90€/mes para plazas extra, productos, membresías, paquetes de servicios y nómina.',
        },
        cta: {
          overline: 'Empieza ahora',
          title: 'Recibe tu próxima reserva en Salovare.',
          subtitle: 'Crea tu local en unos minutos. Sin tarjeta de crédito, sin contrato y sin coste de instalación.',
          button: 'Crear mi local',
        },
        footer: {
          signIn: 'Inicio de sesión del equipo',
          register: 'Crear local',
          copyright: '© {{year}} Salovare. Todos los derechos reservados.',
        },
      },
      settings: 'Configuración',
      home: {
        welcomeBack: '¡Bienvenido de vuelta, {{name}}!',
        description: 'Esta es tu página de inicio donde pronto verás tu panel con reservas, estadísticas y acciones rápidas. Por ahora, disfruta de esta interfaz limpia y moderna diseñada específicamente para las necesidades de tu salón.',
      },
      dashboard: {
        title: 'Panel',
        subtitle: 'Resumen de reservas, ventas y rendimiento de la tienda',
        selectStore: 'Seleccione una tienda para ver el panel.',
        rebuild: 'Reconstruir datos del panel',
        rebuildConfirmTitle: '¿Reconstruir datos del panel?',
        rebuildConfirmMessage: 'Esto reagrega los últimos 12 meses de estadísticas del panel a partir de reservas e ingresos. Los datos agregados existentes en ese período serán reemplazados.',
        rebuildSuccess: 'Datos del panel reconstruidos correctamente.',
        rebuildError: 'Error al reconstruir los datos del panel.',
        activityTypes: {
          appointmentCreated: 'Nueva reserva para {{customerName}}',
          appointmentPaid: 'Pago recibido de {{customerName}}',
          appointmentCompleted: 'Reserva completada para {{customerName}}',
          appointmentCancelled: 'Reserva cancelada para {{customerName}}',
          appointmentNoShow: 'Cliente no asistió: {{customerName}}',
          revenueRecognized: 'Ingreso reconocido para {{customerName}}',
          membershipEnrolled: '{{customerName}} se inscribió en {{planName}}',
          membershipCanceled: '{{customerName}} canceló {{planName}}',
          membershipPaused: '{{customerName}} pausó {{planName}}',
          productSold: 'Venta de producto para {{customerName}}',
          appointmentRefunded: 'Reembolso a {{customerName}}',
          productSaleVoided: 'Venta de producto revertida para {{customerName}}',
        },
      },
      managementAppointments: {
        title: 'Reservas',
        subtitle: 'Vista de calendario para la tienda y el profesional seleccionados.',
        today: 'Hoy',
        professional: 'Profesional',
        myAgenda: 'Mi agenda',
        selectStore: 'Seleccione una tienda para ver la agenda.',
        noPermission: 'No tienes permiso para ver la agenda de reservas.',
          selectProfessionalToView: 'Seleccione un profesional para ver la agenda.',
        loadError: 'Error al cargar la agenda de reservas.',
        unnamedService: 'Servicio sin nombre',
        status: 'Estado',
        customer: 'Cliente',
        services: 'Servicios',
        service: 'Servicio',
        products: 'Productos',
        product: 'Producto',
        totalPrice: 'Total',
        additionalInfo: 'Información adicional',
        timeRange: 'Rango horario',
        professionalsPerService: 'Profesional por servicio',
        professionals: 'Profesionales',
        noAdditionalInfo: 'Sin notas del cliente.',
        noConsultationNotes: 'Aún no hay notas de consulta.',
        openCustomerDetails: 'Abrir detalles del cliente',
        unnamedProfessional: 'Profesional sin asignar',
        detailsTitle: 'Detalles de la reserva',
        timeOff: 'Vacaciones',
        timeOffDetailsTitle: 'Detalles de vacaciones',
        timeOffType: 'Tipo',
        markCompleted: 'Marcar como completada',
        confirmOnRequest: 'Confirmar petición',
        declineOnRequest: 'Rechazar petición',
        confirmingOnRequest: 'Confirmando...',
        decliningOnRequest: 'Rechazando...',
        confirmOnRequestSuccess: 'Reserva confirmada.',
        declineOnRequestSuccess: 'Petición rechazada.',
        onRequestRespondError: 'No se pudo responder a la reserva bajo petición.',
        openToAnyProfessional: 'Cualquier profesional',
        selectProfessionalToConfirm: 'Seleccione el profesional que responde',
        markCancelled: 'Marcar como cancelada',
        markNoShow: 'Marcar como no asistió',
        completing: 'Completando...',
        cancelling: 'Cancelando...',
        markingNoShow: 'Marcando no asistencia...',
        completeError: 'Error al marcar la reserva como completada.',
        statusUpdateError: 'Error al actualizar el estado de la reserva.',
        paymentStatusLabel: 'Estado del pago',
        markPaid: 'Marcar como pagado',
        markingPaid: 'Marcando...',
        markPaidError: 'Error al marcar la reserva como pagada.',
        changeToRegular: 'Cambiar a reserva regular',
        changingToRegular: 'Cambiando...',
        changeToRegularError: 'Error al cambiar la reserva a regular.',
        amountDue: 'Importe a pagar',
        amountPaid: 'Importe pagado',
        additionalCost: 'Coste adicional de horario',
        coveredByMembership: 'Cubierto por membresía',
        pendingMembershipEnrollment: 'Membresía a pagar en el salón',
        membershipPlanNote: 'Incluido con {{plan}}',
        membershipPriceNote: 'Paga la membresía {{plan}} en el salón',
        confirm: {
          completeTitle: '¿Marcar reserva como completada?',
          completeMessage: 'Esto marcará la reserva como completada y pagada. Esta acción no se puede deshacer.',
          completeEnrollTitle: '¿Completar e inscribir en la membresía?',
          completeEnrollMessage: 'Esto completará la reserva e inscribirá al cliente en {{plan}}. Importe a pagar: {{amount}}. Esta acción no se puede deshacer.',
          completeAndBookNext: 'Confirmar y reservar siguiente',
          cancelTitle: '¿Marcar reserva como cancelada?',
          cancelMessage: 'Esto libera el horario. Esta acción no se puede deshacer.',
          noShowTitle: '¿Marcar reserva como no asistió?',
          noShowMessage: 'Esto registra que el cliente no asistió y libera el horario. Esta acción no se puede deshacer.',
          deleteTitle: '¿Eliminar reserva?',
          deleteMessage: 'La reserva se eliminará de la agenda y de los informes. Esta acción no se puede deshacer.',
          markPaidTitle: '¿Marcar reserva como pagada?',
          markPaidMessage: 'Esto registrará la reserva como pagada. Esta acción no se puede deshacer.',
          changeToRegularTitle: '¿Cambiar a reserva regular?',
          changeToRegularMessage: 'Esto elimina la cobertura de membresía de la reserva y aplica los precios regulares de los servicios. Esta acción no se puede deshacer.',
          confirmOnRequestTitle: '¿Confirmar esta reserva bajo petición?',
          confirmOnRequestMessage: 'Esto confirma el horario adicional y lo asigna al profesional seleccionado.',
          declineOnRequestTitle: '¿Rechazar esta reserva bajo petición?',
          declineOnRequestMessage: 'Si ningún otro profesional acepta, la reserva se cancelará y se avisará al cliente.'
        },
        edit: {
          title: 'Editar reserva',
          action: 'Editar reserva',
          date: 'Fecha',
          submit: 'Guardar cambios',
          submitting: 'Guardando...',
          submitError: 'Error al actualizar la reserva.',
          loadError: 'Error al cargar los datos de la reserva.',
          validation: {
            dateRequired: 'Seleccione una fecha.',
            startTimeRequired: 'Seleccione una hora de inicio.'
          }
        },
        delete: {
          action: 'Eliminar reserva',
          error: 'Error al eliminar la reserva.'
        },
        paymentStatus: {
          paid: 'Pagado',
          pendingPayment: 'Pago pendiente',
          unpaid: 'No pagado'
        },
        views: {
          oneDay: '1 día',
          threeDays: '3 días',
          sevenDays: '7 días',
          oneDayShort: '1D',
          threeDaysShort: '3D',
          sevenDaysShort: '7D'
        },
        bookFab: 'Hacer reserva',
        book: {
          title: 'Hacer reserva',
          appointmentContext: 'Horario de la reserva',
          startTime: 'Hora de inicio',
          selectStartTime: 'Seleccionar hora de inicio',
          tapToChangeTime: 'Toca para cambiar',
          customerType: 'Cliente',
          customerTypeExisting: 'Cliente existente',
          customerTypeNew: 'Cliente nuevo',
          customerTypeAnonymous: 'Anónimo / walk-in',
          customerSearchPlaceholder: 'Buscar por nombre, email o teléfono',
          guestName: 'Nombre del invitado (opcional)',
          guestNamePlaceholder: 'Dejar vacío para walk-in',
          services: 'Servicios',
          products: 'Productos',
          productsHelp: 'Añade productos para vender con esta reserva. Se cobran en el pago.',
          professionals: 'Profesional por servicio',
          selectProfessional: 'Seleccionar profesional',
          membership: 'Membresía',
          membershipHelp: 'Elige una membresía existente o inicia un plan. El plan comienza cuando esta cita se complete y se pague en tienda.',
          pendingEnrollmentHelp: 'El cliente se inscribirá cuando esta cita se complete y se pague. Hasta entonces la membresía queda pendiente.',
          planServicesLocked: 'Los servicios del plan están seleccionados y no se pueden quitar. Aún puedes añadir servicios extra.',
          noMembership: 'Ninguna membresía cubre los servicios seleccionados.',
          noMembershipRedemption: 'Sin membresía',
          subscribedTag: 'Suscrito',
          enrollInStoreTag: 'Inscribir en tienda',
          notes: 'Notas',
          notesPlaceholder: 'Notas opcionales para esta reserva',
          submit: 'Confirmar reserva',
          submitting: 'Reservando...',
          success: 'Reserva confirmada con éxito.',
          submitError: 'Error al confirmar la reserva.',
          loadError: 'Error al cargar los datos de reserva.',
          noCustomerReadPermission: 'No tienes permiso para buscar clientes. Elige cliente nuevo o anónimo.',
          warnings: {
            closedDay: 'Esta tienda normalmente está cerrada en la fecha seleccionada. Aun así puedes confirmar la reserva manualmente.',
            outsideHours: 'Esta hora está fuera del horario de apertura de la tienda. Aun así puedes confirmar la reserva manualmente.',
            slotConflict: 'Esta hora se superpone con una reserva existente del profesional seleccionado. Aun así puedes confirmar la reserva manualmente.'
          },
          validation: {
            servicesRequired: 'Selecciona al menos un servicio.',
            customerRequired: 'Selecciona un cliente.',
            professionalRequired: 'Asigna un profesional a cada servicio seleccionado.',
            dateRequired: 'Selecciona una fecha.',
            startTimeRequired: 'Selecciona una hora de inicio.'
          }
        },
        membershipRenewalRisk: 'Riesgo de renovación de membresía: la fecha de la cita es igual o posterior al fin del período actual ({{date}}). Si la membresía no se renueva, se aplicará el precio regular del servicio.'
      },
      notifications: {
        title: 'Notificaciones',
        newAppointment: 'Nueva reserva confirmada para John Doe',
        appointmentCancelled: 'Reserva cancelada por Sarah Smith',
        systemMaintenance: 'Mantenimiento del sistema programado para esta noche',
        twoMinutesAgo: 'Hace 2 minutos',
        oneHourAgo: 'Hace 1 hora',
        threeHoursAgo: 'Hace 3 horas',
      },
      customerNotifications: {
        title: 'Notificaciones',
        empty: 'Aún no hay notificaciones.',
        markAllRead: 'Marcar todas como leídas',
        seeAll: 'Ver todas',
        page: {
          title: 'Notificaciones',
          subtitle: 'Mensajes y actualizaciones de esta tienda',
          error: {
            loading: 'No se pudieron cargar las notificaciones.',
          },
        },
      },
      staffNotifications: {
        title: 'Notificaciones',
        empty: 'Todavía no hay notificaciones.',
        markAllRead: 'Marcar todas como leídas',
        enableSounds: 'Activar sonidos de notificación',
        seeAll: 'Ver todas',
        page: {
          title: 'Notificaciones',
          subtitle: 'Últimas notificaciones in-app para ti',
          table: {
            title: 'Título',
            message: 'Mensaje',
            dateTime: 'Fecha y hora',
            status: 'Estado',
          },
          status: {
            read: 'Leída',
            unread: 'No leída',
          },
          error: {
            loading: 'No se pudieron cargar las notificaciones.',
            noStoreSelected: 'No hay tienda seleccionada.',
          },
        },
		newAppointment: {
          title: 'Nueva cita',
          body: 'Se ha reservado una nueva cita para ti.',
          bodyWithDetails: '{{customerName}} reservó el {{date}} de {{begin}} a {{end}}. Ver detalles.',
        },
        cancelledAppointment: {
          title: 'Cita cancelada',
          body: 'Se canceló una cita asignada a ti.',
        },
        supportChat: {
          title: 'Nuevo chat de cliente',
          body: 'Un cliente inició una conversación con la tienda.',
          messageTitle: 'Nuevo mensaje de chat',
          messageBody: '{{customerName}} envió un mensaje.'
        },
        platformSupport: {
          title: '{{storeName}}',
          body: '{{message}}',
        },
        waitlistJoined: {
          title: 'Un cliente se apuntó a la lista de espera',
          body: 'Un cliente se apuntó a la lista de espera con fechas y horarios preferidos.',
        },
        waitlistSlotOpened: {
          title: 'Se abrió un hueco de la lista de espera',
          body: 'Se abrió un hueco que coincide con un cliente de la lista de espera.',
        },
        appointmentConfirmationRequested: {
          title: 'Reserva bajo petición',
          body: 'Un cliente pidió un horario fuera del horario regular.',
          bodyWithDetails: '{{customerName}} pidió {{date}} de {{begin}} a {{end}}. Confirma o rechaza.',
        },
        updatedAppointment: {
          title: 'Cita actualizada',
          body: 'Se actualizó una cita asignada a ti.',
          bodyWithDetails: 'Se actualizó la cita de {{customerName}} el {{date}} de {{begin}} a {{end}}.',
        },
        other: {
          title: 'Notificación',
          body: 'Tienes una nueva notificación.',
        },
      },
      pushNotifications: {
        enable: 'Activar notificaciones push',
        promptTitle: '¿Activar notificaciones push?',
        notNow: 'Ahora no',
        staffHint: 'Recibe avisos de citas nuevas, cambios y cancelaciones aunque Salovare esté cerrado.',
        customerHint: 'Recibe avisos cuando una cita se reserve, cambie, se cancele o esté por llegar.',
        denied: 'Las notificaciones están bloqueadas en el navegador. Permítelas en los ajustes del sitio para activarlas.',
        unsupported: 'Este navegador no admite notificaciones push.',
        unavailable: 'Las notificaciones push todavía no están disponibles.',
        error: 'No se pudieron actualizar las notificaciones push. Inténtalo de nuevo.',
      },
      store: {
        clickToChange: 'Haz clic para cambiar de tienda',
        currentStore: 'Tienda actual',
        selectStore: 'Seleccionar Tienda',
        registrationTitle: 'Registro de Tienda',
        registrationSubtitle: 'Crear una nueva tienda',
        accessDenied: 'Acceso denegado. Necesitas permiso para crear tiendas.',
      },
      user: {
        clickToOpenMenu: 'Haz clic para abrir menú',
        profile: 'Perfil',
        logout: 'Cerrar sesión',
        defaultName: 'Usuario',
      },
      storePlan: {
        upgradeRequired: 'Esta función está disponible en el plan Pro. Mejora el plan para desbloquearla.',
        upgradeCta: 'Mejorar a Pro',
        upgradeToPro: 'Mejorar a Pro',
        freePlanTitle: 'Plan gratuito',
        freeBadge: 'Gratis',
        freePlanDescription: 'Tu local está en el plan Gratis. Mejora a Pro para profesionales extra, locales extra, productos, membresías, nómina, lista de espera, horario adicional y SMS.',
        lapsedTitle: 'Reactivar Pro',
        lapsedBadge: 'Pro finalizado',
        lapsedDescription: 'Tu suscripción Pro ha terminado. Reactívala para recuperar profesionales extra, locales extra y funciones Pro. El local sigue online en el plan Gratis.',
        reactivateAction: 'Reactivar Pro',
        reactivateTitle: 'Reactiva tu cuenta',
        reactivateDescription: 'Tu plan Pro ha terminado. Reactívalo para recuperar profesionales extra, locales extra y funciones Pro. El local sigue online.',
        paymentFailedTitle: 'Actualiza tu método de pago',
        paymentFailedDescription: 'No pudimos procesar el último pago. Actualiza la tarjeta en el portal de facturación para mantener Pro.',
        paymentFailedAction: 'Actualizar pago',
        extraStoreTitle: 'Los locales extra son una función Pro',
        extraStoreMessage: 'El plan Gratis incluye un local. Mejora a Pro para añadir otro local.',
        extraMemberTitle: 'Los profesionales extra son una función Pro',
        extraMemberMessage: 'El plan Gratis incluye un profesional. Mejora a Pro para añadir miembros del equipo.',
        smsUpgradeTitle: 'El SMS está incluido en Pro',
        smsUpgradeMessage: 'Mejora a Pro para enviar SMS y WhatsApp de citas.',
        prioritySupportBadge: 'Pro',
        upgradeDialog: {
          eyebrow: 'Salovare Pro',
          title: 'Desbloquea toda la plataforma',
          lapsedTitle: 'Recupera Pro',
          subtitle: 'Crece más allá de un solo profesional con pagos, herramientas de equipo y mensajería.',
          priceNote: '{{introPrice}}/mes durante los primeros {{months}} meses, luego {{regularPrice}}/mes. Cancela cuando quieras.',
          askOwner: 'Pide al dueño del local que active el plan Pro.',
          crownAria: 'Función Pro. Abrir detalles de la mejora.',
          crownTooltip: 'Función Pro',
          lockedPageTitle: 'Esta es una función Pro',
          lockedPageSubtitle: 'Mejora el plan para desbloquear productos, membresías, nómina y el resto de herramientas Pro.',
        },
      },
      ownerProfile: {
        title: 'Perfil',
        subtitle: 'Tus datos personales y ausencias',
        saved: 'Perfil guardado',
        tabs: {
          profile: 'Perfil',
          timeOff: 'Ausencias',
        },
        error: {
          load: 'No se pudo cargar tu perfil.',
          save: 'No se pudo guardar tu perfil.',
        },
      },
      stores: {
        title: 'Tiendas',
        subtitle: 'Detalles y configuración de la tienda seleccionada',
        addNew: 'Agregar Nueva Tienda',
        creationBlockedTitle: 'Configuración de facturación requerida',
        creationBlockedMessage: 'Mejora a Pro en tu local actual antes de añadir otro local.',
        creationBlockedAction: 'Abrir configuración de pago',
        totalStores: 'Total de Tiendas',
        managedStores: 'Tiendas Gestionadas',
        searchPlaceholder: 'Buscar tiendas...',
        filters: 'Filtros',
        noResults: 'No se encontraron tiendas que coincidan con tu búsqueda',
        noStores: 'No hay tiendas disponibles',
        emptySelected: 'Selecciona o crea una tienda para empezar.',
        tabs: {
          main: 'Principal',
          images: 'Imágenes',
          roles: 'Roles y Permisos',
          rolesShort: 'Roles',
          onRequestHours: 'Horario adicional',
          onRequestHoursShort: 'Horario',
          additionalCosts: 'Costes adicionales',
          additionalCostsShort: 'Costes',
          waitlist: 'Lista de espera',
          branding: 'Marca',
        },
        branding: {
          title: 'Colores de marca',
          description: 'Elige hasta cuatro colores para tus páginas de reserva y para esta área de gestión. Deja un color vacío para mantener el color predeterminado de Salovare.',
          roles: {
            primary: { label: 'Principal', help: 'Barras superiores, menús y superficies oscuras.' },
            accent: { label: 'Acento', help: 'Botones, enlaces, destacados y elementos seleccionados.' },
            text: { label: 'Texto', help: 'Texto principal sobre fondos claros.' },
            background: { label: 'Fondo', help: 'Fondo de las páginas.' },
          },
          hexPlaceholder: '#RRGGBB',
          invalidHex: 'Usa un color hexadecimal, por ejemplo #A15C43.',
          pickColor: 'Elegir el color {{role}}',
          useDefault: 'Usar predeterminado',
          defaultValue: 'Predeterminado: {{color}}',
          resetAll: 'Restablecer los colores de Salovare',
          previewTitle: 'Vista previa',
          preview: {
            storeName: 'Tu local',
            customer: 'Cliente',
            management: 'Gestión',
            login: 'Inicio de sesión',
            previous: 'Anterior',
            next: 'Siguiente',
            customerTitle: 'Página de reservas del cliente',
            managementTitle: 'Área de gestión',
            managementMenu: 'Navegación',
            managementCard: 'Hoy',
            loginTitle: 'Inicia sesión para continuar',
            bookButton: 'Reservar ahora',
            menuItem: 'Agenda',
            serviceName: 'Corte y barba',
            serviceMeta: '45 min · con Ana',
            selected: 'Seleccionado',
          },
          warnings: {
            textContrast: 'Este color de texto es difícil de leer sobre el fondo, así que se usará un tono más legible.',
            accentContrast: 'Este color de acento es difícil de ver sobre el fondo, así que se usará un tono más legible en enlaces e iconos.',
          },
          saved: 'Colores de marca guardados.',
          saveError: 'No se pudieron guardar los colores de marca.',
          upgradeTitle: 'Los colores de marca personalizados son una función Pro',
          upgradeMessage: 'Mejora a Pro para usar tus propios colores en las páginas de reserva y en el área de gestión.',
          readOnly: 'No tienes permiso para cambiar los colores de marca.',
          images: {
            title: 'Imágenes de marca',
            description: 'Añade imágenes opcionales para la cabecera de gestión, la cabecera del cliente y las páginas de autenticación. Las imágenes se redimensionan automáticamente.',
            managementLogo: { label: 'Logotipo de gestión', help: 'Se muestra en la cabecera de gestión en escritorio y móvil.' },
            customerLogo: { label: 'Logotipo del cliente', help: 'Se muestra en la cabecera del cliente en escritorio y móvil.' },
            loginLogo: { label: 'Logotipo de inicio de sesión', help: 'Se muestra en las páginas de inicio de sesión y registro del cliente.' },
            loginBackground: { label: 'Fondo de inicio de sesión', help: 'Se muestra detrás de las páginas de autenticación del cliente.' },
            favicon: { label: 'Icono del navegador', help: 'Se muestra en la pestaña del navegador de este local.' },
            saved: 'Imagen de marca guardada.',
            deleted: 'Imagen de marca eliminada.',
            confirmDelete: '¿Eliminar esta imagen de marca?',
            error: {
              uploading: 'No se pudo subir la imagen de marca.',
              deleting: 'No se pudo eliminar la imagen de marca.',
            },
          },
        },
          manager: 'Gerente',
          teamMember: 'Miembro del Equipo',
          active: 'Activo',
          inactive: 'Inactivo',
        actions: {
          view: 'Ver',
          edit: 'Editar',
          billing: 'Actualizar datos de pago',
          teamMembers: 'Miembros del Equipo',
          roles: 'Roles y Permisos',
          images: 'Gestionar Imágenes',
          copyBookingLink: 'Copiar enlace de reserva',
          delete: 'Eliminar',
          enable: 'Habilitar',
          disable: 'Deshabilitar',
          more: 'Más Opciones'
        },
        table: {
          name: 'Nombre de la Tienda',
          role: 'Rol',
          permissions: 'Permisos',
          selectRole: 'Seleccione un rol',
          optional: 'Opcional',
          selected: 'Seleccionado',
          status: 'Estado',
          actions: 'Acciones'
        },
        delete: {
          title: 'Eliminar Tienda',
          message: '¿Estás seguro de que quieres eliminar "{name}"? Esta acción no se puede deshacer.'
        },
        feedback: {
          bookingLinkCopied: 'Enlace de reserva copiado al portapapeles'
        },
        authorizationHours: {
          helper: 'Horario extra fuera del funcionamiento normal. Los clientes piden la reserva y el equipo la confirma.',
          save: 'Guardar horario adicional',
          saved: 'Horario adicional guardado',
          overlapOpening: 'El horario adicional no puede solaparse con el horario de apertura.',
          overlapPeriods: 'Los periodos adicionales no pueden solaparse.',
          openingHoursLabel: 'Horario de apertura',
        },
        timePricing: {
          helper: 'Cobra más por reservas que empiecen en los días u horas elegidos, también en horario extra. El porcentaje usa los precios de los servicios, nunca el precio de la membresía.',
          shareTitle: 'Parte de los profesionales',
          shareHelper: 'Cuánto del coste adicional reciben los profesionales además del sueldo o comisión. 0 se queda en la tienda.',
          shareType: 'Tipo de parte',
          shareValue: 'Valor de la parte',
          rulesTitle: 'Reglas por día y hora',
          addRule: 'Añadir regla',
          empty: 'Sin reglas de coste adicional. Los clientes ven solo el precio habitual.',
          ruleLabel: 'Regla {{index}}',
          weekDays: 'Días de la semana',
          allDay: 'Todo el día',
          allDayHelper: 'Se aplica a todo el día, incluido el horario extra. Cambiar el horario de apertura más adelante no cambia esta regla.',
          from: 'Desde',
          to: 'Hasta',
          presets: 'Rellenar con horario de apertura o adicional',
          adjustmentType: 'Tipo de coste adicional',
          adjustmentValue: 'Valor',
          save: 'Guardar costes adicionales',
          saved: 'Costes adicionales guardados',
          adjustmentTypes: {
            Percentage: 'Porcentaje de los precios de los servicios',
            FixedAmount: 'Importe fijo',
          },
          validation: {
            weekDays: 'Cada regla necesita al menos un día de la semana.',
            amountNegative: 'El coste adicional no puede ser negativo.',
            percentMax: 'El porcentaje no puede superar 100.',
            periodRequired: 'Elige todo el día o un horario.',
            overlap: 'Las reglas no pueden solaparse el mismo día.',
            shareNegative: 'La parte de los profesionales no puede ser negativa.',
            sharePercent: 'El porcentaje de la parte no puede superar 100.',
          },
        },
        images: {
          title: 'Imágenes de la Tienda',
          addImages: 'Agregar imágenes',
          galleryTitle: 'Galería',
          logoTitle: 'Logotipo',
          logoHelp: 'Se usa como icono de la aplicación en la pantalla de inicio de los clientes.',
          appNameTitle: 'Nombre de la app',
          appNamePlaceholder: 'ej. Jhon Barber',
          appNameTooLong: 'Usa como máximo {{max}} caracteres.',
          appNameSaved: 'Nombre de la app guardado.',
          saveAppName: 'Guardar',
          cropTitle: 'Recortar logotipo',
          cropZoom: 'Zoom',
          confirmDeleteLogo: '¿Quitar este logotipo de la tienda?',
          upload: 'Subir Imágenes',
          uploading: 'Subiendo...',
          confirmDelete: '¿Estás seguro de que quieres eliminar esta imagen?',
          noImages: 'No hay imágenes disponibles',
          maxImagesReached: 'Máximo de {{max}} imágenes alcanzado',
          reorderHelp: 'Arrastra para cambiar el orden que ven los clientes.',
          drag: 'Arrastra para reordenar',
          moveEarlier: 'Mover antes',
          moveLater: 'Mover después',
          error: {
            loading: 'Error al cargar imágenes',
            uploading: 'Error al subir imágenes',
            deleting: 'Error al eliminar imagen',
            reorder: 'Error al guardar el orden de las imágenes',
            maxImages: 'Máximo de {{max}} imágenes permitidas. Actualmente tienes {{current}} imágenes. Puedes agregar hasta {{allowed}} imagen(es) más.',
            noFile: 'No se proporcionó ningún archivo.',
            fileEmpty: 'El archivo está vacío.',
            fileTooLarge: 'El tamaño del archivo excede el límite máximo permitido de {{maxSize}} MB.',
            invalidFileType: 'Tipo de archivo no permitido. Formatos aceptados: {{formats}}',
            invalidMimeType: 'Tipo de archivo no permitido. Tipos MIME aceptados: {{mimeTypes}}',
            fileValidationError: 'Archivo {{index}} ({{fileName}}): {{error}}',
            cropFailed: 'No se pudo recortar la imagen. Prueba con otro archivo.',
            savingAppName: 'Error al guardar el nombre de la app.',
          }
        },
        error: {
          loading: 'Error al cargar tiendas',
          notFound: 'Tienda no encontrada',
          updateStatus: 'Error al actualizar el estado de la tienda',
          updatePaymentSettings: 'Error al actualizar la configuración de pago',
          copyBookingLink: 'Error al copiar el enlace de reserva',
          bookingLinkUnavailable: 'El enlace de reserva no está disponible para esta tienda',
          updateAuthorizationHours: 'Error al guardar el horario adicional',
          updateTimePricing: 'Error al guardar los costes adicionales'
        },
        pagination: {
          rowsPerPage: 'Filas por página:',
          of: 'de'
        },
        billing: {
          title: 'Mantén tu suscripción Pro activa',
          description: 'Añade o actualiza los datos de pago para conservar las funciones Pro.',
          issueTitle: 'Tu tienda necesita atención de facturación',
          issueDescription: 'No pudimos cobrar el último pago de la suscripción. Actualiza la facturación para mantener la tienda activa.',
          action: 'Gestionar facturación',
          openPortal: 'Gestionar facturación',
          opening: 'Abriendo portal de facturación seguro...',
          errors: {
            ownerOnly: 'Solo los propietarios de la tienda pueden acceder al portal de facturación.',
            open: 'Error al abrir el portal de facturación.',
            invalidUrl: 'La URL del portal de facturación no es válida.'
          }
        },
        checkout: {
          action: 'Suscribirme ahora',
          opening: 'Redirigiendo al checkout...',
          errors: {
            ownerOnly: 'Solo los propietarios de la tienda pueden iniciar una sesión de checkout.',
            open: 'Error al iniciar el checkout. Por favor inténtalo de nuevo.',
            invalidUrl: 'La URL del checkout no es válida.'
          }
        },
        stripeConnect: {
          title: 'Pagos de clientes (Stripe)',
          loading: 'Comprobando el estado de la cuenta Stripe...',
          notStarted: {
            title: 'Configura los pagos de tu tienda',
            description: 'Conecta tu tienda con Stripe para que los clientes puedan pagar en línea reservas y suscripciones.'
          },
          pending: {
            title: 'Termina la configuración de la cuenta Stripe',
            description: 'Stripe aún necesita más información antes de que esta tienda pueda aceptar pagos de clientes. Continúa el formulario seguro de Stripe para enviar los datos pendientes.'
          },
          status: {
            NotStarted: 'No iniciado',
            Pending: 'Acción requerida',
            Complete: 'Listo'
          },
          flags: {
            detailsSubmitted: 'Detalles enviados',
            cardPayments: {
              active: 'Pagos con tarjeta activos',
              pending: 'Pagos con tarjeta pendientes',
              inactive: 'Pagos con tarjeta inactivos'
            }
          },
          membershipBilling: {
            pending: {
              title: 'Los pagos con tarjeta aún se están activando',
              description: 'El onboarding de Stripe está completo, pero los pagos con tarjeta aún no están activos. Esto suele resolverse en unos minutos. Actualiza el estado o continúa la configuración de Stripe si se requiere más información.'
            },
            inactive: {
              title: 'Completa la configuración de pagos con tarjeta para suscripciones',
              description: 'Los planes de suscripción necesitan pagos con tarjeta activos en tu cuenta Stripe. Continúa la configuración segura de Stripe para enviar los datos pendientes y luego actualiza el estado aquí.'
            }
          },
          actions: {
            start: 'Configurar pagos Stripe',
            continue: 'Continuar configuración Stripe',
            refresh: 'Actualizar estado',
            opening: 'Abriendo Stripe...'
          },
          feedback: {
            complete: 'Los pagos Stripe ya están habilitados para esta tienda.',
            membershipBillingReady: 'La facturación de suscripciones ya está lista. Ya puedes crear planes de suscripción.'
          },
          errors: {
            ownerOnly: 'Solo los propietarios de la tienda pueden gestionar el onboarding de Stripe.',
            loadStatus: 'Error al cargar el estado del onboarding de Stripe.',
            open: 'Error al abrir el onboarding de Stripe.',
            refresh: 'Error al actualizar el estado de la cuenta Stripe.',
            invalidUrl: 'La URL de onboarding de Stripe no es válida.'
          }
        },
        access: {
          checking: 'Comprobando el acceso a esta tienda...',
          refresh: 'Actualizar acceso',
          backToStores: 'Volver a tiendas',
          billingAttentionTitle: 'Tu tienda necesita atención de facturación',
          switchStoreLabel: 'Cambiar a otra tienda',
          ownerHint: 'Abre el portal seguro de facturación para agregar los datos de pago y restaurar el acceso de gestión.',
          nonOwnerHint: 'Solo el propietario de la tienda puede restaurar el acceso en facturación. Pídele que actualice los datos de pago.',
          portalCardTitle: 'Restaurar acceso desde facturación',
          portalCardDescription: 'Cuando se actualicen los datos de pago o la suscripción, actualiza el acceso y Salovare volverá a desbloquear la tienda.',
          reasons: {
            trialExpired: {
              badge: 'Prueba terminada',
              title: 'Tu prueba gratuita ha terminado',
              description: 'Esta tienda está pausada hasta que se actualice la facturación.'
            },
            subscriptionCanceled: {
              badge: 'Suscripción cancelada',
              title: 'La suscripción de esta tienda fue cancelada',
              description: 'Reactiva la facturación para desbloquear de nuevo el área de gestión.'
            },
            subscriptionExpired: {
              badge: 'Suscripción vencida',
              title: 'La suscripción de esta tienda ha vencido',
              description: 'Actualiza la facturación para desbloquear de nuevo el área de gestión.'
            },
            subscriptionSuspended: {
              badge: 'Suscripción suspendida',
              title: 'La suscripción de esta tienda está suspendida',
              description: 'Resuelve el problema de facturación atrasada para desbloquear de nuevo el área de gestión.'
            },
            paymentMethodRequired: {
              badge: 'Método de pago requerido',
              title: 'Método de pago requerido',
              description: 'Agrega un método de pago para activar la suscripción de esta tienda y restaurar el acceso.'
            },
            subscriptionPastDue: {
              badge: 'Pago fallido',
              title: 'Falló el pago de la suscripción',
              description: 'El último pago de la suscripción falló. Actualiza la facturación para restaurar el acceso a esta tienda.'
            },
            extraStoreDeactivated: {
              badge: 'Local desactivado',
              title: 'Este local extra está desactivado',
              description: 'La facturación Pro ha caducado. El local más antiguo permanece en el plan Gratis. Restaura la facturación para reabrir este local extra.'
            }
          }
        },
        view: {
          subtitle: 'Ver detalles e información de la tienda',
          basicInfo: 'Información Básica',
          name: 'Nombre de la Tienda',
          slug: 'Slug (URL)',
          status: 'Estado',
          about: 'Acerca de',
          supportedLanguages: 'Idiomas Soportados',
          storeLanguage: 'Idioma Predeterminado de la Tienda',
          address: 'Dirección',
          street: 'Calle',
          door: 'Puerta',
          additionalInfo: 'Información Adicional',
          city: 'Ciudad',
          state: 'Estado',
          zipCode: 'Código Postal',
          country: 'País',
          openingHours: 'Horario de Atención',
          contact: 'Contacto',
          timeZone: 'Zona Horaria',
          slotInterval: 'Intervalo de Horario',
          images: 'Imágenes',
          paymentSettings: 'Configuración de Pago',
          payOnBooking: 'Aceptar pagos en línea al reservar',
          payOnBookingDescription: 'Cuando esté activado, los clientes podrán pagar su reserva en línea al momento de reservar.',
          stripeRequiredForOnlinePayments: 'La integración de pago de Stripe debe configurarse antes de habilitar los pagos en línea.',
          addImages: 'Agregar Imágenes',
          upload: 'Subir Imágenes',
          uploading: 'Subiendo...',
          confirmDelete: '¿Estás seguro de que quieres eliminar esta imagen?',
          noImages: 'No hay imágenes disponibles',
          maxImagesReached: 'Máximo de {{max}} imágenes alcanzado',
          error: {
            loading: 'Error al cargar imágenes',
            uploading: 'Error al subir imágenes',
            deleting: 'Error al eliminar imagen',
            maxImages: 'Máximo de {{max}} imágenes permitidas. Actualmente tienes {{current}} imágenes. Puedes agregar hasta {{allowed}} imagen(es) más.',
            noFile: 'No se proporcionó ningún archivo.',
            fileEmpty: 'El archivo está vacío.',
            fileTooLarge: 'El tamaño del archivo excede el límite máximo permitido de {{maxSize}} MB.',
            invalidFileType: 'Tipo de archivo no permitido. Formatos aceptados: {{formats}}',
            invalidMimeType: 'Tipo de archivo no permitido. Tipos MIME aceptados: {{mimeTypes}}',
            fileValidationError: 'Archivo {{index}} ({{fileName}}): {{error}}'
          }
        }
      },
      teamMembers: {
        title: 'Miembros del Equipo',
        subtitle: 'Gestiona los miembros del equipo de tu tienda',
        addNew: 'Agregar Nuevo Miembro del Equipo',
        totalTeamMembers: 'Total de Miembros del Equipo',
        searchPlaceholder: 'Buscar miembros del equipo...',
        filters: 'Filtros',
        noResults: 'No se encontraron miembros del equipo que coincidan con tu búsqueda',
        noTeamMembers: 'No hay miembros del equipo disponibles',
        actions: {
          view: 'Ver',
          edit: 'Editar',
          manageAgenda: 'Agenda',
          manageCompensation: 'Gestionar Compensación',
          enable: 'Habilitar',
          disable: 'Deshabilitar',
          more: 'Más Opciones'
        },
        table: {
          name: 'Nombre',
          email: 'Email',
          manager: 'Gerente',
          services: 'Servicios',
          status: 'Estado',
          actions: 'Acciones'
        },
        status: {
          active: 'Activo',
          inactive: 'Inactivo',
          acceptsBookings: 'Acepta reservas',
          noCustomerBookings: 'Sin reservas de clientes'
        },
        acceptsCustomerBookings: {
          label: 'Atiende reservas',
          help: 'Cuando está activo, este profesional aparece en la agenda, en los planes de membresía y en la página de reserva del cliente.',
          chargeNotice: 'Los profesionales que atienden reservas se cobran.',
          currentPeriodNotice: 'Este profesional se cobrará en el período de facturación actual y no se cobrará en el próximo período de facturación.'
        },
        manager: 'Gerente',
        teamMember: 'Miembro del Equipo',
        services: {
          showMore: 'Mostrar más',
          showLess: 'Mostrar menos',
          moreServices: '+{count} más'
        },
        registration: {
          title: 'Agregar Nuevo Miembro del Equipo',
          subtitle: 'Crear una nueva cuenta de miembro del equipo para tu tienda',
          personalInfo: 'Información Personal',
          name: 'Nombre',
          email: 'Dirección de Email',
          phone: 'Número de Teléfono',
          jobTitle: 'Cargo',
          jobTitlePlaceholder: 'ej. Estilista Senior',
          genre: 'Género',
          male: 'Masculino',
          female: 'Femenino',
          birthDate: 'Fecha de Nacimiento',
          language: 'Idioma',
          role: 'Perfil',
          permissions: 'Permisos',
          selectRole: 'Seleccionar perfil',
          fromRole: 'Del Perfil',
          additional: 'Adicional',
          includesPermissions_one: 'Incluye {{count}} permiso',
          includesPermissions_other: 'Incluye {{count}} permisos',
          rolePermissionsInfo: 'Los permisos marcados con "Del Perfil" están incluidos en el perfil seleccionado y no se pueden modificar. Puede agregar permisos adicionales a continuación.',
          storeAssignment: 'Asignación de Tienda',
          currentStore: 'Tienda Actual',
          isManager: 'Es Gerente',
          services: 'Servicios',
          noServices: 'No hay servicios disponibles para esta tienda',
          create: 'Crear',
          assign: 'Agregar a la Tienda',
          emailCheck: {
            check: 'Verificar',
            available: 'Este email está disponible. Complete los datos restantes a continuación.',
            existingTitle: 'Miembro del equipo ya registrado',
            existingMessage: '{{name}} ({{email}}) ya está registrado en Salovare. ¿Desea agregar este miembro del equipo a su tienda? Su perfil existente no se modificará.',
            existingConfirm: 'Agregar a esta tienda',
            existingConfirmed: '{{name}} se agregará a esta tienda. Configure el perfil, permisos y servicios a continuación.',
            alreadyInStore: 'Este miembro del equipo ya está asignado a esta tienda.',
            error: 'No se pudo verificar este email. Inténtelo de nuevo.'
          },
          success: {
            created: '¡Miembro del Equipo creado exitosamente! Email de configuración de contraseña enviado.',
            assigned: 'Miembro del equipo agregado a esta tienda exitosamente.'
          },
          error: {
            requiredFields: 'Nombre y email son campos requeridos',
            loadingServices: 'Error al cargar servicios',
            creation: 'Error al crear miembro del equipo'
          },
          steps: {
            step1: {
              title: 'Información Personal',
              description: 'Ingrese los datos personales e información de contacto del miembro del equipo'
            },
            step2: {
              title: 'Perfil y Permisos',
              description: 'Asigne un perfil y configure los permisos para este miembro del equipo'
            },
            step3: {
              title: 'Servicios',
              description: 'Seleccione los servicios que este miembro del equipo puede proporcionar'
            },
            step4: {
              title: 'Revisión',
              description: 'Revise toda la información antes de crear el miembro del equipo'
            }
          },
          validation: {
            nameRequired: 'El nombre es requerido',
            emailRequired: 'El email es requerido',
            emailInvalid: 'Por favor, ingresa un email válido',
            phoneRequired: 'El número de teléfono es requerido',
            servicesRequired: 'Por favor, selecciona al menos un servicio',
            emailCheckRequired: 'Verifique este email antes de continuar.',
            alreadyInStore: 'Este miembro del equipo ya está asignado a esta tienda.',
            existingMemberConfirmationRequired: 'Confirme que desea agregar este miembro del equipo existente a su tienda.'
          },
          namePlaceholder: 'Ingresa el nombre',
          emailPlaceholder: 'Ingresa la dirección de email',
          phonePlaceholder: 'Ingresa el número de teléfono',
          selectGenre: 'Selecciona el género',
          servicePrice: 'Precio',
          serviceDuration: 'Duración',
          noServicesSelected: 'Ningún servicio seleccionado',
          defaultPrice: 'Precio Predeterminado',
          defaultDuration: 'Duración Predeterminada',
          minutes: 'minutos',
          customSettings: 'Configuraciones Personalizadas',
          customPrice: 'Precio Personalizado',
          customPriceHelp: 'Deja vacío para usar el precio predeterminado',
          price: 'Precio',
          duration: 'Duración',
          custom: 'personalizado',
          default: 'predeterminado'
        },
        billing: {
          confirmTitle: 'Costo de miembro adicional',
          confirmIntro: 'Agregar un profesional que atiende reservas implica un costo adicional de suscripción de {{price}}/mes por plaza más allá del miembro incluido.',
          confirmAdd: 'Confirmar y agregar miembro',
          confirmEnable: 'Confirmar y activar miembro',
          immediateCharge: 'Se cobrará {{amount}} ahora por el resto de este período de facturación (hasta {{date}}).',
          recurringCharge: 'Luego {{amount}}/mes desde tu próxima fecha de facturación ({{date}}).',
          recurringChargeNoDate: 'Luego {{amount}}/mes en cada renovación.',
          removalPolicy: 'Si desactivas este miembro antes del {{date}}, seguirás siendo cobrado por esta plaza hasta el final del período de facturación actual. No se cobrará en el próximo período de facturación.',
          removalPolicyNoDate: 'Si desactivas este miembro antes de la próxima fecha de facturación, seguirás siendo cobrado por esta plaza hasta el final del período de facturación actual. No se cobrará en el próximo período de facturación.',
          trialNotice: 'Esto agregará {{price}}/mes después de que termine tu prueba. Sin cargo durante la prueba.',
          reviewChargeNotice: 'Agregar un profesional que atiende reservas añadirá {{price}}/mes a tu suscripción.',
          assignExistingNotice: 'Agregar este miembro costará {{amount}}/mes cuando la facturación esté activa. La eliminación surte efecto desde el próximo período de facturación ({{date}}).',
          assignExistingNoticeNoDate: 'Agregar este miembro costará {{amount}}/mes cuando la facturación esté activa.',
          disabledNotice: 'Este miembro fue desactivado. La facturación de la plaza continúa hasta el próximo período de facturación.',
          syncFailed: 'No se pudo actualizar tu suscripción para el miembro adicional. Revisa Detalles de Pago e inténtalo de nuevo.'
        },
        picture: {
          upload: 'Cambiar foto',
          uploading: 'Subiendo...',
          delete: 'Quitar foto',
          deleting: 'Quitando...',
          editProfilePicture: 'Editar foto de perfil',
          modalTitle: 'Foto de perfil',
          modalDescription: 'Cambiar o quitar la foto de perfil de este miembro del equipo.',
          confirmDelete: '¿Quitar la foto de perfil de este miembro del equipo?',
          uploadError: 'Error al subir foto de perfil.',
          deleteError: 'Error al eliminar foto de perfil.'
        },
        timeOff: {
          modalTitle: 'Gestionar Vacaciones',
          modalSubtitle: 'Programar indisponibilidad para {{name}}',
          scheduledTitle: 'Vacaciones programadas',
          addTitle: 'Agregar vacaciones',
          empty: 'No hay vacaciones programadas en este período.',
          fullDay: 'Día completo',
          add: 'Agregar vacaciones',
          adding: 'Agregando...',
          delete: 'Eliminar',
          confirmDeleteTitle: 'Eliminar vacaciones',
          confirmDeleteMessage: '¿Eliminar esta entrada de vacaciones?',
          types: {
            AnnualLeave: 'Vacaciones anuales',
            SickLeave: 'Baja por enfermedad',
            Other: 'Otro'
          },
          fields: {
            date: 'Fecha',
            type: 'Tipo',
            fullDay: 'Día completo',
            fullDayHelp: 'Cuando está activado, el miembro del equipo no está disponible durante todo el día.',
            from: 'Desde',
            to: 'Hasta',
            details: 'Detalles',
            detailsPlaceholder: 'Notas opcionales'
          },
          validation: {
            dateRequired: 'La fecha es obligatoria.',
            pastDate: 'No se pueden programar vacaciones en el pasado.',
            invalidPeriod: 'La hora de inicio debe ser anterior a la hora de fin.',
            pastPeriod: 'Las vacaciones no pueden comenzar en el pasado.'
          },
          success: {
            created: 'Vacaciones agregadas correctamente.',
            deleted: 'Vacaciones eliminadas correctamente.'
          },
          error: {
            loading: 'Error al cargar las vacaciones.',
            create: 'Error al agregar vacaciones.',
            delete: 'Error al eliminar vacaciones.',
            conflict: 'Estas vacaciones se superponen con una entrada existente.',
            pastDate: 'No se pueden programar vacaciones en el pasado.'
          }
        },
        agenda: {
          title: 'Agenda',
          subtitle: 'Gestiona el horario de trabajo, las aperturas especiales y las vacaciones de este profesional.',
          tabs: {
            workingHours: 'Horario de trabajo',
            specialOpenings: 'Aperturas especiales',
            timeOff: 'Vacaciones',
          },
          error: {
            loading: 'No se pudo cargar la agenda.',
          },
        },
        workingHours: {
          title: 'Horario de trabajo',
          useStoreHours: 'Usar el horario de la tienda',
          storeHoursLabel: 'Horario de la tienda',
          save: 'Guardar horario de trabajo',
          success: {
            saved: 'Horario de trabajo guardado.',
          },
          error: {
            save: 'No se pudo guardar el horario de trabajo.',
          },
          validation: {
            overlapPeriods: 'Los períodos de trabajo no pueden solaparse.',
          },
        },
        specialOpenings: {
          calendarLabel: 'Seleccionar fechas',
          calendarHelp: 'Toca las fechas para añadirlas o quitarlas. Los días con borde ya tienen una apertura especial.',
          defaultFrom: 'Apertura predeterminada',
          defaultTo: 'Cierre predeterminado',
          emptySelection: 'Selecciona una o más fechas en el calendario.',
          save: 'Guardar días de apertura',
          saving: 'Guardando...',
          existingTitle: 'Aperturas especiales guardadas',
          existingEmpty: 'No hay aperturas especiales en este período.',
          confirmDeleteTitle: 'Eliminar apertura especial',
          confirmDeleteMessage: '¿Quitar esta apertura especial? Los clientes dejarán de ver esos horarios para este profesional.',
          validation: {
            datesRequired: 'Selecciona al menos una fecha.',
            invalidPeriod: 'La hora de apertura debe ser anterior a la de cierre.',
          },
          success: {
            saved: 'Días extra de apertura guardados.',
            deleted: 'Apertura especial eliminada.',
          },
          error: {
            loading: 'Error al cargar las aperturas especiales.',
            save: 'Error al guardar las aperturas especiales.',
            delete: 'Error al eliminar la apertura especial.',
          },
        },
        error: {
          loading: 'Error al cargar miembros del equipo',
          noStoreSelected: 'Ninguna tienda seleccionada. Por favor, selecciona una tienda primero.',
        },
        fallback: {
          message: 'Por favor, selecciona una tienda del menú superior para gestionar miembros del equipo.',
          goToStores: 'Ir a Tiendas'
        },
        update: {
          title: 'Actualizar Miembro del Equipo',
          subtitle: 'Modifica la información y configuración del miembro del equipo',
          update: 'Actualizar',
          discardChanges: 'Descartar Cambios',
          discardSuccess: 'Cambios descartados. Formulario restaurado a los datos originales.',
          discardError: 'Error al descartar cambios.',
          success: {
            updated: '¡Miembro del Equipo actualizado exitosamente!'
          },
          error: {
            loading: 'Error al cargar información del miembro del equipo',
            update: 'Error al actualizar miembro del equipo'
          },
          steps: {
            step1: {
              title: 'Información Personal',
              description: 'Actualiza los datos personales e información de contacto del miembro del equipo'
            },
            step2: {
              title: 'Perfil y Permisos',
              description: 'Actualiza el perfil y permisos para este miembro del equipo'
            },
            step3: {
              title: 'Servicios',
              description: 'Actualiza los servicios que este miembro del equipo puede proporcionar'
            },
            step4: {
              title: 'Revisión',
              description: 'Revise toda la información antes de actualizar el miembro del equipo'
            }
          }
        }
      },
      servicesManagement: {
        title: 'Servicios',
        subtitle: 'Gestiona los servicios de tu tienda',
        tabs: {
          services: 'Servicios',
          categories: 'Categorías',
        },
        addNew: 'Agregar Nuevo Servicio',
        noServices: 'No hay servicios disponibles',
        emptyHint: 'Crea tu primer servicio para empezar a asignarlo a miembros del equipo y reservas.',
        card: {
          noDescription: 'No se proporcionó descripción',
          primaryService: 'Servicio principal',
          secondaryService: 'Servicio secundario',
          duration: 'Duración',
          price: 'Precio'
        },
        actions: {
          edit: 'Editar',
          delete: 'Eliminar'
        },
        fields: {
          name: 'Nombre del Servicio',
          description: 'Descripción del Servicio',
          type: 'Tipo de Servicio',
          typeHelpTitle: '¿Qué significa el tipo de servicio?',
          typeHelpAriaLabel: '¿Qué significa el tipo de servicio?',
          typeHelpPrimary: 'Principal es el servicio por el que vino el cliente. La reserva dura el tiempo de este servicio. Úsalo para cosas como corte, coloración o manicura.',
          typeHelpSecondary: 'Secundario es un extra que se hace al mismo tiempo que un servicio principal. No alarga la reserva. Úsalo para cosas como cejas o barba hechas durante el corte.',
          typeHelpExample: 'Ejemplo: un corte de 30 minutos (principal) más cejas (secundario). El cliente queda reservado 30 minutos, no 30 más el tiempo de las cejas. Si también deben poder reservar solo ese extra, activa “Permitir reserva independiente” abajo.',
          duration: 'Duración',
          price: 'Precio',
          isActive: 'Activo',
          isActiveHelp: 'Desactiva esto para crear o guardar el servicio como inactivo.',
          isStandalone: 'Permitir reserva independiente',
          isStandaloneHelp: 'Activa esto cuando los clientes puedan reservar el servicio sin un servicio principal.',
          teamMembers: 'Miembros del equipo que pueden realizar este servicio',
          teamMembersHelp: 'Los clientes solo pueden reservar este servicio si al menos un miembro del equipo está asignado a él.',
          noTeamMembersAvailable: 'No hay miembros del equipo disponibles para asignar.'
        },
        types: {
          primary: 'Principal',
          secondary: 'Secundario'
        },
        bookingRule: {
          primary: 'Servicio principal',
          standalone: 'Independiente',
          requiresPrimary: 'Requiere servicio principal'
        },
        status: {
          active: 'Activo',
          inactive: 'Inactivo'
        },
        dialog: {
          createTitle: 'Agregar Servicio',
          editTitle: 'Editar Servicio',
          saveCreate: 'Crear Servicio',
          saveEdit: 'Guardar Cambios',
          steps: {
            details: 'Detalles',
            pricing: 'Precios',
            consent: 'Consentimiento',
          },
        },
        unassignedWarning: {
          title: 'Ningún miembro del equipo asignado',
          message: 'Los clientes no podrán reservar este servicio porque ningún miembro del equipo está asignado para realizarlo.',
          confirmCreate: 'Crear de todos modos',
          confirmSave: 'Guardar de todos modos'
        },
        delete: {
          title: 'Eliminar Servicio',
          message: '¿Estás seguro de que quieres eliminar "{{name}}"? Esta acción no se puede deshacer.',
          confirm: 'Eliminar',
          cancel: 'Cancelar'
        },
        feedback: {
          created: '¡Servicio creado exitosamente!',
          updated: '¡Servicio actualizado exitosamente!',
          deleted: '¡Servicio eliminado exitosamente!'
        },
        validation: {
          nameRequired: 'El nombre del servicio es obligatorio para todos los idiomas soportados',
          priceRequired: 'El precio es obligatorio y debe ser mayor que 0',
          durationRequired: 'La duración es obligatoria'
        },
        error: {
          loading: 'Error al cargar servicios',
          create: 'Error al crear servicio',
          update: 'Error al actualizar servicio',
          delete: 'Error al eliminar servicio',
          noStoreSelected: 'Ninguna tienda seleccionada. Por favor, selecciona una tienda primero.'
        },
        fallback: {
          message: 'Por favor, selecciona una tienda del menú superior para gestionar servicios.',
          goToStores: 'Ir a Tiendas'
        }
      },
      productsManagement: {
        title: 'Productos',
        subtitle: 'Gestiona los productos y las ventas de tu tienda',
        tabs: {
          products: 'Productos',
          sales: 'Ventas',
          settings: 'Comisión',
          commission: 'Comisión',
        },
        addNew: 'Añadir producto',
        recordSale: 'Registrar venta',
        noProducts: 'Aún no hay productos',
        emptyHint: 'Crea tu primer producto para venderlo en tienda y al reservar.',
        card: {
          noDescription: 'No se proporcionó descripción',
          price: 'Precio',
          stock: 'En stock',
          outOfStock: 'Agotado',
          lowStock: 'Stock bajo',
        },
        actions: {
          edit: 'Editar',
          delete: 'Eliminar',
          sell: 'Registrar venta',
        },
        fields: {
          name: 'Nombre del producto',
          description: 'Descripción del producto',
          price: 'Precio',
          stock: 'Cantidad en stock',
          stockHelp: 'Déjalo vacío para no controlar stock. Las ventas nunca se bloquean por stock.',
          isActive: 'Activo',
          isActiveHelp: 'Desactívalo para ocultar el producto de nuevas ventas.',
          image: 'Imagen del producto',
          imageHelp: 'Opcional. Las imágenes se redimensionan antes de subir, igual que las fotos de la tienda.',
          imageRemoveConfirm: '¿Quitar esta imagen del producto?',
        },
        status: {
          active: 'Activo',
          inactive: 'Inactivo',
        },
        dialog: {
          createTitle: 'Añadir producto',
          editTitle: 'Editar producto',
          saveCreate: 'Crear producto',
          saveEdit: 'Guardar cambios',
        },
        delete: {
          title: 'Eliminar producto',
          message: '¿Seguro que quieres eliminar "{{name}}"? Esta acción no se puede deshacer.',
          confirm: 'Eliminar',
          cancel: 'Cancelar',
        },
        sale: {
          title: 'Registrar venta de productos',
          product: 'Producto',
          quantity: 'Cantidad',
          addLine: 'Añadir',
          lines: 'Aún no se han añadido productos',
          total: 'Total',
          professional: 'Profesional',
          noProfessional: 'Sin profesional',
          professionalLocked: 'Solo puedes registrar ventas a tu nombre. Asignar a otro profesional requiere permiso extra.',
          customer: 'Cliente',
          walkIn: 'Anónimo',
          notes: 'Notas',
          commissionHint: 'Comisión estimada: {{amount}}',
          save: 'Registrar venta',
          validation: {
            noLines: 'Añade al menos un producto',
            quantityInvalid: 'La cantidad debe ser al menos 1',
            amountInvalid: 'El importe del pago no es válido',
          },
        },
        sales: {
          title: 'Ventas',
          detailsTitle: 'Detalles de la venta',
          advancedSearch: 'Búsqueda avanzada',
          hideAdvancedSearch: 'Ocultar búsqueda avanzada',
          table: {
            date: 'Fecha',
            channel: 'Canal',
            customer: 'Cliente',
            professional: 'Profesional',
            items: 'Artículos',
            total: 'Total',
            collected: 'Cobrado',
            discount: 'Descuento',
            commission: 'Comisión',
            paymentMethod: 'Pago',
            status: 'Estado',
            audit: 'Auditoría',
          },
          channels: {
            staffInStore: 'En tienda',
            customerSelfService: 'En línea',
          },
          statuses: {
            pending: 'Pendiente',
            completed: 'Completada',
            voided: 'Revertida',
          },
          sellerAssignedBy: 'Vendedor asignado por {{name}}',
          customerLinesModifiedBy: 'Líneas del cliente editadas por {{name}}',
          void: 'Revertir',
          voidConfirm: '¿Revertir esta venta completada? El stock y la comisión se revertirán.',
          empty: 'No hay ventas de productos en este período.',
        },
        checkout: {
          sectionTitle: 'Productos',
          customerSelection: 'Selección del cliente',
          customerSelectionHint: 'El cliente eligió estos artículos. No pagan comisión salvo que se acredite a un profesional.',
          addedInStore: 'Añadidos en tienda',
          readOnlyHint: 'Necesitas permiso extra para cambiar lo que el cliente seleccionó.',
        },
        settings: {
          title: 'Comisión de productos',
          description: 'El mismo porcentaje se aplica a todos los profesionales. Los cambios solo afectan a ventas futuras.',
          commissionPercent: 'Porcentaje de comisión',
          commissionPercentHelp: 'Introduce un valor entre 0 y 100. Este porcentaje queda congelado en cada venta completada.',
          selfServiceNote: 'Los productos que el cliente elige por su cuenta no pagan comisión, salvo que un gestor acredite a un profesional.',
          save: 'Guardar',
          saved: 'Ajustes de productos guardados',
        },
        feedback: {
          created: '¡Producto creado correctamente!',
          updated: '¡Producto actualizado correctamente!',
          deleted: '¡Producto eliminado correctamente!',
          saleRecorded: 'Venta de productos registrada.',
          saleVoided: 'Venta de productos revertida.',
          settingsSaved: 'Ajustes de productos guardados.',
        },
        validation: {
          nameRequired: 'El nombre del producto es obligatorio en todos los idiomas admitidos',
          priceRequired: 'El precio es obligatorio y debe ser mayor que 0',
          stockInvalid: 'La cantidad en stock no puede ser negativa',
        },
        error: {
          loading: 'Error al cargar productos',
          create: 'Error al crear producto',
          update: 'Error al actualizar producto',
          delete: 'Error al eliminar producto',
          createSale: 'Error al registrar la venta de productos',
          voidSale: 'Error al revertir la venta de productos',
          settings: 'Error al guardar los ajustes de productos',
          image: 'Error al actualizar la imagen del producto',
          noStoreSelected: 'Ninguna tienda seleccionada. Por favor, selecciona una tienda primero.',
        },
        fallback: {
          message: 'Por favor, selecciona una tienda del menú superior para gestionar productos.',
          goToStores: 'Ir a Tiendas',
        },
      },
      serviceCategoriesManagement: {
        addNew: 'Agregar Categoría',
        noCategories: 'Aún no hay categorías',
        emptyHint: 'Crea categorías para organizar servicios para tu equipo y reservas de clientes.',
        card: {
          unnamed: 'Categoría sin nombre',
          serviceCount: '{{count}} servicios',
        },
        actions: {
          drag: 'Arrastrar para reordenar',
          moveUp: 'Mover arriba',
          moveDown: 'Mover abajo',
          edit: 'Editar',
          delete: 'Eliminar',
        },
        status: {
          active: 'Activa',
          inactive: 'Inactiva',
        },
        fields: {
          name: 'Nombre',
          description: 'Descripción',
          active: 'Activa',
          services: 'Servicios asignados',
          noServicesAvailable: 'No hay servicios disponibles para asignar.',
        },
        dialog: {
          createTitle: 'Agregar Categoría',
          editTitle: 'Editar Categoría',
          saveCreate: 'Crear Categoría',
          saveEdit: 'Guardar Cambios',
        },
        delete: {
          title: 'Eliminar Categoría',
          message: '¿Estás seguro de que deseas eliminar "{{name}}"? Los servicios permanecerán pero ya no estarán agrupados en esta categoría.',
          confirm: 'Eliminar',
        },
        feedback: {
          created: '¡Categoría creada exitosamente!',
          updated: '¡Categoría actualizada exitosamente!',
          deleted: '¡Categoría eliminada exitosamente!',
        },
        validation: {
          nameRequired: 'El nombre de la categoría es obligatorio para todos los idiomas soportados',
        },
        error: {
          loading: 'Error al cargar categorías',
          create: 'Error al crear categoría',
          update: 'Error al actualizar categoría',
          delete: 'Error al eliminar categoría',
          reorder: 'Error al guardar el orden de categorías',
          noStoreSelected: 'Ninguna tienda seleccionada. Por favor, selecciona una tienda primero.',
        },
      },
      membershipPlansManagement: {
        title: 'Planes de Suscripción',
        subtitle: 'Crea y gestiona planes de suscripción recurrentes para tus clientes',
        tabs: {
          plans: 'Planes',
          customerMemberships: 'Membresías de Clientes',
          customerMembershipsShort: 'Membresías',
          settings: 'Configuración',
        },
        revenueSettings: {
          title: 'Ajustes de ingresos por suscripciones',
          subtitle: 'Margen de la tienda y día de pago para la parte del pote de suscripciones. Aplica solo a ingresos por suscripciones, no a reservas regulares.',
          storeMarginPercent: 'Margen de la tienda en suscripciones (%)',
          membershipSharePayDay: 'Día de pago de partes de suscripciones',
          payDayOption: 'Día {{day}}',
          payDayHint: 'El pote de enero se paga en febrero en este día.',
          distributionModel: 'Modelo de distribución',
          distributionModelOptions: {
            monthlyPot: 'Pote mensual',
            periodUsage: 'Según el uso',
          },
          distributionModelHints: {
            monthlyPot: 'En el día de pago de la tienda, los períodos de suscripción cerrados y aún no pagados se agrupan y se reparte por minutos de duración entre los profesionales que completaron esas visitas.',
            periodUsage: 'En el día de pago de la tienda, cada período de facturación cerrado y aún no pagado se paga por separado. Cada profesional recibe la parte del pote de ese período según sus visitas. Si una visita incluye servicios de más de un profesional, se divide por el precio de lista original (por ejemplo Corte €10 + Barba €20 en un pote de €45 paga €15 y €30). Las visitas no usadas de planes limitados se quedan en la tienda.',
          },
          distributionModelSwitchWarning: 'Entra en vigor el próximo día de pago ({{date}}). Los ingresos de suscripción cerrados y aún no pagados se repartirán con el modelo elegido ese día. Los importes ya pagados no se revierten.',
          pendingUndistributed: '{{amount}} espera pago el {{date}} con el modelo seleccionado.',
          error: {
            loading: 'Error al cargar los ajustes de ingresos por suscripciones.',
            save: 'Error al guardar los ajustes de ingresos por suscripciones.',
            marginRange: 'El margen de la tienda debe estar entre 0 y 100.',
          },
          success: {
            saved: 'Ajustes de ingresos por suscripciones guardados correctamente.',
          },
        },
        addNew: 'Agregar Plan',
        noPlans: 'No hay planes de suscripción disponibles',
        emptyHint: 'Crea tu primer plan para ofrecer suscripciones recurrentes a tus clientes.',
        card: {
          unnamed: 'Plan de suscripción',
          price: 'Precio',
          perMonth: '/mes',
          includedServices: 'Servicios incluidos',
          subscribers: 'Suscriptores',
          customerCancellation: 'El cliente puede cancelar'
        },
        actions: {
          edit: 'Editar',
          activate: 'Activar',
          deactivate: 'Desactivar'
        },
        fields: {
          name: 'Nombre del Plan',
          price: 'Precio Mensual',
          billingInterval: 'Intervalo de Facturación',
          entitlementType: 'Beneficio',
          allowanceQuantity: 'Usos por mes',
          commitmentMonths: 'Compromiso mínimo (meses)',
          commitmentMonthsPlaceholder: 'Opcional',
          commitmentMonthsHelp: 'Déjalo vacío si los clientes pueden cancelar en cualquier momento después del primer período de facturación.',
          allowCustomerCancellation: 'Permitir cancelación por el cliente',
          allowCustomerCancellationHelp: 'Cuando está activado, los clientes pueden cancelar su suscripción desde el portal del cliente.',
          includedServices: 'Servicios Incluidos',
          noServicesAvailable: 'No hay servicios activos disponibles. Crea servicios antes de agregar un plan.',
          includedProfessionals: 'Profesionales (opcional)',
          includedProfessionalsHelp: 'Deja todos sin marcar para permitir todos los profesionales. Marca profesionales específicos para restringir este plan a esos miembros.',
          noProfessionalsAvailable: 'No se encontraron profesionales activos.'
        },
        billingInterval: {
          monthly: 'Mensual'
        },
        entitlement: {
          unlimited: 'Uso ilimitado',
          fixedAllowance: 'Límite fijo por mes',
          allowance: '{{count}} usos por mes'
        },
        status: {
          active: 'Activo',
          inactive: 'Inactivo'
        },
        dialog: {
          createTitle: 'Agregar Plan de Suscripción',
          editTitle: 'Editar Plan de Suscripción',
          saveCreate: 'Crear Plan',
          saveEdit: 'Guardar Cambios'
        },
        deactivate: {
          title: 'Desactivar Plan de Suscripción',
          message: '¿Estás seguro de que deseas desactivar "{{name}}"? Los suscriptores actuales mantendrán el plan hasta que lo cancelen. Los nuevos clientes no podrán suscribirse.',
          confirm: 'Desactivar',
          cancel: 'Cancelar'
        },
        activate: {
          title: 'Activar Plan de Suscripción',
          message: '¿Estás seguro de que deseas activar "{{name}}"? Los clientes podrán suscribirse a este plan nuevamente.',
          confirm: 'Activar',
          cancel: 'Cancelar'
        },
        feedback: {
          created: '¡Plan de suscripción creado con éxito!',
          updated: '¡Plan de suscripción actualizado con éxito!',
          activated: '¡Plan de suscripción activado con éxito!',
          deactivated: '¡Plan de suscripción desactivado con éxito!'
        },
        validation: {
          nameRequired: 'El nombre del plan es obligatorio en todos los idiomas soportados',
          priceRequired: 'El precio es obligatorio y debe ser mayor que 0',
          allowanceRequired: 'La cantidad de usos es obligatoria y debe ser mayor que 0',
          commitmentRequired: 'Los meses de compromiso deben ser mayores que 0 cuando se especifican',
          servicesRequired: 'Selecciona al menos un servicio incluido'
        },
        error: {
          loading: 'Error al cargar planes de suscripción',
          create: 'Error al crear plan de suscripción',
          update: 'Error al actualizar plan de suscripción',
          activate: 'Error al activar plan de suscripción',
          deactivate: 'Error al desactivar plan de suscripción',
          billingNotReady: 'La facturación de suscripciones aún no está lista. Completa la configuración de Stripe en la página Detalles de pago e inténtalo de nuevo.',
          noStoreSelected: 'No hay tienda seleccionada. Selecciona una tienda primero.'
        },
        paymentModeAdjust: {
          title: 'Cambiar modo de pago de suscripciones',
          message: 'Esta tienda está configurada solo para pagos online de suscripciones, pero Stripe aún no está configurado. Para crear este plan con pagos en tienda, el modo de pago de suscripciones cambiará de Solo online a Ambos.',
          confirm: 'Continuar y crear plan',
          error: 'Error al actualizar la configuración de pagos. Inténtalo de nuevo.',
        },
        fallback: {
          message: 'Selecciona una tienda en el menú superior para gestionar planes de suscripción.',
          goToStores: 'Ir a Tiendas'
        }
      },
      permissions: {
        infoAriaLabel: 'Detalles del permiso',
      },
      roles: {
        title: 'Roles y Permisos',
        subtitle: 'Gestiona roles y permisos de tu tienda',
        addNew: 'Agregar Nuevo Rol',
        noRoles: 'No se encontraron roles',
        searchPlaceholder: 'Buscar roles...',
        actions: {
          edit: 'Editar',
          delete: 'Eliminar',
          view: 'Ver'
        },
        card: {
          permissions: 'Permisos',
          permissionCount: '{count} permiso{plural}',
          teamMembers: 'Miembros del Equipo',
          teamMemberCount: '{count} miembro{plural}'
        },
        modal: {
          create: {
            title: 'Crear Nuevo Rol',
            subtitle: 'Define un nuevo rol con permisos específicos'
          },
          edit: {
            title: 'Editar Rol',
            subtitle: 'Actualiza el nombre y permisos del rol'
          },
          form: {
            name: 'Nombre del Rol',
            nameRequired: 'El nombre del rol es requerido para todos los idiomas soportados',
            permissions: 'Permisos',
            permissionsRequired: 'Al menos un permiso debe ser seleccionado',
            selectAll: 'Seleccionar Todos',
            deselectAll: 'Deseleccionar Todos',
            categories: {
              teamMembers: 'Miembros del Equipo',
              stores: 'Tiendas',
              services: 'Servicios',
              appointments: 'Reservas',
              memberships: 'Membresías',
              customers: 'Clientes',
              products: 'Productos',
              stock: 'Stock',
              expenses: 'Gastos',
              employeePayments: 'Pagos de Empleados',
              chat: 'Chat',
              other: 'Otros'
            }
          },
          save: 'Guardar Rol',
          saving: 'Guardando...',
          cancel: 'Cancelar',
          success: {
            created: '¡Rol creado exitosamente!',
            updated: '¡Rol actualizado exitosamente!'
          },
          error: {
            create: 'Error al crear rol',
            update: 'Error al actualizar rol',
            delete: 'Error al eliminar rol',
            inUse: 'No se puede eliminar rol que está actualmente asignado a miembros del equipo'
          }
        },
        delete: {
          title: 'Eliminar Rol',
          message: '¿Estás seguro de que quieres eliminar "{{name}}"? Esta acción no se puede deshacer.',
          confirm: 'Eliminar',
          cancel: 'Cancelar',
          inUse: 'Este rol está actualmente asignado a miembros del equipo y no puede ser eliminado.'
        },
        error: {
          loading: 'Error al cargar roles',
          noStoreSelected: 'Ninguna tienda seleccionada. Por favor, selecciona una tienda primero.'
        }
      },
      storeUpdate: {
        title: 'Actualizar Tienda',
        subtitle: 'Modifica la información y configuración de la tienda',
        storeInfo: 'Información de la Tienda',
        editSection: 'Editar Información de la Tienda',
        form: {
          name: 'Nombre de la Tienda',
          status: 'Estado de la Tienda'
        },
        enabled: 'Habilitado',
        disabled: 'Deshabilitado',
        enabledDescription: 'La tienda está activa y visible para los clientes',
        disabledDescription: 'La tienda está inactiva y oculta de los clientes',
        success: 'Tienda actualizada exitosamente',
        slugChangeTitle: '¿Cambiar la URL de la tienda?',
        slugChangeMessage: 'El enlace antiguo de los clientes dejará de funcionar.',
        slugChangeConfirm: 'Cambiar URL',
        discardChanges: 'Descartar Cambios',
        discardSuccess: 'Cambios descartados. Formulario restaurado a los datos originales.',
        discardError: 'Error al descartar cambios.',
        error: {
          loading: 'Error al cargar información de la tienda',
          saving: 'Error al actualizar tienda'
        }
      },
      customer: {
        navigation: {
          home: 'Inicio',
          appointments: 'Reservas',
          memberships: 'Membresías',
          profile: 'Perfil',
          settings: 'Configuración'
        },
        memberships: {
          title: 'Membresías',
          subtitle: 'Suscríbete a un plan recurrente y disfruta de servicios incluidos cada mes.',
          card: {
            unnamed: 'Plan de membresía',
            perMonth: '/mes',
            commitment: 'Compromiso mínimo: {{count}} meses',
            pendingCheckout: 'Completa el checkout para activar esta membresía.',
            cancelsAtPeriodEnd: 'Puedes seguir usando esta membresía hasta el {{date}}. Después de esa fecha, se cancelará.'
          },
          entitlement: {
            unlimited: 'Uso ilimitado',
            allowance: '{{count}} usos por mes'
          },
          usage: {
            unlimited: 'Usos ilimitados restantes en este período',
            remaining: '{{remaining}} de {{total}} usos restantes en este período'
          },
          actions: {
            subscribe: 'Suscribirse',
            subscribing: 'Redirigiendo...',
            continueCheckout: 'Continuar el pago',
            bookAppointment: 'Reservar cita',
            bookWithMembership: 'Reservar con membresía',
            cancel: 'Cancelar membresía',
            canceling: 'Cancelando...',
            pause: 'Pausar membresía',
            pausing: 'Pausando...',
            resume: 'Reanudar membresía',
            resuming: 'Reanudando...',
            resumeCancellation: 'Mantener membresía',
            resumingCancellation: 'Actualizando...',
            manage: 'Gestionar'
          },
          cancel: {
            confirmTitle: 'Cancelar membresía',
            confirmMessage: 'Puedes seguir usando esta membresía hasta el {{date}} — el último período ya pagado. Después de esa fecha, se cancelará. ¿Deseas continuar?',
            success: 'La membresía sigue activa hasta el {{date}}. Después de esa fecha, se cancelará.',
            error: 'No se pudo cancelar la membresía. Inténtalo de nuevo.'
          },
          pause: {
            confirmTitle: 'Pausar membresía',
            confirmMessage: 'La facturación se detendrá mientras la membresía esté pausada. Puedes reanudarla en cualquier momento.',
            success: 'Tu membresía ha sido pausada.',
            error: 'No se pudo pausar la membresía. Inténtalo de nuevo.'
          },
          resume: {
            success: 'Tu membresía ha sido reanudada.',
            error: 'No se pudo reanudar la membresía. Inténtalo de nuevo.'
          },
          resumeCancellation: {
            success: 'Tu membresía seguirá renovándose.',
            error: 'No se pudo mantener tu membresía activa. Inténtalo de nuevo.'
          },
          status: {
            pendingCheckout: 'Checkout pendiente',
            active: 'Activa',
            trialing: 'En prueba',
            pastDue: 'Pago atrasado',
            paused: 'Pausada',
            canceled: 'Cancelada',
            expired: 'Expirada'
          },
          current: {
            title: 'Tu membresía',
            pendingCheckout: 'Completa el checkout para activar tu membresía.'
          },
          checkout: {
            success: 'Checkout de membresía completado. Tu suscripción aparecerá en breve.',
            processing: 'Estamos confirmando tu pago. Tu suscripción aparecerá aquí cuando se complete.',
            canceled: 'El checkout de membresía fue cancelado.',
            error: 'No se pudo iniciar el checkout de membresía. Inténtalo de nuevo.',
            invalidUrl: 'El servidor no devolvió la URL de checkout.'
          },
          empty: {
            title: 'No hay planes de membresía disponibles',
            subtitle: 'Esta tienda aún no ha publicado planes de membresía.'
          },
          inStoreOnlyMessage: 'Visita la tienda para inscribirte en este plan de membresía.',
          bothModeHint: 'Puedes suscribirte en línea o inscribirte en tienda con el equipo.',
          browseOnly: 'La inscripción en membresías se realiza en tienda. Consulta los planes abajo e inicia una reserva — un miembro del equipo te inscribirá y cobrará la cuota en el salón.',
          error: {
            loading: 'Error al cargar los planes de membresía.'
          }
        },
        menu: {
          profile: 'Perfil',
          logout: 'Cerrar sesión'
        },
        addToHomeScreen: {
          bannerTitle: 'Instalar como app',
          subtitle: 'Añade esta tienda a la pantalla de inicio',
          action: 'Instalar como app',
          iosGuideTitle: 'Añadir a la pantalla de inicio',
          iosFallback: 'Toca Compartir en Safari y luego Añadir a pantalla de inicio.',
          androidUnavailable: 'Abre esta página en Chrome para instalar la app.',
        },
        appointments: {
          title: 'Hacer reserva',
          comingSoon: '¡Funcionalidad de reservas próximamente!',
          bookNow: 'Reservar ahora',
          myAppointments: {
            title: 'Mis reservas',
            subtitle: 'Tus reservas más recientes.',
            loadError: 'Error al cargar reservas.',
            emptyTitle: 'Aún no tienes reservas.',
            emptySubtitle: 'Comienza creando tu primera reserva.',
            services: 'Servicios',
            professional: 'Profesional',
            unnamedService: 'Servicio sin nombre',
            unnamedProfessional: 'Profesional desconocido',
            amountDue: 'Importe a pagar',
            additionalCost: 'Coste adicional de horario',
            coveredByMembership: 'Cubierto por membresía',
            pendingMembershipEnrollment: 'Membresía a pagar en el salón',
            membershipPlanNote: 'Incluido con {{plan}}',
            membershipPriceNote: 'Paga la membresía {{plan}} en el salón',
            payNow: 'Pagar ahora',
            paying: 'Redirigiendo...',
            cancel: 'Cancelar cita',
            cancelTitle: '¿Cancelar cita?',
            cancelMessage: 'Esta cita se cancelará y el horario volverá a estar disponible.',
            cancelConfirm: 'Cancelar cita',
            cancelSuccess: 'Cita cancelada correctamente.',
            cancelError: 'No se pudo cancelar la cita. Inténtalo de nuevo.',
            cancelTooLate: 'No se pueden cancelar citas con menos de 2 horas de antelación.',
            awaitingConfirmationNote: 'El salón aún debe confirmar este horario adicional. Te avisaremos cuando se acepte o se rechace.'
          },
          payment: {
            success: 'Pago completado con éxito.',
            canceled: 'Pago cancelado.',
            failed: '¡El pago falló! Haz clic en "Pagar ahora" para intentarlo de nuevo.',
            failedNotBooked: 'El pago no se completó, así que la cita no quedó reservada.',
            processing: 'Pago en proceso. Se actualizará en breve.',
            expired: '¡La sesión de pago caducó! Haz clic en "Pagar ahora" para intentarlo de nuevo.',
            checkoutError: 'Error al iniciar el pago. Inténtalo de nuevo.'
          },
          status: {
            pending: 'Pendiente',
            awaitingConfirmation: 'Pendiente de confirmación',
            awaitingPayment: 'Pendiente de pago',
            cancelled: 'Cancelada',
            confirmed: 'Confirmada',
            completed: 'Completada',
            noShow: 'No asistió',
            unknown: 'Desconocida'
          },
          booking: {
            stepServices: 'Servicios',
            categoryAll: 'Todos',
            stepProfessionals: 'Profesionales',
            stepCalendar: 'Fecha y Hora',
            stepReview: 'Revisar y Confirmar',
            selectServices: 'Seleccione los servicios',
            selectAtLeastOne: 'Seleccione al menos un servicio',
            servicesSelected: '{{count}} seleccionado(s)',
            next: 'Siguiente',
            back: 'Atrás',
            confirm: 'Confirmar',
            anyProfessional: 'Cualquier profesional',
            specificProfessional: 'Profesional específico',
            professionalPerService: 'Profesional por servicio',
            selectProfessional: 'Seleccione el profesional',
            selectProfessionalForService: 'Seleccione el profesional para {{service}}',
            professionalNotAble: 'Este profesional no puede realizar uno o más servicios seleccionados.',
            specificProfessionalHelper: 'Use esta opción solo cuando un profesional pueda realizar todos los servicios seleccionados.',
            specificProfessionalUnavailable: 'Ningún profesional puede realizar todos los servicios seleccionados en este momento. Use Profesional por servicio.',
            incompatibleProfessionalTitle: '{{name}} no puede realizar todos los servicios seleccionados',
            incompatibleProfessionalBody: '{{name}} no puede realizar los siguientes servicios:',
            selectAnotherProfessional: 'Seleccionar otro profesional',
            selectDifferentPerService: 'Profesional diferente por servicio',
            suggestion: 'Sugerencia: {{service1}} con {{name1}} y {{service2}} con {{name2}}. Puede cambiar abajo.',
            suggestionSingle: 'Un solo profesional puede realizar todos los servicios seleccionados.',
            selectDate: 'Seleccione la fecha',
            selectTime: 'Seleccione la hora',
            pickDate: 'Elegir fecha',
            morning: 'Mañana',
            afternoon: 'Tarde',
            evening: 'Noche',
            noSlots: 'No hay horarios disponibles para esta fecha.',
            chooseProfessional: 'Elegir profesional',
            multipleAvailable: 'Hay más de un profesional disponible. ¿A quién prefiere?',
            selectTimeForProfessional: 'Seleccione la hora para {{professional}}',
            noSlotsForProfessional: 'No hay horarios disponibles para este profesional en la fecha seleccionada.',
            selectTimeMobileHint: 'Elige una hora por profesional. Los servicios siguientes se habilitan después de que termine el anterior.',
            professionalsReady: '{{selected}} de {{total}} profesionales con horario definido',
            selectedTimeLabel: 'Seleccionado: {{time}}',
            availableFrom: 'Desde {{time}}',
            selectPreviousTimeFirst: 'Selecciona primero la hora anterior',
            requiresCompanionService: 'Selecciona al menos un servicio principal o secundario independiente para continuar con esta reserva.',
            selectCompanionProfessionalFirst: 'Selecciona primero el profesional del servicio principal o secundario independiente que acompaña a este servicio.',
            companionProfessionalOnly: 'Solo están disponibles para este servicio los profesionales ya seleccionados para los servicios complementarios.',
            summary: 'Resumen',
            dateTime: 'Fecha y hora',
            services: 'Servicios',
            professional: 'Profesional',
            totalPrice: 'Total',
            paymentMethod: 'Pago',
            payInStore: 'En tienda',
            payOnline: 'Online',
            payWithMembershipUnlimited: 'Usar membresía: {{plan}}',
            payWithMembershipAllowance: 'Usar membresía: {{plan}} ({{remaining}} restantes)',
            membershipSuccess: '¡Reserva confirmada con tu membresía!',
            detailsPlaceholder: 'Detalles adicionales (opcional)',
            success: '¡Reserva confirmada con éxito!',
            awaitingConfirmationSuccess: 'Solicitud enviada. Confirmaremos este horario adicional con el salón.',
            requiresConfirmationHint: 'Este horario está fuera del horario regular. El salón debe confirmarlo antes de reservarlo.',
            onRequestSlot: 'Bajo petición',
            additionalCost: 'Coste adicional de horario',
            products: 'Productos',
            addProducts: 'Añadir productos',
            addProductsHint: '¿Quiere añadir productos a esta reserva?',
            noThanks: 'No, gracias',
            editProducts: 'Editar',
            noProductsSelected: 'Ningún producto seleccionado',
            productsTotal: 'Total de productos',
            onRequestAnyProfessionalHint: 'Cualquier profesional disponible puede aceptar esta petición de horario adicional.',
            error: 'Error al reservar. Inténtelo de nuevo.',
            paymentNotCompleted: 'El pago no se completó, así que la cita no quedó reservada. Puede intentarlo de nuevo.',
            paymentReleaseFailed: 'El pago no se completó. Si este horario sigue en Mis reservas, cancélelo para liberar el hueco.',
            membershipPlans: 'Planes de membresía',
            yourMemberships: 'Tus membresías',
            selectedMembershipPlan: 'Plan de membresía seleccionado',
            discoverMembershipPrompt: '¿Te interesa una membresía?',
            seeMembershipPlans: 'Ver planes',
            membershipPlanServices: '{{count}} servicio(s) incluido(s)',
            membershipPlanCovered: 'Cubierto',
            membershipPlanActive: 'Membresía activa',
            membershipPlanNotEnrolled: 'Inscribirse en tienda',
            coveredByMembership: 'Cubierto por membresía',
            dueInStore: 'A pagar en tienda',
            membershipExtrasHint: 'Los servicios del plan permanecen seleccionados. Puedes añadir servicios extra de pago.',
            enrollInStoreNote: 'Un miembro del equipo te inscribirá en este plan en tienda. Paga {{price}} en el salón por este período de membresía.',
            planHowItWorksUnlimited: 'Cómo funciona: paga la cuota mensual y usa los servicios incluidos tantas veces como quieras en cada período de facturación.',
            planHowItWorksAllowance: 'Cómo funciona: paga la cuota mensual y usa los servicios incluidos hasta {{count}} veces en cada período de facturación.',
            coveredServiceTitle: 'Servicio cubierto por membresía',
            coveredServiceBody: '"{{service}}" está incluido en tu membresía "{{plan}}". ¿Deseas reservar a través de la membresía o pagar el precio regular?',
            goWithMembership: 'Usar membresía',
            keepRegular: 'Precio regular',
            renewalRiskTitle: 'Recordatorio de renovación de membresía',
            renewalRiskBody: 'Tu período de membresía actual termina el {{date}}. Si la membresía no se renueva antes de la fecha de la cita, se aplicará el precio regular del servicio. Puedes continuar reservando.',
            continueAnyway: 'Continuar',
            waitlist: {
              prompt: '¿No encuentras el hueco perfecto?',
              cta: 'Apúntate a la lista de espera',
              title: 'Apuntarse a la lista de espera',
              subtitle: 'Elige fechas y horarios preferidos. Te avisaremos si se abre un hueco coincidente.',
              join: 'Unirme a la lista',
              addRow: 'Añadir otra fecha',
              removeRow: 'Quitar fecha',
              timePlaceholder: 'Selecciona un horario',
              any: 'Cualquiera',
              from: 'Desde',
              to: 'Hasta',
              success: 'Te has apuntado a la lista de espera. Te avisaremos si se abre un hueco coincidente.',
              error: {
                preferences: 'Selecciona al menos una fecha y un horario.',
                services: 'Selecciona servicios antes de unirte a la lista de espera.',
                save: 'No se pudo unir a la lista de espera. Inténtalo de nuevo.',
              },
            },
          }
        },
        profile: {
          title: 'Mi Perfil',
          subtitle: 'Administra tu foto de perfil, número de teléfono, idioma y tu cuenta.',
          name: 'Nombre',
          comingSoon: '¡Gestión de perfil próximamente!',
          phone: {
            label: 'Número de teléfono',
            placeholder: 'Introduce el número de teléfono',
            helper: 'Se usa para recordatorios de citas por SMS.',
            saveSuccess: 'Número de teléfono guardado.',
            saveError: 'Error al guardar el número de teléfono.',
            invalid: 'Introduce un número de teléfono válido.'
          },
          picture: {
            upload: 'Subir Foto',
            uploading: 'Subiendo...',
            delete: 'Eliminar Foto',
            deleting: 'Eliminando...',
            uploadSuccess: 'Foto de perfil subida exitosamente.',
            uploadError: 'Error al subir foto de perfil.',
            deleteSuccess: 'Foto de perfil eliminada exitosamente.',
            deleteError: 'Error al eliminar foto de perfil.',
            confirmDelete: '¿Estás seguro de que quieres eliminar tu foto de perfil?'
          }
        },
        settings: {
          title: 'Configuración',
          subtitle: 'Personaliza tus preferencias para esta tienda.',
          comingSoon: '¡Funcionalidad de configuración próximamente!'
        },
        store: {
          notFound: 'Tienda no encontrada',
          location: 'Ubicación',
          openingHours: 'Horario de Atención',
          closed: 'Cerrado',
          open: 'Abierto',
          openUntil: 'Abierto hasta',
          opensAt: 'Abre a las',
          opensTomorrow: 'Abre mañana',
          opensOn: 'Abre el',
          at: 'a las',
          additionalHours: 'Horario adicional',
          additionalHoursUntil: 'Horario adicional hasta',
          additionalHoursFrom: 'Horario adicional desde',
          contact: 'Contacto',
          getDirections: 'Obtener Direcciones',
          noImages: 'No hay imágenes disponibles',
          noServices: 'No hay servicios disponibles',
          noTeamMembers: 'No hay miembros del equipo disponibles',
          noAbout: 'No hay información disponible',
          tabs: {
            overview: 'Resumen',
            location: 'Ubicación',
            images: 'Imágenes',
            services: 'Servicios',
            products: 'Productos',
            team: 'Equipo',
            about: 'Acerca de'
          }
        },
        products: {
          title: 'Productos',
          subtitle: 'Añade productos al reservar.',
          addWhenBooking: 'Añadir al reservar',
          outOfStock: 'Agotado',
        }
      },
    },
  },
};

Object.assign(resources.en.translation, legalTranslationsEn);
Object.assign(resources.pt.translation, legalTranslationsPt);
Object.assign(resources.es.translation, legalTranslationsEs);

// These keys extend existing sections, so they are merged in depth rather than assigned.
const mergeDeep = (target, source) => {
  Object.entries(source).forEach(([key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      target[key] = mergeDeep(target[key] && typeof target[key] === 'object' ? target[key] : {}, value);
    } else {
      target[key] = value;
    }
  });
  return target;
};

mergeDeep(resources.en.translation, prepaymentTranslationsEn);
mergeDeep(resources.pt.translation, prepaymentTranslationsPt);
mergeDeep(resources.es.translation, prepaymentTranslationsEs);
mergeDeep(resources.en.translation, servicePackageTranslationsEn);
mergeDeep(resources.pt.translation, servicePackageTranslationsPt);
mergeDeep(resources.es.translation, servicePackageTranslationsEs);
mergeDeep(resources.en.translation, servicePackageManagementTranslationsEn);
mergeDeep(resources.pt.translation, servicePackageManagementTranslationsPt);
mergeDeep(resources.es.translation, servicePackageManagementTranslationsEs);
mergeDeep(resources.en.translation, consentFormTranslationsEn);
mergeDeep(resources.pt.translation, consentFormTranslationsPt);
mergeDeep(resources.es.translation, consentFormTranslationsEs);
mergeDeep(resources.en.translation, stockTranslationsEn);
mergeDeep(resources.pt.translation, stockTranslationsPt);
mergeDeep(resources.es.translation, stockTranslationsEs);
mergeDeep(resources.en.translation, expenseTranslationsEn);
mergeDeep(resources.pt.translation, expenseTranslationsPt);
mergeDeep(resources.es.translation, expenseTranslationsEs);
mergeDeep(resources.en.translation, vatTranslationsEn);
mergeDeep(resources.pt.translation, vatTranslationsPt);
mergeDeep(resources.es.translation, vatTranslationsEs);
mergeDeep(resources.en.translation, revenueMoloniTranslationsEn);
mergeDeep(resources.pt.translation, revenueMoloniTranslationsPt);
mergeDeep(resources.es.translation, revenueMoloniTranslationsEs);
mergeDeep(resources.en.translation, freeServiceTranslationsEn);
mergeDeep(resources.pt.translation, freeServiceTranslationsPt);
mergeDeep(resources.es.translation, freeServiceTranslationsEs);
mergeDeep(resources.en.translation, serviceComboTranslationsEn);
mergeDeep(resources.pt.translation, serviceComboTranslationsPt);
mergeDeep(resources.es.translation, serviceComboTranslationsEs);
mergeDeep(resources.en.translation, servicePricingTranslationsEn);
mergeDeep(resources.pt.translation, servicePricingTranslationsPt);
mergeDeep(resources.es.translation, servicePricingTranslationsEs);
mergeDeep(resources.en.translation, dashboardTranslationsEn);
mergeDeep(resources.pt.translation, dashboardTranslationsPt);
mergeDeep(resources.es.translation, dashboardTranslationsEs);

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: getInitialLanguage(),
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGE_CODES,
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;
